/* assets/configurator-base-lib.js */

(function (global) {
  'use strict';

  var VERSION = 'fence-v10-credits';

  /* Default fence polygon -- 20x20m closed square. Fence is independent
     of any house foundation so we pick a generous starting envelope; the
     user can drag any corner to reshape and the rest of the engine
     handles arbitrary convex/concave outlines just as in base. */
  var DEFAULT_POLYGON = [
    [-10.0, -10.0],
    [ 10.0, -10.0],
    [ 10.0,  10.0],
    [-10.0,  10.0]
  ];

  var STATE = {
    variant: 1,
    lvl: 1,
    isMetric: true,
    /* Fresh load / restart shows the 3D model. The view is deliberately not
       persisted -- boot() frames it with fitCameraToFootprint() and the 2D
       plan is one tap away. */
    view: '3d',
    opacity2D: 0.15,
    vizStage: 1,
    assemblyStep: 0,
    slopeDeg: 0,
    slopeActive: false,
    mapLocked: false,
    polygon: JSON.parse(JSON.stringify(DEFAULT_POLYGON)),
    /* Fence gaps -- list of openings on the fence perimeter.
       Each entry: { id, edgeIdx, tFrac, width }
       - edgeIdx: which polygon edge (0..n-1) carries the gap
       - tFrac:   center of the gap along the edge (0..1)
       - width:   gap width in metres
       Posts that fall inside a gap are skipped; corner posts are
       skipped only if BOTH adjacent edges have a gap reaching the
       corner (SNAP_TO_CORNER threshold = 0.5 m). */
    gates: [],
    /* User override for post spacing -- null = use variant default,
       a number = use that value (still clamped to per-variant min/max).
       Reset to null whenever the variant changes. */
    postSpacingOverride: null,
    /* User override for the wood-fence panel height (in metres).
       null = use VARIANT_DEFAULTS.wood.panelHeightM. Reset to null
       on variant change. Clamped to [panelHeightMinM, panelHeightMaxM]. */
    woodHeight: null,
    /* Per-variant style override. For WOOD fence:
         'privacy'    -- boards touching (full coverage)
         'spaced'     -- small gap (~5 mm) between boards for ventilation
         'shadow_box' -- boards alternating on both sides of rails
                        (a.k.a. board-on-board / good-neighbor fence)
         'picket'     -- wide decorative gap, classic front-yard look
       Vinyl / steel / concrete-panel variants ignore this for now
       (they have only one canonical style each). */
    woodStyle: 'privacy'
  };
  var _nextGateId = 1;
  /* When true, clicks on the fence canvas insert a new gap on the
     closest edge instead of selecting a corner / panning the map. */
  var _gapInsertMode = false;
  var GAP_SNAP_TO_CORNER_M = 0.5;

  var DEFAULTS = JSON.parse(JSON.stringify(STATE));

  var MIN_DIM = 1.0;
  var MAX_COORD = 30.0;
  var SNAP_M = 0.050;

  var VARIANT_DEFAULTS = {
    /* Fence variants -- concrete blocks / steel / gabion. Kept in the
       same VARIANT_DEFAULTS map so getVariantDefaults() works the
       same way as in base. Quantities and component identifiers will
       be wired up once the products/fence.json metafields are filled
       in Shopify. For now the engineering parameters here drive
       2D/3D rendering and the future BoQ. */
    /* Variant 1 -- WOOD. US stick-built privacy fence (most common in
       suburban USA). Built in-place by the carpenter, not from prefab
       panels, so on slope the rails follow grade (RACKED) and the
       pickets stay plumb -- each picket's top sliding along the rail
       line, dropping by pitch x tan(slope) per step.

         - 4x4 PT pine posts (3.5" actual = 89 mm) at 8 ft (2.44 m) o.c.
         - Posts sit ON adjustable post bases (FFP01001, 100x160 mm)
           anchored in the concrete footing -- above-grade installation,
           wood never enters the soil. Brackets are visible ~60 mm
           between post bottom and grade.
         - 6 ft (1.83 m) panel height; mid rail added when panelH >= 1.83 m
         - 2x4 PT rails (1.5" x 3.5") top + bottom + optional mid
         - Planed plank pickets, width by style: 6" (160x19, KVR16019)
           for full-coverage styles, 4" (100x19, KVR10019) for the
           decorative picket / open-board styles. Dog-ear tops on all.
         - Pyramid post cap (FEC00023, generic -- material chosen at budgeting)
         - Penetrating wood sealer (MFP00001), perimeter x height x 2 sides
         - Posts anchored in 12" x 28" hole: 4" gravel drain + 24" concrete

       Style switch reshapes only the picket pattern:
         privacy / spaced / shadow_box / picket. */
    wood: {
      postSpacingM:        2.440,             /* 8 ft */
      postSpacingMinM:     1.830,             /* 6 ft */
      postSpacingMaxM:     2.440,             /* 8 ft (US standard limit) */
      kickHeightM:         0.000,
      panelHeightM:        1.830,             /* 6 ft default */
      panelHeightMinM:     0.900,             /* 3 ft (low picket) */
      panelHeightMaxM:     2.400,             /* 8 ft (tall privacy) */
      postSideM:           0.089,             /* 4x4 actual */
      postExposedExtraM:   0.080,             /* post extends above top rail */
      postEmbedM:          0.600,             /* 24" concrete embed */
      excavationDepthM:    0.700,             /* gravel 0.10 + concrete 0.60 */
      excavationSideM:     0.300,             /* 12" post-hole auger drill --
                                                 interpreted as DIAMETER for
                                                 cylindrical fence post holes */
      gravelLayerM:        0.100,
      railSideM:           0.038,             /* 2x4 thickness (1.5") */
      railHeightM:         0.089,             /* 2x4 width (3.5") */
      pickWidthM:          0.100,             /* 4" planed plank (100mm) */
      pickThicknessM:      0.019,             /* 3/4" */
      picketBottomClearM:  0.060,             /* 60 mm = matches bracket top.
                                                 Pickets + rails sit ABOVE the
                                                 visible part of the post base
                                                 bracket, never overlap it. */
      capHeightM:          0.050,
      bracketVisibleM:     0.060,             /* adjustable post base (FFP01001)
                                                 height visible above concrete */
      bracketEmbedM:       0.040              /* anchor stub embedded in concrete */
    },
    /* Variant 2 -- VINYL. Hollow PVC posts (5x5 in ~ 0.13 m) at 8 ft.
       Standard 6 ft privacy panel slides into routed slots. */
    vinyl: {
      postSpacingM:      2.400,
      postSpacingMinM:   1.500,
      postSpacingMaxM:   3.000,
      kickHeightM:       0.000,
      panelHeightM:      1.800,
      postSideM:         0.130,
      panelThicknessM:   0.030,
      excavationDepthM:  0.600,
      gravelLayerM:      0.100
    },
    /* Variant 3 -- CONCRETE BLOCKS. Split-face block posts + dwarf wall
       (= podezdivka) + panel above. Classic CZ build. */
    concrete_blocks: {
      postSpacingM:      2.000,
      postSpacingMinM:   1.500,
      postSpacingMaxM:   2.500,
      kickHeightM:       1.000,
      panelHeightM:      1.000,
      postSideM:         0.200,
      panelThicknessM:   0.050,
      excavationDepthM:  0.500,
      gravelLayerM:      0.100
    },
    /* Variant 4 -- CHAIN LINK. Galvanised mesh stretched between
       slim steel line posts at 10 ft (~3 m). 4-6 ft height typical;
       we use 1.8 m as a generic privacy/security height. */
    chain_link: {
      postSpacingM:      3.000,
      postSpacingMinM:   2.000,
      postSpacingMaxM:   3.500,
      kickHeightM:       0.000,
      panelHeightM:      1.700,         /* mesh fabric height */
      barbedWireH:       0.250,         /* barbed strand above the mesh */
      postSideM:         0.050,         /* tubular slim line post */
      panelThicknessM:   0.010,
      excavationDepthM:  0.500,
      gravelLayerM:      0.000,
      footingDiameterM:  0.200
    },
    /* Variant 5 -- STEEL PANEL. Pre-fabricated welded steel panels
       (3D mesh / vertical bar) between square steel posts at ~2.5 m. */
    steel_panel: {
      postSpacingM:      2.500,
      postSpacingMinM:   2.000,
      postSpacingMaxM:   3.000,
      kickHeightM:       0.000,
      panelHeightM:      2.000,
      postSideM:         0.060,
      panelThicknessM:   0.020,
      excavationDepthM:  0.500,
      gravelLayerM:      0.000,
      footingDiameterM:  0.200
    },
    /* Variant 6 -- GABION. Wire cages filled with stone, 30 cm wide. */
    gabion: {
      postSpacingM:      2.000,
      postSpacingMinM:   1.500,
      postSpacingMaxM:   2.500,
      kickHeightM:       0.000,
      panelHeightM:      2.000,
      postSideM:         0.300,
      panelThicknessM:   0.300,
      excavationDepthM:  0.300,
      gravelLayerM:      0.100,
      cageWireKgPerM3:   8.000,
      stoneFillKgPerM3:  1700
    },
    /* Variant 7 -- CONCRETE PANEL. Pre-cast concrete panels stacked
       between H-section concrete posts at 2 m. Heavy, no dwarf wall
       needed because the panels themselves go down to grade. */
    concrete_panel: {
      postSpacingM:      2.000,
      postSpacingMinM:   1.800,
      postSpacingMaxM:   2.500,
      kickHeightM:       0.000,
      panelHeightM:      2.000,
      postSideM:         0.150,
      panelThicknessM:   0.050,
      excavationDepthM:  0.700,
      gravelLayerM:      0.100
    }
  };

  var VARIANT_LVL_OVERRIDES = {
    /* Fence variants don't use construction-level tiers; this map is
       kept (empty) so getVariantDefaults still resolves cleanly. */
  };

  function getVariantDefaults(variantKey, lvl) {
    var base = VARIANT_DEFAULTS[variantKey];
    if (!base) return {};
    var merged = {};
    for (var k in base) if (base.hasOwnProperty(k)) merged[k] = base[k];
    var ov = VARIANT_LVL_OVERRIDES[variantKey] && VARIANT_LVL_OVERRIDES[variantKey][lvl];
    if (ov) {
      for (var k2 in ov) if (ov.hasOwnProperty(k2)) merged[k2] = ov[k2];
    }
    return merged;
  }

  /* Return the post-spacing range (min, max, default) for the
     currently-selected fence variant. Used by both the renderer
     (clamps STATE.postSpacingOverride) and the spacing slider UI. */
  function getCurrentSpacingRange() {
    var key = _currentVariantKey();
    var d = getVariantDefaults(key, STATE.lvl);
    return {
      def: d.postSpacingM    || 2.0,
      min: d.postSpacingMinM || 1.5,
      max: d.postSpacingMaxM || 3.0
    };
  }

  /* Return the spacing value actually used by the renderer for the
     current variant -- STATE.postSpacingOverride if set (and inside
     range), otherwise the variant default. */
  function getCurrentSpacing() {
    var r = getCurrentSpacingRange();
    var ov = STATE.postSpacingOverride;
    if (ov == null || !isFinite(ov)) return r.def;
    if (ov < r.min) return r.min;
    if (ov > r.max) return r.max;
    return ov;
  }

  /* Panel height for the WOOD variant.
     Returns STATE.woodHeight clamped to [min,max] if set, else the
     variant default. For non-wood variants we still return the
     variant's panelHeightM so callers that don't know about wood
     specifics get a sensible value. */
  function getCurrentHeightRange() {
    var d = getVariantDefaults('wood', STATE.lvl) || {};
    return {
      def: d.panelHeightM    || 1.830,
      min: d.panelHeightMinM || 0.900,
      max: d.panelHeightMaxM || 2.400
    };
  }

  function getCurrentHeight() {
    if (STATE.variant !== 1) {
      var dd = getVariantDefaults(_currentVariantKey(), STATE.lvl) || {};
      return dd.panelHeightM || 1.8;
    }
    var r = getCurrentHeightRange();
    var ov = STATE.woodHeight;
    if (ov == null || !isFinite(ov)) return r.def;
    if (ov < r.min) return r.min;
    if (ov > r.max) return r.max;
    return ov;
  }

  function setWoodHeight(h) {
    if (h == null || isNaN(h)) {
      STATE.woodHeight = null;
    } else {
      var r = getCurrentHeightRange();
      STATE.woodHeight = Math.max(r.min, Math.min(r.max, h));
    }
    if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
    refreshUI();
    try { _ProjectStore && _ProjectStore.publish && _ProjectStore.publish(); } catch (e) {}
  }

  function _dbg(msg) {
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      global.Configurator.log.log('[base-lib] ' + msg);
    }
  }

  /* Units-aware snap step: 5 cm in metric, 2" in imperial. Both land on
     whole metres / whole feet (20 x 5 cm = 1 m; 6 x 2" = 1 ft) so the
     grids stay clean across the unit toggle. Walls / blocks are cut to
     size -- dimensions do NOT snap to whole block widths. */
  function snap(v) {
    var s = STATE.isMetric ? 0.05 : (2 * 0.0254);
    return Math.round(v / s) * s;
  }

  /* Re-snap every polygon vertex to the active grid. Called from
     toggleUnits so the model re-models (metric 3.678 m -> toggle ->
     3.70 m), not just the label. */
  function snapPolygonToGrid() {
    var poly = STATE.polygon;
    if (!poly || !poly.length) return;
    for (var i = 0; i < poly.length; i++) {
      poly[i][0] = snap(poly[i][0]);
      poly[i][1] = snap(poly[i][1]);
    }
  }

  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  /* fmtDim -- single dimension formatter with the unit baked into the
     string. Metric returns millimetres with thin-space thousand groups
     ("3 700 mm"); imperial returns architectural feet-inches with a
     hyphen ("12'-6\""). Use this for dimension labels on the canvas, on
     length / height readouts, and anywhere a single dimension is shown
     standalone. Do NOT use it for areas (m^2/ft^2), volumes (m^3/ft^3) or
     BoQ length columns -- those stay decimal in metres / feet. */
  function fmtDim(meters) {
    if (STATE.isMetric) {
      var mm = Math.round(meters * 1000);
      return mm.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' mm';
    }
    var totalInches = Math.round(meters / 0.0254);
    var ft = Math.floor(totalInches / 12);
    var inch = totalInches - ft * 12;
    return ft + "'-" + inch + '"';
  }

  /* Legacy helper kept for callers that explicitly want decimal m/ft
     (e.g. BoQ length columns, area/volume composites). */
  function fmtLen(meters, opts) {
    opts = opts || {};
    var precision = (opts.precision == null) ? 2 : opts.precision;
    if (STATE.isMetric) {
      return meters.toFixed(precision);
    }
    var feet = meters * 3.28084;
    return feet.toFixed(precision);
  }

  function unitLabel() {
    return STATE.isMetric ? 'm' : 'ft';
  }

  function getFootprintPolygon() {
    var poly = STATE.polygon;
    if (!poly || !poly.length) return [];
    var out = new Array(poly.length);
    for (var i = 0; i < poly.length; i++) {
      out[i] = [poly[i][0], poly[i][1]];
    }
    return out;
  }

  function getBBox() {
    var poly = STATE.polygon;
    if (!poly || !poly.length) return { L: 0, W: 0 };
    var xMin =  Infinity, xMax = -Infinity;
    var zMin =  Infinity, zMax = -Infinity;
    for (var i = 0; i < poly.length; i++) {
      var x = poly[i][0], z = poly[i][1];
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
    return { L: xMax - xMin, W: zMax - zMin };
  }

  function computeArea() {
    var poly = getFootprintPolygon();
    var a = 0;
    for (var i = 0; i < poly.length; i++) {
      var j = (i + 1) % poly.length;
      a += poly[i][0] * poly[j][1];
      a -= poly[j][0] * poly[i][1];
    }
    return Math.abs(a) / 2;
  }

  function computePerimeter() {
    var poly = getFootprintPolygon();
    var p = 0;
    for (var i = 0; i < poly.length; i++) {
      var j = (i + 1) % poly.length;
      var dx = poly[j][0] - poly[i][0];
      var dy = poly[j][1] - poly[i][1];
      p += Math.sqrt(dx*dx + dy*dy);
    }
    return p;
  }

  function computeStripLength() {
    return computePerimeter();
  }

  function _pointInPolygonOrBoundary(x, z, poly) {
    var n = poly.length;
    var EPS = 0.001;
    for (var i = 0; i < n; i++) {
      var a = poly[i], b = poly[(i + 1) % n];
      var dx = b[0] - a[0], dz = b[1] - a[1];
      var lenSq = dx * dx + dz * dz;
      if (lenSq < 1e-9) continue;
      var t = ((x - a[0]) * dx + (z - a[1]) * dz) / lenSq;
      var tc = t < 0 ? 0 : (t > 1 ? 1 : t);
      var px = a[0] + tc * dx;
      var pz = a[1] + tc * dz;
      if (Math.hypot(x - px, z - pz) < EPS) return true;
    }
    var inside = false;
    for (var i = 0, j = n - 1; i < n; j = i++) {
      var xi = poly[i][0], zi = poly[i][1];
      var xj = poly[j][0], zj = poly[j][1];
      if ((zi > z) !== (zj > z) &&
          x < (xj - xi) * (z - zi) / (zj - zi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  function _distanceToPolygonPerimeter(x, z, poly) {
    var minD = Infinity;
    var n = poly.length;
    for (var i = 0; i < n; i++) {
      var a = poly[i], b = poly[(i + 1) % n];
      var dx = b[0] - a[0], dz = b[1] - a[1];
      var lenSq = dx * dx + dz * dz;
      var px, pz;
      if (lenSq < 1e-9) {
        px = a[0]; pz = a[1];
      } else {
        var t = ((x - a[0]) * dx + (z - a[1]) * dz) / lenSq;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        px = a[0] + t * dx;
        pz = a[1] + t * dz;
      }
      var d = Math.hypot(x - px, z - pz);
      if (d < minD) minD = d;
    }
    return minD;
  }

  function distributePoints(spacing) {
    var poly = getFootprintPolygon();
    if (!poly.length || spacing <= 0) return [];
    var n = poly.length;

    var pts = [];
    for (var i = 0; i < n; i++) {
      var a = poly[i], b = poly[(i + 1) % n];
      var dx = b[0] - a[0], dz = b[1] - a[1];
      var elen = Math.hypot(dx, dz);
      var segs = Math.max(1, Math.ceil(elen / spacing));
      for (var k = 0; k < segs; k++) {
        var t = k / segs;
        pts.push({ x: a[0] + t * dx, z: a[1] + t * dz });
      }
    }

    var xMin =  Infinity, xMax = -Infinity;
    var zMin =  Infinity, zMax = -Infinity;
    for (var i = 0; i < n; i++) {
      var px = poly[i][0], pz = poly[i][1];
      if (px < xMin) xMin = px;
      if (px > xMax) xMax = px;
      if (pz < zMin) zMin = pz;
      if (pz > zMax) zMax = pz;
    }
    var L = xMax - xMin;
    var W = zMax - zMin;
    if (L < 0.5 || W < 0.5) return pts;

    var nX = Math.max(1, Math.ceil(L / spacing));
    var nZ = Math.max(1, Math.ceil(W / spacing));
    var sx = L / nX;
    var sz = W / nZ;
    var minStep = Math.min(sx, sz);
    var perimMargin = minStep * 0.45;
    var dedupTol   = minStep * 0.45;

    for (var i = 1; i < nX; i++) {
      for (var j = 1; j < nZ; j++) {
        var x = xMin + i * sx;
        var z = zMin + j * sz;
        if (!_pointInPolygonOrBoundary(x, z, poly)) continue;
        if (_distanceToPolygonPerimeter(x, z, poly) < perimMargin) continue;
        var skip = false;
        for (var m = 0; m < pts.length; m++) {
          if (Math.hypot(pts[m].x - x, pts[m].z - z) < dedupTol) {
            skip = true; break;
          }
        }
        if (!skip) pts.push({ x: x, z: z });
      }
    }

    return pts;
  }

  function _polygonBBoxIntersectsRect(poly, x0, z0, x1, z1) {
    var polyX0 =  Infinity, polyX1 = -Infinity;
    var polyZ0 =  Infinity, polyZ1 = -Infinity;
    for (var i = 0; i < poly.length; i++) {
      var p = poly[i];
      if (p[0] < polyX0) polyX0 = p[0];
      if (p[0] > polyX1) polyX1 = p[0];
      if (p[1] < polyZ0) polyZ0 = p[1];
      if (p[1] > polyZ1) polyZ1 = p[1];
    }
    if (x1 < polyX0 || x0 > polyX1) return false;
    if (z1 < polyZ0 || z0 > polyZ1) return false;
    return true;
  }

  function _clipSegmentToPolygon(x1, z1, x2, z2, poly) {
    var dx = x2 - x1, dz = z2 - z1;
    var tList = [0, 1];
    var n = poly.length;
    for (var i = 0; i < n; i++) {
      var pa = poly[i], pb = poly[(i + 1) % n];
      var ex = pb[0] - pa[0], ez = pb[1] - pa[1];
      var denom = dx * ez - dz * ex;
      if (Math.abs(denom) < 1e-12) continue;
      var s = ((pa[0] - x1) * ez - (pa[1] - z1) * ex) / denom;
      var t = ((pa[0] - x1) * dz - (pa[1] - z1) * dx) / denom;
      if (s > 1e-9 && s < 1 - 1e-9 && t > 1e-9 && t < 1 - 1e-9) {
        tList.push(s);
      }
    }
    tList.sort(function (a, b) { return a - b; });
    var segments = [];
    for (var i = 0; i < tList.length - 1; i++) {
      var t0 = tList[i], t1 = tList[i + 1];
      if (t1 - t0 < 1e-6) continue;
      var midT = (t0 + t1) / 2;
      var mx = x1 + dx * midT;
      var mz = z1 + dz * midT;
      if (_pointInPolygonOrBoundary(mx, mz, poly)) {
        segments.push({
          x1: x1 + dx * t0, z1: z1 + dz * t0,
          x2: x1 + dx * t1, z2: z1 + dz * t1
        });
      }
    }
    return segments;
  }

  function distributeMeshSheets(sheetL, sheetW, overlap) {
    var poly = getFootprintPolygon();
    if (!poly.length) return [];
    var xMin =  Infinity, xMax = -Infinity;
    var zMin =  Infinity, zMax = -Infinity;
    for (var i = 0; i < poly.length; i++) {
      var p = poly[i];
      if (p[0] < xMin) xMin = p[0];
      if (p[0] > xMax) xMax = p[0];
      if (p[1] < zMin) zMin = p[1];
      if (p[1] > zMax) zMax = p[1];
    }
    var L = xMax - xMin;
    var W = zMax - zMin;
    if (L < 0.1 || W < 0.1) return [];
    var stepX = sheetL - overlap;
    var stepZ = sheetW - overlap;
    if (stepX <= 0 || stepZ <= 0) return [];
    var sheets = [];
    var idx = 0;
    var x = xMin;
    while (x < xMax) {
      var z = zMin;
      while (z < zMax) {
        var x0 = x, x1 = x + sheetL;
        var z0 = z, z1 = z + sheetW;
        if (_polygonBBoxIntersectsRect(poly, x0, z0, x1, z1)) {
          if (_sheetCoversPolygonArea(x0, z0, x1, z1, poly)) {
            sheets.push({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, L: sheetL, W: sheetW, index: idx });
            idx++;
          }
        }
        z += stepZ;
      }
      x += stepX;
    }
    return sheets;
  }

  function _sheetCoversPolygonArea(x0, z0, x1, z1, poly) {
    if (_pointInPolygonOrBoundary((x0 + x1) / 2, (z0 + z1) / 2, poly)) return true;
    var corners = [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];
    for (var i = 0; i < 4; i++) {
      if (_pointInPolygonOrBoundary(corners[i][0], corners[i][1], poly)) return true;
    }
    for (var i = 0; i < poly.length; i++) {
      var p = poly[i];
      if (p[0] >= x0 && p[0] <= x1 && p[1] >= z0 && p[1] <= z1) return true;
    }
    return false;
  }


  /* Canonical variant keys, indexed by STATE.variant - 1.
     Order matches FENCE_VARIANT_COUNT enum + Shopify variant order. */
  var FENCE_VARIANT_KEYS = [
    'wood',
    'vinyl',
    'concrete_blocks',
    'chain_link',
    'steel_panel',
    'gabion',
    'concrete_panel'
  ];

  function variantKey() {
    return FENCE_VARIANT_KEYS[STATE.variant - 1] || 'wood';
  }

  /* ==================================================================
     FENCE BoQ -- three variants (concrete_blocks / steel / gabion).
     All share the same geometric scaffolding:
       - perimeter (m)        = total fence length around the polygon
       - postCount            = corner posts + intermediate posts at
                                postSpacing along each edge
       - panelMeters          = perimeter (= fence length to fill with
                                panels / dwarf wall / gabion mesh)
       - footingM3 per post   = local concrete footing volume
       - gravelM3 / geotextilM2 per post = footing prep

     Defaults below are pragmatic engineering values; user can later
     dial in via metafield overrides on each Shopify variant.
     ================================================================== */

  function _fenceGeometry() {
    /* Use the same post + segment enumeration the renderer uses so
       BoQ totals exactly match what is drawn (gap-aware, snap-to-
       corner). Fall back to a simple perimeter / spacing count if
       the renderer is not yet available. */
    var poly = STATE.polygon;
    var perim = computePerimeter();
    var d = getVariantDefaults(_currentVariantKey(), STATE.lvl) || {};
    var spacing = getCurrentSpacing();

    var postCount = 0;
    var panelMeters = 0;
    var gapMeters = 0;

    if (global.BaseRenderer && typeof global.BaseRenderer._enumerateFencePosts === 'function') {
      var info = global.BaseRenderer._enumerateFencePosts(poly, spacing);
      postCount = info.posts.length;
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        var coveredOnEdge = 0;
        for (var s = 0; s < ed.segments.length; s++) {
          panelMeters += (ed.segments[s].end - ed.segments[s].start);
          coveredOnEdge += (ed.segments[s].end - ed.segments[s].start);
        }
        gapMeters += Math.max(0, ed.len - coveredOnEdge);
      }
    } else {
      var n = poly.length;
      for (var i = 0; i < n; i++) {
        var A = poly[i];
        var B = poly[(i + 1) % n];
        var dx = B[0] - A[0], dy = B[1] - A[1];
        var edgeLen = Math.sqrt(dx * dx + dy * dy);
        var inter = Math.max(0, Math.floor(edgeLen / spacing) - 1);
        postCount += inter;
      }
      postCount += n;
      panelMeters = perim;
    }

    return {
      perim:       perim,
      postCount:   postCount,
      panelCount:  postCount,
      panelMeters: panelMeters,
      gapMeters:   gapMeters,
      gapCount:    (STATE.gates || []).length,
      spacing:     spacing,
      params:      d
    };
  }

  function computeBoQ_ConcreteBlocks() {
    var g = _fenceGeometry();
    var d = g.params;

    /* Footing per post: 0.3x0.3x0.5 concrete + 0.4x0.4x0.1 gravel +
       0.5x0.5 geotextile underneath. */
    var footingConcM3 = 0.3 * 0.3 * 0.5 * g.postCount;
    var footingGravelM3 = 0.4 * 0.4 * 0.1 * g.postCount;
    var geotextilM2 = 0.5 * 0.5 * g.postCount;

    /* Dwarf wall (= podezdivka) made of formwork blocks 400x250x200.
       1m run / 0.4m = 2.5 blocks per metre per course; kickHeight
       (1m) / 0.25m block height = 4 courses -> 10 blocks per metre of
       dwarf wall. The wall runs between posts inside segments only,
       so its length = sum of segment lengths minus post footprints. */
    var postSide = d.postSideM || 0.2;
    var dwarfLengthM = Math.max(0, g.panelMeters - g.postCount * postSide);
    var kickH = (d.kickHeightM || 1.0);
    var formworkBlocks = Math.ceil(dwarfLengthM * (1 / 0.4) * (kickH / 0.25));

    /* Pillar blocks for the posts. Each post = 2m total height /
       block 0.3m = 7 blocks. */
    var pillarBlocks = g.postCount * Math.ceil(2.0 / 0.3);

    /* Coping stone runs the length of the dwarf wall. */
    var copingM = dwarfLengthM;

    /* Cap on top of each post. */
    var postCaps = g.postCount;

    /* Brackets for the panel -- 2 per post (= top + bottom hold). */
    var fixingBrackets = g.postCount * 2;

    /* Rebar inside pillars: 4 verticals x 2.2m (2m + 0.2m overlap into
       footing) per post. */
    var rebarM = g.postCount * 4 * 2.2;

    /* Stirrups: 10 per post. */
    var stirrups = g.postCount * 10;

    return [
      ['FEC00001', footingConcM3,   footingConcM3   * 1.30795],
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      ['FEC00004', formworkBlocks,  formworkBlocks],
      ['FEC00005', pillarBlocks,    pillarBlocks],
      ['FEC00006', copingM,         copingM         * 3.28084],
      ['FEC00007', postCaps,        postCaps],
      ['FEC00017', fixingBrackets,  fixingBrackets],
      ['FEC00012', rebarM,          rebarM          * 3.28084],
      ['FEC00013', stirrups,        stirrups]
    ];
  }

  function computeBoQ_Steel() {
    var g = _fenceGeometry();
    var d = g.params;
    var spacing = getCurrentSpacing();

    /* Footing per post: smaller -- 0.2x0.2x0.5 gravel only (no concrete
       for steel posts; they're set with quick-set or driven). */
    var footingGravelM3 = 0.2 * 0.2 * 0.5 * g.postCount;
    var geotextilM2 = 0.3 * 0.3 * g.postCount;

    /* Panels count = number of full bays inside segments (gap-aware).
       Approximation: panelMeters / spacing, rounded up. */
    var panels = Math.max(0, Math.ceil(g.panelMeters / spacing));
    var brackets = g.postCount * 2;       /* top + bottom bracket per post */
    var fastenerSets = g.postCount;       /* one set per post */

    return [
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      ['FEC00015', panels,          panels],
      ['FEC00016', brackets,        brackets],
      ['FEC00017', fastenerSets,    fastenerSets]
    ];
  }

  function computeBoQ_Gabion() {
    var g = _fenceGeometry();
    var d = g.params;
    var panelH = d.panelHeightM || 2.0;
    var postSide = d.postSideM || 0.3;  /* gabion cage width */

    /* Footing: 0.3x0.3x0.3 gravel + geotextile under each post. */
    var footingGravelM3 = 0.3 * 0.3 * 0.3 * g.postCount;
    var geotextilM2 = 0.4 * 0.4 * g.postCount;

    /* Steel support posts inside the cage. */
    var supportPosts = g.postCount;

    /* Gabion mesh: 2000x2000 panels. Mesh wraps around the cage so
       (panelH / 2m) layers x (panelMeters / 2m) horizontal spans, plus
       inner partitions every ~1m for stability -- approximation:
       2 x panelMeters / 2m. */
    var meshPanels = Math.ceil(2 * g.panelMeters / 2.0 * (panelH / 2.0));

    /* Spacer hooks -- roughly 10 per m^3 of cage volume to keep the
       mesh walls apart. Volume uses panelMeters (gap-aware). */
    var stoneFillM3 = panelH * postSide * g.panelMeters;
    var spacers = Math.ceil(stoneFillM3 * 10);

    /* Reinforcing welded wire mesh sheets -- 1 per 2m of cage. */
    var weldedMeshPcs = Math.ceil(g.panelMeters / 2.0);

    return [
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      ['FEC00008', supportPosts,    supportPosts],
      ['FEC00009', meshPanels,      meshPanels],
      ['FEC00010', spacers,         spacers],
      ['FEC00011', stoneFillM3,     stoneFillM3     * 1.30795],
      ['FEC00014', weldedMeshPcs,   weldedMeshPcs]
    ];
  }

  /* MVP BoQ for the four "simple" variants -- they reuse common items
     where it makes sense (gravel/geotextile footings, fasteners) and
     placeholder FEC items for materials we'll wire up per variant in
     follow-up turns. Each variant has its own function so we can
     refine quantities + item codes one variant at a time. */

  /* -------------------------------------------------------------
     WOOD-fence picket style presets.
     Returns the picket pitch (centre-to-centre) and how many sides
     of the rails carry pickets:
       - privacy     : one side, pitch = picket width (boards touch)
       - spaced      : one side, pitch = width + 5 mm gap
       - shadow_box  : both sides, pitch = 2 x width;
                       back side offset by pitch / 2 -> boards on
                       opposite sides cover each other's gaps
                       (= board-on-board / good-neighbor fence)
       - picket      : one side, pitch = width + 70 mm decorative gap
     ------------------------------------------------------------- */
  /* Per-style geometry. boardW / boardCode select the plank width:
     full-coverage styles (privacy / spaced / shadow_box) use the 6"
     plank (KVR16019, 160 mm); decorative styles (picket / open_board)
     use the narrower 4" plank (KVR10019, 100 mm) for a finer, busier
     rhythm. pitch is derived from the style's own board width. */
  function _getWoodStyleParams(style) {
    var W6 = 0.160, W4 = 0.100;
    switch (style) {
      case 'spaced':     return { boardW: W6, boardCode: 'KVR16019', pitch: W6 + 0.012, sides: 1, offsetFrac: 0   };
      case 'shadow_box': return { boardW: W6, boardCode: 'KVR16019', pitch: W6 * 2.000, sides: 2, offsetFrac: 0.5 };
      case 'picket':     return { boardW: W4, boardCode: 'KVR10019', pitch: W4 + 0.070, sides: 1, offsetFrac: 0   };
      case 'open_board': return { boardW: W4, boardCode: 'KVR10019', pitch: W4 + 0.035, sides: 1, offsetFrac: 0   };
      case 'privacy':
      default:           return { boardW: W6, boardCode: 'KVR16019', pitch: W6 + 0.003, sides: 1, offsetFrac: 0   };
    }
  }

  function computeBoQ_Wood() {
    var g = _fenceGeometry();
    var d = g.params;
    var style = STATE.woodStyle || 'privacy';

    /* Counts driven by gap-aware enumeration */
    var postCount   = g.postCount;
    var spacing     = g.spacing || 2.440;
    var panelMeters = g.panelMeters;
    var bays        = Math.max(0, Math.round(panelMeters / spacing));

    /* Dimensions */
    var postSide   = d.postSideM           || 0.089;
    var panelH     = getCurrentHeight();
    var postExtra  = d.postExposedExtraM   || 0.080;
    var bracketH   = d.bracketVisibleM     || 0.060;
    var concEmbed  = d.postEmbedM          || 0.600;
    var excavD     = d.excavationDepthM    || 0.700;
    var excavS     = d.excavationSideM     || 0.300;
    var gravelL    = d.gravelLayerM        || 0.100;
    var pickBotClr = d.picketBottomClearM  || 0.060;

    /* Posts -- 4x4 PT (KVH10100). Above-grade only: post sits inside
       the bracket U-cavity with its base on the steel base plate
       (~5 mm thick), so lumber length = panelH + postExtra - wallT. */
    var bracketWallT = 0.005;
    var postLenM   = Math.max(0, panelH + postExtra - bracketWallT);
    var totalPostM = postCount * postLenM;

    /* Footings: cylindrical post-hole auger (excavS interpreted as
       DIAMETER). Concrete fills 0.60 m below grade above a 0.10 m
       gravel drainage layer; bracket anchor stub embeds in the
       concrete. */
    var holeR      = excavS / 2;                    /* hole radius (m) */
    var holeArea   = Math.PI * holeR * holeR;       /* hole cross-section (m^2) */
    var concreteM3 = postCount * holeArea * concEmbed;
    var gravelM3   = postCount * holeArea * gravelL;
    var geotextM2  = postCount * holeArea;          /* circle at bottom of hole */
    /* Excavation: full hole volume x 30 % bulking. */
    var excavM3    = postCount * holeArea * excavD * 1.30;

    /* Rails -- 2x4 PT (KVH10060). 2 per bay; mid rail when panelH >= 1.83 m. */
    var hasMid      = (panelH >= 1.829);
    var railsPerBay = hasMid ? 3 : 2;
    var totalRailM  = bays * spacing * railsPerBay;

    /* Pickets -- plank width + code depend on style (4" for decorative,
       6" for full-coverage). */
    var styleP        = _getWoodStyleParams(style);
    var picketsPerBay = Math.max(1, Math.floor(spacing / styleP.pitch)) * styleP.sides;
    var totalPickets  = bays * picketsPerBay;
    var picketLenEa   = Math.max(0, panelH - pickBotClr);
    var totalPicketM  = totalPickets * picketLenEa;
    var plankCode     = styleP.boardCode;

    /* Hardware:
         Adjustable post base (FFP01001): 1 per post. The kit includes
         an integrated threaded anchor rod + nut + washer, so we do NOT
         separately count anchor screws.
         Lag screws (FAS60080, 6x80): 4 per post (bracket -> post side flanges).
         Fence rail brackets (FEC00024): 2 per rail (one at each end) x
         railsPerBay x bays. Replaces toe-screwing -- eliminates
         separate rail-to-post screws.
         Wood screws (FAS50060, 5x60): merged total of picket-to-rail
         fasteners (2 per rail crossing per picket), rail-bracket
         fasteners (7 per bracket -- matches typical kit ratio), and
         post-cap fasteners (2 per cap).
         Post caps (FEC00023): 1 per post. */
    var postBases    = postCount;
    var lagScrews    = postCount * 4;
    var postCaps     = postCount;
    var railBrackets = bays * railsPerBay * 2;
    var bracketScrews = railBrackets * 7;
    /* One screw per plankxrail crossing, centred on both axes (matches
       the assembly visual): pickets x rails-per-bay. */
    var picketScrews = totalPickets * railsPerBay;
    var capScrews    = postCount * 2;
    var allFAS50060  = picketScrews + bracketScrews + capScrews;

    /* Penetrating wood sealer (MFP00001) -- both sides + tops, single coverage.
       User adds extra coats in their own budgeting; we report m^2 to cover once. */
    var stainAreaM2 = panelMeters * panelH * 2;

    /* Imperial conversion factors. Volumes are cubic YARDS, as in the
       central catalog (M3/YD3); FT3 is kept only for insulation. */
    var M  = 3.28084;
    var M2 = 10.7639;
    var M3 = 1.30795;

    return [
      /* Site work */
      ['FEE00001', excavM3,      excavM3      * M3],   /* Excavation */
      ['FEC00001', concreteM3,   concreteM3   * M3],   /* Cast-in-place concrete C20/25 */
      ['FEC00002', gravelM3,     gravelM3     * M3],   /* Gravel base 16-32 mm */
      ['FEC00003', geotextM2,    geotextM2    * M2],   /* Geotextile */
      /* Post anchorage */
      ['FFP01001', postBases,    postBases],           /* Adjustable post base 100x160 */
      /* Lumber */
      ['KVH10100', totalPostM,   totalPostM   * M],    /* 4x4 PT post */
      ['KVH10060', totalRailM,   totalRailM   * M],    /* 2x4 PT rail */
      [plankCode,  totalPicketM, totalPicketM * M],    /* planed plank (4" or 6" per style) */
      /* Accessories */
      ['FEC00023', postCaps,     postCaps],            /* Fence post cap */
      ['FEC00024', railBrackets, railBrackets],        /* Fence rail bracket */
      /* Fasteners */
      ['FAS60080', lagScrews,    lagScrews],           /* 6x80 wood screw -> bracket-to-post */
      ['FAS50060', allFAS50060,  allFAS50060],         /* 5x60 wood screw -> picket + bracket */
      /* Finish */
      ['MFP00001', stainAreaM2,  stainAreaM2  * M2]    /* Penetrating wood sealer */
    ];
  }

  function computeBoQ_Vinyl() {
    var g = _fenceGeometry();
    var footingGravelM3 = 0.25 * 0.25 * 0.6 * g.postCount;
    var geotextilM2 = 0.4 * 0.4 * g.postCount;
    /* Vinyl posts are usually set in a concrete sleeve for stability. */
    var footingConcM3 = 0.20 * 0.20 * 0.6 * g.postCount;
    var panels = Math.max(0, Math.ceil(g.panelMeters / (g.spacing || 2.4)));
    var posts = g.postCount;
    var fastenerSets = posts;
    return [
      ['FEC00001', footingConcM3,   footingConcM3   * 1.30795],
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      ['FEC00015', panels,          panels],
      ['FEC00016', posts,           posts],
      ['FEC00017', fastenerSets,    fastenerSets]
    ];
  }

  function computeBoQ_ChainLink() {
    var g = _fenceGeometry();
    /* Chain link line posts: 0.20x0.20 concrete footing + small gravel. */
    var footingConcM3 = 0.20 * 0.20 * 0.6 * g.postCount;
    var footingGravelM3 = 0.10 * 0.10 * 0.10 * g.postCount;
    var geotextilM2 = 0.3 * 0.3 * g.postCount;
    /* Mesh fabric -- one running metre per metre of fence (gap-aware). */
    var fabricM = g.panelMeters;
    /* Barbed wire -- single strand running the same length as the fabric. */
    var barbedM = g.panelMeters;
    var posts = g.postCount;
    var fastenerSets = posts;
    return [
      ['FEC00001', footingConcM3,   footingConcM3   * 1.30795],
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      /* Reuse the welded wire mesh code for the chain-link fabric
         until a dedicated FEC item is defined. Barbed strand is
         tracked under the same code for now -- we'll split it out
         once a dedicated FEC entry exists. */
      ['FEC00014', fabricM + barbedM, (fabricM + barbedM) * 3.28084],
      ['FEC00016', posts,           posts],
      ['FEC00017', fastenerSets,    fastenerSets]
    ];
  }

  function computeBoQ_ConcretePanel() {
    var g = _fenceGeometry();
    var d = g.params;
    /* Heavy H-posts need a solid concrete footing. */
    var footingConcM3 = 0.30 * 0.30 * 0.7 * g.postCount;
    var footingGravelM3 = 0.40 * 0.40 * 0.10 * g.postCount;
    var geotextilM2 = 0.5 * 0.5 * g.postCount;
    /* Pre-cast panels -- one stack per bay. Each bay = panelH /
       0.5 m panel-board height, so a 2 m panel = 4 boards. */
    var bays = Math.max(0, Math.ceil(g.panelMeters / (g.spacing || 2.0)));
    var panelBoards = bays * Math.ceil((d.panelHeightM || 2.0) / 0.5);
    /* H-posts (= concrete pillar blocks usable as cores). */
    var posts = g.postCount;
    return [
      ['FEC00001', footingConcM3,   footingConcM3   * 1.30795],
      ['FEC00002', footingGravelM3, footingGravelM3 * 1.30795],
      ['FEC00003', geotextilM2,     geotextilM2     * 10.7639],
      ['FEC00005', posts,           posts],
      /* Panel boards approximated by the formwork-block item for now. */
      ['FEC00004', panelBoards,     panelBoards]
    ];
  }

  function computeBoQ() {
    switch (STATE.variant) {
      case 1: return computeBoQ_Wood();
      case 2: return computeBoQ_Vinyl();
      case 3: return computeBoQ_ConcreteBlocks();
      case 4: return computeBoQ_ChainLink();
      case 5: return computeBoQ_Steel();          /* steel_panel */
      case 6: return computeBoQ_Gabion();
      case 7: return computeBoQ_ConcretePanel();
      default: return [];
    }
  }

  var _publishTimer = null;
  /* Round a quantity to 2 decimals, keeping it a Number (so the budget
     can still sum/format it). Avoids long floating-point tails like
     3530.5591999999997 reaching the BoQ display. */
  function _round2(v) {
    if (typeof v !== 'number' || !isFinite(v)) return v;
    return Math.round(v * 100) / 100;
  }

  function publishBoQToBudget() {
    var rows = computeBoQ();
    var variantData = [[], []];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      /* Row shape: [code, metricQty, imperialQty]. Round both
         quantities to 2 decimals; leave the code untouched. */
      variantData.push([r[0], _round2(r[1]), _round2(r[2])]);
    }
    global.ConfiguratorBoQ = {
      source: 'fence',
      variantData: variantData
    };
    if (typeof global.loadVariantCSV === 'function') global.loadVariantCSV();
  }

  function _debouncedPublishBoQ() {
    if (_publishTimer) clearTimeout(_publishTimer);
    _publishTimer = setTimeout(publishBoQToBudget, 400);
  }

  function getCountForInfoBar() {
    /* For all fence variants we surface the number of posts so the
       user can quickly see how the fence scales with the polygon. */
    var g = _fenceGeometry();
    return g.postCount;
  }

  function getCountLabel() {
    var boot = global.BASE_BOOT || {};
    var i18n = boot.i18n || {};
    return i18n.posts || 'Posts';
  }

  function refreshInfoBar() {
    var area  = computeArea();
    var perim = computePerimeter();
    var count = getCountForInfoBar();
    var bb    = getBBox();

    var elL = document.getElementById('cf-stat-l');
    var elW = document.getElementById('cf-stat-w');
    var elU = document.getElementById('cf-stat-unit');
    if (elL) elL.textContent = fmtDim(bb.L);
    if (elW) elW.textContent = fmtDim(bb.W);
    /* fmtDim bakes the unit into the value ("3 700 mm" / "12'-6\"") so
       the dedicated unit slot would otherwise duplicate it. Blank it. */
    if (elU) elU.textContent = '';

    var elA = document.getElementById('cf-stat-area');
    var elP = document.getElementById('cf-stat-perim');
    var elC = document.getElementById('cf-stat-count');
    var elAu = document.getElementById('cf-stat-area-unit');
    var elPu = document.getElementById('cf-stat-perim-unit');
    var elCl = document.getElementById('cf-stat-count-label');
    var elCi = document.getElementById('cf-stat-count-item');

    if (elA)  elA.textContent  = STATE.isMetric ? area.toFixed(2) : (area * 10.7639).toFixed(2);
    if (elP)  elP.textContent  = fmtDim(perim);
    if (elAu) elAu.textContent = STATE.isMetric ? 'm\u00b2' : 'ft\u00b2';
    if (elPu) elPu.textContent = '';

    if (elCi) {
      if (count == null) {
        elCi.style.visibility = 'hidden';
      } else {
        elCi.style.visibility = '';
        if (elC)  elC.textContent  = String(count);
        if (elCl) elCl.textContent = getCountLabel();
      }
    }

    /* Spacing slot -- show the current post spacing for the active fence
       variant. Read-only display; user changes it via the floating slider. */
    var elSp   = document.getElementById('cf-stat-spacing');
    var elSpU  = document.getElementById('cf-stat-spacing-unit');
    var elSpI  = document.getElementById('cf-stat-spacing-item');
    var elSpS  = document.getElementById('cf-stat-spacing-sep');
    if (elSpI && elSpS) {
      elSpI.style.display = '';
      elSpS.style.display = '';
      var sp = getCurrentSpacing();
      if (elSp) elSp.textContent = fmtDim(sp);
      if (elSpU) elSpU.textContent = '';
    }

    /* Wood-only HEIGHT slot. Column visibility itself is toggled by
       _syncWoodInfoColumns; here we just refresh the readout. */
    if (STATE.variant === 1) {
      var elH  = document.getElementById('cf-stat-height');
      var elHu = document.getElementById('cf-stat-height-unit');
      var h = getCurrentHeight();
      if (elH)  elH.textContent  = fmtDim(h);
      if (elHu) elHu.textContent = '';
      if (_HeightSlider && _HeightSlider._updateFill) _HeightSlider._updateFill();
      _syncStyleColumn();
    }
  }

  function refreshUI(skipPublish) {
    refreshInfoBar();
    if (global.BaseRenderer && typeof global.BaseRenderer.build === 'function') {
      global.BaseRenderer.build();
    }
    if (typeof _applyAssemblyStep === 'function') _applyAssemblyStep();
    positionDragHandles();
    if (typeof _GateDragManager !== 'undefined' && _GateDragManager.reproject) _GateDragManager.reproject();
    if (_VisualizationManager && _VisualizationManager.refreshDimensions) _VisualizationManager.refreshDimensions();
    if (!skipPublish) _debouncedPublishBoQ();
    if (_ProjectStore && _ProjectStore._initialized) _ProjectStore.publish();
  }

  var _UndoHistory = {
    _stack: [],
    _MAX:   50,

    push: function () {
      var snap = {
        polygon: JSON.parse(JSON.stringify(STATE.polygon)),
        variant: STATE.variant
      };
      this._stack.push(snap);
      if (this._stack.length > this._MAX) this._stack.shift();
      this._updateButton();
    },

    pop: function () {
      if (!this._stack.length) return null;
      var snap = this._stack.pop();
      this._updateButton();
      return snap;
    },

    canUndo: function () {
      return this._stack.length > 0;
    },

    /* When the stack is empty the Undo button morphs into a "Restart"
       (reset-to-defaults) control. This holds true both right after a
       page refresh (stack starts empty) and after the user has popped
       every step in their session -- a single, consistent behaviour. */
    isResetMode: function () {
      return this._stack.length === 0;
    },

    pushIfChanged: function (prevPolygon, prevVariant) {
      var sameVariant = (prevVariant === STATE.variant);
      var samePoly = JSON.stringify(prevPolygon) === JSON.stringify(STATE.polygon);
      if (sameVariant && samePoly) return;
      var snap = {
        polygon: JSON.parse(JSON.stringify(prevPolygon)),
        variant: prevVariant
      };
      this._stack.push(snap);
      if (this._stack.length > this._MAX) this._stack.shift();
      this._updateButton();
    },

    clear: function () {
      this._stack = [];
      this._updateButton();
    },

    _updateButton: function () {
      var btn = document.getElementById('cf-btn-undo');
      if (!btn) return;
      var resetMode = this.isResetMode();
      /* Button is always enabled -- when no undo steps remain it
         morphs into a "Restart" control with a different icon. The
         theme should swap the SVG via the cf-undo-reset-mode class. */
      btn.disabled = false;
      btn.style.opacity = '';
      btn.style.cursor  = 'pointer';
      btn.title         = resetMode ? 'Reset to default' : 'Undo';
      btn.setAttribute('aria-label', resetMode ? 'Reset to default' : 'Undo');
      btn.classList.toggle('cf-undo-reset-mode', resetMode);
    }
  };

  function undo() {
    /* Empty stack -> button is in restart mode, clicking it resets the
       whole scene to defaults. Works after a page refresh too. */
    if (_UndoHistory.isResetMode()) {
      reset();
      return;
    }
    var snap = _UndoHistory.pop();
    if (!snap) return;
    STATE.polygon = snap.polygon;
    if (typeof snap.variant === 'number') STATE.variant = snap.variant;
    if (typeof snap.lvl === 'number') STATE.lvl = snap.lvl;
    rebuildDragHandles();
    _updateLvlButton();
    selectVariant(STATE.variant, true);
    refreshUI();
  }

  function reset() {
    var prevPoly = JSON.parse(JSON.stringify(STATE.polygon));
    var prevVariant = STATE.variant;
    var prevLvl = STATE.lvl;
    var fresh = JSON.parse(JSON.stringify(DEFAULTS));
    for (var k in fresh) {
      if (fresh.hasOwnProperty(k)) STATE[k] = fresh[k];
    }
    _UndoHistory._stack.push({
      polygon: prevPoly, variant: prevVariant, lvl: prevLvl
    });
    _UndoHistory._updateButton();
    _updateLvlButton();
    rebuildDragHandles();
    selectVariant(STATE.variant);
    refreshUI();
  }

  function cycleLvl() {
    _UndoHistory._stack.push({
      polygon: JSON.parse(JSON.stringify(STATE.polygon)),
      variant: STATE.variant,
      lvl: STATE.lvl
    });
    _UndoHistory._updateButton();
    STATE.lvl = (STATE.lvl >= 3) ? 1 : (STATE.lvl + 1);
    _updateLvlButton();
    _showLvlAvatar();
    refreshUI();
  }

  function _showLvlAvatar() {
    var img = document.getElementById('cf-lvl-avatar');
    if (!img) return;
    var boot = global.BASE_BOOT || {};
    var url = boot.lvlAvatars && boot.lvlAvatars[STATE.lvl];
    if (!url) return;
    img.classList.remove('cf-lvl-avatar-show');
    img.style.display = 'none';
    if (img.src !== url) {
      img.src = url;
    }
    void img.offsetWidth;
    img.style.display = '';
    img.classList.add('cf-lvl-avatar-show');
  }

  function _preloadLvlAvatars() {
    var boot = global.BASE_BOOT || {};
    var avatars = boot.lvlAvatars || {};
    for (var k in avatars) {
      if (avatars.hasOwnProperty(k)) {
        var preImg = new Image();
        preImg.src = avatars[k];
      }
    }
  }

  function _updateLvlButton() {
    var btn = document.getElementById('cf-btn-lvl');
    if (!btn) return;
    btn.classList.toggle('active', STATE.lvl !== 1);
    btn.setAttribute('data-lvl', String(STATE.lvl));
    btn.setAttribute('aria-pressed', STATE.lvl !== 1 ? 'true' : 'false');
    var needle = document.getElementById('cf-lvl-needle');
    if (needle) {
      var deg = (STATE.lvl === 1) ? -60 : (STATE.lvl === 2) ? 0 : 60;
      needle.style.transform = 'rotate(' + deg + 'deg)';
    }
  }

  /* -- Assembly sequence (wood fence) -----------------------------
     17 steps (0..16). Each step from 2 onward adds one part type on
     top of the previous, building the isolated single bay from the
     ground up. The tooltip on each step shows the matching BoQ item's
     localized name (via ConfiguratorBudgetBridge.getBoqItem), falling
     back to a hardcoded English name. partType tags are set by
     _build3D_DetailBay so applyStep can toggle visibility + explode. */
  var FENCE_ASSEMBLY_PARTS = {
    excavation: ['excavation-box'],
    geotextile: ['fence-geotextile'],
    gravel:     ['fence-footing-gravel'],
    concrete:   ['fence-footing-concrete'],
    bracket:    ['fence-bracket'],
    post:       ['fence-post-wood'],
    lag:        ['fence-screw-lag'],
    railbrkt:   ['fence-rail-bracket'],
    railscrewpost: ['fence-screw-railbrkt-post'],   /* 4 screws bracket -> post */
    rail:       ['fence-rail-wood'],
    railscrewrail: ['fence-screw-railbrkt-rail'],    /* 3 screws rail -> bracket */
    plank:      ['fence-picket-wood'],
    plankscrew: ['fence-screw-plank'],
    cap:        ['fence-cap-wood'],
    capscrew:   ['fence-screw-cap']
  };

  /* Cumulative visible part types per step. Step 1 = composed, step 2 =
     exploded, 3..N build up, last = composed. */
  var _WOOD_ASM_ORDER = [
    'excavation', 'geotextile', 'gravel', 'concrete', 'bracket',
    'post', 'lag', 'railbrkt', 'railscrewpost', 'rail', 'railscrewrail',
    'plank', 'plankscrew', 'cap', 'capscrew'
  ];

  /* BoQ code per step (for the tooltip). null = no item.
       0  = Built (full fence)
       1  = composed isolated bay (everything, no explode)
       2  = exploded (everything fanned out)
       3..17 = cumulative build-up (one BoQ item each)
       18 = Built (composed isolated bay again) */
  var WOOD_ASM_STEP_CODE = {
    0:  null,           /* Built */
    1:  null,           /* Composed bay */
    2:  null,           /* Exploded */
    3:  'FEE00001',     /* Excavation */
    4:  'FEC00003',     /* Geotextile */
    5:  'FEC00002',     /* Gravel */
    6:  'FEC00001',     /* Concrete */
    7:  'FFP01001',     /* Adjustable post base */
    8:  'KVH10100',     /* Post */
    9:  'FAS60080',     /* Lag screw 6x80 (post base -> post) */
    10: 'FEC00024',     /* Fence rail bracket */
    11: 'FAS50060',     /* 5x60 -- 4 screws bracket -> post */
    12: 'KVH10060',     /* Rail */
    13: 'FAS50060',     /* 5x60 -- 3 screws rail -> bracket */
    14: 'KVR16019',     /* Plank (code swapped to KVR10019 for narrow styles) */
    15: 'FAS50060',     /* 5x60 -- plank screws */
    16: 'FEC00023',     /* Fence post cap */
    17: 'FAS50060',     /* 5x60 -- cap screws */
    18: 'MFP00001'      /* Penetrating wood sealer */
  };

  /* Fallback names + their pi-dimension values (metric | imperial),
     mirroring BoQ columns R (NOTE METRIC) / S (NOTE IMPERIAL). The live
     bridge value wins when present; these cover the offline/no-bridge
     case. Volume/area items (excavation, concrete, gravel, geotextile,
     sealer) carry no pi dimension. Codes whose exact pi value isn't known
     here are left dimensionless -- the bridge supplies them. */
  var WOOD_ASM_FALLBACK = {
    FEE00001: { name: 'Excavation' },
    FEC00003: { name: 'Geotextile' },
    FEC00002: { name: 'Gravel base' },
    FEC00001: { name: 'Cast-in-place concrete' },
    FFP01001: { name: 'Adjustable post base', metric: '100 \u00d7 160 mm', imperial: '4" \u00d7 8"' },
    KVH10100: { name: '4\u00d74 PT post' },
    FAS60080: { name: 'Wood screw', metric: '6 \u00d7 80', imperial: '#14 \u00d7 3"' },
    FEC00024: { name: 'Fence rail bracket' },
    FAS50060: { name: 'Wood screw', metric: '5 \u00d7 60', imperial: '#10 \u00d7 2-1/2"' },
    KVH10060: { name: '2\u00d74 PT rail' },
    KVR16019: { name: 'Planed plank', metric: '160 \u00d7 19 mm', imperial: '6" \u00d7 3/4"' },
    KVR10019: { name: 'Planed plank', metric: '100 \u00d7 19 mm', imperial: '4" \u00d7 3/4"' },
    FEC00023: { name: 'Fence post cap', metric: '100 \u00d7 100 mm', imperial: '4" \u00d7 4"' },
    MFP00001: { name: 'Penetrating wood sealer' }
  };

  var WOOD_ASM_MAX = 18;

  /* Which part types are visible at a given step (cumulative). */
  function _woodAssemblyVisibleSet(step) {
    var set = {};
    if (step <= 0 || step >= WOOD_ASM_MAX) {
      /* Built -- everything except the excavation wireframe (the hole
         is backfilled in the finished state). */
      for (var key in FENCE_ASSEMBLY_PARTS) {
        if (key === 'excavation') continue;
        var arr0 = FENCE_ASSEMBLY_PARTS[key];
        for (var a0 = 0; a0 < arr0.length; a0++) set[arr0[a0]] = true;
      }
      return set;
    }
    if (step === 1 || step === 2) {
      /* Composed bay (1) and exploded (2) -- everything visible incl.
         excavation. The explode offset (step 2) is applied separately. */
      for (var key1 in FENCE_ASSEMBLY_PARTS) {
        var arr1 = FENCE_ASSEMBLY_PARTS[key1];
        for (var a1 = 0; a1 < arr1.length; a1++) set[arr1[a1]] = true;
      }
      return set;
    }
    /* Steps 3..15 -- cumulative build-up. Step 3 shows order[0], step 4
       shows order[0..1], etc. */
    var upto = step - 3;
    for (var i = 0; i <= upto && i < _WOOD_ASM_ORDER.length; i++) {
      var grp = FENCE_ASSEMBLY_PARTS[_WOOD_ASM_ORDER[i]];
      for (var j = 0; j < grp.length; j++) set[grp[j]] = true;
    }
    return set;
  }

  /* Explode offsets (step 2) -- fan parts apart while PRESERVING their
     real vertical order. Parts that sit lowest in the finished bay
     (geotextile, gravel, concrete at the bottom of the hole) get
     pushed slightly DOWN; parts that sit higher (post, cap) get pushed
     progressively UP; planks/rails fan outward in Z. The result reads
     as a vertically-stretched exploded diagram rather than a reshuffle.
     Schematic, shared spirit with the rack configurator. */
  var WOOD_EXPLODE_OFFSET = {
    'excavation-box':        [0, -0.22, 0],   /* hole outline -- lowest */
    'fence-geotextile':      [0, -0.18, 0],   /* bottom of hole */
    'fence-footing-gravel':  [0, -0.10, 0],
    'fence-footing-concrete':[0, -0.02, 0],
    'fence-bracket':         [0,  0.18, 0],   /* at grade -> lifts up a touch */
    'fence-screw-lag':       [0,  0.30, 0],   /* lift straight up -- keeps the opposed L/R pairs spread */
    'fence-rail-bracket':    [0.22, 0.12, 0.22],
    'fence-screw-railbrkt-post': [0, 0.12, 0],
    'fence-screw-railbrkt-rail': [0, 0.12, 0.30],
    'fence-rail-wood':       [0,  0.12, 0.32],   /* rail sits inner (+Z) -> fans inward */
    'fence-picket-wood':     [0,  0.10, -0.45],  /* default street side; per-instance overrides for shadow box */
    'fence-screw-plank':     [0,  0.10, -0.72],
    'fence-post-wood':       [0,  0.36, 0],    /* tall vertical -> up a bit */
    'fence-cap-wood':        [0,  0.55, 0],    /* top -> just above the post */
    'fence-screw-cap':       [0,  0.66, 0]
  };

  /* Runtime variant key -- uses the canonical FENCE_VARIANT_KEYS list
     so adding a variant only requires editing that one array (plus the
     per-variant builders / BoQ functions below). */
  function _currentVariantKey() {
    return FENCE_VARIANT_KEYS[STATE.variant - 1] || 'wood';
  }

  function cycleAssembly() {
    _AssemblyManager.cycle();
  }

  function _updateAssemblyButton() {
    _AssemblyManager._updateButton();
  }

  var _AssemblyManager = {
    /* Advance one step, wrapping 16 -> 0. Entering the sequence (0 -> 1)
       frames the camera on the isolated bay; leaving it (-> 0) restores
       the previous camera. The build() router sees assemblyStep > 0 and
       draws the detail bay, then we call applyStep to set visibility +
       explode offsets and animate. */
    cycle: function () {
      if (STATE.variant !== 1) return;   /* wood only for now */
      var prev = STATE.assemblyStep;
      var next = (prev + 1) % (WOOD_ASM_MAX + 1);

      var entering = (prev === 0 && next > 0);
      var leaving  = (next === 0 && prev > 0);

      /* Assembly is a 3D-only view. If the user triggers it from the
         2D plan, flip to 3D first so build() draws the isolated bay
         instead of the 2D outline. toggleView resets assemblyStep, so
         set the step AFTER flipping. */
      if (entering && STATE.view !== '3d') {
        if (global.BaseEngine && global.BaseEngine.toggleView) {
          global.BaseEngine.toggleView();
        }
      }

      STATE.assemblyStep = next;
      this._updateButton();

      if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
      refreshUI(true);

      if (entering) {
        _DetailBayManager.frameCamera();
      } else if (leaving) {
        _DetailBayManager.restoreCamera();
      }
      this._animate(prev, next);
    },

    /* Set per-part visibility + position for a given step. Called from
       BaseRenderer.build() right after _build3D_DetailBay repopulates
       the scene group. */
    applyStep: function (step) {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      var visible = _woodAssemblyVisibleSet(step);
      var isExploded = (step === 2);
      var sealed = (step >= WOOD_ASM_MAX);   /* sealer applied -> darken planks */

      eng.sceneGroup.traverse(function (node) {
        var ud = node.userData || {};
        var pt = ud.assetType;
        if (!pt) return;
        /* Only operate on top-level scene children (the parts we built). */
        if (node.parent !== eng.sceneGroup) return;
        if (pt === 'section-cut-aux') return;

        var show = !!visible[pt];
        node.visible = show;
        if (!show) return;

        var base = ud.basePos;
        if (!base) {
          ud.basePos = node.position.clone();
          base = ud.basePos;
        }
        var off = isExploded
          ? (ud.explodeOffset || WOOD_EXPLODE_OFFSET[pt])
          : null;
        if (off) {
          node.position.set(base.x + off[0], base.y + off[1], base.z + off[2]);
        } else {
          node.position.copy(base);
        }
      });

      /* Sealer step -- tint the plank + rail + cap material darker to
         read as a penetrating-sealer finish. Restored on rebuild since
         materials are shared/cached, so we clone-tint per step. */
      this._applySealerTint(sealed);
    },

    _applySealerTint: function (on) {
      var mats = global.BaseRenderer && global.BaseRenderer._materials;
      if (!mats) return;
      var T = global.THREE;
      var targets = ['fenceWoodPicket', 'fenceWoodRail', 'fenceWoodPost', 'fenceWoodCap'];
      for (var i = 0; i < targets.length; i++) {
        var m = mats[targets[i]];
        if (!m || !m.color) continue;
        if (on) {
          if (!m.userData) m.userData = {};
          if (m.userData._origColor == null) m.userData._origColor = m.color.getHex();
          m.color.setHex(m.userData._origColor).multiplyScalar(0.72);
        } else if (m.userData && m.userData._origColor != null) {
          m.color.setHex(m.userData._origColor);
        }
      }
    },

    /* Lightweight tweens: explode in/out (~450 ms) when entering/leaving
       step 2, and a screw-driving animation when a step introduces a new
       fastener type (the screws back off along their drive axis, then
       advance + spin into their seat). */
    _animate: function (prevStep, nextStep) {
      var isExplodeIn  = (nextStep === 2);
      var isExplodeOut = (prevStep === 2 && nextStep !== 2);
      if (isExplodeIn || isExplodeOut) {
        this._animateExplode(isExplodeIn);
        return;
      }
      /* Did this step newly reveal a screw type? Animate it driving in. */
      var prevSet = _woodAssemblyVisibleSet(prevStep);
      var nextSet = _woodAssemblyVisibleSet(nextStep);
      var SCREW_TYPES = ['fence-screw-lag',
                         'fence-screw-railbrkt-post', 'fence-screw-railbrkt-rail',
                         'fence-screw-plank', 'fence-screw-cap'];
      var newScrews = [];
      for (var i = 0; i < SCREW_TYPES.length; i++) {
        var st = SCREW_TYPES[i];
        if (nextSet[st] && !prevSet[st]) newScrews.push(st);
      }
      if (newScrews.length) this._animateScrewDrive(newScrews);

      /* Planks newly revealed -> animate them dropping in as a quick
         wave (one after another) rather than popping in all at once. */
      if (nextSet['fence-picket-wood'] && !prevSet['fence-picket-wood']) {
        this._animatePlankWave();
      }
    },

    /* Plank wave -- each plank starts lifted up + slightly out and drops
       into place, staggered along X so they settle one after another. */
    _animatePlankWave: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      var group = eng.sceneGroup;
      var T = global.THREE;

      /* Gather planks, sorted left->right so the wave sweeps across. */
      var planks = [];
      group.traverse(function (node) {
        if (node.parent !== group) return;
        var ud = node.userData || {};
        if (ud.assetType !== 'fence-picket-wood' || !node.visible) return;
        planks.push({ node: node, seat: (ud.basePos || node.position.clone()) });
      });
      if (!planks.length) return;
      planks.sort(function (a, b) { return a.seat.x - b.seat.x; });

      var LIFT = 0.35;            /* start this far above the seat */
      var PER  = 320;             /* each plank's own drop duration */
      var STAGGER = 55;           /* delay between successive planks */
      var total = PER + STAGGER * (planks.length - 1);
      var start = null;

      function frame(ts) {
        if (start == null) start = ts;
        var elapsed = ts - start;
        for (var i = 0; i < planks.length; i++) {
          var p = planks[i];
          var local = elapsed - i * STAGGER;
          var t = local <= 0 ? 0 : Math.min(1, local / PER);
          var ease = 1 - Math.pow(1 - t, 3);
          var k = (1 - ease) * LIFT;
          p.node.position.set(p.seat.x, p.seat.y + k, p.seat.z);
        }
        if (eng.renderer && eng.scene && eng.camera) {
          eng.renderer.render(eng.scene, eng.camera);
        }
        if (elapsed < total && global.requestAnimationFrame) {
          global.requestAnimationFrame(frame);
        } else {
          /* Settle exactly on seats. */
          for (var j = 0; j < planks.length; j++) planks[j].node.position.copy(planks[j].seat);
        }
      }
      if (global.requestAnimationFrame) global.requestAnimationFrame(frame);
    },

    _animateExplode: function (dirIn) {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      var start = null;
      var DUR = 750;   /* explode tween -- slowed for clarity */
      var group = eng.sceneGroup;
      function frame(ts) {
        if (start == null) start = ts;
        var t = Math.min(1, (ts - start) / DUR);
        var ease = 1 - Math.pow(1 - t, 3);
        var f = dirIn ? ease : (1 - ease);
        group.traverse(function (node) {
          if (node.parent !== group) return;
          var ud = node.userData || {};
          var pt = ud.assetType;
          if (!pt || !node.visible) return;
          var base = ud.basePos;
          var off = ud.explodeOffset || WOOD_EXPLODE_OFFSET[pt];
          if (!base || !off) return;
          node.position.set(base.x + off[0] * f, base.y + off[1] * f, base.z + off[2] * f);
        });
        if (eng.renderer && eng.scene && eng.camera) {
          eng.renderer.render(eng.scene, eng.camera);
        }
        if (t < 1 && global.requestAnimationFrame) global.requestAnimationFrame(frame);
      }
      if (global.requestAnimationFrame) global.requestAnimationFrame(frame);
    },

    _animateScrewDrive: function (screwTypes) {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      var typeSet = {};
      for (var i = 0; i < screwTypes.length; i++) typeSet[screwTypes[i]] = true;
      var group = eng.sceneGroup;

      /* Collect the screw groups to animate + cache their seated pos
         and base orientation (screws are oriented with a quaternion, so
         the spin must be composed onto that, not overwrite it). */
      var screws = [];
      group.traverse(function (node) {
        if (node.parent !== group) return;
        var ud = node.userData || {};
        if (!ud.assetType || !typeSet[ud.assetType]) return;
        if (!node.visible) return;
        var seat = ud.basePos || node.position.clone();
        var dir = ud.driveDir || [0, -1, 0];
        screws.push({ node: node, seat: seat, dir: dir, baseQuat: node.quaternion.clone() });
      });
      if (!screws.length) return;

      var T = global.THREE;
      var BACK = 0.10;          /* back off 10 cm along -driveDir */
      var DUR = 650;   /* screw-driving -- slowed for clarity */
      var start = null;
      function frame(ts) {
        if (start == null) start = ts;
        var t = Math.min(1, (ts - start) / DUR);
        var ease = 1 - Math.pow(1 - t, 3);
        for (var s = 0; s < screws.length; s++) {
          var sc = screws[s];
          /* Start backed off along -dir (offset = BACK at t=0 -> 0 at t=1). */
          var k = (1 - ease) * BACK;
          sc.node.position.set(
            sc.seat.x - sc.dir[0] * k,
            sc.seat.y - sc.dir[1] * k,
            sc.seat.z - sc.dir[2] * k
          );
          /* Spin about the drive axis while driving in, composed onto
             the screw's seated orientation. */
          var spin = ease * Math.PI * 6;
          var axis = new T.Vector3(sc.dir[0], sc.dir[1], sc.dir[2]).normalize();
          var spinQ = new T.Quaternion().setFromAxisAngle(axis, spin);
          sc.node.quaternion.copy(spinQ).multiply(sc.baseQuat);
        }
        if (eng.renderer && eng.scene && eng.camera) {
          eng.renderer.render(eng.scene, eng.camera);
        }
        if (t < 1 && global.requestAnimationFrame) global.requestAnimationFrame(frame);
      }
      if (global.requestAnimationFrame) global.requestAnimationFrame(frame);
    },

    _updateButton: function () {
      var btn = document.getElementById('cf-btn-assembly');
      if (!btn) return;
      var lbl = document.getElementById('cf-stat-assembly');
      if (lbl) lbl.textContent = STATE.assemblyStep + '/' + WOOD_ASM_MAX;
      btn.classList.toggle('active', STATE.assemblyStep !== 0);
      btn.setAttribute('aria-pressed', STATE.assemblyStep !== 0 ? 'true' : 'false');
      btn.setAttribute('data-tooltip-style', 'hint');

      var code = WOOD_ASM_STEP_CODE[STATE.assemblyStep];
      /* Plank step uses the style's actual plank code so the tooltip
         name matches the BoQ (4" vs 6"). */
      if (code === 'KVR16019') {
        var sp = _getWoodStyleParams(STATE.woodStyle || 'privacy');
        if (sp && sp.boardCode) code = sp.boardCode;
      }
      if (!code) {
        var boot0 = global.BASE_BOOT || {};
        var T0 = boot0.i18n || {};
        if (STATE.assemblyStep === 2) {
          btn.title = T0.assembly_exploded || 'Exploded view';
        } else {
          btn.title = T0.assemblyStepsHint || 'Assembly steps';
        }
        return;
      }
      /* Resolve the display name. In the BoQ a "pi" placeholder is
         substituted with the dimension for the active units (metric ->
         col R, imperial -> col S). The bridge, however, may return a name
         that already has the METRIC value baked in (no pi left), so for
         imperial units we must swap that metric token for the imperial
         one. We therefore keep BOTH note values around. */
      var fb = WOOD_ASM_FALLBACK[code];
      var fbName = (fb && fb.name) ? fb.name : (typeof fb === 'string' ? fb : code);
      var metricDim   = (fb && fb.metric)   || null;
      var imperialDim = (fb && fb.imperial) || null;

      var bridgeName = null;
      var bridge = global.ConfiguratorBudgetBridge || null;
      if (bridge && bridge.getBoqItem) {
        var item = bridge.getBoqItem(code);
        if (item) {
          if (item.name && String(item.name).trim()) bridgeName = String(item.name).trim();
          var bm = item.noteMetric   || item.note_metric   || item.noteM   || null;
          var bi = item.noteImperial || item.note_imperial || item.noteImp || null;
          if (bm && String(bm).trim()) metricDim   = String(bm).trim();
          if (bi && String(bi).trim()) imperialDim = String(bi).trim();
        }
      }

      var dim = STATE.isMetric ? metricDim : imperialDim;

      var name;
      if (bridgeName) {
        if (bridgeName.indexOf('\u03c0') !== -1) {
          /* Bridge gave the raw template -- substitute pi for active units. */
          name = _applyPiDimension(bridgeName, dim);
        } else if (!STATE.isMetric && metricDim && imperialDim) {
          /* Bridge baked the METRIC value in but units are imperial --
             swap the metric token for the imperial one. Matched loosely
             (x vs x, flexible spacing) since the baked text may differ
             slightly from our note string. */
          name = _swapDimToken(bridgeName, metricDim, imperialDim);
        } else {
          name = bridgeName;
        }
      } else {
        /* Offline fallback -- append the active-units dimension. */
        name = dim ? (fbName + ' ' + dim).replace(/\s+/g, ' ').trim() : fbName;
      }
      btn.title = name;
    }
  };

  /* Replace a metric dimension token inside a name with its imperial
     equivalent, tolerant to x vs x and variable whitespace. Falls back
     to appending the imperial value if the metric token isn't found. */
  function _swapDimToken(name, metricDim, imperialDim) {
    var esc = function (s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
    /* Strip a trailing unit suffix (mm/cm) from the metric token so the
       numeric core matches whatever the bridge baked in (which may omit
       the unit). */
    var core = String(metricDim).replace(/\s*(mm|cm|m)\s*$/i, '').trim();
    var pat = esc(core)
      .replace(/[\u00d7x]/gi, '[\u00d7x]')
      .replace(/\s+/g, '\\s*');
    /* Allow an optional unit suffix after the numeric core in the name. */
    var re = new RegExp(pat + '(\\s*(?:mm|cm|m))?', 'i');
    if (re.test(name)) {
      return name.replace(re, imperialDim).replace(/\s+/g, ' ').trim();
    }
    return (name + ' ' + imperialDim).replace(/\s+/g, ' ').trim();
  }

  /* Replace the single "pi" placeholder in a BoQ name with the
     active-units dimension string (e.g. "WOOD SCREW pi" + "6 x 80" ->
     "WOOD SCREW 6 x 80"). If there is no pi, the name is already resolved
     and is returned unchanged. A stray pi with no dimension is stripped. */
  function _applyPiDimension(name, dim) {
    if (!name) return name;
    var hasPi = name.indexOf('\u03c0') !== -1;
    if (!hasPi) return name;                 /* already resolved -- leave as-is */
    if (dim) {
      return name.replace(/\s*\u03c0\s*/g, ' ' + dim + ' ').replace(/\s+/g, ' ').trim();
    }
    /* pi present but no dimension known -- strip it so no bare pi shows. */
    return name.replace(/\s*\u03c0\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  var _SlopeManager = {
    init: function () {
      var btn = document.getElementById('cf-btn-slope');
      if (!btn) return;
      var self = this;
      btn.addEventListener('click', function () { self.toggle(); });
      this._wireSlider();
      this._injectArrow();
      this._updateAll();
    },

    _wireSlider: function () {
      var slider = document.getElementById('cf-slope-slider');
      if (!slider) return;
      var maxV = parseInt(slider.max, 10) || 15;
      var self = this;
      var updateFill = function (val) {
        var fillPct = 100 - (val / maxV) * 100;
        slider.style.setProperty('--cfg-fill', fillPct + '%');
      };
      var onChange = function () {
        var v = parseInt(slider.value, 10) || 0;
        STATE.slopeDeg = v;
        updateFill(v);
        var lbl = document.getElementById('cf-slope-value');
        if (lbl) lbl.textContent = v + '\u00b0';
        self._updateArrow();
        if (global.BaseEngine) _rebuildGrid(global.BaseEngine);
        if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
        refreshUI();
      };
      slider.addEventListener('input',  onChange);
      slider.addEventListener('change', onChange);
      slider.value = STATE.slopeDeg;
      updateFill(STATE.slopeDeg);
      var lbl = document.getElementById('cf-slope-value');
      if (lbl) lbl.textContent = STATE.slopeDeg + '\u00b0';
    },

    _injectArrow: function () {
      if (document.getElementById('cf-slope-arrow')) return;
      var wrap = document.getElementById('cf-wrap-base');
      if (!wrap) return;
      var TT = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
      var slopeLabel = TT.slopeArrowLabel || 'SLOPE';
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.id = 'cf-slope-arrow';
      svg.setAttribute('viewBox', '0 0 100 36');
      svg.style.cssText = [
        'position:absolute', 'left:15%', 'top:50%',
        'transform:translate(-50%, -50%)',
        'width:100px', 'height:36px',
        'pointer-events:none', 'display:none', 'z-index:9'
      ].join(';');
      svg.innerHTML = [
        '<line x1="6" y1="18" x2="86" y2="18" stroke="#D2A878" stroke-width="2.5" stroke-linecap="round"/>',
        '<polyline points="78,10 90,18 78,26" fill="none" stroke="#D2A878" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>',
        '<text x="48" y="34" text-anchor="middle" font-family="Montserrat, system-ui, sans-serif" font-size="9" font-weight="700" fill="#D2A878" id="cf-slope-arrow-label">' + slopeLabel + '</text>'
      ].join('');
      wrap.appendChild(svg);
    },

    _updateArrow: function () {
      var svg = document.getElementById('cf-slope-arrow');
      if (!svg) return;
      var visible = STATE.slopeActive && STATE.view === '2d';
      svg.style.display = visible ? 'block' : 'none';
      var lbl = document.getElementById('cf-slope-arrow-label');
      if (lbl) {
        var TT = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
        lbl.textContent = TT.slopeArrowLabel || 'SLOPE';
      }
    },

    _updateButton: function () {
      var btn = document.getElementById('cf-btn-slope');
      if (!btn) return;
      btn.classList.toggle('active', STATE.slopeActive);
      btn.setAttribute('aria-pressed', STATE.slopeActive ? 'true' : 'false');
    },

    _updateSlider: function () {
      var wrap = document.getElementById('cf-slope-slider-wrap');
      if (!wrap) return;
      var visible = STATE.slopeActive && STATE.view === '2d' && STATE.vizStage !== 1;
      wrap.classList.toggle('cf-slope-visible', visible);
    },

    _updateAll: function () {
      this._updateButton();
      this._updateSlider();
      this._updateArrow();
    },

    toggle: function () {
      STATE.slopeActive = !STATE.slopeActive;
      if (STATE.slopeActive && STATE.view !== '2d') {
        if (global.BaseEngine && global.BaseEngine.toggleView) {
          global.BaseEngine.toggleView();
        }
      }
      this._updateAll();
      if (global.BaseEngine) _rebuildGrid(global.BaseEngine);
      if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
      refreshUI();
    }
  };

  function _updateMapLockButton() {
    var btn = document.getElementById('cf-map-lock');
    if (!btn) return;
    btn.classList.toggle('locked', !!STATE.mapLocked);
    btn.setAttribute('aria-pressed', STATE.mapLocked ? 'true' : 'false');
    btn.title = STATE.mapLocked ? 'Unlock map position' : 'Lock map position';
    var shackle = document.getElementById('cf-map-lock-shackle');
    if (shackle) {
      shackle.setAttribute('d', STATE.mapLocked
        ? 'M8 11V7a4 4 0 0 1 8 0v4'
        : 'M8 11V7a4 4 0 0 1 7-2.65');
    }
    _applyMapLockState();
  }

  function _applyMapLockState() {
    if (!_MapManager || !_MapManager.map) return;
    var m = _MapManager.map;
    var locked = !!STATE.mapLocked;
    try {
      if (locked) {
        m.dragPan.disable();
        m.scrollZoom.disable();
        m.boxZoom.disable();
        m.dragRotate.disable();
        m.doubleClickZoom.disable();
        m.touchZoomRotate.disable();
      } else {
        m.dragPan.enable();
        m.scrollZoom.enable();
        m.boxZoom.enable();
        m.dragRotate.enable();
        m.doubleClickZoom.enable();
        m.touchZoomRotate.enable();
      }
    } catch (e) {}
    var rotateInput = document.getElementById('cf-map-rotate');
    if (rotateInput) {
      rotateInput.disabled = locked;
      rotateInput.style.opacity = locked ? '0.4' : '';
      rotateInput.style.pointerEvents = locked ? 'none' : '';
    }
  }

  function _slopeExcavationFactor() {
    if (!STATE.slopeActive || STATE.slopeDeg <= 0) return { extraDepthAvgM: 0, maxDeltaM: 0 };
    var bb = (typeof getBBox === 'function') ? getBBox() : null;
    if (!bb) return { extraDepthAvgM: 0, maxDeltaM: 0 };
    var rad = STATE.slopeDeg * Math.PI / 180;
    var maxDelta = bb.L * Math.tan(rad);
    var avgExtra = maxDelta * 0.5;
    return { extraDepthAvgM: avgExtra, maxDeltaM: maxDelta };
  }

  function _slopeRefBearing() {
    if (_MapManager && typeof _MapManager.bearing === 'number') return _MapManager.bearing;
    return 0;
  }

  function _rotatePoint(x, z, bearingDeg) {
    if (!bearingDeg) return { x: x, z: z };
    var rad = -bearingDeg * Math.PI / 180;
    var cs = Math.cos(rad), sn = Math.sin(rad);
    return { x: x * cs - z * sn, z: x * sn + z * cs };
  }

  function _slopeRefX() {
    var poly = STATE.polygon;
    if (!poly || !poly.length) return 0;
    var bearing = _slopeRefBearing();
    var xMax = -Infinity;
    for (var i = 0; i < poly.length; i++) {
      var r = _rotatePoint(poly[i][0], poly[i][1], bearing);
      if (r.x > xMax) xMax = r.x;
    }
    return xMax;
  }

  function _padSlopeExtra(padX, padZ) {
    if (!STATE.slopeActive || STATE.slopeDeg <= 0) return 0;
    var bearing = _slopeRefBearing();
    var rotated = _rotatePoint(padX, (typeof padZ === 'number' ? padZ : 0), bearing);
    var xMax = _slopeRefX();
    var rad = STATE.slopeDeg * Math.PI / 180;
    var extra = (xMax - rotated.x) * Math.tan(rad);
    return extra > 0 ? extra : 0;
  }

  function _sumPadSlopeExtras(positions) {
    var sum = 0;
    if (!positions || !positions.length) return 0;
    for (var i = 0; i < positions.length; i++) {
      sum += _padSlopeExtra(positions[i].x, positions[i].z);
    }
    return sum;
  }

  function _avgSlopeExtraOverPolygon() {
    if (!STATE.slopeActive || STATE.slopeDeg <= 0) return 0;
    var poly = STATE.polygon;
    if (!poly || poly.length < 3) return 0;
    var area = 0, cxSum = 0, czSum = 0;
    for (var i = 0; i < poly.length; i++) {
      var p0 = poly[i], p1 = poly[(i + 1) % poly.length];
      var cross = p0[0] * p1[1] - p1[0] * p0[1];
      area += cross;
      cxSum += (p0[0] + p1[0]) * cross;
      czSum += (p0[1] + p1[1]) * cross;
    }
    area *= 0.5;
    if (Math.abs(area) < 1e-6) return 0;
    var cx = cxSum / (6 * area);
    var cz = czSum / (6 * area);
    return _padSlopeExtra(cx, cz);
  }

  /* -------------------------------------------------------------
     _forceRender3DFlush
     Chromium Android occasionally retains a stale composite layer
     for the WebGL canvas after the scene group is rebuilt -- the
     renderer paints the new frame correctly (verified via
     gl.readPixels) but the user sees a blank canvas until they
     interact with it. This shows up on multiple paths: variant
     switch, units toggle, style switch, viz mode change, etc.

     Three escalation levels:

       0 (default, "soft") -- sync render() now + a second render
         scheduled via requestAnimationFrame on the next frame.
         Cheap, invisible. Fixes most paths.

       1 ("canvas")        -- also nudges the canvas element's
         opacity to 0.999 for one animation frame and back. This
         forces the compositor to invalidate its cached layer for
         the canvas without re-creating the WebGL context, and
         without the visible 2D detour. Cost: ~one extra paint.

       2 ("roundtrip")     -- also performs a 3D -> 2D -> 3D view
         toggle. Heaviest, briefly shows the 2D scene; rebuilds
         the scene group from scratch. Reserved for paths that
         still fail at level 1.

     Callers pick the lowest level that works for their path. The
     'useRoundTrip' legacy parameter (boolean) is still accepted --
     false -> 0, true -> 2 -- for backwards compatibility.
     ------------------------------------------------------------- */
  function _forceRender3DFlush(level) {
    if (STATE.view !== '3d') return;
    var eng = global.BaseEngine;
    if (!eng) return;
    /* Coerce legacy boolean (false -> 0, true -> 2). Numbers pass through. */
    var lvl = (level === true) ? 2 : (level === false ? 0 : (level | 0));

    function _doRender() {
      try {
        if (STATE.view === '3d' && eng.renderer && eng.scene && eng.camera) {
          eng.renderer.render(eng.scene, eng.camera);
        }
      } catch (e) {}
    }

    /* Level 0 -- sync + RAF render */
    _doRender();
    if (global.requestAnimationFrame) global.requestAnimationFrame(_doRender);

    /* Level 1 -- canvas opacity nudge forces compositor reflow.
       The 0.999 -> 1 change is visually undetectable but invalidates
       the cached composite layer for the canvas. */
    if (lvl >= 1) {
      try {
        var canvas = eng.renderer && eng.renderer.domElement;
        if (canvas && global.requestAnimationFrame) {
          var prev = canvas.style.opacity;
          canvas.style.opacity = '0.999';
          global.requestAnimationFrame(function () {
            canvas.style.opacity = prev || '';
            _doRender();
          });
        }
      } catch (e) {}
    }

    /* Level 2 -- view round-trip (3D -> 2D -> 3D). Visible flicker.
       toggleView() resets vizStage to 0, so we snapshot and restore
       it -- otherwise a style switch while in section-cut / detail
       stage would silently drop the user back to the plain 3D view. */
    if (lvl >= 2 && eng.toggleView) {
      try {
        var savedStage = STATE.vizStage;
        var savedAssembly = STATE.assemblyStep;
        if (_DetailBayManager) _DetailBayManager._suppressRestore = true;
        eng.toggleView();   /* -> 2D */
        eng.toggleView();   /* -> 3D */
        if (_DetailBayManager) _DetailBayManager._suppressRestore = false;
        /* toggleView resets vizStage + assemblyStep to 0; restore both
           so a style/unit switch doesn't drop the user out of section
           cut or assembly mode. */
        var changed = false;
        if (savedStage && savedStage !== STATE.vizStage) {
          STATE.vizStage = savedStage; changed = true;
        }
        if (savedAssembly && savedAssembly !== STATE.assemblyStep) {
          STATE.assemblyStep = savedAssembly; changed = true;
        }
        if (changed) {
          if (_VisualizationManager && _VisualizationManager.apply) {
            _VisualizationManager.apply();
          }
          /* NOTE: we intentionally do NOT call frameCamera() here. This
             path runs on a style/unit switch while the user is already
             inside assembly view -- re-framing would snap the camera away
             from wherever they've orbited to. The camera was framed once
             on entry; a style change should leave it exactly as-is. */
        }
      } catch (e) {}
    }
  }

  function toggleUnits() {
    STATE.isMetric = !STATE.isMetric;
    /* Re-model on the new unit's grid (5 cm metric / 2" imperial), not
       just relabel -- every footprint vertex re-snaps so dimensions land
       on the new clean grid. Drag handles + 3D / BoQ then rebuild from
       the snapped polygon. */
    snapPolygonToGrid();
    if (global.BaseEngine) _rebuildGrid(global.BaseEngine);
    if (STATE.view === '3d' && global.BaseRenderer && global.BaseRenderer.build) {
      global.BaseRenderer.build();
    }
    refreshUI(true);
    _forceRender3DFlush(2);
    _broadcastUnitToBudget();
  }

  function _broadcastUnitToBudget() {
    var sel = document.getElementById('unitSelector');
    if (sel) {
      sel.setAttribute('data-current-unit', STATE.isMetric ? 'metric' : 'imperial');
      sel.dispatchEvent(new CustomEvent('unitChange', {
        detail: { unit: STATE.isMetric ? 'metric' : 'imperial' }, bubbles: true
      }));
    }
  }

  function _syncFromBudgetUnit() {
    var sel = document.getElementById('unitSelector');
    if (!sel) return;
    var u = sel.getAttribute('data-current-unit');
    var nextMetric = (u !== 'imperial');
    if (STATE.isMetric !== nextMetric) {
      STATE.isMetric = nextMetric;
      /* Re-snap footprint to the new unit's grid -- matches toggleUnits. */
      snapPolygonToGrid();
      if (global.BaseEngine) _rebuildGrid(global.BaseEngine);
      /* Rebuild the 3D scene too: the slope-conforming grid is generated
         in build() with a metric/imperial step, so it must regenerate
         when units change (otherwise it keeps the old spacing). */
      if (STATE.view === '3d' && global.BaseRenderer && global.BaseRenderer.build) {
        global.BaseRenderer.build();
      }
      refreshUI(true);
      _forceRender3DFlush(2);
    }
  }

  /* Fence has 7 variants:
       1 = wood            (= timber pickets / privacy / split-rail)
       2 = vinyl           (= PVC privacy panels)
       3 = concrete_blocks (= split-face block posts + dwarf wall + panel)
       4 = chain_link      (= galvanised mesh on steel posts)
       5 = steel_panel     (= welded steel panel between steel posts)
       6 = gabion          (= stone-filled wire cages)
       7 = concrete_panel  (= precast concrete panels in H-posts)
     Names are US-market friendly. Order of variants matches Shopify
     product variants so _syncShopifyVariant maps variant N -> boot.variants[N-1]. */
  var FENCE_VARIANT_COUNT = 7;

  function selectVariant(n, silent) {
    if (n < 1 || n > FENCE_VARIANT_COUNT) return;
    if (!silent && STATE.variant !== n) {
      _UndoHistory.pushIfChanged(STATE.polygon, STATE.variant);
    }
    STATE.variant = n;
    STATE.assemblyStep = 0;
    /* Section plane was positioned for the old variant's elements;
       drop the manual drag so the new variant auto-snaps afresh. */
    if (_SectionCutManager && _SectionCutManager.resetManualOffset) {
      _SectionCutManager.resetManualOffset();
    }
    /* Different variants have different spacing defaults + valid ranges,
       so a value from the previous variant could be out of range. Reset
       to the variant default; user can drag the slider again if needed. */
    STATE.postSpacingOverride = null;
    _updateAssemblyButton();
    /* WIP watermark -- only the wooden fence (variant 1) is
       production-ready. Variants 2..7 get the "UNDER DEVELOPMENT"
       overlay over both 2D and 3D scenes. */
    var wip = document.getElementById('cf-wip-watermark');
    if (wip) wip.classList.toggle('cf-wip-active', n !== 1);
    _syncWoodInfoColumns();
    if (_HeightSlider && _HeightSlider.refresh) _HeightSlider.refresh();
    if (_VisualizationManager && _VisualizationManager.reset) _VisualizationManager.reset();
    _syncVariantNameDisplay();
    if (_SpacingSlider && _SpacingSlider.refresh) _SpacingSlider.refresh();
    if (!silent) _syncShopifyVariant(n);
    refreshUI();
    _forceRender3DFlush(2);
  }

  function nextVariant() {
    var n = STATE.variant + 1;
    if (n > FENCE_VARIANT_COUNT) n = 1;
    selectVariant(n);
  }

  function prevVariant() {
    var n = STATE.variant - 1;
    if (n < 1) n = FENCE_VARIANT_COUNT;
    selectVariant(n);
  }

  /* -------------------------------------------------------------
     Wood-fence style switcher.
     Valid: 'privacy' | 'spaced' | 'shadow_box' | 'picket'.
     Pure picket-pattern change -- rails, posts, dimensions unchanged.
     Triggers full re-render and re-publishes BoQ + ProjectStore.
     ------------------------------------------------------------- */
  var WOOD_STYLES = ['privacy', 'spaced', 'shadow_box', 'picket', 'open_board'];

  function setWoodStyle(style) {
    if (WOOD_STYLES.indexOf(style) === -1) return;
    if (STATE.woodStyle === style) return;
    STATE.woodStyle = style;
    _syncStyleColumn();
    refreshUI();
    /* Style switch rebuilds the picket geometries fast enough that
       Chromium's compositor reliably holds a stale layer. Levels 0
       (RAF) and 1 (canvas opacity) both proved insufficient on
       Android Chrome, so we fall back to level 2 -- a brief 3D->2D->3D
       round-trip that rebuilds the scene group from scratch. Visible
       flicker, but the model always reappears. */
    _forceRender3DFlush(2);
    try { _ProjectStore && _ProjectStore.publish && _ProjectStore.publish(); } catch (e) {}
  }

  function getWoodStyle() { return STATE.woodStyle || 'privacy'; }

  /* Cycle through styles (Privacy -> Spaced -> Shadow -> Picket -> Privacy...).
     Bound to the swap button in the info-bar STYLE column. */
  function cycleWoodStyle() {
    var cur = STATE.woodStyle || 'privacy';
    var i = WOOD_STYLES.indexOf(cur);
    if (i === -1) i = 0;
    setWoodStyle(WOOD_STYLES[(i + 1) % WOOD_STYLES.length]);
  }

  /* Refresh the STYLE column label in the info-bar to match the
     current style. Reads the localised short name from BASE_BOOT.i18n
     (fence_wood_style_*_short keys). */
  function _syncStyleColumn() {
    var nameEl = document.getElementById('cf-stat-style-name');
    if (!nameEl) return;
    var boot = global.BASE_BOOT || {};
    var i18n = boot.i18n || {};
    var style = STATE.woodStyle || 'privacy';
    var key = 'fence_wood_style_' + (style === 'shadow_box' ? 'shadow' : (style === 'open_board' ? 'open' : style)) + '_short';
    var fallback = { privacy: 'Privacy', spaced: 'Spaced', shadow_box: 'Shadow', picket: 'Picket', open_board: 'Open' }[style] || 'Privacy';
    nameEl.textContent = (i18n[key] || fallback).toUpperCase();
  }

  function _initStyleCycleButton() {
    var btn = document.getElementById('cf-style-cycle');
    if (!btn) return;
    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      cycleWoodStyle();
    });
    _syncStyleColumn();
  }

  /* Toggle the AREA/PERIMETER vs HEIGHT/STYLE columns based on the
     current variant. Wood (variant 1) shows HEIGHT + STYLE; everything
     else shows AREA + PERIMETER. Called from selectVariant + boot. */
  function _syncWoodInfoColumns() {
    var isWood = (STATE.variant === 1);
    var colArea   = document.getElementById('cf-info-area');
    var colHeight = document.getElementById('cf-info-height');
    var colPerim  = document.getElementById('cf-info-perim');
    var colStyle  = document.getElementById('cf-info-style');
    if (colArea)   colArea.style.display   = isWood ? 'none' : '';
    if (colPerim)  colPerim.style.display  = isWood ? 'none' : '';
    if (colHeight) colHeight.style.display = isWood ? '' : 'none';
    if (colStyle)  colStyle.style.display  = isWood ? '' : 'none';
    if (isWood) _syncStyleColumn();
  }

  /* Display labels mirror the canonical FENCE_VARIANT_KEYS order. */
  var FENCE_VARIANT_FALLBACK_NAMES = [
    'Wood Fence',
    'Vinyl Fence',
    'Concrete Block Fence',
    'Chain Link Fence',
    'Steel Panel Fence',
    'Gabion Fence',
    'Concrete Panel Fence'
  ];

  function _syncVariantNameDisplay() {
    var nameEl = document.getElementById('cf-base-variant-name');
    if (!nameEl) return;
    var boot = global.BASE_BOOT || {};
    var i18n = boot.i18n || {};
    var key = 'fence_v' + STATE.variant;
    var fallback = FENCE_VARIANT_FALLBACK_NAMES[STATE.variant - 1] || 'Fence';
    nameEl.textContent = i18n[key] || fallback;
  }

  function _syncShopifyVariant(n) {
    var boot = global.BASE_BOOT;
    if (!boot || !boot.variants || !boot.variants[n - 1]) return;
    var v = boot.variants[n - 1];
    if (!v || !v.id) return;
    try {
      var url = new URL(global.location.href);
      url.searchParams.set('variant', String(v.id));
      global.history.replaceState({}, '', url.toString());
    } catch (e) {}

    var selectors = document.querySelectorAll('select[name="id"], input[name="id"]');
    selectors.forEach(function (sel) {
      if (sel.tagName === 'SELECT') {
        for (var i = 0; i < sel.options.length; i++) {
          if (sel.options[i].value === String(v.id)) {
            sel.selectedIndex = i;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      } else {
        sel.value = String(v.id);
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    document.dispatchEvent(new CustomEvent('base:variantChanged', {
      detail: { variant: n, shopifyVariantId: v.id }, bubbles: true
    }));
  }

  function _readShopifyVariantFromURL() {
    var boot = global.BASE_BOOT;
    if (!boot || !boot.variants) return;
    try {
      var url = new URL(global.location.href);
      var vid = url.searchParams.get('variant');
      if (!vid) return;
      for (var i = 0; i < boot.variants.length; i++) {
        if (String(boot.variants[i].id) === String(vid)) {
          STATE.variant = i + 1;
          return;
        }
      }
    } catch (e) {}
  }

  var _handleEls = [];

  function rebuildDragHandles() {
    var container = document.getElementById('cf-base-handles');
    if (!container) return;
    container.innerHTML = '';
    _handleEls = [];

    var poly = getFootprintPolygon();
    var defs = _buildHandleDefs(poly);

    for (var i = 0; i < defs.length; i++) {
      var def = defs[i];
      var el = document.createElement('div');
      el.className = 'cf-base-handle cf-base-handle-' + def.type;
      el.setAttribute('data-handle-index', String(i));
      el.style.display = 'none';
      container.appendChild(el);
      _attachHandleDrag(el, def);
      _handleEls.push({ el: el, def: def });
    }
    positionDragHandles();
  }

  function _buildHandleDefs(poly) {
    var defs = [];
    var n = poly ? poly.length : 0;
    for (var i = 0; i < n; i++) {
      defs.push({ type: 'corner', vertexIndex: i });
    }
    for (var i = 0; i < n; i++) {
      defs.push({ type: 'edge', edgeIndex: i });
    }
    return defs;
  }

  function _clampCoord(v) {
    if (v >  MAX_COORD) return  MAX_COORD;
    if (v < -MAX_COORD) return -MAX_COORD;
    return v;
  }

  function _worldToScreen(x, z) {
    var eng = global.BaseEngine;
    if (!eng || !eng.camera || !eng.renderer) return null;
    var T = global.THREE;
    if (!T) return null;
    var v = new T.Vector3(x, 0, z);
    v.project(eng.camera);
    var canvasRect = eng.renderer.domElement.getBoundingClientRect();
    var parent = document.getElementById('cf-wrap-base');
    var parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
    return {
      left: (canvasRect.left - parentRect.left) + ((v.x + 1) / 2) * canvasRect.width,
      top:  (canvasRect.top  - parentRect.top)  + ((1 - (v.y + 1) / 2)) * canvasRect.height,
      depth: v.z
    };
  }

  function _screenToWorld(clientX, clientY) {
    var eng = global.BaseEngine;
    if (!eng || !eng.camera || !eng.renderer) return null;
    var T = global.THREE;
    if (!T) return null;
    var rect = eng.renderer.domElement.getBoundingClientRect();
    var ndc = new T.Vector2(
      ((clientX - rect.left) / rect.width)  * 2 - 1,
      -((clientY - rect.top)  / rect.height) * 2 + 1
    );
    var ray = new T.Raycaster();
    ray.setFromCamera(ndc, eng.camera);
    var plane = new T.Plane(new T.Vector3(0, 1, 0), 0);
    var p = new T.Vector3();
    ray.ray.intersectPlane(plane, p);
    return p ? { x: p.x, z: p.z } : null;
  }

  function positionDragHandles() {
    if (STATE.view !== '2d') {
      for (var i = 0; i < _handleEls.length; i++) {
        _handleEls[i].el.style.display = 'none';
      }
      return;
    }
    var poly = getFootprintPolygon();
    var n = poly.length;
    for (var i = 0; i < _handleEls.length; i++) {
      var h = _handleEls[i];
      var def = h.def;
      var wx = 0, wz = 0;
      if (def.type === 'corner') {
        wx = poly[def.vertexIndex][0];
        wz = poly[def.vertexIndex][1];
      } else if (def.type === 'edge') {
        var a = poly[def.edgeIndex];
        var b = poly[(def.edgeIndex + 1) % n];
        wx = (a[0] + b[0]) / 2;
        wz = (a[1] + b[1]) / 2;
      }
      var s = _worldToScreen(wx, wz);
      if (!s) continue;
      h.el.style.left = (s.left - 6) + 'px';
      h.el.style.top  = (s.top  - 6) + 'px';
      h.el.style.display = 'block';
    }
  }

  function _attachHandleDrag(el, def) {
    var dragging = false;
    var startWorld = null;
    var startState = null;
    var startPoly = null;

    el.addEventListener('pointerdown', function (e) {
      if (STATE.view !== '2d') return;
      e.preventDefault();
      dragging = true;
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
      var eng = global.BaseEngine;
      if (eng && eng.controls) eng.controls.enabled = false;
      startWorld = _screenToWorld(e.clientX, e.clientY);
      startState = JSON.parse(JSON.stringify(STATE.polygon));
      startPoly = getFootprintPolygon();
    });

    el.addEventListener('pointermove', function (e) {
      if (!dragging || !startWorld) return;
      if (STATE.view !== '2d') { dragging = false; return; }
      var p = _screenToWorld(e.clientX, e.clientY);
      if (!p) return;
      var dx = p.x - startWorld.x;
      var dz = p.z - startWorld.z;
      _applyHandleDelta(def, dx, dz, startState);
      refreshUI();
      var nowPoly = getFootprintPolygon();
      _showEdgeDimensionLabels(_changingEdges(nowPoly, startPoly));
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      try { if (e) el.releasePointerCapture(e.pointerId); } catch (_) {}
      var eng = global.BaseEngine;
      if (eng && eng.controls) eng.controls.enabled = true;
      _hideAllEdgeLabels();
      if (startState) _UndoHistory.pushIfChanged(startState, STATE.variant);
      startState = null;
    }
    el.addEventListener('pointerup',     endDrag);
    el.addEventListener('pointercancel', endDrag);
    el._forceEndDrag = function () {
      if (!dragging) return;
      dragging = false;
      var eng = global.BaseEngine;
      if (eng && eng.controls) eng.controls.enabled = true;
      _hideAllEdgeLabels();
      if (startState) _UndoHistory.pushIfChanged(startState, STATE.variant);
      startState = null;
    };
  }

  function _resetAllHandleDrags() {
    for (var i = 0; i < _handleEls.length; i++) {
      var hel = _handleEls[i] && _handleEls[i].el;
      if (hel && hel._forceEndDrag) hel._forceEndDrag();
    }
    var widget = document.getElementById('cf-budget-widget');
    if (widget && widget._forceEndDrag) widget._forceEndDrag();
  }

  function _changingEdges(currentPoly, startPoly) {
    if (!currentPoly || !startPoly) return [];
    if (currentPoly.length !== startPoly.length) return [];
    var changed = [];
    var n = currentPoly.length;
    for (var i = 0; i < n; i++) {
      var ca = currentPoly[i], cb = currentPoly[(i + 1) % n];
      var sa = startPoly[i],   sb = startPoly[(i + 1) % n];
      var clen = Math.hypot(cb[0] - ca[0], cb[1] - ca[1]);
      var slen = Math.hypot(sb[0] - sa[0], sb[1] - sa[1]);
      if (Math.abs(clen - slen) > 0.0001) changed.push(i);
    }
    return changed;
  }

  function _applyHandleDelta(def, dx, dz, s0) {
    var poly = STATE.polygon;
    if (!poly || !poly.length) return;
    var n = s0.length;
    if (def.type === 'corner') {
      var i = def.vertexIndex;
      if (i < 0 || i >= n) return;
      var nx = snap(s0[i][0] + dx);
      var nz = snap(s0[i][1] + dz);
      poly[i][0] = _clampCoord(nx);
      poly[i][1] = _clampCoord(nz);
    } else if (def.type === 'edge') {
      var ei = def.edgeIndex;
      if (ei < 0 || ei >= n) return;
      var a = s0[ei];
      var b = s0[(ei + 1) % n];
      var edx = b[0] - a[0], edz = b[1] - a[1];
      var len = Math.hypot(edx, edz);
      if (len < 1e-6) return;
      var nrmX = -edz / len, nrmZ = edx / len;
      var proj = dx * nrmX + dz * nrmZ;
      var ax = snap(a[0] + proj * nrmX);
      var az = snap(a[1] + proj * nrmZ);
      var bx = snap(b[0] + proj * nrmX);
      var bz = snap(b[1] + proj * nrmZ);
      poly[ei][0] = _clampCoord(ax);
      poly[ei][1] = _clampCoord(az);
      poly[(ei + 1) % n][0] = _clampCoord(bx);
      poly[(ei + 1) % n][1] = _clampCoord(bz);
    }
  }

  var _edgeLabels = [];
  var _edgeLabelCssInjected = false;

  function _injectEdgeLabelCSS() {
    if (_edgeLabelCssInjected) return;
    _edgeLabelCssInjected = true;
    var css = '#cf-wrap-base .cf-base-dim-label {' +
              '  position: absolute; z-index: 12;' +
              '  background: rgba(20,20,20,0.92); color: #fff;' +
              '  font: 700 11px/1.2 Montserrat, -apple-system, sans-serif;' +
              '  padding: 4px 8px; border-radius: 6px;' +
              '  letter-spacing: 0.02em; white-space: nowrap;' +
              '  pointer-events: none;' +
              '  transform: translate(-50%, -50%);' +
              '  box-shadow: 0 2px 6px rgba(0,0,0,0.25);' +
              '}';
    var style = document.createElement('style');
    style.id = 'cf-base-dim-label-css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function _ensureEdgeLabelPool(n) {
    _injectEdgeLabelCSS();
    var parent = document.getElementById('cf-wrap-base');
    if (!parent) return;
    while (_edgeLabels.length < n) {
      var el = document.createElement('div');
      el.className = 'cf-base-dim-label';
      el.style.display = 'none';
      parent.appendChild(el);
      _edgeLabels.push(el);
    }
  }

  function _hideAllEdgeLabels() {
    for (var i = 0; i < _edgeLabels.length; i++) {
      _edgeLabels[i].style.display = 'none';
    }
  }

  function _showEdgeDimensionLabels(activeEdgeIndices) {
    var poly = getFootprintPolygon();
    if (!poly || !poly.length) { _hideAllEdgeLabels(); return; }
    var n = poly.length;
    _ensureEdgeLabelPool(n);

    for (var i = 0; i < n; i++) {
      var label = _edgeLabels[i];
      if (activeEdgeIndices.indexOf(i) === -1) {
        label.style.display = 'none';
        continue;
      }
      var a = poly[i];
      var b = poly[(i + 1) % n];
      var mx = (a[0] + b[0]) / 2;
      var mz = (a[1] + b[1]) / 2;
      var dx = b[0] - a[0];
      var dz = b[1] - a[1];
      var lenWorld = Math.sqrt(dx * dx + dz * dz);
      label.textContent = fmtDim(lenWorld);

      var ms = _worldToScreen(mx, mz);
      if (!ms) { label.style.display = 'none'; continue; }

      var nlen = Math.sqrt(dx * dx + dz * dz);
      var nxW, nzW;
      if (nlen < 1e-6) {
        nxW = 0; nzW = -1;
      } else {
        nxW = -dz / nlen;
        nzW =  dx / nlen;
      }
      var probe = 0.02;
      if (_pointInPolygonOrBoundary(mx + nxW * probe, mz + nzW * probe, poly)) {
        nxW = -nxW; nzW = -nzW;
      }
      var probeS = _worldToScreen(mx + nxW * probe, mz + nzW * probe);
      var sx, sy, slen;
      if (probeS) {
        sx = probeS.left - ms.left;
        sy = probeS.top  - ms.top;
        slen = Math.sqrt(sx * sx + sy * sy);
        if (slen < 0.001) { sx = 0; sy = -1; slen = 1; }
      } else {
        sx = 0; sy = -1; slen = 1;
      }
      var off = 26;
      var lx = ms.left + (sx / slen) * off;
      var ly = ms.top  + (sy / slen) * off;

      label.style.display = 'block';
      label.style.left = lx + 'px';
      label.style.top  = ly + 'px';
    }
  }

  function _startBudgetWidgetSync() {
    var dstTotal    = document.getElementById('cf-bw-total');
    var dstMaterial = document.getElementById('cf-bw-material');
    var dstExec     = document.getElementById('cf-bw-execution');
    var dstTime     = document.getElementById('cf-bw-time');
    var dstCapacity = document.getElementById('cf-bw-capacity');

    function refresh() {
      var srcTotal    = document.querySelector('[data-budget-total-cost]');
      var srcMaterial = document.querySelector('[data-budget-total-material]');
      var srcExec     = document.querySelector('[data-budget-total-execution]');
      var srcTime     = document.querySelector('[data-budget-total-time]');

      var totalText    = (srcTotal    && srcTotal.textContent)    ? srcTotal.textContent.trim()    : '';
      var materialText = (srcMaterial && srcMaterial.textContent) ? srcMaterial.textContent.trim() : '';
      var execText     = (srcExec     && srcExec.textContent)     ? srcExec.textContent.trim()     : '';
      var timeText     = (srcTime     && srcTime.textContent)     ? srcTime.textContent.trim()     : '';

      var needFallback = (!totalText || totalText === '\u2014' || !materialText || materialText === '\u2014');
      if (needFallback) {
        var matVal  = (typeof global.lastMaterialsTotal === 'number') ? global.lastMaterialsTotal : null;
        var execVal = (typeof global.lastDeliveryTotal  === 'number') ? global.lastDeliveryTotal  : null;
        if (matVal !== null || execVal !== null) {
          var fmt = (typeof global.formatPrice === 'function')
            ? global.formatPrice
            : function (n) { return '$' + Math.round(n || 0); };
          var m = matVal  || 0;
          var e = execVal || 0;
          if (!totalText    || totalText    === '\u2014') totalText    = fmt(m + e);
          if (!materialText || materialText === '\u2014') materialText = fmt(m);
          if (!execText     || execText     === '\u2014') execText     = fmt(e);
        }
      }

      if (dstTotal    && totalText)    dstTotal.textContent    = totalText;
      if (dstMaterial && materialText) dstMaterial.textContent = materialText;
      if (dstExec     && execText)     dstExec.textContent     = execText;
      if (dstTime     && timeText)     dstTime.textContent     = timeText;

      if (dstCapacity) {
        var rows = computeBoQ();
        var excavM3 = 0;
        for (var i = 0; i < rows.length; i++) {
          if (rows[i] && rows[i][0] === 'FEE00001') {
            excavM3 = rows[i][1] || 0;
            break;
          }
        }
        dstCapacity.textContent = STATE.isMetric
          ? excavM3.toFixed(1) + ' m\u00b3'
          : (excavM3 * 35.3147).toFixed(1) + ' ft\u00b3';
      }
    }
    refresh();
    setInterval(refresh, 500);

    setTimeout(function () {
      if (typeof global.updateTotal !== 'function') return;
      var srcTotal = document.querySelector('[data-budget-total-cost]');
      var t = (srcTotal && srcTotal.textContent) ? srcTotal.textContent.trim() : '';
      var stillEmpty = (t === '' || t === '\u2014' || typeof global.lastMaterialsTotal !== 'number');
      if (!stillEmpty) return;
      try { global.updateTotal(); } catch (err) {}
    }, 2500);
  }

  function _initBudgetWidgetDrag() {
    var widget = document.getElementById('cf-budget-widget');
    var toggle = document.getElementById('cf-budget-widget-toggle');
    if (!widget) return;
    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var collapsed = widget.getAttribute('data-collapsed') === 'true';
        widget.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
      });
      toggle.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
    }

    var dragging = false, startX = 0, startY = 0, startLeft = 0, startTop = 0;
    widget.addEventListener('pointerdown', function (e) {
      if (e.target.closest('#cf-budget-widget-toggle')) return;
      dragging = true;
      startX = e.clientX; startY = e.clientY;
      var r = widget.getBoundingClientRect();
      var pr = (widget.offsetParent || document.body).getBoundingClientRect();
      startLeft = r.left - pr.left;
      startTop  = r.top  - pr.top;
      try { widget.setPointerCapture(e.pointerId); } catch (_) {}
    });
    widget.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      var parent = widget.offsetParent || document.body;
      var pr = parent.getBoundingClientRect();
      var nx = Math.max(0, Math.min(pr.width  - widget.offsetWidth,  startLeft + dx));
      var ny = Math.max(0, Math.min(pr.height - widget.offsetHeight, startTop  + dy));
      widget.style.left = nx + 'px';
      widget.style.top  = ny + 'px';
      widget.style.right = 'auto';
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      try { if (e) widget.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    widget.addEventListener('pointerup',     endDrag);
    widget.addEventListener('pointercancel', endDrag);
    widget._forceEndDrag = function () { dragging = false; };
  }

  function share() {
    if (global.ConfiguratorShare && typeof global.ConfiguratorShare.open === 'function') {
      try { global.ConfiguratorShare.open(); return; }
      catch (err) { _dbg('ConfiguratorShare.open threw: ' + (err && err.message ? err.message : err)); }
    }
    var TT = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
    var txt = TT.shareFallbackText || 'Foundation configuration from LEVA';
    var title = TT.shareFallbackTitle || 'Configuration';
    if (navigator.share) {
      navigator.share({ title: title, text: txt }).catch(function () {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(txt);
      alert(TT.shareCopied || 'Copied to clipboard');
    }
  }

  function openBudgetDrawer() {
    /* Any user-initiated open of the drawer counts as "engaged"
       and disables the onboarding nudge for this product. The
       30-day throttle is also written so we don't pop it again
       if they refresh. */
    try {
      var BOOT = global.BASE_BOOT || {};
      global.__cfBudgetUserTouched = true;
      if (BOOT.productId) {
        localStorage.setItem('cf-budget-touched-' + BOOT.productId, String(Date.now()));
      }
      _onboardMarkSeen();
    } catch (_) {}
    if (typeof global.openShareBudgetDrawer === 'function') {
      global.openShareBudgetDrawer('data');
    } else if (typeof global.openRefreshDrawer === 'function') {
      global.openRefreshDrawer();
    } else {
      _dbg('no budget drawer opener on window');
    }
  }

  function toggleView() {
    if (global.BaseEngine && typeof global.BaseEngine.toggleView === 'function') {
      global.BaseEngine.toggleView();
      /* After landing back in 3D (user clicked 2D->3D), Chromium can
         hold a stale composite layer just like on variant/unit/style
         switches. _forceRender3DFlush early-exits when STATE.view is
         not '3d', so calling it unconditionally is safe -- the 3D->2D
         direction is a no-op. */
      _forceRender3DFlush(2);
    }
  }

  /* PDF is the primary deliverable. Until the central BoQ data has loaded
     (names + prices), an export would be incomplete -- so the button stays
     visually disabled (greyed, non-clickable). Once data is ready it is
     promoted to the single RED primary action (white icon) so it stands out. */
  var PDF_BTN_RED = '#D8232A';   /* LEVA red */

  function _initPdfButtonState() {
    var btn = document.getElementById('cf-btn-pdf');
    if (!btn) return;

    if (!document.getElementById('cf-pdf-btn-style')) {
      var st = document.createElement('style');
      st.id = 'cf-pdf-btn-style';
      st.textContent =
        '#cf-btn-pdf.cf-pdf-loading{opacity:.4;pointer-events:none;cursor:default;filter:grayscale(1);}' +
        '#cf-btn-pdf.cf-pdf-ready{background:' + PDF_BTN_RED + ' !important;border-color:' + PDF_BTN_RED + ' !important;color:#fff !important;}' +
        /* White icon regardless of how it is rendered (inline SVG,
           <img>, or a CSS mask / background-image child). */
        '#cf-btn-pdf.cf-pdf-ready > *{filter:brightness(0) invert(1) !important;}';
      document.head.appendChild(st);
    }

    var bridge = global.ConfiguratorBudgetBridge || null;
    function isReady() {
      return !(bridge && typeof bridge.isReady === 'function') || bridge.isReady();
    }
    function promote() {
      btn.classList.remove('cf-pdf-loading');
      btn.classList.add('cf-pdf-ready');
    }

    if (isReady()) { promote(); return; }

    btn.classList.add('cf-pdf-loading');
    if (bridge && typeof bridge.onReady === 'function') {
      try { bridge.onReady(promote); } catch (e) {}
    }
    var waited = 0;
    var iv = setInterval(function () {
      if (isReady()) { clearInterval(iv); promote(); }
      else if ((waited += 300) >= 30000) { clearInterval(iv); promote(); }  /* fail-open after 30 s */
    }, 300);
  }

  function wireSidebar() {
    var on = function (id, fn) {
      var el = document.getElementById(id);
      if (el) el.addEventListener('click', fn);
    };
    on('cf-btn-view',     toggleView);
    on('cf-btn-undo',     undo);
    on('cf-btn-lvl',      cycleLvl);
    on('cf-btn-assembly', cycleAssembly);
    _SlopeManager.init();
    on('cf-btn-unit',     toggleUnits);
    on('cf-btn-budget',   openBudgetDrawer);
    on('cf-btn-share',    share);
    on('cf-btn-pdf',      function () { _openPdfSocialModal(); });
    on('cf-btn-ar',       function () { _openArModal(); });

    var prevBtn = document.getElementById('cf-base-variant-prev');
    var nextBtn = document.getElementById('cf-base-variant-next');
    if (prevBtn) prevBtn.addEventListener('click', prevVariant);
    if (nextBtn) nextBtn.addEventListener('click', nextVariant);

    _syncVariantNameDisplay();

    /* PDF button: disabled until data loads, then promoted to red primary. */
    _initPdfButtonState();

    var opacitySlider = document.getElementById('cf-2d-opacity-slider');
    if (opacitySlider) {
      opacitySlider.addEventListener('input', function (e) {
        var sv = parseInt(e.target.value, 10);
        STATE.opacity2D = (100 - sv) / 100;
        if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
      });
    }

    _startBudgetWidgetSync();
    _initBudgetWidgetDrag();
    _init2DZoom();
    _init2DMapPan();
    _bindIconTooltips();
    _VisualizationManager.init();
    _CornerInserter.init();
    _GapInserter.init();
    _GateDragManager.init();
    _SectionDragManager.init();
    _SpacingSlider.init();
    _HeightSlider.init();
    _initStyleCycleButton();
    _syncWoodInfoColumns();
    _UndoHistory._updateButton();
    _updateLvlButton();
    _updateAssemblyButton();
    _preloadLvlAvatars();

    var _bridgeRetry = 0;
    var _bridgeTimer = setInterval(function () {
      _bridgeRetry++;
      var bridge = global.ConfiguratorBudgetBridge;
      if (bridge && bridge.isReady && bridge.isReady()) {
        _updateAssemblyButton();
        clearInterval(_bridgeTimer);
      } else if (_bridgeRetry > 150) {
        clearInterval(_bridgeTimer);
      }
    }, 200);

    global.addEventListener('prices-updated', function () {
      _updateAssemblyButton();
    });

    global.addEventListener('unitChange', _syncFromBudgetUnit);
    setTimeout(_syncFromBudgetUnit, 50);
  }

  var _CORE_CSS_INJECTED = false;
  function _injectCoreCSS() {
    if (_CORE_CSS_INJECTED) return;
    if (document.getElementById('cf-core-css-inline')) { _CORE_CSS_INJECTED = true; return; }
    var css = [
      ':root { --cfg-color-primary: #D2A878; }',
      '.cfg-slider-vert-wrap {',
      '  display: inline-flex; flex-direction: column; align-items: center;',
      '  background: transparent; padding: 0; user-select: none;',
      '}',
      '.cfg-slider-vert {',
      '  -webkit-appearance: none; appearance: none;',
      '  width: 4px; height: 130px;',
      '  background: linear-gradient(to top,',
      '    var(--cfg-color-primary, #D2A878) 0%,',
      '    var(--cfg-color-primary, #D2A878) var(--cfg-fill, 50%),',
      '    #e0e0e0 var(--cfg-fill, 50%),',
      '    #e0e0e0 100%);',
      '  border-radius: 10px; outline: none; cursor: pointer; touch-action: none;',
      '  writing-mode: tb-lr; -webkit-writing-mode: vertical-lr;',
      '}',
      '.cfg-slider-vert::-webkit-slider-thumb {',
      '  -webkit-appearance: none; appearance: none;',
      '  width: 12px; height: 12px;',
      '  background: var(--cfg-color-primary, #D2A878);',
      '  border: none; border-radius: 50%; cursor: pointer;',
      '  box-shadow: 0 2px 6px rgba(0,0,0,0.20);',
      '}',
      '.cfg-slider-vert::-moz-range-thumb {',
      '  width: 12px; height: 12px;',
      '  background: var(--cfg-color-primary, #D2A878);',
      '  border: none; border-radius: 50%; cursor: pointer;',
      '  box-shadow: 0 2px 6px rgba(0,0,0,0.20);',
      '}',
      '.cf-dim-chip {',
      '  position: absolute; transform: translate(-50%, -50%);',
      '  background: #ffffff; border-radius: 8px; padding: 6px 10px;',
      '  font-family: Montserrat, system-ui, sans-serif;',
      '  font-size: 12px; font-weight: 700; color: #1a1a1a;',
      '  box-shadow: 0 2px 8px rgba(0,0,0,0.12);',
      '  display: inline-flex; align-items: center; gap: 6px;',
      '  white-space: nowrap; pointer-events: none; touch-action: none;',
      '  user-select: none; z-index: 8;',
      '}'
    ].join('\n');
    var s = document.createElement('style');
    s.id = 'cf-core-css-inline';
    s.textContent = css;
    document.head.appendChild(s);
    _CORE_CSS_INJECTED = true;
  }

  /* ---------------------------------------------------------------
     SectionCutManager
     Provides assembly step 2 = vertical section cut through the
     3D scene. The cutting plane is computed deterministically per
     variant + polygon so the result is identical for every user.

     Pipeline:
       1. compute()  -- find a cut plane (THREE.Plane) per variant
       2. apply()    -- set material.clippingPlanes on every mesh
                       in the scene group (called from BaseRenderer)
       3. addCaps()  -- overlay opaque cap meshes so the cut surface
                       reads as solid instead of hollow
       4. clear()    -- remove clipping + caps for all other steps

     Plane orientation: perpendicular to the LONGER polygon edge --
     that maximises the amount of material visible in the slice.
     Position: passes through the polygon centroid for slab / strip
     variants; for piers / precast / screws it snaps onto the
     element nearest the centroid so the cut actually intersects
     something instead of slicing between objects.
     --------------------------------------------------------------- */
  var _SectionCutManager = {
    _capsGroup: null,
    _outlineGroup: null,
    _activePlane: null,
    _activeOffset: 0,
    /* When the user drags the section plane we store the chosen coord
       here; subsequent refreshes use it instead of auto-positioning.
       Reset to null on variant / polygon change so the auto-pick
       behaviour returns for a fresh scene. */
    _manualOffset: null,
    _dragRange: null,    /* {min,max} along the cut axis, for clamping */

    /* Compute a THREE.Plane that slices the scene vertically through
       a sensible spot. Returns null if THREE isn't ready yet (caller
       must noop). */
    compute: function (poly, variantKey, eng) {
      var T = global.THREE;
      if (!T || !poly || poly.length < 3) return null;

      /* 1. find polygon bbox + centroid */
      var xs = poly.map(function (p) { return p[0]; });
      var zs = poly.map(function (p) { return p[1]; });
      var xMin = Math.min.apply(null, xs), xMax = Math.max.apply(null, xs);
      var zMin = Math.min.apply(null, zs), zMax = Math.max.apply(null, zs);
      var cx = (xMin + xMax) / 2;
      var cz = (zMin + zMax) / 2;
      var bbL = xMax - xMin;
      var bbW = zMax - zMin;

      /* 2. pick orientation -- cut perpendicular to the longer edge.
         If the polygon is wider than it is deep we cut along the X
         axis (plane normal = +Z), otherwise along Z (normal = +X). */
      var cutAlongX = (bbL >= bbW);
      var normal = cutAlongX
        ? new T.Vector3(0, 0, 1)
        : new T.Vector3(1, 0, 0);

      /* 3. position the plane. For uniform variants (slab / strip)
         the centroid is fine. For discrete elements (piers, precast,
         screws) the plane must pass THROUGH one of them or the cut
         will land in empty space and show nothing useful. */
      var planeCoord = cutAlongX ? cz : cx;

      var elementPositions = this._findElementPositions(variantKey, eng);
      if (elementPositions && elementPositions.length) {
        var best = null, bestDist = Infinity;
        for (var i = 0; i < elementPositions.length; i++) {
          var p = elementPositions[i];
          var d = cutAlongX
            ? Math.abs(p.z - cz)
            : Math.abs(p.x - cx);
          if (d < bestDist) { bestDist = d; best = p; }
        }
        if (best) planeCoord = cutAlongX ? best.z : best.x;
      }

      /* Record the draggable range along the cut axis (polygon bbox
         plus a small margin) so the drag handler can clamp. */
      this._dragRange = cutAlongX
        ? { min: zMin, max: zMax }
        : { min: xMin, max: xMax };

      /* If the user has dragged the plane, honour their chosen coord
         (clamped to the polygon) instead of the auto-picked element. */
      if (typeof this._manualOffset === 'number' && isFinite(this._manualOffset)) {
        var lo = this._dragRange.min, hi = this._dragRange.max;
        planeCoord = Math.max(lo, Math.min(hi, this._manualOffset));
      }

      /* THREE.Plane equation: n.x + constant = 0
         Fragments are clipped where n.x + constant < 0. We want to
         hide the NEAR half of the model (the half closest to the
         default camera angle), so the cut surface ends up facing
         the user without them having to orbit. Practically that
         means picking a normal that points TOWARDS the visible
         half, then making the plane sit at planeCoord. */
      var plane = new T.Plane(normal.clone().negate(), planeCoord);
      this._activePlane = plane;
      this._activeOffset = planeCoord;
      this._activeCutAlongX = cutAlongX;
      return plane;
    },

    /* Pull world positions of discrete elements out of the scene
       graph, so the section-cut plane can auto-snap to pass THROUGH a
       post rather than landing in an empty bay. For fences the natural
       target is the posts (all variants tag them). */
    _findElementPositions: function (variantKey, eng) {
      if (!eng || !eng.sceneGroup) return [];
      var POST_TYPES = {
        'fence-post-wood': true, 'fence-post': true, 'fence-post-vinyl': true,
        'fence-post-cp': true, 'fence-post-chain': true, 'fence-post-steel': true,
        'gabion-post': true
      };
      var hits = [];
      eng.sceneGroup.traverse(function (node) {
        if (!node || !node.userData) return;
        if (POST_TYPES[node.userData.assetType]) {
          var pos = new global.THREE.Vector3();
          node.getWorldPosition(pos);
          hits.push(pos);
        }
      });
      return hits;
    },

    /* Walk the scene group and attach our plane to every material
       so the GPU clips fragments on the far side of it. We also
       enable per-material 'clipShadows: false' because the scene's
       shadows look weird when the casting mesh is half-clipped.
       Returns the number of materials touched (handy for logging). */
    apply: function (plane) {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup || !plane) return 0;
      var n = 0;
      eng.sceneGroup.traverse(function (node) {
        /* Hide excavation wireframes while the section cut is active --
           the clipped gold line-boxes read as visual noise across the
           cut face, and the concrete/gravel footing caps already show
           what's below grade. They're restored in clear(). */
        if (node.userData && node.userData.assetType === 'excavation-box') {
          node.visible = false;
          return;
        }
        if (!node.material) return;
        var mats = Array.isArray(node.material) ? node.material : [node.material];
        for (var i = 0; i < mats.length; i++) {
          var m = mats[i];
          if (!m) continue;
          m.clippingPlanes = [plane];
          m.clipShadows = false;
          m.needsUpdate = true;
          n++;
        }
      });
      return n;
    },

    /* Remove any active clipping + cap geometry. Called when the
       user leaves step 2 of the assembly cycle. */
    clear: function () {
      var eng = global.BaseEngine;
      if (eng && eng.sceneGroup) {
        eng.sceneGroup.traverse(function (node) {
          /* Restore excavation wireframes hidden by apply(). */
          if (node.userData && node.userData.assetType === 'excavation-box') {
            node.visible = true;
          }
          if (!node.material) return;
          var mats = Array.isArray(node.material) ? node.material : [node.material];
          for (var i = 0; i < mats.length; i++) {
            if (mats[i]) {
              mats[i].clippingPlanes = null;
              mats[i].needsUpdate = true;
            }
          }
        });
      }
      this._removeCapsGroup();
      this._activePlane = null;
    },

    _removeCapsGroup: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      if (this._capsGroup) {
        eng.sceneGroup.remove(this._capsGroup);
        this._disposeGroup(this._capsGroup);
        this._capsGroup = null;
      }
      if (this._outlineGroup) {
        eng.sceneGroup.remove(this._outlineGroup);
        this._disposeGroup(this._outlineGroup);
        this._outlineGroup = null;
      }
    },

    _disposeGroup: function (group) {
      group.traverse(function (n) {
        if (n.geometry) n.geometry.dispose();
        if (n.material) {
          var ms = Array.isArray(n.material) ? n.material : [n.material];
          ms.forEach(function (m) { m && m.dispose && m.dispose(); });
        }
      });
    },

    /* Add a thin highlighted line along the cut so the user can see
       exactly where the slice plane sits in 3D -- without this it
       reads as just "half the model deleted". */
    addOutline: function (plane, poly, eng) {
      var T = global.THREE;
      if (!T || !plane || !poly || !eng) return;
      var grp = new T.Group();
      grp.userData.assetType = 'section-cut-aux';

      var cutAlongX = this._activeCutAlongX;
      var coord = this._activeOffset;
      var heightTop = 0.6;
      var heightBot = -2.0;

      var xs = poly.map(function (p) { return p[0]; });
      var zs = poly.map(function (p) { return p[1]; });
      var xMin = Math.min.apply(null, xs), xMax = Math.max.apply(null, xs);
      var zMin = Math.min.apply(null, zs), zMax = Math.max.apply(null, zs);

      var a, b;
      if (cutAlongX) {
        a = new T.Vector3(xMin - 0.5, heightBot, coord);
        b = new T.Vector3(xMax + 0.5, heightBot, coord);
      } else {
        a = new T.Vector3(coord, heightBot, zMin - 0.5);
        b = new T.Vector3(coord, heightBot, zMax + 0.5);
      }
      var top = a.clone(); top.y = heightTop;
      var top2 = b.clone(); top2.y = heightTop;

      var lineGeo = new T.BufferGeometry().setFromPoints([a, b, top2, top, a]);
      var lineMat = new T.LineBasicMaterial({ color: 0xd2a878, linewidth: 2 });
      grp.add(new T.Line(lineGeo, lineMat));

      eng.sceneGroup.add(grp);
      this._outlineGroup = grp;
    },

    /* Stencil-buffer cap fill -- turns the "thin-shell hole" effect
       into a proper solid cross-section.

       Per-material variant: we don't paint every cut surface the
       same neutral grey. Instead we group the solid meshes by their
       source material and run an independent stencil pass per
       group, then draw a coloured cap using that material's own
       diffuse colour. The result: gravel reads brown, concrete
       reads grey, precast block reads its own tone -- same as the
       3D bodies they belong to.

       Rebar / wire mesh is rendered as line geometry, so the cap
       trick doesn't apply directly; we still want their cross-
       section to show up as small filled circles inside the slab
       cap. We do that as a second pass that ignores the stencil
       and uses additive blending so the rebar lines pop on top of
       the concrete cap.

       Algorithm per material group:
         1. clear stencil to 0
         2. render BACK faces of every mesh in the group with the
            cut clipping plane, INCREMENTing the stencil counter
         3. render FRONT faces DECREMENTing
            -> counter != 0 exactly where the cut is inside any mesh
              from this material group
         4. draw a big quad on the cut plane in the material's own
            colour, gated by 'stencilFunc: NotEqual, ref: 0'
            and CLEARING the stencil back to 0 with 'Zero' ops so
            the next material group starts from a clean slate

       Each pass is given its own renderOrder block (group N uses
       10*N+0 / +1 / +2) so Three.js processes them in the right
       sequence. */
    addCaps: function (plane, poly, eng) {
      var T = global.THREE;
      if (!T || !plane || !poly || !eng) return;

      var grp = new T.Group();
      grp.userData.assetType = 'section-cut-aux';

      /* Asset types that read as solid bodies -> get the per-material
         coloured cap. Wireframe assets (mesh / rebar) get a
         separate overlay pass below. */
      var SOLID_TYPES = {
        /* Fence solid components -- capped so the section cut shows a
           clean coloured cross-section through posts, rails, pickets,
           brackets and footings rather than the raw clipped shell. */
        'fence-post-wood':         true,
        'fence-rail-wood':         true,
        'fence-picket-wood':       true,
        'fence-cap-wood':          true,
        'fence-bracket':           true,
        'fence-footing-concrete':  true,
        'fence-footing-gravel':    true,
        'fence-post':              true,
        'fence-post-cap':          true,
        'fence-panel':             true,
        'fence-post-vinyl':        true,
        'fence-panel-vinyl':       true,
        'fence-post-cp':           true,
        'fence-panel-cp':          true,
        'fence-dwarf-wall':        true,
        'fence-post-chain':        true,
        'fence-post-steel':        true,
        'fence-panel-steel':       true,
        'gabion-fill':             true,
        'gabion-post':             true,
        'anchor':                  true
      };
      /* Wireframe / line assets -- drawn over the cap as accent lines
         so the user can still see the rebar grid intersecting the
         slab cross-section. */
      var WIRE_TYPES = {
        'mesh':  true,
        'rebar': true
      };

      /* Bucket meshes by their material UUID. Meshes that share the
         same material end up in the same group, so a single
         coloured cap covers everything that visually belongs
         together (e.g. all concrete pours).

         We darken the cap colour to 70% because the cap is drawn
         with MeshBasicMaterial (unlit), while the source meshes use
         MeshStandardMaterial which is attenuated by scene lighting.
         Cloning the raw colour straight across makes the caps look
         noticeably brighter than the surrounding 3D -- multiplying
         by 0.7 brings them roughly in line with how the rest of the
         lit geometry reads on screen. */
      var CAP_BRIGHTNESS = 0.7;
      var groups = {};
      eng.sceneGroup.traverse(function (node) {
        if (!node.isMesh || !node.geometry || !node.material) return;
        if (node.userData && node.userData.assetType === 'section-cut-aux') return;
        var t = (node.userData && node.userData.assetType)
              || (node.parent && node.parent.userData && node.parent.userData.assetType);
        if (!t || !SOLID_TYPES[t]) return;
        var mats = Array.isArray(node.material) ? node.material : [node.material];
        for (var mi = 0; mi < mats.length; mi++) {
          var m = mats[mi];
          if (!m || !m.color) continue;
          var key = m.uuid;
          if (!groups[key]) {
            var capColor = m.color.clone().multiplyScalar(CAP_BRIGHTNESS);
            groups[key] = { color: capColor, meshes: [], typeHint: t };
          }
          groups[key].meshes.push({ node: node, materialIndex: mi });
        }
      });

      var groupKeys = Object.keys(groups);
      _dbg('[section-cut] addCaps: ' + groupKeys.length + ' material groups');

      var xs = poly.map(function (p) { return p[0]; });
      var zs = poly.map(function (p) { return p[1]; });
      var xMin = Math.min.apply(null, xs), xMax = Math.max.apply(null, xs);
      var zMin = Math.min.apply(null, zs), zMax = Math.max.apply(null, zs);
      var capH = 4.0;
      var capW = (this._activeCutAlongX ? (xMax - xMin) : (zMax - zMin)) + 4;
      var capCenterX = this._activeCutAlongX ? (xMin + xMax) / 2 : this._activeOffset;
      var capCenterZ = this._activeCutAlongX ? this._activeOffset : (zMin + zMax) / 2;
      var capY = capH / 2 - 2.5;
      var self = this;

      function makeStencilMat(faceSide, op, refClear) {
        return new T.MeshBasicMaterial({
          side: faceSide,
          clippingPlanes: [plane],
          colorWrite: false,
          depthWrite: false,
          depthTest: true,
          stencilWrite: true,
          stencilFunc: T.AlwaysStencilFunc,
          stencilRef: 0,
          stencilMask: 0xff,
          stencilFail: T.KeepStencilOp,
          stencilZFail: op,
          stencilZPass: op
        });
      }

      function buildCapMesh(color, renderOrder) {
        /* The cap material uses NotEqual / Zero so the same pass
           both draws the colour AND resets the stencil to 0 -- that
           way the next material group's stencil pass starts clean
           without needing an explicit clear. */
        var capMat = new T.MeshBasicMaterial({
          color: color,
          side: T.DoubleSide,
          clippingPlanes: null,
          stencilWrite: true,
          stencilRef: 0,
          stencilMask: 0xff,
          stencilFunc: T.NotEqualStencilFunc,
          stencilFail: T.ZeroStencilOp,
          stencilZFail: T.ZeroStencilOp,
          stencilZPass: T.ZeroStencilOp
        });
        var capGeo = new T.PlaneGeometry(capW, capH);
        var cap = new T.Mesh(capGeo, capMat);
        cap.renderOrder = renderOrder;
        if (self._activeCutAlongX) {
          cap.position.set(capCenterX, capY, self._activeOffset);
        } else {
          cap.rotation.y = Math.PI / 2;
          cap.position.set(self._activeOffset, capY, capCenterZ);
        }
        return cap;
      }

      /* Each group gets its own renderOrder window so the stencil
         passes for group N+1 don't fight with the cap from group N.
         We leave plenty of room (10 slots per group) for the line
         overlay below. */
      for (var gi = 0; gi < groupKeys.length; gi++) {
        var g = groups[groupKeys[gi]];
        var base = 10 + gi * 10;
        var matBack  = makeStencilMat(T.BackSide,  T.IncrementWrapStencilOp);
        var matFront = makeStencilMat(T.FrontSide, T.DecrementWrapStencilOp);
        for (var i = 0; i < g.meshes.length; i++) {
          var entry = g.meshes[i];
          var src = entry.node;
          src.updateMatrixWorld(true);
          var geom = src.geometry.clone();
          geom.applyMatrix4(src.matrixWorld);
          var mB = new T.Mesh(geom, matBack);
          var mF = new T.Mesh(geom, matFront);
          mB.renderOrder = base;
          mF.renderOrder = base + 1;
          grp.add(mB);
          grp.add(mF);
        }
        var cap = buildCapMesh(g.color, base + 2);
        grp.add(cap);
      }

      /* Wire-frame overlay pass -- rebar / kari mesh lines that
         actually cross the cut plane render on top of whatever cap
         sits behind them. We just clone the line geometry, give it
         a clipping plane (so we only see the half-cut side), and
         draw it with a high render order. No stencil needed; lines
         have no interior to fill. */
      var wireOverlayCount = 0;
      eng.sceneGroup.traverse(function (node) {
        if (node.userData && node.userData.assetType === 'section-cut-aux') return;
        if (!(node.isLine || node.isLineSegments)) return;
        var t = (node.userData && node.userData.assetType)
              || (node.parent && node.parent.userData && node.parent.userData.assetType);
        if (!t || !WIRE_TYPES[t]) return;
        if (!node.material || !node.geometry) return;
        wireOverlayCount++;
      });
      _dbg('[section-cut] addCaps: ' + wireOverlayCount + ' wire overlays already clipped via main pass');
      /* Note: line meshes inherit the main clipping pipeline (we
         call apply() on every material in the scene), so the rebar
         already auto-clips. We don't duplicate them here -- that
         would draw them twice and make the lines look bold. */

      eng.sceneGroup.add(grp);
      if (this._capsGroup) {
        eng.sceneGroup.remove(this._capsGroup);
        this._disposeGroup(this._capsGroup);
      }
      this._capsGroup = grp;
    },

    /* Re-run the whole pipeline for the current state. Called from
       BaseRenderer after every build so the cut survives variant /
       level / polygon edits. Heavily logged so a console session
       can trace exactly why the cut didn't appear. */
    refresh: function () {
      var eng = global.BaseEngine;
      _dbg('[section-cut] refresh: vizStage=' + STATE.vizStage + ' view=' + STATE.view + ' eng=' + !!eng);
      if (!eng) return;
      var inSection = (STATE.vizStage === 2 && STATE.view === '3d');
      if (!inSection) {
        _dbg('[section-cut] refresh: not in section-cut mode, clearing');
        this.clear();
        return;
      }
      var poly = getFootprintPolygon();
      if (!poly || poly.length < 3) {
        _dbg('[section-cut] refresh: invalid polygon, clearing');
        this.clear();
        return;
      }
      var vk = _currentVariantKey();
      _dbg('[section-cut] refresh: computing plane for variant=' + vk + ' polyN=' + poly.length);
      var plane = this.compute(poly, vk, eng);
      if (!plane) {
        _dbg('[section-cut] refresh: compute returned null, clearing');
        this.clear();
        return;
      }
      _dbg('[section-cut] refresh: plane normal=' + JSON.stringify(plane.normal) + ' constant=' + plane.constant + ' offset=' + this._activeOffset + ' cutAlongX=' + this._activeCutAlongX);
      /* Drop any caps/outline from a previous refresh before building
         new ones. Without this, dragging the section plane (which calls
         refresh on every pointermove) stacks a fresh caps + outline
         group each frame, leaving a growing pile of clipped wireframes
         in the scene. */
      this._removeCapsGroup();
      var matCount = this.apply(plane);
      _dbg('[section-cut] refresh: applied clipping to ' + matCount + ' materials');
      this.addCaps(plane, poly, eng);
      this.addOutline(plane, poly, eng);
      _dbg('[section-cut] refresh: caps + outline added');
    },

    /* Returns true when section-cut mode is active (3D + vizStage 2),
       so the canvas drag handler knows whether to grab pointer events. */
    isActive: function () {
      return STATE.vizStage === 2 && STATE.view === '3d';
    },

    /* True when the section plane cuts along the X axis (normal = +/-Z),
       i.e. the user drags front-to-back (changes z); false = drags
       left-to-right (changes x). */
    isCutAlongX: function () {
      return !!this._activeCutAlongX;
    },

    /* Drag entry points. setManualOffset records the user's chosen
       coordinate along the cut axis and re-runs the clip/cap pass.
       resetManualOffset returns to automatic element-snapping. */
    setManualOffset: function (coord) {
      this._manualOffset = coord;
      this.refresh();
    },
    resetManualOffset: function () {
      this._manualOffset = null;
    },
    getActiveOffset: function () {
      return this._activeOffset;
    },
    getDragRange: function () {
      return this._dragRange;
    }
  };

  /* Drag-to-move the 3D section plane. Active only while section-cut
     mode is on (3D + vizStage 2). A horizontal pointer drag on the
     canvas slides the cutting plane along its axis; OrbitControls are
     suspended for the duration so the camera doesn't spin instead. */
  var _SectionDragManager = {
    _dragging: false,
    _startClient: null,
    _startOffset: 0,
    _bound: false,

    init: function () {
      if (this._bound) return;
      var eng = global.BaseEngine;
      if (!eng || !eng.renderer || !eng.renderer.domElement) return;
      var el = eng.renderer.domElement;
      var self = this;

      el.addEventListener('pointerdown', function (e) {
        if (!_SectionCutManager.isActive()) return;
        self._dragging = true;
        self._startClient = { x: e.clientX, y: e.clientY };
        self._startOffset = _SectionCutManager.getActiveOffset();
        if (eng.controls) eng.controls.enabled = false;
        try { el.setPointerCapture(e.pointerId); } catch (_) {}
        e.preventDefault();
      });

      el.addEventListener('pointermove', function (e) {
        if (!self._dragging) return;
        if (!_SectionCutManager.isActive()) { self._endDrag(e); return; }
        /* Map screen-space drag to a world delta on the ground plane,
           then take the component along the cut axis. Using the ground
           plane projection keeps the plane following the cursor at any
           zoom / orbit angle. */
        var pStart = _screenToWorld(self._startClient.x, self._startClient.y);
        var pNow   = _screenToWorld(e.clientX, e.clientY);
        if (!pStart || !pNow) return;
        var delta = _SectionCutManager.isCutAlongX()
          ? (pNow.z - pStart.z)
          : (pNow.x - pStart.x);
        _SectionCutManager.setManualOffset(self._startOffset + delta);
        e.preventDefault();
      });

      var endFn = function (e) { self._endDrag(e); };
      el.addEventListener('pointerup',     endFn);
      el.addEventListener('pointercancel', endFn);
      this._bound = true;
    },

    _endDrag: function (e) {
      if (!this._dragging) return;
      this._dragging = false;
      var eng = global.BaseEngine;
      if (eng) {
        if (eng.controls) eng.controls.enabled = true;
        if (e && eng.renderer && eng.renderer.domElement) {
          try { eng.renderer.domElement.releasePointerCapture(e.pointerId); } catch (_) {}
        }
      }
    }
  };

  var _BaseDimensions = {
    svgEl: null,
    chipLayer: null,
    chipEls: [],
    SVG_NS: 'http://www.w3.org/2000/svg',

    init: function () {
      var root = document.getElementById('cf-wrap-base');
      if (!root) return;
      if (!this.svgEl) {
        var svg = document.createElementNS(this.SVG_NS, 'svg');
        svg.id = 'cf-base-dims-svg';
        svg.style.cssText = [
          'position:absolute', 'top:0', 'left:0',
          'width:100%', 'height:100%',
          'pointer-events:none', 'display:none', 'z-index:6'
        ].join(';');
        root.appendChild(svg);
        this.svgEl = svg;
        var self = this;
        global.addEventListener('resize', function () { self.refresh(); });
      }
      if (!this.chipLayer) {
        var existing = document.getElementById('cf-base-dim-chips');
        if (existing) {
          this.chipLayer = existing;
        } else {
          var layer = document.createElement('div');
          layer.id = 'cf-base-dim-chips';
          layer.style.cssText = [
            'position:absolute', 'top:0', 'left:0',
            'width:100%', 'height:100%',
            'pointer-events:none', 'z-index:7'
          ].join(';');
          root.appendChild(layer);
          this.chipLayer = layer;
        }
      }
    },

    _clearChips: function () {
      for (var i = 0; i < this.chipEls.length; i++) {
        if (this.chipEls[i] && this.chipEls[i].parentNode) {
          this.chipEls[i].parentNode.removeChild(this.chipEls[i]);
        }
      }
      this.chipEls = [];
    },

    hide: function () {
      this.init();
      if (this.svgEl) { this.svgEl.style.display = 'none'; this.svgEl.innerHTML = ''; }
      if (this.chipLayer) this.chipLayer.style.display = 'none';
      this._clearChips();
    },

    refresh: function () {
      this.init();
      if (!this.svgEl) return;
      var show = (STATE.view === '2d' && STATE.vizStage === 1);
      if (!show) { this.hide(); return; }

      this.svgEl.style.display = '';
      this.svgEl.innerHTML = '';
      this.chipLayer.style.display = '';
      this._clearChips();

      var eng = global.BaseEngine;
      if (!eng || !eng.cameraOrtho || !eng.renderer) return;
      var canvas = eng.renderer.domElement;
      var rect = canvas.getBoundingClientRect();
      var w = rect.width, h = rect.height;
      if (w < 10 || h < 10) return;
      this.svgEl.setAttribute('viewBox', '0 0 ' + w + ' ' + h);

      var T = global.THREE;
      var cam = eng.cameraOrtho;
      var poly = getFootprintPolygon();
      if (!poly || poly.length < 2) return;

      function proj(x, z) {
        var v = new T.Vector3(x, 0, z).project(cam);
        return {
          x: (v.x * 0.5 + 0.5) * w,
          y: (1 - (v.y * 0.5 + 0.5)) * h
        };
      }

      var cx = 0, cz = 0;
      for (var i = 0; i < poly.length; i++) { cx += poly[i][0]; cz += poly[i][1]; }
      cx /= poly.length; cz /= poly.length;

      var GRAY = '#888', DARK = '#1a1a1a';
      var DIM_OFFSET = 32;
      var EXT_GAP = 4, EXT_OVER = 6, ARROW = 6;
      var unit = STATE.isMetric ? 'm' : 'ft';

      for (var i = 0; i < poly.length; i++) {
        var a = poly[i];
        var b = poly[(i + 1) % poly.length];
        var lenM = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (lenM < 0.05) continue;

        var lenDisp = STATE.isMetric ? lenM : (lenM * 3.28084);
        var label = lenDisp.toFixed(2) + ' ' + unit;

        var pA = proj(a[0], a[1]);
        var pB = proj(b[0], b[1]);
        var mid = proj((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
        var cs  = proj(cx, cz);

        var ex = pB.x - pA.x, ey = pB.y - pA.y;
        var elen = Math.hypot(ex, ey);
        if (elen < 1) continue;

        var nxs = -ey / elen, nys = ex / elen;
        var dot = (mid.x - cs.x) * nxs + (mid.y - cs.y) * nys;
        if (dot < 0) { nxs = -nxs; nys = -nys; }

        var oxA = pA.x + nxs * DIM_OFFSET;
        var oyA = pA.y + nys * DIM_OFFSET;
        var oxB = pB.x + nxs * DIM_OFFSET;
        var oyB = pB.y + nys * DIM_OFFSET;

        var exAx = pA.x + nxs * EXT_GAP;
        var exAy = pA.y + nys * EXT_GAP;
        var exAend_x = pA.x + nxs * (DIM_OFFSET + EXT_OVER);
        var exAend_y = pA.y + nys * (DIM_OFFSET + EXT_OVER);

        var exBx = pB.x + nxs * EXT_GAP;
        var exBy = pB.y + nys * EXT_GAP;
        var exBend_x = pB.x + nxs * (DIM_OFFSET + EXT_OVER);
        var exBend_y = pB.y + nys * (DIM_OFFSET + EXT_OVER);

        this._addLine(exAx, exAy, exAend_x, exAend_y, GRAY, 0.4);
        this._addLine(exBx, exBy, exBend_x, exBend_y, GRAY, 0.4);
        this._addLine(oxA, oyA, oxB, oyB, DARK, 0.5);

        var tx = -ex / elen, ty = -ey / elen;
        this._addLine(oxA, oyA, oxA + tx * ARROW + nxs * (ARROW * 0.5), oyA + ty * ARROW + nys * (ARROW * 0.5), DARK, 0.5);
        this._addLine(oxA, oyA, oxA + tx * ARROW - nxs * (ARROW * 0.5), oyA + ty * ARROW - nys * (ARROW * 0.5), DARK, 0.5);
        this._addLine(oxB, oyB, oxB - tx * ARROW + nxs * (ARROW * 0.5), oyB - ty * ARROW + nys * (ARROW * 0.5), DARK, 0.5);
        this._addLine(oxB, oyB, oxB - tx * ARROW - nxs * (ARROW * 0.5), oyB - ty * ARROW - nys * (ARROW * 0.5), DARK, 0.5);

        var chipX = (oxA + oxB) / 2 + nxs * 14;
        var chipY = (oyA + oyB) / 2 + nys * 14;
        this._addChip(chipX, chipY, label);
      }
    },

    _addLine: function (x1, y1, x2, y2, color, width) {
      var line = document.createElementNS(this.SVG_NS, 'line');
      line.setAttribute('x1', x1); line.setAttribute('y1', y1);
      line.setAttribute('x2', x2); line.setAttribute('y2', y2);
      line.setAttribute('stroke', color);
      line.setAttribute('stroke-width', width);
      line.setAttribute('stroke-linecap', 'round');
      this.svgEl.appendChild(line);
    },

    _addChip: function (x, y, text) {
      var chip = document.createElement('div');
      chip.className = 'cf-dim-chip cf-dim-chip--label-only';
      chip.style.left = x + 'px';
      chip.style.top  = y + 'px';
      chip.textContent = text;
      this.chipLayer.appendChild(chip);
      this.chipEls.push(chip);
    }
  };

  var _VisualizationManager = {
    DISABLED: false,

    init: function () {
      _injectCoreCSS();
      var btn = document.getElementById('cf-btn-viz');
      if (this.DISABLED) {
        if (btn) btn.style.display = 'none';
        STATE.vizStage = 0;
        var wrap = document.getElementById('cf-2d-opacity-slider-wrap');
        if (wrap) wrap.classList.remove('cf-2d-visible');
        return;
      }
      STATE.vizStage = 0;
      if (!btn) return;
      var self = this;
      btn.addEventListener('click', function () { self.cycle(); });
      this._wireSlider();
      this.apply();
    },

    _wireSlider: function () {
      var slider = document.getElementById('cf-2d-opacity-slider');
      if (!slider) return;
      var maxV = parseInt(slider.max, 10) || 100;
      var self = this;
      var updateFill = function (pct) {
        var fillPct = 100 - (pct / maxV) * 100;
        slider.style.setProperty('--cfg-fill', fillPct + '%');
      };
      var onChange = function () {
        var sv = parseInt(slider.value, 10) || 0;
        STATE.opacity2D = (100 - sv) / 100;
        updateFill(sv);
        self._applyOpacityToMaterials();
      };
      slider.addEventListener('input',  onChange);
      slider.addEventListener('change', onChange);
      var initSliderVal = Math.round((1 - STATE.opacity2D) * 100);
      slider.value = initSliderVal;
      updateFill(initSliderVal);
    },

    cycle: function () {
      if (this.DISABLED) return;
      /* 3-stage cycle:
           0 = off (= default material visibility, no overlays)
           1 = stage view (= opacity-faded materials, dimensions on top)
           2 = section cut (= vertical clipping plane, 3D only)
         Section cut is meaningless in 2D plan view, so we wrap back to
         0 when the user is in 2D. The isolated single-bay detail lives
         on the Assembly button now, not here. */
      var next = (STATE.vizStage + 1) % 3;
      if (next === 2 && STATE.view !== '3d') next = 0;
      STATE.vizStage = next;
      _dbg('[viz-cycle] vizStage=' + STATE.vizStage + ' view=' + STATE.view);
      this.apply();
    },

    reset: function () {
      STATE.vizStage = 0;
      if (this.DISABLED) return;
      this.apply();
    },

    apply: function () {
      if (this.DISABLED) return;
      this._applyOpacityToMaterials();
      if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
      this._updateButton();
      this._updateSliderVisibility();
      this._updateStageOverlays();
      if (_SlopeManager && _SlopeManager._updateSlider) _SlopeManager._updateSlider();
      _BaseDimensions.refresh();
      /* Trigger the section-cut pipeline whenever the stage state
         changes. The refresh function itself handles the early
         return when the stage isn't 2. */
      if (_SectionCutManager) _SectionCutManager.refresh();
    },

    refreshDimensions: function () {
      if (this.DISABLED) return;
      _BaseDimensions.refresh();
    },

    _applyOpacityToMaterials: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      /* Stage 1 = faded materials so dimensions + overlays read on
         top. Stage 2 (section cut) wants FULL opacity so the cut
         surface looks solid -- fading would defeat the point. */
      var in2DStage = (STATE.view === '2d' && STATE.vizStage === 1);
      var in3DStage = (STATE.view === '3d' && STATE.vizStage === 1);
      eng.sceneGroup.traverse(function (node) {
        if (!node.isMesh || !node.material) return;
        var ud = (node.parent && node.parent.userData) || node.userData || {};
        var isMeshWire = (ud.assetType === 'mesh');
        var isRebar    = (ud.assetType === 'rebar');
        var op;
        if (isMeshWire || isRebar) {
          op = 1.0;
        } else if (in3DStage) {
          op = STATE.opacity2D;
        } else if (in2DStage) {
          op = STATE.opacity2D;
        } else {
          op = 1.0;
        }
        var m = node.material;
        if (Array.isArray(m)) {
          for (var i = 0; i < m.length; i++) {
            m[i].transparent = op < 1;
            m[i].opacity = op;
            m[i].depthWrite = (op > 0.99);
            m[i].needsUpdate = true;
          }
        } else {
          m.transparent = op < 1;
          m.opacity = op;
          m.depthWrite = (op > 0.99);
          m.needsUpdate = true;
        }
      });
    },

    _updateButton: function () {
      var btn = document.getElementById('cf-btn-viz');
      if (!btn) return;
      var stage = STATE.vizStage;
      btn.classList.toggle('active', stage !== 0);
      btn.setAttribute('data-stage', String(stage));
      btn.setAttribute('aria-pressed', stage !== 0 ? 'true' : 'false');
      var lbl = document.getElementById('cf-stat-viz') || btn.querySelector('.cf-viz-label');
      if (lbl) {
        /* Labels:
             stage 0 -> "off"        (no overlay)
             stage 1 -> "1/2"        (stage view)
             stage 2 -> "2/2"        (section cut)
           In 2D we don't expose stage 2, but if the cycle wrapped in
           3D before the user switched to 2D, the cached value can still
           be 2 -- render the literal stage so the user sees the state. */
        lbl.textContent = (stage === 0) ? 'off' : (stage + '/2');
      }
    },

    _updateSliderVisibility: function () {
      var wrap = document.getElementById('cf-2d-opacity-slider-wrap');
      if (!wrap) return;
      var visible = (STATE.vizStage === 1);
      wrap.classList.toggle('cf-2d-visible', visible);
    },

    _updateStageOverlays: function () {
      var wrap = document.getElementById('cf-wrap-base');
      if (!wrap) return;
      var inStageView = (STATE.vizStage === 1);
      wrap.classList.toggle('cf-stage-view', inStageView);
      if (inStageView) {
        if (_MapManager && _MapManager.map && _MapManager.map.dragPan) _MapManager.map.dragPan.disable();
      } else {
        if (_MapManager && _MapManager.map && _MapManager.map.dragPan && STATE.view === '2d') _MapManager.map.dragPan.enable();
      }
    }
  };

  /* Camera framing for the detail bay (vizStage 3). Frames the
     isolated single-bay assembly that _build3D_DetailBay drew around
     the origin, and restores the previous camera/target when leaving
     the stage so the user lands back where they were. */
  var _DetailBayManager = {
    _extents: null,         /* {width,height,postSide} set by the builder */
    _saved: null,           /* camera pose to restore on exit */

    frameCamera: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.cameraPersp) return;
      var cam = eng.cameraPersp;
      /* Save current pose once (don't overwrite if already saved). */
      if (!this._saved) {
        this._saved = {
          pos: cam.position.clone(),
          target: (eng.controls && eng.controls.target)
            ? eng.controls.target.clone() : new global.THREE.Vector3(0, 0, 0)
        };
      }
      var ext = this._extents || { width: 2.44, height: 1.83 };
      /* Frame the bay from a standing person's viewpoint: eye height
         ~1.55 m above grade, set back far enough to see the whole bay,
         offset to one side for a natural oblique angle (not dead-on).
         Looks slightly down toward the bay's mid-height. */
      var spanMax = Math.max(ext.width, ext.height);
      var dist = spanMax * 1.45 + 2.6;
      var eyeY = Math.min(1.55, ext.height * 0.95);   /* eye level, capped for short bays */
      var cy = ext.height * 0.50;                       /* look at mid-height of the bay */
      cam.position.set(ext.width * 0.55, eyeY, dist);
      cam.lookAt(0, cy, 0);
      if (eng.controls) {
        eng.controls.target.set(0, cy, 0);
        eng.controls.update();
      }
    },

    restoreCamera: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.cameraPersp || !this._saved) { this._saved = null; return; }
      var cam = eng.cameraPersp;
      cam.position.copy(this._saved.pos);
      cam.lookAt(this._saved.target);
      if (eng.controls) {
        eng.controls.target.copy(this._saved.target);
        eng.controls.update();
      }
      this._saved = null;
    }
  };

  function _snapToNearestPolygonEdge(x, z) {
    var poly = STATE.polygon;
    if (!poly || poly.length < 2) return null;
    var n = poly.length;
    var best = null;
    for (var i = 0; i < n; i++) {
      var a = poly[i];
      var b = poly[(i + 1) % n];
      var ax = a[0], az = a[1];
      var bx = b[0], bz = b[1];
      var ex = bx - ax, ez = bz - az;
      var lenSq = ex * ex + ez * ez;
      if (lenSq < 1e-6) continue;
      var t = ((x - ax) * ex + (z - az) * ez) / lenSq;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      var px = ax + t * ex;
      var pz = az + t * ez;
      var d = Math.hypot(x - px, z - pz);
      if (!best || d < best.dist) {
        best = { edgeIdx: i, tFrac: t, x: px, z: pz, dist: d };
      }
    }
    return best;
  }

  function _insertCornerOnEdge(edgeIdx, tFrac) {
    var poly = STATE.polygon;
    if (!poly || poly.length < 3) return false;
    var n = poly.length;
    if (edgeIdx < 0 || edgeIdx >= n) return false;

    var a = poly[edgeIdx];
    var b = poly[(edgeIdx + 1) % n];
    var ax = a[0], az = a[1];
    var bx = b[0], bz = b[1];

    var px = ax + tFrac * (bx - ax);
    var pz = az + tFrac * (bz - az);

    var distA = Math.hypot(px - ax, pz - az);
    var distB = Math.hypot(px - bx, pz - bz);

    var sharedIdx, sharedV, otherEndOnE, adjOtherIdx;
    if (distA <= distB) {
      sharedIdx     = edgeIdx;
      sharedV       = a;
      otherEndOnE   = b;
      adjOtherIdx   = (edgeIdx - 1 + n) % n;
    } else {
      sharedIdx     = (edgeIdx + 1) % n;
      sharedV       = b;
      otherEndOnE   = a;
      adjOtherIdx   = (edgeIdx + 2) % n;
    }
    var adjOther = poly[adjOtherIdx];

    var lenE   = Math.hypot(otherEndOnE[0] - sharedV[0], otherEndOnE[1] - sharedV[1]);
    var lenAdj = Math.hypot(adjOther[0]    - sharedV[0], adjOther[1]    - sharedV[1]);
    if (lenE < 0.2 || lenAdj < 0.2) return false;

    var d = Math.min(distA, distB);
    d = Math.min(d, lenE * 0.49, lenAdj * 0.49);
    if (d < 0.15) return false;

    var v1x = snap(sharedV[0] + (d / lenE)   * (otherEndOnE[0] - sharedV[0]));
    var v1z = snap(sharedV[1] + (d / lenE)   * (otherEndOnE[1] - sharedV[1]));
    var v2x = snap(sharedV[0] + (d / lenAdj) * (adjOther[0]    - sharedV[0]));
    var v2z = snap(sharedV[1] + (d / lenAdj) * (adjOther[1]    - sharedV[1]));
    var v3x = snap(v1x + v2x - sharedV[0]);
    var v3z = snap(v1z + v2z - sharedV[1]);

    var v1 = [_clampCoord(v1x), _clampCoord(v1z)];
    var v2 = [_clampCoord(v2x), _clampCoord(v2z)];
    var v3 = [_clampCoord(v3x), _clampCoord(v3z)];

    if (distA <= distB) {
      poly.splice(sharedIdx, 1, v2, v3, v1);
    } else {
      poly.splice(sharedIdx, 1, v1, v3, v2);
    }
    return true;
  }

  /* ----------------------------------------------------------------
     GAP INSERTER -- toggled by the "Add gap" toolbar button. When
     active, the next click on the 2D canvas snaps to the nearest
     polygon edge and inserts a new gap centred on that point.
     Default gap width = 2 m (clamped to leave 0.5 m of fence at
     each end of the edge so the gap never spans the whole edge).

     Outside insert mode, a click near an existing gap (within 0.6 m
     in world coords) deletes that gap -- a lightweight way to manage
     openings without dedicated drag handles. A full drag UI for
     resizing / moving gaps can be added later; this MVP covers the
     "insert + delete" cycle.
     ---------------------------------------------------------------- */
  var _GapInserter = {
    pendingPlacement: false,
    DEFAULT_WIDTH_M: 2.0,
    MIN_EDGE_LEN_M:  1.0,
    DELETE_HIT_M:    0.6,

    _setPending: function (on) {
      this.pendingPlacement = !!on;
      var canvas = document.getElementById('cf-canvas-base');
      if (canvas) canvas.style.cursor = on ? 'crosshair' : '';
      var btn = document.getElementById('cf-btn-gate');
      if (btn) {
        btn.classList.toggle('active', !!on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      _gapInsertMode = this.pendingPlacement;
    },

    _findGapNear: function (x, z) {
      var poly = STATE.polygon;
      var gates = STATE.gates || [];
      var best = null;
      for (var i = 0; i < gates.length; i++) {
        var g = gates[i];
        var A = poly[g.edgeIdx];
        var B = poly[(g.edgeIdx + 1) % poly.length];
        var dx = B[0] - A[0], dy = B[1] - A[1];
        var edgeLen = Math.hypot(dx, dy);
        if (edgeLen < 0.01) continue;
        var ux = dx / edgeLen, uy = dy / edgeLen;
        var gStart = g.tFrac * edgeLen - g.width / 2;
        var gEnd   = g.tFrac * edgeLen + g.width / 2;
        gStart = Math.max(0, gStart);
        gEnd   = Math.min(edgeLen, gEnd);
        /* Project (x,z) onto the edge and check whether the
           projection falls inside [gStart, gEnd] AND the
           perpendicular distance is within DELETE_HIT_M. */
        var t = ((x - A[0]) * ux + (z - A[1]) * uy);
        if (t < gStart - this.DELETE_HIT_M) continue;
        if (t > gEnd   + this.DELETE_HIT_M) continue;
        var projX = A[0] + ux * t;
        var projY = A[1] + uy * t;
        var perp = Math.hypot(x - projX, z - projY);
        if (perp > this.DELETE_HIT_M) continue;
        if (!best || perp < best.dist) best = { id: g.id, dist: perp };
      }
      return best;
    },

    init: function () {
      var self = this;
      var btn = document.getElementById('cf-btn-gate');
      if (btn) {
        btn.addEventListener('click', function () {
          if (STATE.view !== '2d') return;
          self._setPending(!self.pendingPlacement);
        });
      }
      var canvas = document.getElementById('cf-canvas-base');
      if (canvas) {
        canvas.addEventListener('click', function (e) {
          if (STATE.view !== '2d') return;
          var p = _screenToWorld(e.clientX, e.clientY);
          if (!p) return;
          if (self.pendingPlacement) {
            /* INSERT a new gap. */
            var snap = _snapToNearestPolygonEdge(p.x, p.z);
            if (!snap) return;
            var poly = STATE.polygon;
            var A = poly[snap.edgeIdx];
            var B = poly[(snap.edgeIdx + 1) % poly.length];
            var edgeLen = Math.hypot(B[0] - A[0], B[1] - A[1]);
            if (edgeLen < self.MIN_EDGE_LEN_M) { self._setPending(false); return; }
            var w = Math.min(self.DEFAULT_WIDTH_M, edgeLen - 0.5);
            if (w < 0.5) w = 0.5;
            var halfFrac = (w / 2) / edgeLen;
            var tF = Math.max(halfFrac, Math.min(1 - halfFrac, snap.tFrac));
            STATE.gates.push({
              id: _nextGateId++,
              edgeIdx: snap.edgeIdx,
              tFrac: tF,
              width: w
            });
            self._setPending(false);
            if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
            if (_GateDragManager && _GateDragManager.rebuild) _GateDragManager.rebuild();
            refreshUI();
            return;
          }
          /* DELETE: if a click lands near an existing gap, drop it. */
          var hit = self._findGapNear(p.x, p.z);
          if (!hit) return;
          STATE.gates = STATE.gates.filter(function (g) { return g.id !== hit.id; });
          if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
          if (_GateDragManager && _GateDragManager.rebuild) _GateDragManager.rebuild();
          refreshUI();
        });
      }
    }
  };

  /* -------------------------------------------------------------
     _GateDragManager
     Renders 3 drag handles per gate (2D only):
       - 'A' (red, 16px circle)        left side post position
       - 'B' (red, 16px circle)        right side post position
       - 'center' (white, 12px circle) gap midpoint
     Drag A/B  -> resize gap (width changes, opposite side anchored)
     Drag center -> move whole gap along edge (width preserved)
     Width + distance labels follow the drag.

     Logic is a direct port of the original fence configurator's
     GateManager._bindHandleDrag, kept identical so behaviour matches
     what users already know.
     ------------------------------------------------------------- */
  var _GateDragManager = {
    handles: [],
    dimLayer: null,
    dimLabels: [],
    dragging: null,

    init: function () {
      this._ensureDimLayer();
      this._bindPointerHandlers();
    },

    _ensureDimLayer: function () {
      var parent = document.getElementById('cf-wrap-base');
      if (!parent) return;
      var layer = document.getElementById('cf-gate-dimlayer');
      if (!layer) {
        layer = document.createElement('div');
        layer.id = 'cf-gate-dimlayer';
        layer.style.cssText =
          'position:absolute;inset:0;pointer-events:none;z-index:9;' +
          'font-family:Montserrat,-apple-system,sans-serif;';
        parent.appendChild(layer);
      }
      this.dimLayer = layer;
    },

    /* Build / rebuild handle DOM after gap changes (insert, delete, drag). */
    rebuild: function () {
      this._ensureDimLayer();
      var parent = this.dimLayer;
      if (!parent) return;
      /* Clear existing handles + labels. */
      for (var i = 0; i < this.handles.length; i++) {
        if (this.handles[i].el && this.handles[i].el.parentNode) {
          this.handles[i].el.parentNode.removeChild(this.handles[i].el);
        }
      }
      this.handles = [];
      for (var j = 0; j < this.dimLabels.length; j++) {
        if (this.dimLabels[j].parentNode) this.dimLabels[j].parentNode.removeChild(this.dimLabels[j]);
      }
      this.dimLabels = [];

      var gates = STATE.gates || [];
      var self = this;
      for (var k = 0; k < gates.length; k++) {
        var g = gates[k];
        /* Build A, B, center handles for this gate. */
        ['A', 'B', 'center'].forEach(function (side) {
          var el = document.createElement('div');
          var isCenter = (side === 'center');
          var size = isCenter ? 14 : 18;
          el.className = 'cf-gate-handle cf-gate-handle--' + side.toLowerCase();
          el.dataset.gateId = String(g.id);
          el.dataset.side = side;
          el.style.cssText =
            'position:absolute;width:' + size + 'px;height:' + size + 'px;' +
            'border-radius:50%;background:' + (isCenter ? '#fff' : '#D3212D') + ';' +
            'border:2px solid ' + (isCenter ? '#D3212D' : '#fff') + ';' +
            'box-shadow:0 2px 4px rgba(0,0,0,0.30);' +
            'cursor:grab;pointer-events:auto;touch-action:none;' +
            'transform:translate(-50%, -50%);display:none;z-index:11;';
          parent.appendChild(el);
          self.handles.push({ el: el, gateId: g.id, side: side });
        });
        /* Width label sits above the gap midpoint. */
        var lbl = document.createElement('div');
        lbl.className = 'cf-gate-label';
        lbl.dataset.gateId = String(g.id);
        lbl.style.cssText =
          'position:absolute;padding:3px 8px;border-radius:4px;' +
          'background:rgba(20,20,20,0.85);color:#fff;font-size:12px;' +
          'font-weight:700;letter-spacing:0.02em;white-space:nowrap;' +
          'transform:translate(-50%, -120%);pointer-events:none;z-index:11;' +
          'display:none;';
        parent.appendChild(lbl);
        this.dimLabels.push(lbl);
      }
      this.reproject();
    },

    /* Reposition handles + labels each frame (or after any view change). */
    reproject: function () {
      var visible = (STATE.view === '2d');
      var gates = STATE.gates || [];
      var poly  = STATE.polygon;
      if (!poly || poly.length < 2) return;
      /* HANDLES */
      for (var i = 0; i < this.handles.length; i++) {
        var h = this.handles[i];
        var g = _findGateById(h.gateId);
        if (!g || !visible) { h.el.style.display = 'none'; continue; }
        var A = poly[g.edgeIdx];
        var B = poly[(g.edgeIdx + 1) % poly.length];
        var dx = B[0] - A[0], dy = B[1] - A[1];
        var edgeLen = Math.hypot(dx, dy);
        if (edgeLen < 0.01) { h.el.style.display = 'none'; continue; }
        var ux = dx / edgeLen, uy = dy / edgeLen;
        var center = g.tFrac * edgeLen;
        var halfW  = g.width / 2;
        var s;
        if (h.side === 'A') s = Math.max(0, center - halfW);
        else if (h.side === 'B') s = Math.min(edgeLen, center + halfW);
        else s = center;
        var wx = A[0] + s * ux;
        var wz = A[1] + s * uy;
        var pos = _worldToScreen(wx, wz);
        if (!pos) { h.el.style.display = 'none'; continue; }
        h.el.style.left = pos.left + 'px';
        h.el.style.top  = pos.top  + 'px';
        h.el.style.display = 'block';
      }
      /* WIDTH LABELS */
      for (var j = 0; j < this.dimLabels.length; j++) {
        var lbl = this.dimLabels[j];
        var gid = parseInt(lbl.dataset.gateId, 10);
        var g2  = _findGateById(gid);
        if (!g2 || !visible) { lbl.style.display = 'none'; continue; }
        var A2 = poly[g2.edgeIdx];
        var B2 = poly[(g2.edgeIdx + 1) % poly.length];
        var dx2 = B2[0] - A2[0], dy2 = B2[1] - A2[1];
        var len2 = Math.hypot(dx2, dy2);
        if (len2 < 0.01) { lbl.style.display = 'none'; continue; }
        var cx2 = A2[0] + g2.tFrac * dx2;
        var cz2 = A2[1] + g2.tFrac * dy2;
        var ps2 = _worldToScreen(cx2, cz2);
        if (!ps2) { lbl.style.display = 'none'; continue; }
        lbl.style.left = ps2.left + 'px';
        lbl.style.top  = ps2.top  + 'px';
        lbl.style.display = 'block';
        lbl.textContent = fmtDim(g2.width);
      }
    },

    _bindPointerHandlers: function () {
      var self = this;
      var parent = document.getElementById('cf-wrap-base');
      if (!parent) return;
      parent.addEventListener('pointerdown', function (e) {
        if (STATE.view !== '2d') return;
        var tgt = e.target;
        if (!tgt || !tgt.classList || !tgt.classList.contains('cf-gate-handle')) return;
        var gid  = parseInt(tgt.dataset.gateId, 10);
        var side = tgt.dataset.side;
        var g    = _findGateById(gid);
        if (!g) return;
        e.preventDefault();
        e.stopPropagation();
        try { tgt.setPointerCapture(e.pointerId); } catch (_) {}
        self.dragging = { gateId: gid, side: side, el: tgt };
        tgt.style.cursor = 'grabbing';
        var eng = global.BaseEngine;
        if (eng && eng.controls) eng.controls.enabled = false;
        self._showDragDimensions(g, side);
      });
      parent.addEventListener('pointermove', function (e) {
        if (!self.dragging) return;
        var d = self.dragging;
        var g = _findGateById(d.gateId);
        if (!g) return;
        var poly = STATE.polygon;
        var A = poly[g.edgeIdx];
        var B = poly[(g.edgeIdx + 1) % poly.length];
        var p = _screenToWorld(e.clientX, e.clientY);
        if (!p) return;
        var dx = B[0] - A[0], dy = B[1] - A[1];
        var edgeLen = Math.hypot(dx, dy);
        if (edgeLen < 0.01) return;
        /* Project pointer onto the edge to get the position along it. */
        var t = ((p.x - A[0]) * dx + (p.z - A[1]) * dy) / (edgeLen * edgeLen);
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        var sPos = t * edgeLen;
        if (d.side === 'center') {
          /* Move the whole gap, keep width -- clamp inside edge. */
          var halfW = g.width / 2;
          var newCenter = sPos;
          if (newCenter - halfW < 0) newCenter = halfW;
          if (newCenter + halfW > edgeLen) newCenter = edgeLen - halfW;
          g.tFrac = newCenter / edgeLen;
        } else {
          /* Side drag (A or B) -- resize gap, opposite side anchored. */
          var beforeCenter = g.tFrac * edgeLen;
          var beforeHalf   = g.width / 2;
          var beforeA = beforeCenter - beforeHalf;
          var beforeB = beforeCenter + beforeHalf;
          var newA = beforeA, newB = beforeB;
          if (d.side === 'A') newA = Math.min(sPos, beforeB - 0.5);
          else                newB = Math.max(sPos, beforeA + 0.5);
          newA = Math.max(0, newA);
          newB = Math.min(edgeLen, newB);
          g.width = newB - newA;
          g.tFrac = ((newA + newB) / 2) / edgeLen;
        }
        if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
        self.reproject();
        self._showDragDimensions(g, d.side);
        refreshInfoBar();
      });
      function endDrag(e) {
        if (!self.dragging) return;
        var el = self.dragging.el;
        if (el) {
          el.style.cursor = 'grab';
          try { el.releasePointerCapture(e.pointerId); } catch (_) {}
        }
        self.dragging = null;
        var eng = global.BaseEngine;
        if (eng && eng.controls) eng.controls.enabled = true;
        self._clearDragDimensions();
      }
      parent.addEventListener('pointerup',     endDrag);
      parent.addEventListener('pointercancel', endDrag);
    },

    /* Live dimension labels while dragging:
         A drag    -> distA + width   (left distance + gap width change)
         B drag    -> distB + width   (right distance + gap width change)
         center    -> distA + distB   (both distances change, width const) */
    _showDragDimensions: function (g, side) {
      this._clearDragDimensions();
      var poly = STATE.polygon;
      var A = poly[g.edgeIdx];
      var B = poly[(g.edgeIdx + 1) % poly.length];
      var dx = B[0] - A[0], dy = B[1] - A[1];
      var edgeLen = Math.hypot(dx, dy);
      if (edgeLen < 0.01) return;
      var ux = dx / edgeLen, uy = dy / edgeLen;
      var center = g.tFrac * edgeLen;
      var halfW  = g.width / 2;
      var distA = Math.max(0, center - halfW);
      var distB = Math.max(0, edgeLen - center - halfW);
      var showDistA = (side === 'A' || side === 'center');
      var showDistB = (side === 'B' || side === 'center');
      var showWidth = (side === 'A' || side === 'B');
      if (showDistA) {
        this._addDragLabel(A[0] + (distA / 2) * ux, A[1] + (distA / 2) * uy, fmtDim(distA));
      }
      if (showWidth) {
        this._addDragLabel(A[0] + center * ux, A[1] + center * uy, fmtDim(g.width), -22);
      }
      if (showDistB) {
        var p = center + halfW + distB / 2;
        this._addDragLabel(A[0] + p * ux, A[1] + p * uy, fmtDim(distB));
      }
    },

    _addDragLabel: function (wx, wz, text, offsetY) {
      this._ensureDimLayer();
      if (!this.dimLayer) return;
      var pos = _worldToScreen(wx, wz);
      if (!pos) return;
      var el = document.createElement('div');
      el.className = 'cf-gate-drag-dim';
      el.style.cssText =
        'position:absolute;left:' + pos.left + 'px;top:' + (pos.top + (offsetY || 0)) + 'px;' +
        'transform:translate(-50%, -50%);' +
        'padding:3px 8px;border-radius:4px;background:rgba(20,20,20,0.92);' +
        'color:#fff;font:700 12px Montserrat,-apple-system,sans-serif;' +
        'white-space:nowrap;pointer-events:none;z-index:12;' +
        'font-family:Montserrat,-apple-system,sans-serif;';
      el.textContent = text;
      this.dimLayer.appendChild(el);
      if (!this._dragDimEls) this._dragDimEls = [];
      this._dragDimEls.push(el);
    },

    _clearDragDimensions: function () {
      if (!this._dragDimEls) return;
      for (var i = 0; i < this._dragDimEls.length; i++) {
        var el = this._dragDimEls[i];
        if (el.parentNode) el.parentNode.removeChild(el);
      }
      this._dragDimEls = [];
    }
  };

  function _findGateById(id) {
    var gates = STATE.gates || [];
    for (var i = 0; i < gates.length; i++) {
      if (gates[i].id === id) return gates[i];
    }
    return null;
  }

  /* -------------------------------------------------------------
     _SpacingSlider
     Floating slider (bottom of canvas, above info bar) for adjusting
     post spacing. Range comes from the current variant via
     getCurrentSpacingRange() and is reset to default on variant switch.
     ------------------------------------------------------------- */
  /* -------------------------------------------------------------
     _SpacingSlider
     Slider lives as an absolutely-positioned child of the Spacing
     column in the info bar -- CSS handles all positioning (10px gap
     above the column, full column width). This module only wires
     the change event, syncs the range to the active variant, and
     updates the gradient fill. The value is read from the info bar
     so no separate chip is needed.
     ------------------------------------------------------------- */
  var _SpacingSlider = {
    init: function () {
      var slider = document.getElementById('cf-spacing-slider');
      if (!slider) return;
      var self = this;
      slider.addEventListener('input', function () {
        var v = parseFloat(slider.value);
        if (!isFinite(v)) return;
        STATE.postSpacingOverride = v;
        self._updateFill();
        if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
        refreshInfoBar();
        if (_GateDragManager && _GateDragManager.reproject) _GateDragManager.reproject();
        /* Rebuild BoQ so the total modal reflects the new spacing.
           Debounced (400 ms) so rapid slider drag doesn't thrash. */
        _debouncedPublishBoQ();
        /* Persist override to localStorage (and later Firestore via
           _ProjectStore). Debounced (500 ms) inside publish(). */
        try { _ProjectStore && _ProjectStore.publish && _ProjectStore.publish(); } catch (e) {}
      });
      this.refresh();
    },
    /* Sync slider min/max/value to the current variant. Always operates
       in metres -- the info-bar value handles unit conversion. */
    refresh: function () {
      var slider = document.getElementById('cf-spacing-slider');
      if (!slider) return;
      var r = getCurrentSpacingRange();
      slider.min  = r.min.toFixed(2);
      slider.max  = r.max.toFixed(2);
      slider.step = '0.05';
      slider.value = getCurrentSpacing().toFixed(2);
      this._updateFill();
    },
    _updateFill: function () {
      var slider = document.getElementById('cf-spacing-slider');
      if (!slider) return;
      var v   = parseFloat(slider.value);
      var lo  = parseFloat(slider.min);
      var hi  = parseFloat(slider.max);
      if (!isFinite(v) || !isFinite(lo) || !isFinite(hi) || hi <= lo) return;
      var pct = ((v - lo) / (hi - lo)) * 100;
      slider.style.setProperty('--cfg-fill', pct.toFixed(1) + '%');
    }
  };

  /* Wood-fence panel-height slider. Mirror of _SpacingSlider but
     bound to STATE.woodHeight. Visible only when variant === 1 -- the
     containing column #cf-info-height is hidden by _syncWoodInfoColumns
     for other variants. Always operates in metres internally; display
     unit (m / ft) handled by refreshInfoBar. */
  var _HeightSlider = {
    init: function () {
      var slider = document.getElementById('cf-height-slider');
      if (!slider) return;
      var self = this;
      slider.addEventListener('input', function () {
        var v = parseFloat(slider.value);
        if (!isFinite(v)) return;
        STATE.woodHeight = v;
        self._updateFill();
        if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
        refreshInfoBar();
        /* Rebuild BoQ so the total modal reflects the new height
           (panel area, picket length, stain area all scale with it). */
        _debouncedPublishBoQ();
        try { _ProjectStore && _ProjectStore.publish && _ProjectStore.publish(); } catch (e) {}
      });
      this.refresh();
    },
    refresh: function () {
      var slider = document.getElementById('cf-height-slider');
      if (!slider) return;
      var r = getCurrentHeightRange();
      slider.min  = r.min.toFixed(2);
      slider.max  = r.max.toFixed(2);
      slider.step = '0.05';
      slider.value = getCurrentHeight().toFixed(2);
      this._updateFill();
    },
    _updateFill: function () {
      var slider = document.getElementById('cf-height-slider');
      if (!slider) return;
      var v   = parseFloat(slider.value);
      var lo  = parseFloat(slider.min);
      var hi  = parseFloat(slider.max);
      if (!isFinite(v) || !isFinite(lo) || !isFinite(hi) || hi <= lo) return;
      var pct = ((v - lo) / (hi - lo)) * 100;
      slider.style.setProperty('--cfg-fill', pct.toFixed(1) + '%');
    }
  };

  var _CornerInserter = {
    pendingPlacement: false,
    _setPending: function (on) {
      this.pendingPlacement = !!on;
      var canvas = document.getElementById('cf-canvas-base');
      if (canvas) canvas.style.cursor = on ? 'crosshair' : '';
      var btn = document.getElementById('cf-btn-insert-corner');
      if (btn) {
        btn.classList.toggle('active', !!on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    },
    init: function () {
      var self = this;
      var btn = document.getElementById('cf-btn-insert-corner');
      if (btn) {
        btn.addEventListener('click', function () {
          if (STATE.view !== '2d') return;
          self._setPending(!self.pendingPlacement);
        });
      }
      var canvas = document.getElementById('cf-canvas-base');
      if (canvas) {
        canvas.addEventListener('click', function (e) {
          if (!self.pendingPlacement) return;
          if (STATE.view !== '2d') { self._setPending(false); return; }
          var p = _screenToWorld(e.clientX, e.clientY);
          if (!p) return;
          var snapped = _snapToNearestPolygonEdge(p.x, p.z);
          if (!snapped) return;
          var prevPoly = JSON.parse(JSON.stringify(STATE.polygon));
          var ok = _insertCornerOnEdge(snapped.edgeIdx, snapped.tFrac);
          self._setPending(false);
          if (!ok) return;
          _UndoHistory.pushIfChanged(prevPoly, STATE.variant);
          rebuildDragHandles();
          if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
          refreshUI();
        });
      }
    }
  };

  var _AssetDrawer = {
    _ready: false,
    _raycaster: null,
    _mouse: null,
    _selectedMesh: null,
    _origMaterial: null,

    _HIGHLIGHT_HEX: 0xff8c42,

    _findBoQRow: function (boqCode) {
      var rows = computeBoQ();
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (Array.isArray(r) && r[0] === boqCode) {
          return { code: r[0], qty: r[1] || 0, qtyAlt: r[2] || 0 };
        }
        if (r && r.code === boqCode) return r;
      }
      return null;
    },

    _buildInstance: function (mesh) {
      if (!mesh || !mesh.userData || !mesh.userData.boqCode) return null;
      var ud = mesh.userData;
      var bridge = global.ConfiguratorBudgetBridge || null;

      var boqItem = (bridge && bridge.getBoqItem) ? bridge.getBoqItem(ud.boqCode) : null;
      var name     = (boqItem && boqItem.name)     || ud.boqCode;
      var category = (boqItem && boqItem.category) || ud.category || ud.assetType;
      var unit     = boqItem
        ? (STATE.isMetric ? boqItem.unitMetric : boqItem.unitImperial)
        : null;
      if (!unit) unit = STATE.isMetric ? 'pcs' : 'pcs';

      var userPrice = (bridge && bridge.getUserPrice) ? bridge.getUserPrice(ud.boqCode) : null;
      var product = userPrice ? {
        title:    userPrice.title,
        url:      userPrice.url,
        image:    userPrice.image,
        source:   userPrice.source,
        price:    userPrice.price,
        currency: userPrice.currency,
        unit:     unit
      } : null;

      var row = this._findBoQRow(ud.boqCode);
      var qty = (row && typeof row.qty === 'number') ? row.qty : 0;
      var unitPrice = userPrice ? userPrice.price : 0;
      var totalPrice = qty * unitPrice;

      var pos = mesh.getWorldPosition ? mesh.getWorldPosition(new global.THREE.Vector3()) : { x: 0, y: 0, z: 0 };
      var inst = {
        id:                 ud.assetType + ':' + (ud.index != null ? ud.index : 0),
        name:               name,
        type:               name,
        boqCode:            ud.boqCode,
        category:           category,
        unit:               unit,
        projectQuantity:    qty,
        projectUnitPrice:   unitPrice,
        projectTotalPrice:  totalPrice,
        position:           { x: pos.x, y: pos.y, z: pos.z }
      };
      if (typeof ud.length === 'number') inst.length = ud.length;
      if (product) inst.product = product;
      return inst;
    },

    _i18nLabels: function () {
      var boot = global.BASE_BOOT || {};
      var i18n = boot.i18n || {};
      var ad = i18n.assetDrawer || {};
      var labels = {};
      for (var k in ad) { if (ad.hasOwnProperty(k)) labels[k] = ad[k]; }
      return labels;
    },

    _highlight: function (mesh) {
      this._clearHighlight();
      if (!mesh || !mesh.material) return;
      this._selectedMesh = mesh;
      this._origMaterial = mesh.material;
      try {
        var T = global.THREE;
        mesh.material = new T.MeshStandardMaterial({ color: this._HIGHLIGHT_HEX, roughness: 0.6, metalness: 0 });
      } catch (_) {}
    },

    _clearHighlight: function () {
      if (this._selectedMesh && this._origMaterial) {
        try { this._selectedMesh.material = this._origMaterial; } catch (_) {}
      }
      this._selectedMesh = null;
      this._origMaterial = null;
    },

    init: function () {
      if (this._ready) return;
      if (!global.ConfiguratorAssetDrawer) {
        _dbg('AssetDrawer: ConfiguratorAssetDrawer global not loaded yet');
        var self = this;
        setTimeout(function () { self.init(); }, 200);
        return;
      }
      var T = global.THREE;
      if (!T) return;
      this._raycaster = new T.Raycaster();
      this._mouse = new T.Vector2();

      var self = this;
      global.ConfiguratorAssetDrawer.init({
        registry: function (id) {
          if (!self._selectedMesh) return null;
          return self._buildInstance(self._selectedMesh);
        },
        onClose: function () { self._clearHighlight(); },
        labels: this._i18nLabels(),
        drawerScopeClass: 'base-asset-drawer'
      });

      this._bindCanvas();
      this._ready = true;
      _dbg('AssetDrawer initialised');
    },

    _bindCanvas: function () {
      var self = this;
      var eng = global.BaseEngine;
      if (!eng || !eng.renderer || !eng.renderer.domElement) {
        setTimeout(function () { self._bindCanvas(); }, 100);
        return;
      }
      var canvas = eng.renderer.domElement;
      var pressStart = { x: 0, y: 0, t: 0 };

      canvas.addEventListener('pointerdown', function (e) {
        pressStart.x = e.clientX;
        pressStart.y = e.clientY;
        pressStart.t = Date.now();
      });

      canvas.addEventListener('pointerup', function (e) {
        if (STATE.view !== '3d') return;
        if (_CornerInserter && _CornerInserter.pendingPlacement) return;
        var dx = e.clientX - pressStart.x;
        var dy = e.clientY - pressStart.y;
        var dt = Date.now() - pressStart.t;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) return;
        if (dt > 500) return;
        self._handleCanvasClick(e);
      });
    },

    _handleCanvasClick: function (e) {
      var eng = global.BaseEngine;
      if (!eng || !eng.scene || !eng.cameraPersp || !eng.sceneGroup) return;
      var canvas = eng.renderer.domElement;
      var rect = canvas.getBoundingClientRect();
      this._mouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
      this._mouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
      this._raycaster.setFromCamera(this._mouse, eng.cameraPersp);
      var hits = this._raycaster.intersectObjects(eng.sceneGroup.children, true);
      if (!hits || !hits.length) {
        if (global.ConfiguratorAssetDrawer && global.ConfiguratorAssetDrawer.isOpen && global.ConfiguratorAssetDrawer.isOpen()) {
          global.ConfiguratorAssetDrawer.close();
        }
        return;
      }
      var mesh = null;
      for (var i = 0; i < hits.length; i++) {
        var h = hits[i].object;
        if (h && h.userData && h.userData.boqCode) { mesh = h; break; }
      }
      if (!mesh) return;
      this._highlight(mesh);
      var instance = this._buildInstance(mesh);
      if (!instance) return;
      global.ConfiguratorAssetDrawer.open(instance.id);
    }
  };

  var _ProjectStore = {
    SCHEMA_VERSION:  1,
    MASTER_PAGE_URL: 'https://www.theleva.com/pages/my',
    CONFIG_KEY:      'base',

    _projectId:   null,
    _saveTimer:   null,
    _subscribers: {},
    _initialized: false,

    init: function () {
      if (this._initialized) return;
      var boot = global.BASE_BOOT || {};
      var pid = boot.linkedMainProjectId || null;
      var activeKey = this._activeKey();
      if (!pid) {
        try { pid = localStorage.getItem(activeKey); } catch (_) {}
      }
      if (!pid) {
        pid = this._generateUuid();
        try { localStorage.setItem(activeKey, pid); } catch (_) {}
      }
      this._projectId = pid;

      var self = this;
      global.addEventListener('storage', function (e) { self._onStorageEvent(e); });
      global.addEventListener('pagehide', function () { self.publishNow(); });

      this._initialized = true;
      _dbg('ProjectStore: project=' + pid + ' customer=' + this.getCustomerId());
    },

    getProjectId:  function () { return this._projectId; },
    getCustomerId: function () {
      var boot = global.BASE_BOOT || {};
      return (boot.customerId != null) ? String(boot.customerId) : 'anon';
    },

    _activeKey: function () {
      return 'levaActiveProject:c' + this.getCustomerId();
    },
    _payloadKey: function (configKey) {
      return 'levaProject:p' + this._projectId + ':c' + this.getCustomerId() + ':' + configKey;
    },

    _generateUuid: function () {
      if (global.crypto && global.crypto.randomUUID) {
        try { return global.crypto.randomUUID(); } catch (_) {}
      }
      return 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    },

    _readTotalsFromWidget: function () {
      function parseNum(id) {
        var el = document.getElementById(id);
        if (!el) return 0;
        var t = (el.textContent || '').replace(/[^0-9.,-]/g, '').replace(',', '.');
        return parseFloat(t) || 0;
      }
      return {
        materialKc:  parseNum('cf-bw-material'),
        executionKc: parseNum('cf-bw-execution'),
        totalKc:     parseNum('cf-bw-total'),
        hours:       parseNum('cf-bw-time')
      };
    },

    serialize: function () {
      var boot = global.BASE_BOOT || {};
      var poly = getFootprintPolygon();
      var bb   = getBBox();
      var area = computeArea();
      var per  = computePerimeter();
      var mapData = null;
      if (_MapManager && _MapManager.center) {
        mapData = {
          address: _MapManager.address || null,
          center:  _MapManager.center  || null,
          bearing: _MapManager.bearing || 0
        };
      }
      return {
        v:             this.SCHEMA_VERSION,
        configKey:     this.CONFIG_KEY,
        productHandle: boot.productHandle || null,
        productId:     boot.productId     || null,
        productTitle:  boot.productTitle  || null,
        variant:       STATE.variant,
        variantKey:    ['slab','strip','piers','precast','screws'][STATE.variant - 1] || null,
        lvl:           STATE.lvl,
        woodStyle:     STATE.woodStyle || 'privacy',
        woodHeight:    STATE.woodHeight,
        postSpacingOverride: STATE.postSpacingOverride,
        slope:         { active: STATE.slopeActive, deg: STATE.slopeDeg },
        mapLocked:     STATE.mapLocked,
        units:         STATE.isMetric ? 'm' : 'ft',
        shape: {
          polygon:     poly,
          bboxL:       bb.L,
          bboxW:       bb.W,
          area:        area,
          perimeter:   per,
          cornerCount: poly.length
        },
        totals:        this._readTotalsFromWidget(),
        map:           mapData,
        updatedAt:     new Date().toISOString()
      };
    },

    publish: function () {
      var self = this;
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(function () { self.publishNow(); }, 500);
    },

    publishNow: function () {
      if (!this._initialized || !this._projectId) return;
      try {
        var payload = this.serialize();
        localStorage.setItem(this._payloadKey(this.CONFIG_KEY), JSON.stringify(payload));
      } catch (e) {
        _dbg('ProjectStore publish error: ' + (e && e.message ? e.message : e));
      }
    },

    read: function (configKey) {
      if (!this._projectId || !configKey) return null;
      try {
        var raw = localStorage.getItem(this._payloadKey(configKey));
        if (!raw) return null;
        var data = JSON.parse(raw);
        if (!data || data.v !== this.SCHEMA_VERSION) return null;
        return data;
      } catch (e) { return null; }
    },

    list: function () {
      if (!this._projectId) return [];
      var prefix = 'levaProject:p' + this._projectId + ':c' + this.getCustomerId() + ':';
      var keys = [];
      try {
        for (var i = 0; i < localStorage.length; i++) {
          var k = localStorage.key(i);
          if (k && k.indexOf(prefix) === 0) {
            keys.push(k.substring(prefix.length));
          }
        }
      } catch (e) {}
      return keys;
    },

    clear: function () {
      if (!this._projectId) return;
      try { localStorage.removeItem(this._payloadKey(this.CONFIG_KEY)); } catch (e) {}
    },

    subscribe: function (configKey, callback) {
      if (typeof callback !== 'function') return;
      if (!this._subscribers[configKey]) this._subscribers[configKey] = [];
      this._subscribers[configKey].push(callback);
    },

    unsubscribe: function (configKey, callback) {
      var arr = this._subscribers[configKey];
      if (!arr) return;
      for (var i = arr.length - 1; i >= 0; i--) {
        if (arr[i] === callback) arr.splice(i, 1);
      }
    },

    _onStorageEvent: function (e) {
      if (!this._projectId || !e || !e.key) return;
      var prefix = 'levaProject:p' + this._projectId + ':c' + this.getCustomerId() + ':';
      if (e.key.indexOf(prefix) !== 0) return;
      var configKey = e.key.substring(prefix.length);
      if (configKey === this.CONFIG_KEY) return;
      var arr = this._subscribers[configKey];
      if (!arr || !arr.length) return;
      var data = null;
      try { if (e.newValue) data = JSON.parse(e.newValue); } catch (_) {}
      for (var i = 0; i < arr.length; i++) {
        try { arr[i](data); } catch (_) {}
      }
    }
  };

  function _restoreFromProjectStore() {
    var data = _ProjectStore.read(_ProjectStore.CONFIG_KEY);
    if (!data) return false;
    var sh = data.shape || {};
    if (Array.isArray(sh.polygon) && sh.polygon.length >= 3) {
      var newPoly = [];
      for (var i = 0; i < sh.polygon.length; i++) {
        var p = sh.polygon[i];
        if (Array.isArray(p) && p.length === 2 && typeof p[0] === 'number' && typeof p[1] === 'number') {
          newPoly.push([_clampCoord(p[0]), _clampCoord(p[1])]);
        }
      }
      if (newPoly.length >= 3) STATE.polygon = newPoly;
    }
    if (typeof data.variant === 'number' && data.variant >= 1 && data.variant <= 5) {
      STATE.variant = data.variant;
    }
    if (typeof data.lvl === 'number' && data.lvl >= 1 && data.lvl <= 3) {
      STATE.lvl = data.lvl;
    }
    if (typeof data.woodStyle === 'string' && WOOD_STYLES.indexOf(data.woodStyle) !== -1) {
      STATE.woodStyle = data.woodStyle;
    }
    if (typeof data.woodHeight === 'number' && isFinite(data.woodHeight)) {
      STATE.woodHeight = data.woodHeight;
    } else if (data.woodHeight === null) {
      STATE.woodHeight = null;
    }
    if (typeof data.postSpacingOverride === 'number' && isFinite(data.postSpacingOverride)) {
      STATE.postSpacingOverride = data.postSpacingOverride;
    } else if (data.postSpacingOverride === null) {
      STATE.postSpacingOverride = null;
    }
    if (data.slope && typeof data.slope.deg === 'number') {
      STATE.slopeDeg = Math.max(0, Math.min(15, data.slope.deg | 0));
      STATE.slopeActive = !!data.slope.active;
    }
    if (data.units === 'ft') STATE.isMetric = false;
    if (data.units === 'm')  STATE.isMetric = true;
    STATE.mapLocked = !!data.mapLocked;
    if (STATE.mapLocked && data.map && Array.isArray(data.map.center) && data.map.center.length === 2) {
      _pendingMapRestore = {
        center:  data.map.center,
        address: data.map.address || '',
        bearing: typeof data.map.bearing === 'number' ? data.map.bearing : 0
      };
    }
    _dbg('ProjectStore: state restored from storage');
    return true;
  }

  var _pendingMapRestore = null;

  function _applyPendingMapRestore() {
    if (!_pendingMapRestore) return;
    if (!global.mapboxgl || !_MapManager || !_MapManager._loadMap) {
      setTimeout(_applyPendingMapRestore, 200);
      return;
    }
    var data = _pendingMapRestore;
    _pendingMapRestore = null;
    if (typeof data.bearing === 'number') _MapManager.bearing = data.bearing;
    try {
      _MapManager._loadMap(data.center, data.address);
      _dbg('Map restored from storage: ' + JSON.stringify(data.center));
    } catch (e) {
      _dbg('Map restore failed: ' + e.message);
    }
  }

  function _init2DZoom() {
    function getCanvas() {
      return global.BaseEngine && global.BaseEngine.renderer && global.BaseEngine.renderer.domElement;
    }
    function getOrtho() {
      return global.BaseEngine && global.BaseEngine.cameraOrtho;
    }
    function render() {
      var eng = global.BaseEngine;
      if (eng && eng.renderer && eng.camera) eng.renderer.render(eng.scene, eng.camera);
    }
    function applyZoom(factor) {
      var cam = getOrtho();
      if (!cam) return;
      cam.zoom = Math.max(0.2, Math.min(8, cam.zoom * factor));
      cam.updateProjectionMatrix();
      render();
      if (typeof positionDragHandles === 'function') positionDragHandles();
      if (_VisualizationManager && _VisualizationManager.refreshDimensions) _VisualizationManager.refreshDimensions();
      _orthoEventEmitter.fire();
    }

    var canvas = getCanvas();
    if (!canvas) { setTimeout(_init2DZoom, 100); return; }

    canvas.addEventListener('wheel', function (e) {
      if (STATE.view !== '2d') return;
      e.preventDefault();
      var factor = e.deltaY > 0 ? (1 / 1.1) : 1.1;
      applyZoom(factor);
    }, { passive: false });

    var pinch = { active: false, lastDist: 0 };
    canvas.addEventListener('touchstart', function (e) {
      if (STATE.view !== '2d') return;
      if (e.touches.length === 2) {
        pinch.active = true;
        pinch.lastDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    }, { passive: true });
    canvas.addEventListener('touchmove', function (e) {
      if (STATE.view !== '2d' || !pinch.active || e.touches.length !== 2) return;
      e.preventDefault();
      var dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (pinch.lastDist > 0) applyZoom(dist / pinch.lastDist);
      pinch.lastDist = dist;
    }, { passive: false });
    function endPinch(e) {
      if (!e.touches || e.touches.length < 2) pinch.active = false;
    }
    canvas.addEventListener('touchend',    endPinch);
    canvas.addEventListener('touchcancel', endPinch);
  }

  function _init2DMapPan() {
    var canvas = global.BaseEngine && global.BaseEngine.renderer && global.BaseEngine.renderer.domElement;
    if (!canvas) { setTimeout(_init2DMapPan, 100); return; }
    var drag = { active: false, lastX: 0, lastY: 0, pointerId: null };

    function canPan() {
      if (STATE.view !== '2d') return false;
      if (!_MapManager || !_MapManager.map) return false;
      if (_CornerInserter && _CornerInserter.pendingPlacement) return false;
      var mapDiv = document.getElementById('cf-mapbox');
      if (!mapDiv || !mapDiv.classList.contains('cf-map-visible')) return false;
      return true;
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (!canPan()) return;
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      drag.active = true;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      drag.pointerId = e.pointerId;
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    });

    canvas.addEventListener('pointermove', function (e) {
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      if (!canPan()) { drag.active = false; return; }
      var dx = e.clientX - drag.lastX;
      var dy = e.clientY - drag.lastY;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      if (_MapManager.map && (dx !== 0 || dy !== 0)) {
        _MapManager.map.panBy([-dx, -dy], { animate: false });
      }
    });

    function endDrag(e) {
      if (!drag.active) return;
      drag.active = false;
      try { if (e) canvas.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    canvas.addEventListener('pointerup',     endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('pointerleave',  endDrag);
  }

  function _injectTooltipCSS() {
    if (document.getElementById('cf-tooltip-runtime-css-base')) return;
    var css = [
      '#cf-wrap-base .cf-icon-btn { position: relative !important; overflow: visible !important; }',
      '#cf-wrap-base .cf-sidebar { overflow: visible !important; }',
      '#cf-wrap-base .cf-icon-btn::after {',
      '  content: attr(title) !important;',
      '  position: absolute !important;',
      '  right: calc(100% + 8px) !important;',
      '  top: 50% !important;',
      '  transform: translateY(-50%) !important;',
      '  background: rgba(20, 20, 20, 0.92) !important;',
      '  color: #fff !important;',
      '  padding: 6px 10px !important;',
      '  border-radius: 6px !important;',
      '  font: 600 11px/1.2 Montserrat, -apple-system, sans-serif !important;',
      '  letter-spacing: 0.02em !important;',
      '  white-space: nowrap !important;',
      '  pointer-events: none !important;',
      '  opacity: 0 !important;',
      '  transition: opacity 0.15s ease !important;',
      '  z-index: 9999 !important;',
      '  display: block !important;',
      '  visibility: visible !important;',
      '}',
      '#cf-wrap-base .cf-icon-btn:hover::after,',
      '#cf-wrap-base .cf-icon-btn:active::after,',
      '#cf-wrap-base .cf-icon-btn.show-tip::after { opacity: 1 !important; }'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'cf-tooltip-runtime-css-base';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function _bindIconTooltips() {
    _injectTooltipCSS();
    var btns = document.querySelectorAll('#cf-wrap-base .cf-icon-btn');
    var tipTimer = null;
    btns.forEach(function (btn) {
      btn.addEventListener('touchstart', function () {
        if (tipTimer) clearTimeout(tipTimer);
        document.querySelectorAll('#cf-wrap-base .cf-icon-btn.show-tip').forEach(function (b) {
          b.classList.remove('show-tip');
        });
        btn.classList.add('show-tip');
        tipTimer = setTimeout(function () { btn.classList.remove('show-tip'); }, 1500);
      }, { passive: true });
    });
  }

  function _rebuildGrid(eng) {
    var T = global.THREE;
    if (!T || !eng || !eng.scene) return;
    if (eng._gridHelper) {
      eng.scene.remove(eng._gridHelper);
      if (eng._gridHelper.geometry) eng._gridHelper.geometry.dispose();
      if (eng._gridHelper.material) eng._gridHelper.material.dispose();
      eng._gridHelper = null;
    }
    var size = 80;
    var step = STATE.isMetric ? 1.0 : 0.3048;
    var divisions = Math.round(size / step);
    eng._gridHelper = new T.GridHelper(size, divisions, 0xcccccc, 0xe5e5e5);
    /* When slope is active, tilt the entire grid by the slope angle
       around an axis perpendicular to the bearing direction so the
       grid lines lie on the terrain plane. Intrinsic Euler 'YZX':
         1. Yaw by bearing so local +X points uphill.
         2. Tilt by slope around the new local +Z (now perpendicular
            to the uphill direction, in the horizontal plane). */
    /* When slope is active we DON'T tilt the grid. A tilted GridHelper
       rotates about its own centre (X=0), whereas the fence grade is
       referenced to xMax -- different pivots, so the two never line up
       and the grid appears to slope against the fence. Instead we leave
       the grid flat and let the terrain plane (heighted per-vertex from
       _padSlopeExtra, the same formula the fence uses) represent the
       slope as a true ground surface under the model. */
    eng._gridHelper.rotation.set(0, 0, 0);
    if (STATE.slopeActive && STATE.slopeDeg > 0) {
      /* Slope active -> hide the flat base grid entirely. The slope-
         conforming grid built in _build3D_SlopeViz (heighted per-vertex
         from the same _padSlopeExtra the fence uses) takes its place, so
         there's exactly one grid and it sits on grade under the model. */
      eng._gridHelper.visible = false;
      eng.scene.add(eng._gridHelper);
      return;
    }
    eng._gridHelper.visible = true;
    eng.scene.add(eng._gridHelper);
  }

  /* The flat base grid must be hidden whenever slope is active (the
     slope-conforming grid from _build3D_SlopeViz replaces it). Several
     call sites toggle grid visibility, so they all route through this. */
  function _applyGridVisibility(eng) {
    if (!eng || !eng._gridHelper) return;
    var slope = STATE.slopeActive && STATE.slopeDeg > 0;
    eng._gridHelper.visible = !slope;
  }

  var _orthoEventEmitter = {
    _listeners: [],
    addEventListener: function (name, cb) {
      if (name === 'change' && typeof cb === 'function') this._listeners.push(cb);
    },
    fire: function () {
      for (var i = 0; i < this._listeners.length; i++) {
        try { this._listeners[i](); } catch (e) {}
      }
    }
  };

  function _loadMapboxSDK() {
    if (global.mapboxgl) return Promise.resolve();
    if (global.__cfBaseMapSDKPromise) return global.__cfBaseMapSDKPromise;
    global.__cfBaseMapSDKPromise = new Promise(function (resolve, reject) {
      if (!document.getElementById('cf-mapbox-gl-css')) {
        var css = document.createElement('link');
        css.id = 'cf-mapbox-gl-css';
        css.rel = 'stylesheet';
        css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
        document.head.appendChild(css);
      }
      var existing = document.getElementById('cf-mapbox-gl-js');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Mapbox SDK load failed')); });
        return;
      }
      var script = document.createElement('script');
      script.id = 'cf-mapbox-gl-js';
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('Mapbox SDK load failed')); };
      document.head.appendChild(script);
    });
    return global.__cfBaseMapSDKPromise;
  }

  function _injectMapCSS() {
    if (document.getElementById('cf-base-map-css')) return;
    var css = [
      '#cf-mapbox {',
      '  position: absolute !important; inset: 0;',
      '  width: 100% !important; height: 100% !important;',
      '  z-index: 0; background-color: #f5f5f5;',
      '  transition: opacity 0.35s ease; display: none;',
      '}',
      '#cf-mapbox.cf-map-visible { display: block; }',
      '#cf-wrap-base .cf-map-fade {',
      '  position: absolute; inset: 0; z-index: 1;',
      '  pointer-events: none; display: none;',
      '  background:',
      '    linear-gradient(to right,  rgba(245,245,245,1) 0%, rgba(245,245,245,0.85) 4%, rgba(245,245,245,0.50) 8%, rgba(245,245,245,0) 14%) left   / 100% 100% no-repeat,',
      '    linear-gradient(to left,   rgba(245,245,245,1) 0%, rgba(245,245,245,0.85) 4%, rgba(245,245,245,0.50) 8%, rgba(245,245,245,0) 14%) right  / 100% 100% no-repeat,',
      '    linear-gradient(to bottom, rgba(245,245,245,1) 0%, rgba(245,245,245,0.85) 4%, rgba(245,245,245,0.50) 8%, rgba(245,245,245,0) 14%) top    / 100% 100% no-repeat,',
      '    linear-gradient(to top,    rgba(245,245,245,1) 0%, rgba(245,245,245,0.85) 4%, rgba(245,245,245,0.50) 8%, rgba(245,245,245,0) 14%) bottom / 100% 100% no-repeat;',
      '}',
      '#cf-wrap-base .cf-map-fade.cf-map-visible { display: block; }',
      '#cf-mapbox .mapboxgl-ctrl-attrib, #cf-mapbox .mapboxgl-ctrl-logo { display: none !important; }',
      '#cf-wrap-base .cf-map-credit {',
      '  position: absolute; left: 10px; bottom: 10px; z-index: 150;',
      '  font: 9px/1 system-ui, sans-serif; color: rgba(0,0,0,0.5);',
      '  background: rgba(255,255,255,0.7); padding: 3px 6px; border-radius: 4px;',
      '  pointer-events: none; display: none;',
      '}',
      '#cf-wrap-base .cf-map-credit.cf-map-visible { display: block; }',
      '#cf-wrap-base.cf-has-map .cf-canvas { background-color: transparent !important; }',
      '#cf-wrap-base.cf-has-map { background: #f5f5f5; }',
      '.cf-map-modal-backdrop {',
      '  position: fixed; inset: 0; z-index: 9999;',
      '  background: rgba(0,0,0,0.45); display: none;',
      '  align-items: center; justify-content: center;',
      '}',
      '.cf-map-modal-backdrop.open { display: flex; }',
      '.cf-map-modal {',
      '  background: #fff; border-radius: 14px; box-shadow: 0 20px 60px rgba(0,0,0,0.30);',
      '  padding: 18px 18px 14px; width: min(380px, calc(100vw - 32px));',
      '  font-family: Montserrat, -apple-system, sans-serif;',
      '}',
      '.cf-map-modal h3 {',
      '  margin: 0 0 12px; font-size: 14px; font-weight: 700; color: #111;',
      '  letter-spacing: 0.02em;',
      '}',
      '.cf-map-modal-input-wrap { position: relative; }',
      '.cf-map-modal input {',
      '  width: 100%; padding: 10px 12px; border: 1px solid #e0e0e0; border-radius: 8px;',
      '  font: 13px/1.3 Montserrat, sans-serif; outline: none; box-sizing: border-box;',
      '}',
      '.cf-map-modal input:focus { border-color: #5A6B47; }',
      '.cf-map-modal-suggestions {',
      '  position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px;',
      '  background: #fff; border: 1px solid #e0e0e0; border-radius: 8px;',
      '  box-shadow: 0 6px 20px rgba(0,0,0,0.10);',
      '  max-height: 220px; overflow-y: auto; display: none; z-index: 10;',
      '}',
      '.cf-map-modal-suggestions.open { display: block; }',
      '.cf-map-modal-sugg-item {',
      '  padding: 9px 12px; font: 12px/1.35 Montserrat, sans-serif; color: #333;',
      '  cursor: pointer; border-bottom: 1px solid #f0f0f0;',
      '}',
      '.cf-map-modal-sugg-item:last-child { border-bottom: none; }',
      '.cf-map-modal-sugg-item:hover { background: #f5f5f0; }',
      '.cf-map-modal-buttons { display: flex; gap: 8px; margin-top: 12px; }',
      '.cf-map-modal-btn {',
      '  flex: 1; padding: 9px 14px; border: none; border-radius: 8px;',
      '  font: 700 12px Montserrat, sans-serif; cursor: pointer;',
      '  letter-spacing: 0.02em;',
      '}',
      '.cf-map-modal-btn-cancel { background: #f0f0f0; color: #555; }',
      '.cf-map-modal-btn-cancel:hover { background: #e6e6e6; }',
      '.cf-map-modal-btn-ok { background: #5A6B47; color: #fff; }',
      '.cf-map-modal-btn-ok:hover { background: #4a5a3b; }',
      '.cf-map-modal-btn-ok:disabled { opacity: 0.4; cursor: not-allowed; }',
      '#cf-wrap-base .cf-map-controls.open { display: flex; }',
      '#cf-wrap-base .cf-map-controls > * { pointer-events: auto; }',
      '.cf-map-rotate-vert {',
      '  display: flex; flex-direction: column; align-items: center; gap: 8px;',
      '}',
      '.cf-map-rotate-slider {',
      '  -webkit-appearance: none; appearance: none;',
      '  accent-color: var(--cf-accent, #5A6B47);',
      '  writing-mode: vertical-lr; direction: rtl;',
      '  width: 4px; height: 320px;',
      '  background: linear-gradient(to top, var(--cf-accent, #5A6B47) 0%, var(--cf-accent, #5A6B47) var(--cf-fill, 0%), #e0e0e0 var(--cf-fill, 0%), #e0e0e0 100%);',
      '  border-radius: 10px; outline: none; cursor: pointer;',
      '}',
      '.cf-map-rotate-slider::-webkit-slider-thumb {',
      '  -webkit-appearance: none; appearance: none;',
      '  width: 12px; height: 12px; background: var(--cf-accent, #5A6B47);',
      '  border: none; border-radius: 50%; cursor: pointer;',
      '  box-shadow: 0 2px 6px rgba(0,0,0,0.20);',
      '}',
      '.cf-map-rotate-slider::-moz-range-thumb {',
      '  width: 12px; height: 12px; background: var(--cf-accent, #5A6B47);',
      '  border: none; border-radius: 50%; cursor: pointer;',
      '  box-shadow: 0 2px 6px rgba(0,0,0,0.20);',
      '}',
      '.cf-map-right-col { display: flex; flex-direction: column; gap: 10px; }',
      '.cf-map-style-row { display: flex; gap: 6px; align-items: center; }',
      '.cf-map-style-btn {',
      '  width: 42px; height: 32px; border: none; border-radius: 8px; cursor: pointer;',
      '  background: rgba(255,255,255,0.92); color: #333; font: 700 10px sans-serif;',
      '  box-shadow: 0 2px 6px rgba(0,0,0,0.08);',
      '  display: flex; align-items: center; justify-content: center;',
      '  transition: all 0.15s;',
      '}',
      '.cf-map-style-btn.active { background: #111; color: #fff; }',
      '.cf-map-lock-btn { color: #888; }',
      '.cf-map-lock-btn.locked { background: #c44; color: #fff; }',
      '.cf-map-lock-btn.locked svg { stroke: #fff; }',
      '#cf-wrap-base .cf-icon-btn.cf-map-btn-active { background: #5A6B47 !important; }',
      '#cf-wrap-base .cf-icon-btn.cf-map-btn-active img,',
      '#cf-wrap-base .cf-icon-btn.cf-map-btn-active svg { filter: brightness(0) invert(1) !important; }',
      '#cf-wrap-base .cf-icon-btn.cf-map-btn-active:hover { background: #5A6B47 !important; }'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'cf-base-map-css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function _injectMapDOM() {
    var wrap = document.getElementById('cf-wrap-base');
    if (!wrap) return;
    var canvas = document.getElementById('cf-canvas-base');

    if (!document.getElementById('cf-mapbox')) {
      var mapDiv = document.createElement('div');
      mapDiv.id = 'cf-mapbox';
      if (canvas) wrap.insertBefore(mapDiv, canvas); else wrap.appendChild(mapDiv);
    }
    if (!wrap.querySelector('.cf-map-fade')) {
      var fade = document.createElement('div');
      fade.className = 'cf-map-fade';
      if (canvas) wrap.insertBefore(fade, canvas); else wrap.appendChild(fade);
    }
    if (!document.getElementById('cf-map-credit')) {
      var credit = document.createElement('div');
      credit.id = 'cf-map-credit';
      credit.className = 'cf-map-credit';
      credit.textContent = '\u00a9 Mapbox \u00a9 OpenStreetMap';
      wrap.appendChild(credit);
    }
    if (!document.getElementById('cf-map-controls')) {
      var ctrls = document.createElement('div');
      ctrls.id = 'cf-map-controls';
      ctrls.className = 'cf-map-controls cf-map-stacked';
      ctrls.innerHTML =
        '<div class="cf-map-rotate-vert">' +
        '  <input type="range" id="cf-map-rotate" class="cf-map-rotate-slider" min="0" max="360" step="1" value="0" aria-label="Rotation">' +
        '</div>' +
        '<div class="cf-map-right-col">' +
        '  <div class="cf-map-style-row">' +
        '    <button id="cf-map-style-streets" class="cf-map-style-btn active" type="button">MAP</button>' +
        '    <button id="cf-map-style-satellite" class="cf-map-style-btn" type="button">SAT</button>' +
        '    <button id="cf-map-lock" class="cf-map-style-btn cf-map-lock-btn" type="button" title="Lock map position" aria-pressed="false">' +
        '      <svg id="cf-map-lock-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '        <rect x="5" y="11" width="14" height="9" rx="2"/>' +
        '        <path d="M8 11V7a4 4 0 0 1 8 0v4" id="cf-map-lock-shackle"/>' +
        '      </svg>' +
        '    </button>' +
        '  </div>' +
        '</div>';
      wrap.appendChild(ctrls);
    }
    if (!document.getElementById('cf-map-modal-backdrop')) {
      var modal = document.createElement('div');
      modal.id = 'cf-map-modal-backdrop';
      modal.className = 'cf-map-modal-backdrop';
      modal.innerHTML =
        '<div class="cf-map-modal" role="dialog" aria-modal="true">' +
        '  <h3>Map location</h3>' +
        '  <div class="cf-map-modal-input-wrap">' +
        '    <input id="cf-map-address-input" type="text" placeholder="Address, city or place..." autocomplete="off">' +
        '    <div id="cf-map-suggestions" class="cf-map-modal-suggestions"></div>' +
        '  </div>' +
        '  <div class="cf-map-modal-buttons">' +
        '    <button id="cf-map-modal-cancel" type="button" class="cf-map-modal-btn cf-map-modal-btn-cancel">Cancel</button>' +
        '    <button id="cf-map-modal-ok" type="button" class="cf-map-modal-btn cf-map-modal-btn-ok" disabled>Load</button>' +
        '  </div>' +
        '</div>';
      document.body.appendChild(modal);
    }
    var sidebar = wrap.querySelector('.cf-sidebar');
    if (sidebar && !document.getElementById('cf-btn-map')) {
      var btn = document.createElement('button');
      btn.id = 'cf-btn-map';
      btn.className = 'cf-icon-btn';
      btn.type = 'button';
      var _mapI18n = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
      btn.title = _mapI18n.fence_map || _mapI18n.addBackground || 'Add map background';
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z"/><path d="M9 4v16"/><path d="M15 6v16"/></svg>';
      var shareBtn = document.getElementById('cf-btn-share');
      if (shareBtn && shareBtn.parentNode === sidebar) {
        sidebar.insertBefore(btn, shareBtn.nextSibling);
      } else {
        sidebar.appendChild(btn);
      }
    }
  }

  var _MapManager = (function () {
    var I18N_DEFAULTS = {
      rotation:           'Rotation',
      streets:            'Roadmap',
      satellite:          'Satellite',
      addressPlaceholder: 'Address, city or place\u2026',
      load:               'Load',
      cancel:             'Cancel',
      modalTitle:         'Map location',
      addBackground:      'Add map background',
      changeLocation:     'Change map location'
    };

    var MM = {
      _deps: null,
      _i18n: I18N_DEFAULTS,
      _storageKey: null,
      MAPBOX_TOKEN: null,
      STYLE_STREETS:   'mapbox://styles/mapbox/light-v11',
      STYLE_SATELLITE: 'mapbox://styles/mapbox/satellite-streets-v12',

      map: null,
      currentStyle: 'streets',
      center: null,
      zoomLevel: null,
      bearing: 0,
      address: '',
      _geocodeAbort: null,
      _suggestionItems: [],
      _suppressStorageEvent: false,

      _bindUI: function () {
        var self = this;
        if (!global.mapboxgl) return;
        global.mapboxgl.accessToken = this.MAPBOX_TOKEN;

        var btn = document.getElementById('cf-btn-map');
        if (btn) btn.addEventListener('click', function () {
          var mapDiv = document.getElementById('cf-mapbox');
          var visible = mapDiv && mapDiv.classList.contains('cf-map-visible');
          if (self.map && visible) self._hideMap();
          else self._openModal();
        });

        var backdrop = document.getElementById('cf-map-modal-backdrop');
        var input    = document.getElementById('cf-map-address-input');
        var okBtn    = document.getElementById('cf-map-modal-ok');
        var cancelBtn= document.getElementById('cf-map-modal-cancel');
        var sugg     = document.getElementById('cf-map-suggestions');
        if (!backdrop || !input || !okBtn || !cancelBtn || !sugg) return;

        backdrop.addEventListener('click', function (e) {
          if (e.target === backdrop) self._closeModal();
        });
        cancelBtn.addEventListener('click', function () { self._closeModal(); });

        var debounceT = null;
        input.addEventListener('input', function () {
          clearTimeout(debounceT);
          var q = input.value.trim();
          if (q.length < 3) {
            sugg.classList.remove('open');
            okBtn.disabled = true;
            return;
          }
          debounceT = setTimeout(function () { self._geocode(q); }, 300);
        });

        okBtn.addEventListener('click', function () {
          var selected = null;
          for (var i = 0; i < self._suggestionItems.length; i++) {
            if (self._suggestionItems[i].selected) { selected = self._suggestionItems[i]; break; }
          }
          if (!selected) selected = self._suggestionItems[0];
          if (!selected) return;
          self._loadMap(selected.center, selected.label);
          self._closeModal();
        });

        var btnS  = document.getElementById('cf-map-style-streets');
        var btnSa = document.getElementById('cf-map-style-satellite');
        if (btnS)  btnS.addEventListener('click',  function () { self._setStyle('streets'); });
        if (btnSa) btnSa.addEventListener('click', function () { self._setStyle('satellite'); });

        var btnLock = document.getElementById('cf-map-lock');
        if (btnLock) {
          btnLock.addEventListener('click', function () {
            STATE.mapLocked = !STATE.mapLocked;
            _updateMapLockButton();
            if (_ProjectStore && _ProjectStore._initialized) _ProjectStore.publish();
          });
        }

        var rotateInput = document.getElementById('cf-map-rotate');
        if (rotateInput) {
          var updateRotateFill = function () {
            var pct = (parseFloat(rotateInput.value) / 360) * 100;
            rotateInput.style.setProperty('--cf-fill', pct + '%');
          };
          rotateInput.addEventListener('input', function () {
            var deg = parseInt(rotateInput.value, 10);
            self.bearing = deg;
            if (self.map) {
              if (self._deps.getView() === '3d') {
                self._syncMapTo3D(false);
              } else {
                self.map.setBearing(deg);
              }
            }
            updateRotateFill();
            if (STATE.slopeActive && STATE.slopeDeg > 0) {
              if (self._slopeRefreshTimer) clearTimeout(self._slopeRefreshTimer);
              self._slopeRefreshTimer = setTimeout(function () {
                self._slopeRefreshTimer = null;
                if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
                refreshUI();
              }, 1000);
            }
          });
          updateRotateFill();
        }

        this._updateSidebarButton();
      },

      _openModal: function () {
        var bd = document.getElementById('cf-map-modal-backdrop');
        var input = document.getElementById('cf-map-address-input');
        if (bd) bd.classList.add('open');
        if (input) { input.value = this.address || ''; setTimeout(function () { input.focus(); }, 50); }
      },
      _closeModal: function () {
        var bd = document.getElementById('cf-map-modal-backdrop');
        var sugg = document.getElementById('cf-map-suggestions');
        if (bd) bd.classList.remove('open');
        if (sugg) sugg.classList.remove('open');
      },

      _geocode: function (query) {
        var self = this;
        if (this._geocodeAbort) this._geocodeAbort.abort();
        this._geocodeAbort = new AbortController();
        var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
                  encodeURIComponent(query) + '.json?access_token=' + this.MAPBOX_TOKEN +
                  '&autocomplete=true&limit=5&types=address,place,locality,neighborhood,region';
        fetch(url, { signal: this._geocodeAbort.signal })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var sugg = document.getElementById('cf-map-suggestions');
            var okBtn = document.getElementById('cf-map-modal-ok');
            if (!sugg || !okBtn) return;
            sugg.innerHTML = '';
            var feats = (data && data.features) || [];
            self._suggestionItems = feats.map(function (f, i) {
              return { label: f.place_name, center: f.center, selected: i === 0 };
            });
            if (!self._suggestionItems.length) {
              sugg.classList.remove('open');
              okBtn.disabled = true;
              return;
            }
            self._suggestionItems.forEach(function (s) {
              var div = document.createElement('div');
              div.className = 'cf-map-modal-sugg-item';
              div.textContent = s.label;
              div.addEventListener('click', function () {
                self._suggestionItems.forEach(function (it) { it.selected = false; });
                s.selected = true;
                var input = document.getElementById('cf-map-address-input');
                if (input) input.value = s.label;
                sugg.classList.remove('open');
                okBtn.disabled = false;
              });
              sugg.appendChild(div);
            });
            sugg.classList.add('open');
            okBtn.disabled = false;
          })
          .catch(function (e) {
            if (e && e.name !== 'AbortError') _dbg('geocode failed: ' + e.message);
          });
      },

      _computeZoomForLat: function (lat) {
        var wrap = document.getElementById('cf-wrap-base');
        if (!wrap) return 18;
        var canvasW = wrap.clientWidth;
        var cam = this._deps.orthoCamera;
        if (!cam) return 18;
        var frustumW = (cam.right - cam.left) / (cam.zoom || 1);
        var ourMpp = frustumW / canvasW;
        return Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / ourMpp);
      },

      _loadMap: function (center, label) {
        var self = this;
        this.center = center;
        this.address = label;
        this.zoomLevel = this._computeZoomForLat(center[1]);

        var mapDiv = document.getElementById('cf-mapbox');
        var creditEl = document.getElementById('cf-map-credit');
        var wrap = document.getElementById('cf-wrap-base');
        var fade = document.querySelector('#cf-wrap-base .cf-map-fade');
        if (mapDiv) mapDiv.classList.add('cf-map-visible');
        if (creditEl) creditEl.classList.add('cf-map-visible');
        if (wrap) wrap.classList.add('cf-has-map');
        if (fade) fade.classList.add('cf-map-visible');

        /* No scene.background to clear -- Stage renders with a transparent
           clear colour, so #cf-mapbox under the canvas is visible as soon
           as the wrapper gets .cf-has-map. */

        if (this.map) {
          this.map.jumpTo({ center: center, zoom: this.zoomLevel, bearing: this.bearing });
        } else {
          this.map = new global.mapboxgl.Map({
            container: 'cf-mapbox',
            style: this.STYLE_STREETS,
            center: center,
            zoom: this.zoomLevel,
            bearing: this.bearing,
            pitch: 0,
            attributionControl: false,
            interactive: false,
            preserveDrawingBuffer: true
          });
          this.map.dragPan.disable();
          this.map.scrollZoom.disable();
          this.map.boxZoom.disable();
          this.map.dragRotate.disable();
          this.map.keyboard.disable();
          this.map.doubleClickZoom.disable();
          this.map.touchZoomRotate.disable();

          this._updateSidebarButton();

          var canvas = document.getElementById('cf-canvas-base');
          if (canvas) canvas.style.pointerEvents = 'auto';

          var syncZoom = function () {
            if (!self.map || !self.center) return;
            if (self._deps.getView() !== '2d-top') return;
            var z = self._computeZoomForLat(self.center[1]);
            if (Math.abs(z - self.zoomLevel) > 0.005) {
              self.zoomLevel = z;
              self.map.setZoom(z);
            }
          };
          if (this._deps.orthoControls && this._deps.orthoControls.addEventListener) {
            this._deps.orthoControls.addEventListener('change', syncZoom);
          }
          window.addEventListener('resize', function () { setTimeout(syncZoom, 50); });
          setTimeout(syncZoom, 100);

          if (this._deps.controls && this._deps.controls.addEventListener) {
            var _syncPending = false;
            this._deps.controls.addEventListener('change', function () {
              if (self._deps.getView() !== '3d' || !self.map || !self.center) return;
              if (_syncPending) return;
              _syncPending = true;
              requestAnimationFrame(function () {
                _syncPending = false;
                if (self._deps.getView() === '3d' && self.map && self.center) self._syncMapTo3D(false);
              });
            });
          }

          this.map.on('style.load', function () {
            self._install3DLayers();
            if (self._deps.getView() === '3d') self._syncMapTo3D(false);
          });

          this.map.on('moveend', function () {
            if (!self.map) return;
            var c = self.map.getCenter();
            self.center = [c.lng, c.lat];
            if (self._deps.getView() !== '3d') {
              self.bearing = self.map.getBearing();
            }
            if (_ProjectStore && _ProjectStore._initialized) _ProjectStore.publish();
          });

          _applyMapLockState();

          if (typeof ResizeObserver !== 'undefined' && mapDiv) {
            new ResizeObserver(function () {
              if (self.map) { self.map.resize(); syncZoom(); }
            }).observe(mapDiv);
          }
        }

        var ctrls = document.getElementById('cf-map-controls');
        if (ctrls) ctrls.classList.add('open');
        this._deps.onChange();
      },

      _hideMap: function () {
        var mapDiv = document.getElementById('cf-mapbox');
        var credit = document.getElementById('cf-map-credit');
        var wrap = document.getElementById('cf-wrap-base');
        var fade = document.querySelector('#cf-wrap-base .cf-map-fade');
        var ctrls = document.getElementById('cf-map-controls');
        if (mapDiv) mapDiv.classList.remove('cf-map-visible');
        if (credit) credit.classList.remove('cf-map-visible');
        if (fade)   fade.classList.remove('cf-map-visible');
        if (wrap)   wrap.classList.remove('cf-has-map');
        if (ctrls)  ctrls.classList.remove('open');
        /* Nothing to paint back: the canvas stays transparent and
           #cf-wrap-base's own #f5f5f5 shows through instead. Re-opaquing
           the scene here would permanently hide the map on the next load
           and there would be no error to find. */
      },

      _setStyle: function (s) {
        if (!this.map) return;
        this.currentStyle = s;
        var url = s === 'satellite' ? this.STYLE_SATELLITE : this.STYLE_STREETS;
        this.map.setStyle(url);
        var b1 = document.getElementById('cf-map-style-streets');
        var b2 = document.getElementById('cf-map-style-satellite');
        if (b1) b1.classList.toggle('active', s === 'streets');
        if (b2) b2.classList.toggle('active', s === 'satellite');
        this._deps.onChange();
      },

      _install3DLayers: function () {
        if (!this.map) return;
        var m = this.map;
        try {
          if (!m.getLayer('cf-sky')) {
            m.addLayer({
              id: 'cf-sky', type: 'sky',
              paint: { 'sky-type': 'atmosphere', 'sky-atmosphere-sun-intensity': 5 }
            });
          }
        } catch (e) {}
        try {
          if (!m.getLayer('cf-3d-buildings') && m.getSource('composite')) {
            m.addLayer({
              id: 'cf-3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 14,
              paint: {
                'fill-extrusion-color': '#d6cfc2',
                'fill-extrusion-height': ['get', 'height'],
                'fill-extrusion-base': ['get', 'min_height'],
                'fill-extrusion-opacity': 0.85
              }
            });
          }
        } catch (e) {}
        this._set3DLayerVisibility(this._deps.getView() === '3d');
      },

      _computeMapStateFor3D: function () {
        if (!this._deps.camera || !this._deps.controls || !this.center) return null;
        var target = this._deps.controls.target;
        var pos = this._deps.camera.position;
        var offX = pos.x - target.x;
        var offY = pos.y - target.y;
        var offZ = pos.z - target.z;
        var horiz = Math.sqrt(offX*offX + offZ*offZ);
        var dist = Math.sqrt(horiz*horiz + offY*offY);
        if (dist < 0.01) return null;
        var elev = Math.atan2(offY, horiz);
        var pitch = Math.max(0, Math.min(85, 90 - elev * 180 / Math.PI));
        var sceneBearingDeg = Math.atan2(-offX, offZ) * 180 / Math.PI;
        var bearing = (this.bearing || 0) + sceneBearingDeg;
        var wrap = document.getElementById('cf-wrap-base');
        var canvasH = wrap ? wrap.clientHeight : 600;
        var fovRad = this._deps.camera.fov * Math.PI / 180;
        var heightAtTarget = 2 * dist * Math.tan(fovRad / 2);
        var mpp = heightAtTarget / canvasH;
        var lat = this.center[1];
        var zoom = Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / mpp);
        return { pitch: pitch, bearing: bearing, zoom: zoom };
      },

      _syncMapTo3D: function (animate) {
        if (!this.map || !this.center) return;
        var s = this._computeMapStateFor3D();
        if (!s) return;
        if (animate) this.map.easeTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom, duration: 400 });
        else         this.map.jumpTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom });
      },

      _syncMapTo2D: function (animate) {
        if (!this.map || !this.center) return;
        var z = this._computeZoomForLat(this.center[1]);
        this.zoomLevel = z;
        if (animate) this.map.easeTo({ pitch: 0, bearing: this.bearing || 0, zoom: z, duration: 400 });
        else         this.map.jumpTo({ pitch: 0, bearing: this.bearing || 0, zoom: z });
      },

      _set3DLayerVisibility: function (visible) {
        if (!this.map) return;
        var v = visible ? 'visible' : 'none';
        var ids = ['cf-3d-buildings', 'cf-sky'];
        for (var i = 0; i < ids.length; i++) {
          try {
            if (this.map.getLayer(ids[i])) this.map.setLayoutProperty(ids[i], 'visibility', v);
          } catch (e) {}
        }
      },

      _updateSidebarButton: function () {
        var btn = document.getElementById('cf-btn-map');
        if (!btn) return;
        if (this.map) {
          btn.classList.add('cf-map-btn-active');
          btn.title = this._i18n.changeLocation;
        } else {
          btn.classList.remove('cf-map-btn-active');
          btn.title = this._i18n.addBackground;
        }
      },

      _panMeters: function (dirX, dirZ) {
        if (!this.map) return;
        var lat = this.center[1];
        var zoom = this.map.getZoom();
        var mpp = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
        var dx = dirX / mpp;
        var dy = dirZ / mpp;
        this.map.panBy([dx, dy], { animate: false });
        var c = this.map.getCenter();
        this.center = [c.lng, c.lat];
        this._deps.onChange();
      },

      serialize: function () {
        if (!this.map || !this.center) return null;
        return {
          center: this.center,
          bearing: this.bearing || 0,
          style: this.currentStyle,
          address: this.address
        };
      },
      restore: function (data) {
        if (!data || !data.center) return;
        this.bearing = data.bearing || 0;
        var rotateInput = document.getElementById('cf-map-rotate');
        if (rotateInput) {
          rotateInput.value = Math.round(this.bearing);
          rotateInput.style.setProperty('--cf-fill', (this.bearing / 360 * 100) + '%');
        }
        this._loadMap(data.center, data.address || '');
        if (data.style === 'satellite') this._setStyle('satellite');
      },

      persist: function () {
        if (!this._storageKey) return;
        var data = this.serialize();
        if (!data) return;
        try {
          this._suppressStorageEvent = true;
          localStorage.setItem(this._storageKey, JSON.stringify(data));
          var self = this;
          setTimeout(function () { self._suppressStorageEvent = false; }, 0);
        } catch (e) {}
      },
      loadFromStorage: function () {
        if (!this._storageKey) return false;
        try {
          var raw = localStorage.getItem(this._storageKey);
          if (!raw) return false;
          var data = JSON.parse(raw);
          this.restore(data);
          return true;
        } catch (e) { return false; }
      },

      _onStorageEvent: function (e) {
        if (this._suppressStorageEvent) return;
        if (!this._storageKey || e.key !== this._storageKey) return;
        if (!e.newValue) return;
        try {
          var data = JSON.parse(e.newValue);
          var oldCenter = this.center;
          if (!oldCenter || data.center[0] !== oldCenter[0] || data.center[1] !== oldCenter[1]) {
            this.restore(data);
          } else {
            if (typeof data.bearing === 'number') {
              this.bearing = data.bearing;
              if (this.map) this.map.setBearing(data.bearing);
              var ri = document.getElementById('cf-map-rotate');
              if (ri) {
                ri.value = Math.round(data.bearing);
                ri.style.setProperty('--cf-fill', (data.bearing / 360 * 100) + '%');
              }
            }
            if (data.style && data.style !== this.currentStyle) this._setStyle(data.style);
          }
        } catch (err) {}
      }
    };

    var _initialized = false;
    MM.init = function (opts) {
      opts = opts || {};
      var hostOnChange = opts.onChange || function () {};
      MM._deps = opts.engine ? Object.assign({}, opts.engine, {
        getView:       opts.getView       || function () { return '2d-top'; },
        getRootSize:   opts.getRootSize   || function () { return { w: window.innerWidth, h: window.innerHeight }; },
        getPadStep:    opts.getPadStep    || function () { return 2; },
        onChange:      function () { MM.persist(); hostOnChange(); },
        requestRender: opts.requestRender || function () {}
      }) : null;
      /* The host passes the full boot.i18n (snake_case Shopify keys),
         but MapManager's slots are camelCase. Bridge the relevant
         fence_map_* / map_* keys onto the expected names so the map UI
         localizes instead of falling back to English. */
      var _bi = opts.i18n || {};
      var _mapped = {
        rotation:           _bi.fence_map_rotate     || _bi.map_rotate,
        streets:            _bi.fence_map_streets     || _bi.map_streets,
        satellite:          _bi.fence_map_satellite,
        addressPlaceholder: _bi.fence_map_address_placeholder || _bi.map_address_placeholder,
        load:               _bi.fence_map_load        || _bi.map_load,
        modalTitle:         _bi.fence_map_modal_title || _bi.map_modal_title,
        addBackground:      _bi.fence_map,
        changeLocation:     _bi.fence_map_change      || _bi.map_change
      };
      Object.keys(_mapped).forEach(function (k) { if (_mapped[k] == null) delete _mapped[k]; });
      MM._i18n = Object.assign({}, I18N_DEFAULTS, _bi, _mapped);
      MM._storageKey = opts.storageKey || 'levaMap:default';
      MM.MAPBOX_TOKEN = opts.token || MM.MAPBOX_TOKEN;
      if (_initialized) return;
      _initialized = true;
      window.addEventListener('storage', function (e) { MM._onStorageEvent(e); });
      MM._bindUI();
    };

    return MM;
  })();

  function _initMap() {
    var token = (global.BASE_BOOT && global.BASE_BOOT.mapboxToken) || global.MAPBOX_TOKEN;
    if (!token) {
      _dbg('map: no MAPBOX_TOKEN \u2014 map feature disabled');
      return;
    }
    _injectMapCSS();
    _injectMapDOM();
    _loadMapboxSDK().then(function () {
      var boot = global.BASE_BOOT || {};
      var S = global.ConfiguratorStage;
      /* Stage builds the ortho camera in init(), but MapManager takes its
         _deps once and binds listeners on them immediately -- a null ortho
         here shows up much later as "the map does not stay with the model"
         and logs nothing. Force it before we hand anything over. */
      if (S && !S.orthoCam && typeof S._ensureOrtho === 'function') S._ensureOrtho();
      var storageKey = 'levaMap:p' + (boot.productId || '0') + ':c' + (boot.customerId || 'anon');
      _MapManager.init({
        token: token,
        storageKey: storageKey,
        i18n: boot.i18n || {},
        engine: {
          /* Concrete cameras and controls, never BaseEngine.camera /
             BaseEngine.controls: those are swapped on every view toggle
             now, and _deps would keep whatever happened to be current at
             boot -- which for a restored 2D project is the ortho pair. */
          camera:        S.persCam,
          controls:      S.persControls,
          orthoCamera:   S.orthoCam,
          orthoControls: _orthoEventEmitter
        },
        getView: function () { return STATE.view === '2d' ? '2d-top' : '3d'; },
        getRootSize: function () {
          var w = document.getElementById('cf-wrap-base');
          return { w: w ? w.clientWidth : 0, h: w ? w.clientHeight : 0 };
        },
        getPadStep: function () {
          var bb = (typeof getBBox === 'function') ? getBBox() : null;
          if (!bb) return 2;
          return Math.max(1, Math.min(20, (bb.L + bb.W) * 0.025));
        },
        onChange: function () {},
        requestRender: function () {
          var eng = global.BaseEngine;
          if (eng && eng.renderer && eng.camera) eng.renderer.render(eng.scene, eng.camera);
        }
      });
      _MapManager.loadFromStorage();
    }).catch(function (err) {
      _dbg('map: ' + (err && err.message ? err.message : err));
    });
  }

  /* ---------------------------------------------------------------------
     VIEWPORT -- ConfiguratorStage (assets/configurator-ui.js)

     Step F1 of the configurator-ui migration. The inline engine is gone:
     Scene, both cameras, renderer, OrbitControls, lights, resize wiring and
     the animation loop now live in the shared asset. BaseEngine IS the
     Stage -- not an adapter around it -- with the fence-specific surface
     attached on top, exactly the way the container does it.

     Old member names are kept as getters so the ~200 eng.* call sites in
     this lib did not have to be touched:

       eng.sceneGroup  -> Stage.group
       eng.cameraPersp -> Stage.persCam
       eng.cameraOrtho -> Stage.orthoCam

     Getters, not copies: Stage.camera is swapped on every view toggle.

     What stays here because it is fence domain, not viewport: the polygon
     surface, world/screen projection, the grid (slope-aware), footprint
     framing with a persistent ortho D, and the whole toggleView side-band
     (handle resets, viz manager, map sync, section cut).

     NOTE the scene has no background. Stage renders with a transparent
     clear colour so #cf-mapbox can show through from underneath; the
     #cf-wrap-base wrapper carries the same #f5f5f5 the old engine painted
     into the scene, so an unmapped viewport looks identical.
     --------------------------------------------------------------------- */
  global.BaseEngine = global.ConfiguratorStage || global.BaseEngine || null;

  function _defineAlias(S, name, prop) {
    try {
      Object.defineProperty(S, name, {
        configurable: true,
        get: function () { return this[prop]; }
      });
    } catch (e) { _dbg('alias ' + name + ' failed', e); }
  }

  function _attachEngineSurface(S) {
    _defineAlias(S, 'sceneGroup',  'group');
    _defineAlias(S, 'cameraPersp', 'persCam');
    _defineAlias(S, 'cameraOrtho', 'orthoCam');

    /* Persistent orthographic framing. Stage.setView('2d') recomputes the
       frustum from the group bbox and drops zoom to 1 every time it runs --
       including from its own resize handler -- which would throw away the
       framing fitCameraToFootprint() computed and the PDF export relies on.
       We keep D and the centre here and put them back after Stage touches
       the camera. */
    S._currentOrthoD = S._currentOrthoD || 12;
    S._orthoCenter   = S._orthoCenter   || { x: 0, z: 0 };

    S._restoreOrtho = function () {
      var d = this._currentOrthoD;
      if (!d || !this.orthoCam || !this.renderer) return;
      var cv = this.renderer.domElement;
      var w = cv.clientWidth, h = cv.clientHeight;
      if (!w || !h) return;
      var a = w / h;
      var c = this._orthoCenter || { x: 0, z: 0 };
      this.orthoCam.left = -d * a; this.orthoCam.right = d * a;
      this.orthoCam.top = d; this.orthoCam.bottom = -d;
      this.orthoCam.zoom = 1;
      this.orthoCam.position.set(c.x, 30, c.z);
      this.orthoCam.up.set(0, 0, -1);
      this.orthoCam.lookAt(c.x, 0, c.z);
      this.orthoCam.updateProjectionMatrix();
      if (this.orthoControls) this.orthoControls.target.set(c.x, 0, c.z);
    };

    /* Public access to the live STATE -- exposed read-only via
       these helpers so external modules (FloorplanLib, future
       Structure configurator, dashboard view) can read & write
       the footprint without reaching into module-private vars. */
    S.getState = function () { return STATE; };
    S.getPolygon = function () {
      return STATE.polygon ? JSON.parse(JSON.stringify(STATE.polygon)) : null;
    };
    S.setPolygon = function (newPoly) {
      if (!Array.isArray(newPoly) || newPoly.length < 3) return false;
      STATE.polygon = JSON.parse(JSON.stringify(newPoly));
      try { rebuildDragHandles(); } catch (e) { _dbg('rebuildDragHandles after setPolygon failed', e); }
      if (global.BaseRenderer && global.BaseRenderer.build) {
        try { global.BaseRenderer.build(); } catch (e) { _dbg('build after setPolygon failed', e); }
      }
      if (_MapManager && _MapManager.map && _MapManager.center) {
        try {
          if (STATE.view === '2d') _MapManager._syncMapTo2D(true);
          else _MapManager._syncMapTo3D(true);
        } catch (e) {}
      }
      if (_SlopeManager && _SlopeManager._updateAll) {
        try { _SlopeManager._updateAll(); } catch (e) {}
      }
      return true;
    };

    /* Project a world-space point (XZ-plane, Y=0) to screen
       coordinates relative to #cf-wrap-base. Used by FloorplanLib
       to position HTML drag handles on top of the Three.js scene. */
    S.worldToScreen = function (x, z) { return _worldToScreen(x, z); };

    /* Inverse -- screen-space client coordinates to world XZ. Used
       by FloorplanLib while a wall handle is being dragged. */
    S.screenToWorld = function (clientX, clientY) { return _screenToWorld(clientX, clientY); };

    /* Show/hide the foundations drag handles. FloorplanLib calls
       this when entering floorplan-edit mode so the polygon and
       its corner/edge handles get out of the way of the wall
       handles. The render group itself stays -- we just toggle the
       HTML pin layer. */
    S.setFoundationsHandlesVisible = function (visible) {
      var container = document.getElementById('cf-base-handles');
      if (!container) return;
      container.style.display = visible ? '' : 'none';
    };

    /* Overrides Stage.fitCameraToFootprint(), which frames the group bbox
       as a bounding sphere. The fence frames the footprint polygon instead
       (the model is a thin perimeter -- a sphere around it wastes most of
       the viewport) and remembers the ortho D it computed. */
    S.fitCameraToFootprint = function () {
      var poly = getFootprintPolygon();
      if (!poly || poly.length < 2) return;
      var canvas = this.renderer && this.renderer.domElement;
      if (!canvas) return;
      var w = canvas.clientWidth, h = canvas.clientHeight;
      if (!w || !h) return;

      var xMin =  Infinity, xMax = -Infinity;
      var zMin =  Infinity, zMax = -Infinity;
      for (var i = 0; i < poly.length; i++) {
        if (poly[i][0] < xMin) xMin = poly[i][0];
        if (poly[i][0] > xMax) xMax = poly[i][0];
        if (poly[i][1] < zMin) zMin = poly[i][1];
        if (poly[i][1] > zMax) zMax = poly[i][1];
      }
      var bbL = xMax - xMin;
      var bbW = zMax - zMin;
      if (bbL < 0.5 && bbW < 0.5) return;

      var PADDING = 4.0;
      var aspect = w / h;
      var halfW = bbL * 0.5 + PADDING;
      var halfH = bbW * 0.5 + PADDING;
      var d = Math.max(halfH, halfW / aspect);
      d = Math.max(d, 6);
      this._currentOrthoD = d;

      var cx = (xMin + xMax) / 2;
      var cz = (zMin + zMax) / 2;
      this._orthoCenter = { x: cx, z: cz };
      this._restoreOrtho();

      var dist = Math.max(bbL, bbW) * 1.6 + 6;
      if (this.persCam) {
        this.persCam.position.set(cx + dist * 0.6, dist * 0.6, cz + dist * 0.6);
        this.persCam.lookAt(cx, 0, cz);
      }
      if (this.persControls) {
        this.persControls.target.set(cx, 0, cz);
        this.persControls.update();
      }
      positionDragHandles();
    };

    /* Camera swap only. Deliberately NOT Stage.setView(): its '3d' branch
       calls fitCameraToFootprint(), which would yank the perspective camera
       back to a default framing on every single toggle, and its '2d' branch
       reframes the ortho from scratch. The fence keeps whatever the user
       set up and restores its own ortho framing instead. */
    S._applyViewCamera = function () {
      var twoD = (STATE.view === '2d');
      if (twoD) this._ensureOrtho();
      this.camera   = twoD ? this.orthoCam : this.persCam;
      this.controls = twoD ? this.orthoControls : this.persControls;
      if (this.persControls) this.persControls.enabled = !twoD;
      this.view = twoD ? '2d' : '3d';
      if (twoD) this._restoreOrtho();
    };

    S.toggleView = function () {
      _resetAllHandleDrags();
      if (_CornerInserter && _CornerInserter.pendingPlacement) {
        _CornerInserter._setPending(false);
      }
      STATE.view = (STATE.view === '2d') ? '3d' : '2d';
      var wasAssembly = (STATE.assemblyStep > 0);
      STATE.vizStage = 0;
      STATE.assemblyStep = 0;
      /* Skip camera restore when the toggle is part of an internal
         round-trip flush (style/unit switch) -- the flush restores the
         state + framing itself afterwards. */
      if (wasAssembly && _DetailBayManager && !_DetailBayManager._suppressRestore) {
        _DetailBayManager.restoreCamera();
      }
      if (_AssemblyManager && _AssemblyManager._updateButton) {
        _AssemblyManager._updateButton();
      }
      this._applyViewCamera();
      var wrap = document.getElementById('cf-wrap-base');
      if (wrap) {
        wrap.classList.toggle('cf-view-2d', STATE.view === '2d');
        wrap.classList.toggle('cf-view-3d', STATE.view === '3d');
      }
      if (this._gridHelper) _applyGridVisibility(this);
      var viewIcon = document.getElementById('cf-view-icon');
      if (viewIcon) {
        viewIcon.src = (STATE.view === '2d')
          ? 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/3d.png?v=1772625064'
          : 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/2d.png?v=1772625064';
      }
      positionDragHandles();
      if (_GateDragManager && _GateDragManager.reproject) _GateDragManager.reproject();
      if (_SpacingSlider && _SpacingSlider.refresh) _SpacingSlider.refresh();
      if (_VisualizationManager && _VisualizationManager.apply) _VisualizationManager.apply();
      if (_MapManager && _MapManager.map && _MapManager.center) {
        if (STATE.view === '2d') {
          _MapManager._set3DLayerVisibility(false);
          _MapManager._syncMapTo2D(true);
        } else {
          _MapManager._set3DLayerVisibility(true);
          _MapManager._syncMapTo3D(true);
        }
      }
      if (_SlopeManager && _SlopeManager._updateAll) _SlopeManager._updateAll();
      /* Section cut only renders in 3D -- toggle into 2D wipes any
         active clipping planes so the orthographic plan doesn't end
         up with chunks missing from materials that were clipped a
         moment ago. */
      if (_SectionCutManager) _SectionCutManager.refresh();
    };
  }

  function _initEngine() {
    var S = global.ConfiguratorStage;
    if (!S) {
      _dbg('engine init failed: configurator-ui.js asset not loaded');
      return false;
    }
    global.BaseEngine = S;
    _attachEngineSurface(S);

    var ok = S.init({
      rootId:   'cf-wrap-base',
      canvasId: 'cf-canvas-base',
      fov: 40, near: 0.1, far: 200,
      cameraPos: [14, 12, 14],
      target: [0, 0, 0],
      /* The old engine left OrbitControls' distance limits at their
         defaults (0 .. Infinity). Stage defaults to 1.5 .. 40, which a
         30 m run of fence would hit the moment fitCameraToFootprint()
         pushes the camera out -- update() would clamp it straight back
         and the framing would collapse. */
      minDistance: 0.5, maxDistance: 500,
      /* No maxPolarAngle: Stage's default (PI * 0.85) is what lets the
         user tilt below the horizon and look at the model from under the
         grid, the same as the container and the A-frame. The inline engine
         used to stop dead at PI * 0.49 (horizon) -- that was a fence
         peculiarity, not a decision worth carrying over. */
      /* Pan off: the fence is the start of a project and belongs on the
         axis. Framing is fitCameraToFootprint()'s job, and the map has
         its own pan pad. */
      enablePan: false,
      enableDamping: true, dampingFactor: 0.08,
      lights: true,
      log: function (m) { _dbg('[stage] ' + m); },
      onResize: function () {
        /* Stage's resize re-runs setView('2d') while in 2D, which
           overwrites the frustum. Put ours back, then re-pin the HTML
           handles to the world. */
        S._restoreOrtho();
        positionDragHandles();
      }
    });
    if (!ok) return false;

    _rebuildGrid(S);
    S._applyViewCamera();

    /* Stage's own loop renders and updates controls; the fence needs two
       more things on the same frame, and the asset has no hook for it, so
       we replace the loop with the same work plus ours. */
    S.renderer.setAnimationLoop(function () {
      if (S.controls && S.camera === S.persCam) S.controls.update();
      S.renderer.setClearColor(0x000000, 0);
      if (S.camera) S.renderer.render(S.scene, S.camera);
      /* Drive any external per-frame work that needs world-to-screen
         tracking (FloorplanLib's wall-drag handles ride on this). */
      if (global.FloorplanLib && typeof global.FloorplanLib._onFrame === 'function') {
        global.FloorplanLib._onFrame();
      }
      /* Keep gate handles + width labels stuck to the world as the user
         pans / zooms the orthographic map in 2D. Only runs in 2D since
         the handles are hidden in 3D anyway. */
      if (STATE.view === '2d' && typeof _GateDragManager !== 'undefined' &&
          _GateDragManager.handles && _GateDragManager.handles.length) {
        _GateDragManager.reproject();
      }
    });

    return true;
  }

  global.BaseRenderer = {
    _materials: null,

    /* Lazily generate a tileable gravel texture on a canvas -- small
       light/dark grey pebbles on a medium-grey base. Used as the map
       for the fenceGravel material below. Runs once (per first render
       that needs it) and is cached on the renderer instance. */
    _gravelTexture: null,
    _makeGravelTexture: function () {
      if (this._gravelTexture) return this._gravelTexture;
      var T = global.THREE;
      if (typeof document === 'undefined') return null;
      var size = 256;
      var canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      var ctx = canvas.getContext('2d');
      /* Base -- darker grey so gravel reads clearly against the lighter
         concrete plug above it. */
      ctx.fillStyle = '#5e5e5e';
      ctx.fillRect(0, 0, size, size);
      /* Pebbles -- random circular blobs of varying grey */
      var pebbleCount = 110;
      for (var i = 0; i < pebbleCount; i++) {
        var x = Math.random() * size;
        var y = Math.random() * size;
        var r = 5 + Math.random() * 14;     /* 5-19 px radius */
        var v = 60 + Math.floor(Math.random() * 80);   /* 60-140 grey (darker) */
        ctx.fillStyle = 'rgb(' + v + ',' + v + ',' + v + ')';
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        /* Subtle highlight rim on the top-left of each pebble */
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(x - r * 0.25, y - r * 0.25, r * 0.85, Math.PI, Math.PI * 1.5);
        ctx.stroke();
      }
      var tex = new T.CanvasTexture(canvas);
      tex.wrapS = T.RepeatWrapping;
      tex.wrapT = T.RepeatWrapping;
      tex.repeat.set(2, 2);
      this._gravelTexture = tex;
      return tex;
    },

    _ensureMaterials: function () {
      var T = global.THREE;
      if (this._materials) return this._materials;
      this._materials = {
        fill2D:      new T.MeshBasicMaterial({ color: 0xb8b8b8, transparent: true, opacity: 0.45, side: T.DoubleSide }),
        edge2D:      new T.LineBasicMaterial({ color: 0x222222 }),
        /* Fence variants -- cached so shaders compile only once and
           variant switching never lands in a state where the renderer
           hasn't yet uploaded a freshly-allocated material's program. */
        /* PBR, not Lambert. Stage renders with ACES filmic tone mapping and
           lifts the image with a RoomEnvironment PMREM map -- MeshLambert
           does not react to scene.environment at all, so a Lambert model
           under ACES just reads dark. Values follow the container:
           timber 0.78-0.82/0, steel + galv 0.45/0.85, concrete and
           aggregate 0.92-0.95/~0. The emissive fudge that used to fake
           fill light on brackets and screws is gone -- the env map does
           that job now, and emissive would only wash them out. */
        fenceWoodPost:        new T.MeshStandardMaterial({ color: 0x6a4a2a, roughness: 0.82, metalness: 0 }),
        fenceWoodPanel:       new T.MeshStandardMaterial({ color: 0xa07050, roughness: 0.80, metalness: 0 }),
        fenceWoodRail:        new T.MeshStandardMaterial({ color: 0x7a5530, roughness: 0.80, metalness: 0 }),
        fenceWoodPicket:      new T.MeshStandardMaterial({ color: 0xb88860, roughness: 0.78, metalness: 0 }),
        fenceWoodCap:         new T.MeshStandardMaterial({ color: 0xb88860, roughness: 0.78, metalness: 0 }),
        fenceBracket:         new T.MeshStandardMaterial({ color: 0xeef1f4, roughness: 0.45, metalness: 0.85 }),
        fenceScrew:           new T.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.45, metalness: 0.85 }),
        fenceConcrete:        new T.MeshStandardMaterial({ color: 0xdadada, roughness: 0.95, metalness: 0 }),
        fenceGravel:          new T.MeshStandardMaterial({ map: this._makeGravelTexture(), color: 0xffffff, roughness: 0.95, metalness: 0 }),
        fenceVinylPost:       new T.MeshStandardMaterial({ color: 0xd5d5d5, roughness: 0.55, metalness: 0 }),
        fenceVinylPanel:      new T.MeshStandardMaterial({ color: 0xeaeaea, roughness: 0.55, metalness: 0 }),
        fenceConcretePost:    new T.MeshStandardMaterial({ color: 0xa5a5a5, roughness: 0.92, metalness: 0 }),
        fenceConcreteDwarf:   new T.MeshStandardMaterial({ color: 0xb0a890, roughness: 0.92, metalness: 0 }),
        fenceConcretePanel:   new T.MeshStandardMaterial({ color: 0x8a7a55, roughness: 0.92, metalness: 0 }),
        fenceConcreteCap:     new T.MeshStandardMaterial({ color: 0x8a8a8a, roughness: 0.92, metalness: 0 }),
        fenceChainLinkPost:   new T.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.45, metalness: 0.85 }),
        fenceChainLinkMesh:   new T.LineBasicMaterial({ color: 0x9a9a9a }),
        fenceChainLinkBarbed: new T.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.45, metalness: 0.85 }),
        fenceSteelPost:       new T.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.45, metalness: 0.85 }),
        fenceSteelPanel:      new T.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.45, metalness: 0.85 }),
        fenceGabionStone:     new T.MeshStandardMaterial({ color: 0x9a8a6a, roughness: 0.92, metalness: 0 }),
        fenceGabionCage:      new T.MeshBasicMaterial({ color: 0x222222, wireframe: true }),
        fenceGabionPost:      new T.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.45, metalness: 0.85 }),
        fenceConcretePanelPost:  new T.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.92, metalness: 0 }),
        fenceConcretePanelSlab:  new T.MeshStandardMaterial({ color: 0xa0a0a0, roughness: 0.92, metalness: 0 })
      };
      return this._materials;
    },

    build: function () {
      var T = global.THREE;
      var eng = global.BaseEngine;
      if (!T || !eng || !eng.sceneGroup) return;
      var G = eng.sceneGroup;
      while (G.children.length) {
        var c = G.children[0];
        G.remove(c);
        if (c.geometry && c.geometry.dispose) c.geometry.dispose();
      }
      var poly = getFootprintPolygon();
      if (!poly.length) return;

      if (STATE.view === '2d') {
        this._build2D(poly);
      } else if (STATE.assemblyStep > 0) {
        /* Assembly mode -- isolated single bay (2 posts + one infill +
           footings) built around the origin, with per-step visibility
           and explode offsets applied afterwards. Reacts to spacing /
           height / style. Wood variant only for now. */
        this._build3D_DetailBay();
        _AssemblyManager.applyStep(STATE.assemblyStep);
      } else {
        this._build3D(poly);
        this._build3D_SlopeViz(poly);
      }

      /* After every rebuild the freshly-created materials need the
         clipping planes re-attached if we're currently in section
         cut mode (viz stage 2 + 3D view). */
      if (_SectionCutManager && STATE.vizStage === 2 && STATE.view === '3d') {
        _SectionCutManager.refresh();
      }
    },

    _build3D_SlopeViz: function (poly) {
      if (!STATE.slopeActive || STATE.slopeDeg <= 0) return;
      var T = global.THREE;
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      if (!poly || !poly.length) return;

      var xMin =  Infinity, xMax = -Infinity;
      var zMin =  Infinity, zMax = -Infinity;
      for (var i = 0; i < poly.length; i++) {
        var p = poly[i];
        if (p[0] < xMin) xMin = p[0];
        if (p[0] > xMax) xMax = p[0];
        if (p[1] < zMin) zMin = p[1];
        if (p[1] > zMax) zMax = p[1];
      }
      var bbL = xMax - xMin;
      var bbW = zMax - zMin;
      if (bbL < 0.1 || bbW < 0.1) return;

      var cx = (xMin + xMax) / 2;
      var cz = (zMin + zMax) / 2;
      var L = bbL * 2.5;
      var W = bbW * 2.0;
      var x0 = cx - L / 2, x1 = cx + L / 2;
      var z0 = cz - W / 2, z1 = cz + W / 2;

      /* A ground GRID (not a filled plane) that conforms to the slope.
         Every vertex is heighted with the SAME terrain formula the fence
         uses (_terrainY = -_padSlopeExtra at absolute world X/Z), so the
         grid lines lie exactly on grade under the model -- same direction,
         same height, no separate beige surface. */
      var step = STATE.isMetric ? 1.0 : 0.3048;
      var verts = [];
      function gy(x, z) { return -_padSlopeExtra(x, z); }

      /* Lines running along Z (constant X), stepping across X. */
      for (var x = x0; x <= x1 + 1e-6; x += step) {
        verts.push(x, gy(x, z0), z0,  x, gy(x, z1), z1);
      }
      /* Lines running along X (constant Z), stepping across Z. */
      for (var z = z0; z <= z1 + 1e-6; z += step) {
        verts.push(x0, gy(x0, z), z,  x1, gy(x1, z), z);
      }

      var geom = new T.BufferGeometry();
      geom.setAttribute('position', new T.Float32BufferAttribute(verts, 3));
      var mat = new T.LineBasicMaterial({ color: 0xd8d8d8, transparent: true, opacity: 0.9 });
      var gridLines = new T.LineSegments(geom, mat);
      gridLines.userData = { assetType: 'slope-terrain' };
      eng.sceneGroup.add(gridLines);
    },

    _addExcavationPit: function (cx, cz, sizeX, sizeZ, topY, depth) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var verts = [
        cx - sizeX/2, topY,         cz - sizeZ/2,
        cx + sizeX/2, topY,         cz - sizeZ/2,
        cx + sizeX/2, topY,         cz + sizeZ/2,
        cx - sizeX/2, topY,         cz + sizeZ/2,
        cx - sizeX/2, topY - depth, cz - sizeZ/2,
        cx + sizeX/2, topY - depth, cz - sizeZ/2,
        cx + sizeX/2, topY - depth, cz + sizeZ/2,
        cx - sizeX/2, topY - depth, cz + sizeZ/2
      ];
      var edges = [
        0,1, 1,2, 2,3, 3,0,
        4,5, 5,6, 6,7, 7,4,
        0,4, 1,5, 2,6, 3,7
      ];
      var lineGeom = new T.BufferGeometry();
      var positions = [];
      for (var e = 0; e < edges.length; e++) {
        var idx = edges[e] * 3;
        positions.push(verts[idx], verts[idx+1], verts[idx+2]);
      }
      lineGeom.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
      var lineMat = new T.LineBasicMaterial({ color: 0xD2A878, transparent: true, opacity: 0.7 });
      var box = new T.LineSegments(lineGeom, lineMat);
      box.userData = { assetType: 'excavation-box' };
      eng.sceneGroup.add(box);
    },

    /* Cylindrical excavation wireframe -- for fence post holes drilled
       by a post-hole auger. Same gold-line style as _addExcavationPit
       but renders a circular profile instead of a square one.
       Top and bottom rings (16 segments) plus a sparser set of vertical
       connectors so the cylinder reads clearly without visual clutter. */
    _addExcavationCylinder: function (cx, cz, radius, topY, depth) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var segments = 16;
      var bottomY = topY - depth;
      var positions = [];
      var i, a1, a2;
      /* Top ring */
      for (i = 0; i < segments; i++) {
        a1 = (i       / segments) * Math.PI * 2;
        a2 = ((i + 1) / segments) * Math.PI * 2;
        positions.push(
          cx + radius * Math.cos(a1), topY, cz + radius * Math.sin(a1),
          cx + radius * Math.cos(a2), topY, cz + radius * Math.sin(a2)
        );
      }
      /* Bottom ring */
      for (i = 0; i < segments; i++) {
        a1 = (i       / segments) * Math.PI * 2;
        a2 = ((i + 1) / segments) * Math.PI * 2;
        positions.push(
          cx + radius * Math.cos(a1), bottomY, cz + radius * Math.sin(a1),
          cx + radius * Math.cos(a2), bottomY, cz + radius * Math.sin(a2)
        );
      }
      /* Vertical connectors -- every 2nd segment so 8 lines total */
      for (i = 0; i < segments; i += 2) {
        a1 = (i / segments) * Math.PI * 2;
        positions.push(
          cx + radius * Math.cos(a1), topY,    cz + radius * Math.sin(a1),
          cx + radius * Math.cos(a1), bottomY, cz + radius * Math.sin(a1)
        );
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
      var mat = new T.LineBasicMaterial({ color: 0xD2A878, transparent: true, opacity: 0.7 });
      var line = new T.LineSegments(geo, mat);
      line.userData = { assetType: 'excavation-box' };
      eng.sceneGroup.add(line);
    },

    /* Instanced 3D gravel fill -- real pebbles (low-poly spheres ~3-4 cm)
       packed into the footing hole, used only in close-up views (detail
       stage + section cut) where the flat texture would read as wallpaper.
       Uses a single InstancedMesh so hundreds of pebbles cost one draw
       call. Pebbles are jittered in position, scale and grey tone for a
       natural look. cx/cz = hole centre, radius = hole radius, topY =
       grade Y, layerH = gravel layer thickness. */
    _gravelSphereGeo: null,
    _addGravelPebbles: function (cx, cz, radius, topY, layerH) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      if (!T || !eng || !eng.sceneGroup) return;
      if (!this._gravelSphereGeo) {
        /* Low-poly icosphere-ish: a detail-1 icosahedron is only 80
           tris and reads as a rounded stone once scaled small. */
        this._gravelSphereGeo = new T.IcosahedronGeometry(0.020, 1);
      }
      var baseR = 0.020;                       /* 2 cm base radius -> ~4 cm stones */
      /* Estimate how many pebbles fill the cylinder at ~60% packing. */
      var holeVol   = Math.PI * radius * radius * layerH;
      var pebbleVol = (4 / 3) * Math.PI * baseR * baseR * baseR;
      var count = Math.max(8, Math.min(600, Math.round(holeVol / pebbleVol * 0.55)));

      var mat = new T.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0, vertexColors: false });
      var inst = new T.InstancedMesh(this._gravelSphereGeo, mat, count);
      inst.userData = { assetType: 'fence-footing-gravel', boqCode: 'FEC00002', category: 'aggregate' };

      var dummy = new T.Object3D();
      var color = new T.Color();
      var topPack = topY - 0.005;              /* tuck just under grade */
      var botPack = topY - layerH;
      for (var i = 0; i < count; i++) {
        /* Random point in the cylinder (rejection-free: polar). */
        var ang = Math.random() * Math.PI * 2;
        var rr  = Math.sqrt(Math.random()) * (radius - baseR * 0.5);
        var px = cx + Math.cos(ang) * rr;
        var pz = cz + Math.sin(ang) * rr;
        var py = botPack + Math.random() * (topPack - botPack);
        var s  = 0.7 + Math.random() * 0.7;    /* 0.7-1.4x scale */
        dummy.position.set(px, py, pz);
        dummy.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
        dummy.scale.set(s, s, s);
        dummy.updateMatrix();
        inst.setMatrixAt(i, dummy.matrix);
        /* Grey tone variation 0.30-0.55 luminance (darker than the
           lighter concrete plug above). */
        var g = 0.30 + Math.random() * 0.25;
        color.setRGB(g, g, g);
        inst.setColorAt(i, color);
      }
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      eng.sceneGroup.add(inst);
    },

    /* Build an extruded "dog-ear" picket -- a flat board whose top two
       corners are chamfered at 45 deg, the most common US privacy-fence
       picket profile. width = X (board face), height = Y, thick = Z.
       The chamfer cut height equals half the board width (a 45 deg cut
       from the side edge to the top centre region). Cached per
       (w,h,t) signature so repeated identical pickets reuse geometry. */
    _dogEarCache: null,
    _makeDogEarGeometry: function (w, h, t) {
      var T = global.THREE;
      if (!this._dogEarCache) this._dogEarCache = {};
      var key = w.toFixed(3) + '_' + h.toFixed(3) + '_' + t.toFixed(3);
      if (this._dogEarCache[key]) return this._dogEarCache[key];

      /* Chamfer size -- 45 deg cut, clamped so it never exceeds a third of
         the board height for very short pickets. */
      var cut = Math.min(w * 0.45, h * 0.33);
      var hw = w / 2, hh = h / 2;
      /* 2D outline in the board's face plane (X = width, Y = height),
         centred on origin. Start bottom-left, go clockwise, chamfer the
         two top corners. */
      var shape = new T.Shape();
      shape.moveTo(-hw, -hh);
      shape.lineTo( hw, -hh);
      shape.lineTo( hw,  hh - cut);
      shape.lineTo( hw - cut, hh);
      shape.lineTo(-hw + cut, hh);
      shape.lineTo(-hw,  hh - cut);
      shape.closePath();

      var geo = new T.ExtrudeGeometry(shape, { depth: t, bevelEnabled: false });
      /* ExtrudeGeometry extrudes along +Z starting at z=0; recentre so
         the board straddles the origin in Z like a BoxGeometry would. */
      geo.translate(0, 0, -t / 2);
      this._dogEarCache[key] = geo;
      return geo;
    },

    _polyToShape: function (poly) {
      var T = global.THREE;
      var shape = new T.Shape();
      shape.moveTo(poly[0][0], poly[0][1]);
      for (var i = 1; i < poly.length; i++) shape.lineTo(poly[i][0], poly[i][1]);
      shape.closePath();
      return shape;
    },

    _build2D: function (poly) {
      var T = global.THREE;
      var mats = this._ensureMaterials();
      var eng = global.BaseEngine;

      /* For fence: no foundation slab fill -- just the polygon outline
         (the property boundary). The fence components (posts, dwarf
         wall, panels) are rendered ON the outline by per-variant
         helpers below. We DO still draw a faint fill so the user can
         see the enclosed area, but at very low opacity. */
      mats.fill2D.opacity = Math.min(STATE.opacity2D, 0.10);
      var shape = this._polyToShape(poly);
      var geom = new T.ShapeGeometry(shape);
      var mesh = new T.Mesh(geom, mats.fill2D);
      mesh.rotation.x = Math.PI / 2;
      mesh.position.y = 0.001;
      eng.sceneGroup.add(mesh);

      var pts = [];
      for (var i = 0; i < poly.length; i++) {
        pts.push(new T.Vector3(poly[i][0], 0.002, poly[i][1]));
      }
      pts.push(new T.Vector3(poly[0][0], 0.002, poly[0][1]));
      var lineGeom = new T.BufferGeometry().setFromPoints(pts);
      var line = new T.Line(lineGeom, mats.edge2D);
      eng.sceneGroup.add(line);

      /* Fence-specific overlays -- 7 variants, see VARIANT_DEFAULTS
         keys for ordering. Each variant draws posts at corners +
         intermediates along each edge at postSpacing, then a ribbon
         visualising the panel/dwarf-wall between posts in the variant's
         signature colour. */
      switch (STATE.variant) {
        case 1: this._addFenceWood2D(poly);          break;
        case 2: this._addFenceVinyl2D(poly);         break;
        case 3: this._addFenceConcrete2D(poly);      break;
        case 4: this._addFenceChainLink2D(poly);     break;
        case 5: this._addFenceSteel2D(poly);         break;
        case 6: this._addFenceGabion2D(poly);        break;
        case 7: this._addFenceConcretePanel2D(poly); break;
      }
    },

    /* ------------------------------------------------------------------
       Helpers shared by the three fence variants. Returns positions of
       all posts (corners + intermediates) plus, for each edge, the unit
       direction so a downstream renderer can lay panels along the edge.
       ------------------------------------------------------------------ */
    /* ------------------------------------------------------------------
       Enumerate the posts + edge segments around the polygon, taking
       any gaps in STATE.gates into account. Returns:
         {
           posts: [{ x, y, corner }],
           edges: [{ A, B, ux, uy, len, segments: [{ start, end }] }]
         }

       Splitting rules:
         - Each edge is sliced into one or more SEGMENTS by the gaps
           that sit on that edge. A segment runs from one gap end to
           the next gap start (or from the edge start to the first
           gap, etc).
         - If a gap's start/end falls within SNAP_TO_CORNER_M of a
           corner, it absorbs that corner -- the corner post is then
           dropped and the panel run ends at the corner geometrically.
         - Intermediate posts are placed inside each segment at the
           variant's postSpacing distribution (equal panels across
           the segment).
         - Corner posts are placed at polygon vertices UNLESS both
           adjacent edges have gaps reaching the corner (i.e. the
           corner is "inside" a continuous opening).
       ------------------------------------------------------------------ */
    _enumerateFencePosts: function (poly, spacing) {
      var n = poly.length;
      var SNAP = GAP_SNAP_TO_CORNER_M;
      var gates = (STATE.gates || []).slice();
      var edges = [];
      var coveredFromOut = new Array(n).fill(false);  /* gap reaches edge start  */
      var coveredFromIn  = new Array(n).fill(false);  /* gap reaches edge end    */

      /* First pass -- compute segments per edge using snap-to-corner. */
      for (var i = 0; i < n; i++) {
        var A = poly[i];
        var B = poly[(i + 1) % n];
        var dx = B[0] - A[0], dy = B[1] - A[1];
        var edgeLen = Math.sqrt(dx * dx + dy * dy);
        if (edgeLen < 0.01) {
          edges.push({ A: A, B: B, ux: 0, uy: 0, len: 0, segments: [] });
          continue;
        }
        var ux = dx / edgeLen, uy = dy / edgeLen;

        /* Gates on this edge, sorted by position along the edge. */
        var edgeGates = [];
        for (var g = 0; g < gates.length; g++) {
          if (gates[g].edgeIdx === i) edgeGates.push(gates[g]);
        }
        edgeGates.sort(function (a, b) { return a.tFrac - b.tFrac; });

        var cursor = 0;
        var segs = [];
        for (var gi = 0; gi < edgeGates.length; gi++) {
          var gate = edgeGates[gi];
          var gateStart = gate.tFrac * edgeLen - gate.width / 2;
          var gateEnd   = gate.tFrac * edgeLen + gate.width / 2;
          if (gateStart < SNAP)              { gateStart = 0;       coveredFromOut[i] = true; }
          if (gateEnd   > edgeLen - SNAP)    { gateEnd   = edgeLen; coveredFromIn[(i + 1) % n] = true; }
          gateStart = Math.max(0, gateStart);
          gateEnd   = Math.min(edgeLen, gateEnd);
          if (gateStart > cursor + 0.001) segs.push({ start: cursor, end: gateStart });
          cursor = Math.max(cursor, gateEnd);
        }
        if (cursor < edgeLen - 0.001) segs.push({ start: cursor, end: edgeLen });

        edges.push({ A: A, B: B, ux: ux, uy: uy, len: edgeLen, segments: segs });
      }

      /* Second pass -- collect posts. Intermediate posts inside each
         segment (excluding endpoints -- corner endpoints are added in
         the dedicated corner loop below; segment endpoints flush with
         the edge endpoints would be duplicates). */
      var posts = [];
      for (var e = 0; e < edges.length; e++) {
        var ed = edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var segLen = seg.end - seg.start;
          if (segLen < 0.01) continue;
          var slots = Math.max(1, Math.round(segLen / spacing));
          var step  = segLen / slots;
          /* Endpoints: include them only if NOT flush with the edge
             ends (those are corner posts, handled below). */
          for (var k = 0; k <= slots; k++) {
            var t = seg.start + step * k;
            var atEdgeStart = (Math.abs(t)         < 0.001);
            var atEdgeEnd   = (Math.abs(t - ed.len) < 0.001);
            if (atEdgeStart || atEdgeEnd) continue;
            posts.push({
              x: ed.A[0] + ed.ux * t,
              y: ed.A[1] + ed.uy * t,
              corner: false
            });
          }
        }
      }

      /* Corner posts -- placed at polygon vertices unless both
         adjacent edges have gaps reaching this corner. */
      for (var c = 0; c < n; c++) {
        var prevEdgeIdx = (c - 1 + n) % n;
        var covered = coveredFromIn[c] && coveredFromOut[c];
        if (covered) continue;
        posts.push({ x: poly[c][0], y: poly[c][1], corner: true });
      }

      return { posts: posts, edges: edges };
    },

    /* Draw a ribbon along each segment of the polygon (skipping gaps).
       'dashed' selects a dashed line material for visualising panels
       on top of a solid dwarf-wall ribbon. */
    _addFenceSegmentRibbons: function (edges, color, width, dashed) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      for (var e = 0; e < edges.length; e++) {
        var ed = edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var segLen = seg.end - seg.start;
          if (segLen < 0.01) continue;
          var x0 = ed.A[0] + ed.ux * seg.start;
          var y0 = ed.A[1] + ed.uy * seg.start;
          var x1 = ed.A[0] + ed.ux * seg.end;
          var y1 = ed.A[1] + ed.uy * seg.end;
          var pts = [
            new T.Vector3(x0, 0.004, y0),
            new T.Vector3(x1, 0.004, y1)
          ];
          var g = new T.BufferGeometry().setFromPoints(pts);
          var mat = dashed
            ? new T.LineDashedMaterial({ color: color, dashSize: 0.30, gapSize: 0.20, linewidth: width })
            : new T.LineBasicMaterial({ color: color, linewidth: width });
          var line = new T.Line(g, mat);
          if (dashed) line.computeLineDistances();
          eng.sceneGroup.add(line);
        }
      }
    },

    /* Draw a faint dashed marker across each gap so the user can see
       where the openings sit. The marker is the SAME line on the
       polygon outline but dashed in a contrasting colour. */
    _addFenceGapMarkers: function (edges) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      for (var e = 0; e < edges.length; e++) {
        var ed = edges[e];
        /* Build the "gap intervals" = the complement of segments on
           this edge. We need to know where panels stop and start. */
        var bounds = [];
        bounds.push(0);
        for (var s = 0; s < ed.segments.length; s++) {
          bounds.push(ed.segments[s].start);
          bounds.push(ed.segments[s].end);
        }
        bounds.push(ed.len);
        for (var k = 0; k + 1 < bounds.length; k += 2) {
          var gStart = bounds[k];
          var gEnd   = bounds[k + 1];
          if (gEnd - gStart < 0.05) continue;
          var x0 = ed.A[0] + ed.ux * gStart;
          var y0 = ed.A[1] + ed.uy * gStart;
          var x1 = ed.A[0] + ed.ux * gEnd;
          var y1 = ed.A[1] + ed.uy * gEnd;
          var pts = [
            new T.Vector3(x0, 0.005, y0),
            new T.Vector3(x1, 0.005, y1)
          ];
          var g = new T.BufferGeometry().setFromPoints(pts);
          var mat = new T.LineDashedMaterial({
            color: 0xe06030, dashSize: 0.20, gapSize: 0.10, linewidth: 2
          });
          var line = new T.Line(g, mat);
          line.computeLineDistances();
          eng.sceneGroup.add(line);
        }
      }
    },

    _addFencePostMarkers: function (posts, color, size, shape) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mat = new T.MeshBasicMaterial({ color: color });
      for (var i = 0; i < posts.length; i++) {
        var g;
        if (shape === 'circle') {
          g = new T.CircleGeometry(size / 2, 16);
        } else {
          g = new T.PlaneGeometry(size, size);
        }
        var m = new T.Mesh(g, mat);
        m.rotation.x = -Math.PI / 2;
        m.position.set(posts[i].x, 0.006, posts[i].y);
        eng.sceneGroup.add(m);
      }
    },

    _addFenceWood2D: function (poly) {
      var d = getVariantDefaults('wood', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.09;
      var info = this._enumerateFencePosts(poly, spacing);
      /* Warm timber-brown ribbon */
      this._addFenceSegmentRibbons(info.edges, 0xa07050, 3, false);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x5a3a1a, Math.max(postSide, 0.12), 'square');
    },

    _addFenceVinyl2D: function (poly) {
      var d = getVariantDefaults('vinyl', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.13;
      var info = this._enumerateFencePosts(poly, spacing);
      /* Off-white vinyl ribbon */
      this._addFenceSegmentRibbons(info.edges, 0xeaeaea, 3, false);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0xbcbcbc, postSide, 'square');
    },

    _addFenceConcrete2D: function (poly) {
      var d = getVariantDefaults('concrete_blocks', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.20;
      var info = this._enumerateFencePosts(poly, spacing);
      this._addFenceSegmentRibbons(info.edges, 0x6b6b6b, 3, false);
      this._addFenceSegmentRibbons(info.edges, 0xb5b5b5, 1, true);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x333333, postSide, 'square');
    },

    _addFenceChainLink2D: function (poly) {
      var d = getVariantDefaults('chain_link', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.06;
      var info = this._enumerateFencePosts(poly, spacing);
      /* Dashed silver -- represents the diamond mesh visually */
      this._addFenceSegmentRibbons(info.edges, 0x9a9a9a, 1, true);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x4a4a4a, Math.max(postSide, 0.08), 'circle');
    },

    _addFenceSteel2D: function (poly) {
      var d = getVariantDefaults('steel_panel', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.06;
      var info = this._enumerateFencePosts(poly, spacing);
      this._addFenceSegmentRibbons(info.edges, 0x4a4a4a, 2, false);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x111111, Math.max(postSide, 0.10), 'square');
    },

    _addFenceGabion2D: function (poly) {
      var d = getVariantDefaults('gabion', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.30;
      var info = this._enumerateFencePosts(poly, spacing);
      this._addFenceSegmentRibbons(info.edges, 0x8a7a5a, 4, false);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x2a2a2a, postSide / 2, 'circle');
    },

    _addFenceConcretePanel2D: function (poly) {
      var d = getVariantDefaults('concrete_panel', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM || 0.15;
      var info = this._enumerateFencePosts(poly, spacing);
      /* Concrete-grey ribbon -- solid, thicker than steel */
      this._addFenceSegmentRibbons(info.edges, 0x9a9a9a, 4, false);
      this._addFenceGapMarkers(info.edges);
      this._addFencePostMarkers(info.posts, 0x4a4a4a, postSide, 'square');
    },

    _build3D: function (poly) {
      switch (STATE.variant) {
        case 1: return this._build3D_FenceWood(poly);
        case 2: return this._build3D_FenceVinyl(poly);
        case 3: return this._build3D_FenceConcrete(poly);
        case 4: return this._build3D_FenceChainLink(poly);
        case 5: return this._build3D_FenceSteel(poly);
        case 6: return this._build3D_FenceGabion(poly);
        case 7: return this._build3D_FenceConcretePanel(poly);
      }
    },

    /* ------------------------------------------------------------------
       FENCE 3D -- three variants.

       Common scaffolding (gap-aware via _enumerateFencePosts):
         - each polygon edge is split into segments by any gaps; posts
           and panels are rendered ONLY inside segments, leaving the
           gap area empty
         - corner posts are dropped when both adjacent edges' gaps
           reach the corner (snap-to-corner absorption)
         - per-variant geometry: concrete (post + dwarf wall + panel),
           steel (post + panel), gabion (continuous cage box + inner
           steel post)
       All meshes carry userData.assetType / boqCode for section cut
       and AR export pipelines.
       ------------------------------------------------------------------ */

    _build3D_FenceConcrete: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('concrete_blocks', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM      || 0.20;
      var kickH    = d.kickHeightM    || 1.0;
      var panelH   = d.panelHeightM   || 1.0;
      var panelT   = d.panelThicknessM|| 0.05;

      var info = this._enumerateFencePosts(poly, spacing);

      var postTotalH = kickH + panelH + 0.05;
      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(postSide, postTotalH, postSide);
        var mPost = new T.Mesh(gPost, mats.fenceConcretePost);
        mPost.position.set(p.x, postTotalH / 2, p.y);
        mPost.userData = { assetType: 'fence-post', boqCode: 'FEC00005', category: 'concrete' };
        eng.sceneGroup.add(mPost);
        var gCap = new T.BoxGeometry(postSide + 0.04, 0.05, postSide + 0.04);
        var mCap = new T.Mesh(gCap, mats.fenceConcreteCap);
        mCap.position.set(p.x, postTotalH + 0.025, p.y);
        mCap.userData = { assetType: 'fence-post-cap', boqCode: 'FEC00007', category: 'concrete' };
        eng.sceneGroup.add(mCap);
      }

      /* Dwarf wall + panel per segment per edge -- gaps are skipped. */
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var slots = Math.max(1, Math.round((seg.end - seg.start) / spacing));
          var step  = (seg.end - seg.start) / slots;
          for (var k = 0; k < slots; k++) {
            var t0 = seg.start + step * k + postSide / 2;
            var t1 = seg.start + step * (k + 1) - postSide / 2;
            var panelLen = t1 - t0;
            if (panelLen <= 0.01) continue;
            var cx = ed.A[0] + ed.ux * (t0 + panelLen / 2);
            var cy = ed.A[1] + ed.uy * (t0 + panelLen / 2);
            var angle = Math.atan2(ed.uy, ed.ux);

            var gDwarf = new T.BoxGeometry(panelLen, kickH, postSide * 0.9);
            var mDwarf = new T.Mesh(gDwarf, mats.fenceConcreteDwarf);
            mDwarf.position.set(cx, kickH / 2, cy);
            mDwarf.rotation.y = -angle;
            mDwarf.userData = { assetType: 'fence-dwarf-wall', boqCode: 'FEC00004', category: 'concrete' };
            eng.sceneGroup.add(mDwarf);

            var gPanel = new T.BoxGeometry(panelLen, panelH, panelT);
            var mPanel = new T.Mesh(gPanel, mats.fenceConcretePanel);
            mPanel.position.set(cx, kickH + panelH / 2, cy);
            mPanel.rotation.y = -angle;
            mPanel.userData = { assetType: 'fence-panel', boqCode: 'FEC00017', category: 'metal' };
            eng.sceneGroup.add(mPanel);
          }
        }
      }
    },

    _build3D_FenceSteel: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('steel_panel', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM      || 0.06;
      var panelH   = d.panelHeightM   || 2.0;
      var panelT   = d.panelThicknessM|| 0.02;

      var info = this._enumerateFencePosts(poly, spacing);

      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(postSide, panelH, postSide);
        var mPost = new T.Mesh(gPost, mats.fenceSteelPost);
        mPost.position.set(p.x, panelH / 2, p.y);
        mPost.userData = { assetType: 'fence-post-steel', boqCode: 'FEC00015', category: 'metal' };
        eng.sceneGroup.add(mPost);
      }

      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var slots = Math.max(1, Math.round((seg.end - seg.start) / spacing));
          var step  = (seg.end - seg.start) / slots;
          for (var k = 0; k < slots; k++) {
            var t0 = seg.start + step * k + postSide / 2;
            var t1 = seg.start + step * (k + 1) - postSide / 2;
            var panelLen = t1 - t0;
            if (panelLen <= 0.01) continue;
            var cx = ed.A[0] + ed.ux * (t0 + panelLen / 2);
            var cy = ed.A[1] + ed.uy * (t0 + panelLen / 2);
            var angle = Math.atan2(ed.uy, ed.ux);
            var gPanel = new T.BoxGeometry(panelLen, panelH * 0.95, panelT);
            var mPanel = new T.Mesh(gPanel, mats.fenceSteelPanel);
            mPanel.position.set(cx, panelH / 2, cy);
            mPanel.rotation.y = -angle;
            mPanel.userData = { assetType: 'fence-panel-steel', boqCode: 'FEC00015', category: 'metal' };
            eng.sceneGroup.add(mPanel);
          }
        }
      }
    },

    _build3D_FenceGabion: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('gabion', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM      || 0.30;
      var panelH   = d.panelHeightM   || 2.0;

      var info = this._enumerateFencePosts(poly, spacing);

      /* Continuous cage per segment -- gaps are skipped */
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var segLen = seg.end - seg.start;
          if (segLen < 0.05) continue;
          var t0 = seg.start;
          var cx = ed.A[0] + ed.ux * (t0 + segLen / 2);
          var cy = ed.A[1] + ed.uy * (t0 + segLen / 2);
          var angle = Math.atan2(ed.uy, ed.ux);

          var gStone = new T.BoxGeometry(segLen, panelH, postSide);
          var mStone = new T.Mesh(gStone, mats.fenceGabionStone);
          mStone.position.set(cx, panelH / 2, cy);
          mStone.rotation.y = -angle;
          mStone.userData = { assetType: 'gabion-fill', boqCode: 'FEC00011', category: 'stone' };
          eng.sceneGroup.add(mStone);

          var gCage = new T.BoxGeometry(segLen, panelH, postSide);
          var mCage = new T.Mesh(gCage, mats.fenceGabionCage);
          mCage.position.set(cx, panelH / 2, cy);
          mCage.rotation.y = -angle;
          mCage.userData = { assetType: 'gabion-cage', boqCode: 'FEC00009', category: 'metal' };
          eng.sceneGroup.add(mCage);
        }
      }

      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(0.06, panelH * 1.05, 0.06);
        var mPost = new T.Mesh(gPost, mats.fenceGabionPost);
        mPost.position.set(p.x, (panelH * 1.05) / 2, p.y);
        mPost.userData = { assetType: 'gabion-post', boqCode: 'FEC00008', category: 'metal' };
        eng.sceneGroup.add(mPost);
      }
    },

    _build3D_FenceWood: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var self = this;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('wood', STATE.lvl) || {};
      var style = STATE.woodStyle || 'privacy';
      var spacing = getCurrentSpacing();

      /* Dimensions ---------------------------------------------------- */
      var postSide   = d.postSideM           || 0.089;
      var postExtra  = d.postExposedExtraM   || 0.080;
      var panelH     = getCurrentHeight();
      var railSide   = d.railSideM           || 0.038;
      var railH      = d.railHeightM         || 0.089;
      var pickT      = d.pickThicknessM      || 0.019;
      var pickBotClr = d.picketBottomClearM  || 0.060;
      var capH       = d.capHeightM          || 0.050;
      var bracketH   = d.bracketVisibleM     || 0.060;
      var excavD     = d.excavationDepthM    || 0.700;
      var excavS     = d.excavationSideM     || 0.300;
      var gravelL    = d.gravelLayerM        || 0.100;
      var concEmbed  = d.postEmbedM          || 0.600;

      var styleP     = _getWoodStyleParams(style);
      var pickW      = styleP.boardW;        /* plank width per style (4"/6") */
      var hasMid     = (panelH >= 1.829);

      var info = this._enumerateFencePosts(poly, spacing);

      /* Per-post terrain Y. Reference: uphill polygon edge sits at y=0,
         downhill points drop to y = -_padSlopeExtra(x, z). */
      function _terrainY(px, pz) { return -_padSlopeExtra(px, pz); }

      /* Adjustable post base bracket -- U-shape (galvanised steel), built
         from a horizontal base plate plus two vertical side wings that
         clamp the post between them. The threaded rod that anchors the
         bracket into the concrete sits below grade so we don't render it. */
      var bracketWallT  = 0.005;                                /* 5 mm steel */
      var bracketPlateW = postSide + 0.040;                     /* base plate side */
      var bracketWingW  = postSide;                             /* wing depth = post side */
      var bracketGap    = postSide + 0.004;                     /* post fits between wings */
      var gBracketBase = new T.BoxGeometry(bracketPlateW, bracketWallT, bracketPlateW);
      var gBracketWing = new T.BoxGeometry(bracketWallT, bracketH - bracketWallT, bracketWingW);

      /* Sub-grade footing cylinders -- concrete plug above a gravel
         drainage layer, both filling the auger hole. 16 radial
         segments to match the excavation wireframe ring. */
      var holeR      = excavS / 2;
      var gConcrete  = new T.CylinderGeometry(holeR, holeR, concEmbed, 16, 1, false);
      var gGravel    = new T.CylinderGeometry(holeR, holeR, gravelL,   16, 1, false);

      /* Post sits INSIDE the U-cavity of the bracket -- its base rests
         directly on top of the bracket's base plate, and the wings
         clamp the lower part of the post between them. Length
         (panelH + postExtra - bracketWallT) so its top sits postExtra
         above the panel top. */
      var postH       = panelH + postExtra - bracketWallT;
      var postExposed = postH;

      /* Shared geometries -- cached for this render pass so identical
         boxes don't re-allocate per post / per picket. */
      var gPost    = new T.BoxGeometry(postSide, postH, postSide);
      var gCap     = new T.BoxGeometry(postSide * 1.15, capH, postSide * 1.15);

      /* -- Posts + brackets + caps + excavation wireframes ---------- */
      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var ty = _terrainY(p.x, p.y);

        /* Bracket -- U-shape on grade. */
        var mBracketBase = new T.Mesh(gBracketBase, mats.fenceBracket);
        mBracketBase.position.set(p.x, ty + bracketWallT / 2, p.y);
        mBracketBase.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: i };
        eng.sceneGroup.add(mBracketBase);

        var wingY = ty + bracketWallT + (bracketH - bracketWallT) / 2;
        var wingX = bracketGap / 2 + bracketWallT / 2;
        var mWingL = new T.Mesh(gBracketWing, mats.fenceBracket);
        mWingL.position.set(p.x - wingX, wingY, p.y);
        mWingL.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: i };
        eng.sceneGroup.add(mWingL);
        var mWingR = new T.Mesh(gBracketWing, mats.fenceBracket);
        mWingR.position.set(p.x + wingX, wingY, p.y);
        mWingR.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: i };
        eng.sceneGroup.add(mWingR);

        /* Post -- sits INSIDE the bracket U-cavity, base resting on
           top of the steel base plate (= ty + bracketWallT). */
        var postBottomY = ty + bracketWallT;
        var mPost = new T.Mesh(gPost, mats.fenceWoodPost);
        mPost.position.set(p.x, postBottomY + postH / 2, p.y);
        mPost.userData = { assetType: 'fence-post-wood', boqCode: 'KVH10100', category: 'wood', index: i };
        eng.sceneGroup.add(mPost);

        /* Cap -- pyramid (here simplified to a slightly oversized box)
           sits on top of the post. */
        var mCap = new T.Mesh(gCap, mats.fenceWoodCap);
        mCap.position.set(p.x, postBottomY + postH + capH / 2, p.y);
        mCap.userData = { assetType: 'fence-cap-wood', boqCode: 'FEC00023', category: 'wood', index: i };
        eng.sceneGroup.add(mCap);

        /* Sub-grade footing -- gravel drainage layer at the bottom of
           the hole, concrete plug above it up to grade. The bracket
           base plate rests on top of the concrete. In section-cut
           stage (close-up) we pack real 3D pebbles; otherwise a flat
           textured cylinder keeps the full-fence build cheap. */
        if (STATE.vizStage === 2 && STATE.view === '3d') {
          this._addGravelPebbles(p.x, p.y, excavS / 2, ty - concEmbed, gravelL);
        } else {
          var mGravel = new T.Mesh(gGravel, mats.fenceGravel);
          mGravel.position.set(p.x, ty - excavD + gravelL / 2, p.y);
          mGravel.userData = { assetType: 'fence-footing-gravel', boqCode: 'FEC00002', category: 'aggregate', index: i };
          eng.sceneGroup.add(mGravel);
        }

        var mConcrete = new T.Mesh(gConcrete, mats.fenceConcrete);
        mConcrete.position.set(p.x, ty - concEmbed / 2, p.y);
        mConcrete.userData = { assetType: 'fence-footing-concrete', boqCode: 'FEC00001', category: 'concrete', index: i };
        eng.sceneGroup.add(mConcrete);

        /* Excavation wireframe -- cylindrical (12" post-hole auger drill).
           Diameter = excavS (default 0.30 m), same gold-line style as
           the rectangular pits used for foundation slabs. Renders
           behind the solid concrete/gravel meshes; visible only when
           those meshes are hidden by future toggles. */
        this._addExcavationCylinder(p.x, p.y, excavS / 2, ty, excavD);
      }

      /* -- Rails + pickets per bay ---------------------------------- */
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        var edgeAngle = Math.atan2(ed.uy, ed.ux);

        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var segLen = seg.end - seg.start;
          if (segLen < 0.01) continue;
          var slots = Math.max(1, Math.round(segLen / spacing));
          var step  = segLen / slots;

          for (var k = 0; k < slots; k++) {
            var tA = seg.start + step * k;
            var tB = seg.start + step * (k + 1);
            var tA_in = tA + postSide / 2;
            var tB_in = tB - postSide / 2;
            var bayLen = tB_in - tA_in;
            if (bayLen < 0.05) continue;

            /* World positions of the two flanking posts */
            var pAx = ed.A[0] + ed.ux * tA;
            var pAz = ed.A[1] + ed.uy * tA;
            var pBx = ed.A[0] + ed.ux * tB;
            var pBz = ed.A[1] + ed.uy * tB;
            var tyA = _terrainY(pAx, pAz);
            var tyB = _terrainY(pBx, pBz);

            /* Panel envelope -- top + bottom lines run between the two
               posts and are racked (skewed) along the slope. */
            var topY_A = tyA + panelH;
            var topY_B = tyB + panelH;
            var botY_A = tyA + pickBotClr;
            var botY_B = tyB + pickBotClr;

            /* World centre of the bay (between post inner faces) */
            var cAx = ed.A[0] + ed.ux * tA_in;
            var cAz = ed.A[1] + ed.uy * tA_in;
            var cBx = ed.A[0] + ed.ux * tB_in;
            var cBz = ed.A[1] + ed.uy * tB_in;
            var midX = (cAx + cBx) / 2;
            var midZ = (cAz + cBz) / 2;

            /* Build a single rail spanning between bay endpoints,
               skewed by the slope angle along the edge. We orient the
               box via an orthonormal basis (more robust than Euler
               composition, which mixes axes when the edge isn't world-
               aligned): +X along the rail's 3D direction (incl. slope),
               +Z perpendicular in the horizontal plane, +Y orthogonal
               to both -> near-vertical, leans slightly with the slope. */
            /* CONSTRUCTION: rails sit on the INNER face of the fence
               line, planks are nailed to the OUTER face of the rails.
               So the rail centreline is offset inward by railSide/2 and
               the planks sit further out by pickT/2 from the rail's
               outer face. perpendicular unit vector (-uy, ux) points to
               one side; we treat that as "inside" (toward the polygon
               interior for a CCW outline). Boards therefore present a
               clean continuous face to the street, rails hidden behind.

               Z offsets along the edge-perpendicular:
                 railZ  = +railSide/2          (rail centre, inner side)
                 plankZ = 0                    (plank centre, on the line) */
            var railInset  = railSide / 2;                 /* rail centre inward */
            var plankOuter = 0;                            /* planks on the fence line */

            function _placeRail(yA, yB, tag) {
              var rise = yB - yA;
              var run  = bayLen;
              var len3D = Math.sqrt(rise * rise + run * run);
              var gRail = new T.BoxGeometry(len3D, railH, railSide);
              var mRail = new T.Mesh(gRail, mats.fenceWoodRail);
              /* Shift the rail centre inward (perpendicular to the edge). */
              var rPerpX = -ed.uy * railInset;
              var rPerpZ =  ed.ux * railInset;
              mRail.position.set(midX + rPerpX, (yA + yB) / 2, midZ + rPerpZ);
              var dirX = new T.Vector3(cBx - cAx, rise, cBz - cAz);
              if (dirX.lengthSq() < 1e-8) { dirX.set(1, 0, 0); }
              dirX.normalize();
              var worldY = new T.Vector3(0, 1, 0);
              var dirZ = new T.Vector3().crossVectors(dirX, worldY);
              if (dirZ.lengthSq() < 1e-8) { dirZ.set(0, 0, 1); }
              dirZ.normalize();
              var dirY = new T.Vector3().crossVectors(dirZ, dirX).normalize();
              var basis = new T.Matrix4().makeBasis(dirX, dirY, dirZ);
              mRail.setRotationFromMatrix(basis);
              mRail.userData = { assetType: 'fence-rail-wood', boqCode: 'KVH10060', category: 'wood', tag: tag, bay: k, edge: e };
              eng.sceneGroup.add(mRail);
            }

            /* Rail placement depends on style:
                 - open_board: rails inset to 20 % / 80 % (decorative,
                   planks overhang above & below);
                 - spaced / shadow_box / picket: rails set a little in
                   from the edges (~10 % / 90 %) -- not flush like privacy,
                   not as deep as open;
                 - privacy (and default): rails flush at top/bottom edges.
               Tall panels get a third rail at mid-height. */
            var railInsetFrac =
                (style === 'open_board') ? 0.20 :
                (style === 'spaced' || style === 'shadow_box' || style === 'picket') ? 0.10 :
                null;   /* null -> flush edges */
            if (railInsetFrac !== null) {
              var spanA = topY_A - botY_A;
              var spanB = topY_B - botY_B;
              _placeRail(botY_A + spanA * railInsetFrac, botY_B + spanB * railInsetFrac, 'bottom');
              _placeRail(botY_A + spanA * (1 - railInsetFrac), botY_B + spanB * (1 - railInsetFrac), 'top');
              if (hasMid) {
                _placeRail((botY_A + topY_A) / 2, (botY_B + topY_B) / 2, 'mid');
              }
            } else {
              /* Top rail -- centre offset down by railH/2 from panel top */
              _placeRail(topY_A - railH / 2, topY_B - railH / 2, 'top');
              /* Bottom rail -- centre offset up by railH/2 from panel bottom */
              _placeRail(botY_A + railH / 2, botY_B + railH / 2, 'bottom');
              /* Mid rail for tall panels */
              if (hasMid) {
                var midYA = (topY_A + botY_A) / 2;
                var midYB = (topY_B + botY_B) / 2;
                _placeRail(midYA, midYB, 'mid');
              }
            }

            /* PICKETS -- vertical (plumb) regardless of slope.
               Top of each picket = linearly interpolated panel top at
               its position along the bay. Bottom = panel bottom at
               that position. Each successive picket therefore drops
               by pitch x tan(slope) -- exactly the racked look. */
            var pitch = styleP.pitch;
            var nPickets = Math.max(1, Math.floor((bayLen + 0.001) / pitch));
            var actualPitch = bayLen / nPickets;

            /* Dog-ear tops only on the open-board style; all other
               styles get square-topped planks (dog-ears looked wrong
               on shadow-box especially). */
            var useDogEar = (style === 'open_board');

            function _placePicket(tFromA_in, sideZ) {
              var px = cAx + (cBx - cAx) * (tFromA_in / bayLen);
              var pz = cAz + (cBz - cAz) * (tFromA_in / bayLen);
              /* sideZ = signed perpendicular offset from the fence line
                 (negative = outer/street side, positive = inner side). */
              var perpX = -ed.uy * sideZ;
              var perpZ =  ed.ux * sideZ;

              var frac    = tFromA_in / bayLen;
              var pTopY   = topY_A + (topY_B - topY_A) * frac;
              var pBotY   = botY_A + (botY_B - botY_A) * frac;
              var picketH = pTopY - pBotY;
              if (picketH <= 0.05) return;

              var gPick = useDogEar
                ? self._makeDogEarGeometry(pickW, picketH, pickT)
                : new T.BoxGeometry(pickW, picketH, pickT);
              var mPick = new T.Mesh(gPick, mats.fenceWoodPicket);
              mPick.position.set(px + perpX, (pTopY + pBotY) / 2, pz + perpZ);
              mPick.rotation.y = -edgeAngle;
              mPick.userData = { assetType: 'fence-picket-wood', boqCode: styleP.boardCode, category: 'wood', bay: k, edge: e };
              eng.sceneGroup.add(mPick);
            }

            /* Single-sided styles: planks on the OUTER face of the rail
               (street side). The rail centre is at +railInset (inner);
               its outer face is at the fence line (0); the plank sits a
               further pickT/2 outward -> sideZ = -pickT/2.
               Shadow-box: planks alternate on BOTH faces of the rail. */
            if (styleP.sides === 2) {
              /* Shadow box -- front (outer) row + back (inner) row,
                 offset by half a pitch so gaps are covered from the
                 other side. */
              var outerZ = -(pickT / 2);
              var innerZ = railSide + (pickT / 2);
              for (var pIdx = 0; pIdx < nPickets; pIdx++) {
                _placePicket(actualPitch * (pIdx + 0.5), outerZ);
              }
              var backOffset = actualPitch * styleP.offsetFrac;
              for (var pIdx2 = 0; pIdx2 < nPickets; pIdx2++) {
                var tBack = actualPitch * (pIdx2 + 0.5) + backOffset;
                if (tBack >= bayLen - pickW / 2) continue;
                _placePicket(tBack, innerZ);
              }
            } else {
              var plankZ = -(pickT / 2);     /* outer face of the rail */
              for (var pIdx3 = 0; pIdx3 < nPickets; pIdx3++) {
                _placePicket(actualPitch * (pIdx3 + 0.5), plankZ);
              }
            }
          }
        }
      }
    },

    /* --------------------------------------------------------------
       DETAIL BAY (vizStage 3). A single isolated bay built around the
       origin: two posts spaced by the current post spacing, one infill
       (rails + pickets in the current wood style) between them, plus
       the sub-grade footings (concrete + gravel + bracket) and an
       excavation cylinder per post. Independent of the property
       polygon -- it always sits on a short straight run along +X so the
       camera can frame it cleanly. Reacts to spacing / height / style.
       Wood variant only for now (the production-ready one); other
       variants fall back to the regular full build. */
    _build3D_DetailBay: function () {
      if (STATE.variant !== 1) {
        /* Non-wood variants aren't detailed yet -- show the normal
           full 3D so the stage button still does something sensible. */
        var poly = getFootprintPolygon();
        this._build3D(poly);
        this._build3D_SlopeViz(poly);
        return;
      }
      var T = global.THREE;
      var eng = global.BaseEngine;
      var self = this;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('wood', STATE.lvl) || {};
      var style = STATE.woodStyle || 'privacy';
      var spacing = getCurrentSpacing();

      /* Dimensions (mirror _build3D_FenceWood) */
      var postSide   = d.postSideM           || 0.089;
      var postExtra  = d.postExposedExtraM   || 0.080;
      var panelH     = getCurrentHeight();
      var railSide   = d.railSideM           || 0.038;
      var railH      = d.railHeightM         || 0.089;
      var pickT      = d.pickThicknessM      || 0.019;
      var pickBotClr = d.picketBottomClearM  || 0.060;
      var capH       = d.capHeightM          || 0.050;
      var bracketH   = d.bracketVisibleM     || 0.060;
      var excavD     = d.excavationDepthM    || 0.700;
      var excavS     = d.excavationSideM     || 0.300;
      var gravelL    = d.gravelLayerM        || 0.100;
      var concEmbed  = d.postEmbedM          || 0.600;

      var styleP     = _getWoodStyleParams(style);
      var pickW      = styleP.boardW;
      var hasMid     = (panelH >= 1.829);
      var bracketWallT = 0.005;
      var postH      = panelH + postExtra - bracketWallT;

      /* Two posts on the X axis, centred on the origin. */
      var halfSpan = spacing / 2;
      var postXs = [-halfSpan, halfSpan];

      /* Cache the bay extents so the camera framer can read them. */
      _DetailBayManager._extents = {
        width: spacing, height: panelH, postSide: postSide
      };

      /* Shared geometries */
      var gPost    = new T.BoxGeometry(postSide, postH, postSide);
      var gCap     = new T.BoxGeometry(postSide * 1.15, capH, postSide * 1.15);
      var bracketPlateW = postSide + 0.040;
      var bracketWingW  = postSide;
      var bracketGap    = postSide + 0.004;
      var gBracketBase = new T.BoxGeometry(bracketPlateW, bracketWallT, bracketPlateW);
      var gBracketWing = new T.BoxGeometry(bracketWallT, bracketH - bracketWallT, bracketWingW);
      var holeR     = excavS / 2;
      var gConcrete = new T.CylinderGeometry(holeR, holeR, concEmbed, 16, 1, false);

      /* Flat ground (no slope in the detail view). */
      var ty = 0;

      /* Schematic wood-screw geometry -- a thin shaft with a tapered
         (pointed) tip and a flat head, reading as a lag/wood screw
         rather than a bolt. Built once per length and cached. The screw
         points along +Y in local space (tip at -Y, head at +Y); callers
         rotate/translate it into place. Dark (near-black) zinc finish. */
      if (!self._screwGeoCache) self._screwGeoCache = {};
      function _screwGeo(lenM) {
        var key = lenM.toFixed(3);
        if (self._screwGeoCache[key]) return self._screwGeoCache[key];
        var T2 = global.THREE;
        var rShaft = 0.0035;
        var headH  = 0.006;
        var headR  = 0.0075;
        var tipH   = 0.012;
        var shaftH = Math.max(0.006, lenM - tipH);
        var parts = [];
        /* Shaft (body) -- centred so tip sits at -Y, head at +Y. */
        var gShaft = new T2.CylinderGeometry(rShaft, rShaft, shaftH, 8);
        gShaft.translate(0, -lenM / 2 + tipH + shaftH / 2, 0);
        parts.push(gShaft);
        /* Tapered tip -- cone narrowing downward at the bottom. */
        var gTip = new T2.ConeGeometry(rShaft, tipH, 8);
        gTip.rotateX(Math.PI);                     /* point downward (-Y) */
        gTip.translate(0, -lenM / 2 + tipH / 2, 0);
        parts.push(gTip);
        /* Flat head at the top (+Y). */
        var gHead = new T2.CylinderGeometry(headR, headR, headH, 10);
        gHead.translate(0, lenM / 2 - headH / 2, 0);
        parts.push(gHead);
        /* Merge into one buffer geometry if BufferGeometryUtils exists,
           else keep a group-friendly array. We just store the parts and
           build a Group in _placeScrew (merging util isn't guaranteed). */
        self._screwGeoCache[key] = parts;
        return parts;
      }

      /* Place a wood screw. headXYZ = where the HEAD sits; dir = unit
         vector the screw points/drives along (head -> tip). lenM =
         screw length (head-to-tip). partType tags it for assembly. */
      function _placeScrewDir(headX, headY, headZ, dir, lenM, partType, explodeOff) {
        var T2 = global.THREE;
        var grp = new T2.Group();
        var parts = _screwGeo(lenM);
        for (var pi2 = 0; pi2 < parts.length; pi2++) {
          grp.add(new T2.Mesh(parts[pi2], mats.fenceScrew));
        }
        /* Local screw axis is +Y (head) -> -Y (tip). Orient local +Y to
           point OPPOSITE the drive dir (so the tip faces into the
           structure along dir). */
        var up = new T2.Vector3(0, 1, 0);
        var d  = new T2.Vector3(dir[0], dir[1], dir[2]).normalize();
        var target = d.clone().multiplyScalar(-1);   /* +Y maps to -dir */
        var quat = new T2.Quaternion();
        if (target.dot(up) < -0.9999) {
          /* Antiparallel (drive straight up -> head down): flip 180 deg about
             X for a clean, predictable orientation. */
          quat.setFromAxisAngle(new T2.Vector3(1, 0, 0), Math.PI);
        } else {
          quat.setFromUnitVectors(up, target);
        }
        grp.quaternion.copy(quat);
        /* Position so the HEAD ends up at headXYZ. The head is at local
           +Y.(lenM/2); after rotation that's at headXYZ already if we
           place the group origin there, because we offset geometry about
           the centre -- so shift origin to the screw centre. */
        var centre = new T2.Vector3(headX, headY, headZ).add(d.clone().multiplyScalar(lenM / 2));
        grp.position.copy(centre);
        grp.userData = { assetType: partType, category: 'metal', driveDir: dir };
        if (explodeOff) grp.userData.explodeOffset = explodeOff;
        eng.sceneGroup.add(grp);
      }

      /* Back-compat wrapper for the simpler axis-based screws (rail
         brackets, planks, cap) that don't need precise length/seating. */
      function _placeScrew(x, y, z, axis, partType) {
        var dir, len = 0.045;
        if (axis === 'x')      dir = (x < 0) ? [1, 0, 0] : [-1, 0, 0];
        else if (axis === 'z') dir = (z < 0) ? [0, 0, 1] : [0, 0, -1];
        else                   dir = [0, -1, 0];
        /* Head sits at the given point, tip drives along dir. */
        _placeScrewDir(x, y, z, dir, len, partType);
      }

      var geoThk = 0.012;
      var gGeotext = new T.CylinderGeometry(holeR, holeR, geoThk, 16, 1, false);

      for (var pi = 0; pi < postXs.length; pi++) {
        var px = postXs[pi];

        /* Geotextile -- thin disc lining the very bottom of the hole. */
        var mGeo = new T.Mesh(gGeotext, mats.fenceGravel);
        mGeo.position.set(px, ty - excavD + geoThk / 2, 0);
        mGeo.userData = { assetType: 'fence-geotextile', boqCode: 'FEC00003', category: 'fabric', index: pi };
        eng.sceneGroup.add(mGeo);

        /* Footings -- concrete plug + 3D pebble gravel (close-up view). */
        var mConcrete = new T.Mesh(gConcrete, mats.fenceConcrete);
        mConcrete.position.set(px, ty - concEmbed / 2, 0);
        mConcrete.userData = { assetType: 'fence-footing-concrete', boqCode: 'FEC00001', category: 'concrete', index: pi };
        eng.sceneGroup.add(mConcrete);

        self._addGravelPebbles(px, 0, holeR, ty - concEmbed, gravelL);

        self._addExcavationCylinder(px, 0, holeR, ty, excavD);

        /* Bracket U-shape */
        var mBracketBase = new T.Mesh(gBracketBase, mats.fenceBracket);
        mBracketBase.position.set(px, ty + bracketWallT / 2, 0);
        mBracketBase.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: pi };
        eng.sceneGroup.add(mBracketBase);

        var wingY = ty + bracketWallT + (bracketH - bracketWallT) / 2;
        var wingX = bracketGap / 2 + bracketWallT / 2;
        var mWingL = new T.Mesh(gBracketWing, mats.fenceBracket);
        mWingL.position.set(px - wingX, wingY, 0);
        mWingL.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: pi };
        eng.sceneGroup.add(mWingL);
        var mWingR = new T.Mesh(gBracketWing, mats.fenceBracket);
        mWingR.position.set(px + wingX, wingY, 0);
        mWingR.userData = { assetType: 'fence-bracket', boqCode: 'FFP01001', category: 'metal', index: pi };
        eng.sceneGroup.add(mWingR);

        /* Post + cap */
        var postBottomY = ty + bracketWallT;
        var mPost = new T.Mesh(gPost, mats.fenceWoodPost);
        mPost.position.set(px, postBottomY + postH / 2, 0);
        mPost.userData = { assetType: 'fence-post-wood', boqCode: 'KVH10100', category: 'wood', index: pi };
        eng.sceneGroup.add(mPost);

        var mCap = new T.Mesh(gCap, mats.fenceWoodCap);
        mCap.position.set(px, postBottomY + postH + capH / 2, 0);
        mCap.userData = { assetType: 'fence-cap-wood', boqCode: 'FEC00023', category: 'wood', index: pi };
        eng.sceneGroup.add(mCap);

        /* Lag screws (6x80) -- bracket wings -> post. Two per side, the
           two sides facing OPPOSITE directions (left side drives into
           the post toward +X, right side toward -X). The head sits flush
           on the OUTER face of each wing; the 80 mm shaft drives inward
           through the wing into the post. */
        var lagLen = 0.080;                          /* 6x80 -> 80 mm */
        var lagY1 = ty + bracketWallT + (bracketH - bracketWallT) * 0.35;
        var lagY2 = ty + bracketWallT + (bracketH - bracketWallT) * 0.75;
        var wingOuterL = px - wingX - bracketWallT / 2;   /* outer face, left wing */
        var wingOuterR = px + wingX + bracketWallT / 2;   /* outer face, right wing */
        /* Left wing: head outside (-X), drives toward +X (into post).
           Explodes OUT to the left (-X) + up so the pair separates from
           the right pair in the exploded view. */
        var lagExL = [-0.16, 0.26, 0];
        var lagExR = [ 0.16, 0.26, 0];
        _placeScrewDir(wingOuterL, lagY1, 0, [ 1, 0, 0], lagLen, 'fence-screw-lag', lagExL);
        _placeScrewDir(wingOuterL, lagY2, 0, [ 1, 0, 0], lagLen, 'fence-screw-lag', lagExL);
        /* Right wing: head outside (+X), drives toward -X (into post). */
        _placeScrewDir(wingOuterR, lagY1, 0, [-1, 0, 0], lagLen, 'fence-screw-lag', lagExR);
        _placeScrewDir(wingOuterR, lagY2, 0, [-1, 0, 0], lagLen, 'fence-screw-lag', lagExR);

        /* Cap screws -- 2 driven straight DOWN through the cap into the
           post top. Heads stand proud on the cap's top face so they read
           clearly; spaced apart in Z. */
        var capTopY = postBottomY + postH + capH + 0.004;   /* head proud of cap top */
        _placeScrewDir(px, capTopY, -0.025, [0, -1, 0], 0.050, 'fence-screw-cap');
        _placeScrewDir(px, capTopY,  0.025, [0, -1, 0], 0.050, 'fence-screw-cap');
      }

      /* -- Infill between the two posts ---------------------------- */
      var innerL = -halfSpan + postSide / 2;
      var innerR =  halfSpan - postSide / 2;
      var bayLen = innerR - innerL;
      if (bayLen < 0.05) return;
      var midX = 0;
      var topY = ty + panelH;
      var botY = ty + pickBotClr;

      function _placeRailX(yC, tag) {
        var gRail = new T.BoxGeometry(bayLen, railH, railSide);
        var mRail = new T.Mesh(gRail, mats.fenceWoodRail);
        /* Rail centre offset to the inner (back) side. */
        mRail.position.set(midX, yC, railSide / 2);
        mRail.userData = { assetType: 'fence-rail-wood', boqCode: 'KVH10060', category: 'wood', tag: tag };
        eng.sceneGroup.add(mRail);

        /* Rail brackets -- a simplified U-profile (like a real fence rail
           bracket): a BACK PLATE that sits flat against the post, and
           two SIDE WINGS that wrap the rail's two faces. The rail runs
           along X; the bracket's back plate is in the Y-Z plane (facing
           the post along +/-X) and the wings run inward along X on either
           Z face of the rail. */
        var brT     = 0.004;                 /* sheet-metal thickness */
        var brH     = railH + 0.014;         /* plate a touch taller than rail */
        var brW     = railSide + 0.012;      /* plate spans rail depth + clearance */
        var wingDep = railSide * 0.9;        /* how far the wings reach along the rail */
        var railCZ  = railSide / 2;          /* rail centre in Z (inner side) */

        var gBackPlate = new T.BoxGeometry(brT, brH, brW);
        var gWing      = new T.BoxGeometry(wingDep, brH, brT);

        /* ends[].dir = +1 means the bracket's back plate faces toward
           the post that is at the LEFT (-X) end -> plate on -X side,
           wings reach inward (+X). -1 mirrors for the right end. */
        var ends = [
          { x: innerL, sign:  1 },   /* left post: plate faces -X, wings go +X */
          { x: innerR, sign: -1 }    /* right post: plate faces +X, wings go -X */
        ];
        for (var be = 0; be < ends.length; be++) {
          var ex = ends[be].x;
          var sgn = ends[be].sign;
          /* Explode INWARD into the bay (+X for the left bracket, -X for
             the right) so both rows pull toward the centre, never out
             past the posts. Plus a little lift. */
          var brEx = [sgn * 0.20, 0.14, 0.10];
          /* Back plate flush against the post face. */
          var plateX = ex - sgn * (brT / 2);
          var mPlate = new T.Mesh(gBackPlate, mats.fenceBracket);
          mPlate.position.set(plateX, yC, railCZ);
          mPlate.userData = { assetType: 'fence-rail-bracket', boqCode: 'FEC00024', category: 'metal', tag: tag, explodeOffset: brEx };
          eng.sceneGroup.add(mPlate);

          /* Two wings hugging the rail's +/-Z faces, reaching inward. */
          var wingX = ex + sgn * (wingDep / 2);
          var zFront = railCZ - (railSide / 2 + brT / 2);
          var zBack  = railCZ + (railSide / 2 + brT / 2);
          var mWingF = new T.Mesh(gWing, mats.fenceBracket);
          mWingF.position.set(wingX, yC, zFront);
          mWingF.userData = { assetType: 'fence-rail-bracket', boqCode: 'FEC00024', category: 'metal', tag: tag, explodeOffset: brEx };
          eng.sceneGroup.add(mWingF);
          var mWingB = new T.Mesh(gWing, mats.fenceBracket);
          mWingB.position.set(wingX, yC, zBack);
          mWingB.userData = { assetType: 'fence-rail-bracket', boqCode: 'FEC00024', category: 'metal', tag: tag, explodeOffset: brEx };
          eng.sceneGroup.add(mWingB);

          /* -- 7 screws per bracket (matches the real kit / BoQ) -----
             Group A -- "bracket -> post" (4 screws through the back
             plate). Installed BEFORE the rail. Heads on the inner
             (rail-side) plate face, driving toward the post (-sgn).
             Arranged 2x2 over the plate. */
          var plateInnerX = ex + sgn * 0.004;      /* head a touch proud of plate */
          var zCol = railSide * 0.28;              /* half the column spacing in Z */
          var yRow = brH * 0.26;                   /* half the row spacing in Y */
          /* Post screws explode FURTHER toward the bay centre than the
             bracket itself, so they read as pulled out toward the post. */
          var postScrewEx = [sgn * 0.38, 0.14, 0.08];
          var postScrewPts = [
            [yC + yRow, railCZ - zCol], [yC + yRow, railCZ + zCol],
            [yC - yRow, railCZ - zCol], [yC - yRow, railCZ + zCol]
          ];
          for (var ps = 0; ps < postScrewPts.length; ps++) {
            _placeScrewDir(plateInnerX, postScrewPts[ps][0], postScrewPts[ps][1],
              [-sgn, 0, 0], 0.050, 'fence-screw-railbrkt-post', postScrewEx);
          }

          /* Group B -- "rail -> bracket" (3 screws). Installed AFTER the
             rail: 2 side screws through the wings into the rail (each
             at a different height), + 1 bottom screw driving up into the
             rail underside. Heads stand proud of the wing / rail so they
             read clearly (not buried in the metal). Each explodes along
             its OWN direction: side screws ease out to their own Z face,
             the bottom screw drops straight down. */
          var sideHi = yC + brH * 0.22;
          var sideLo = yC - brH * 0.22;
          var headClr = 0.004;                        /* head stands this far proud */
          var sideExFront = [sgn * 0.20, 0.14, -0.18]; /* front wing -> -Z (its own side) */
          var sideExBack  = [sgn * 0.20, 0.14,  0.18]; /* back wing  -> +Z (its own side) */
          var bottomEx    = [sgn * 0.20, -0.24, 0.10]; /* bottom screw -> straight down */
          /* Front wing (-Z face): head OUTSIDE the front wing, drives +Z
             inward. Back wing (+Z face): head OUTSIDE the back wing,
             drives -Z inward. */
          _placeScrewDir(wingX, sideHi, zFront - brT / 2 - headClr, [0, 0,  1], 0.035, 'fence-screw-railbrkt-rail', sideExFront);
          _placeScrewDir(wingX, sideLo, zBack  + brT / 2 + headClr, [0, 0, -1], 0.035, 'fence-screw-railbrkt-rail', sideExBack);
          /* Bottom screw -- head BELOW the rail, drives up (+Y) into the
             rail underside. */
          _placeScrewDir(wingX, yC - brH / 2 - headClr, railCZ, [0, 1, 0], 0.035, 'fence-screw-railbrkt-rail', bottomEx);
        }
      }

      /* Collect the actual rail Y centres so plank screws land exactly
         on each plankxrail crossing (the rail positions differ by style
         -- open-board 20 %/80 %, spaced/shadow/picket slightly inset
         10 %/90 %, others flush top/bottom, plus an optional mid rail). */
      var railYs = [];
      function _railAt(yC, tag) { railYs.push(yC); _placeRailX(yC, tag); }

      var railInsetFrac =
          (style === 'open_board') ? 0.20 :
          (style === 'spaced' || style === 'shadow_box' || style === 'picket') ? 0.10 :
          null;   /* null -> flush edges */
      if (railInsetFrac !== null) {
        var span = topY - botY;
        _railAt(botY + span * railInsetFrac, 'bottom');
        _railAt(botY + span * (1 - railInsetFrac), 'top');
        if (hasMid) _railAt((botY + topY) / 2, 'mid');
      } else {
        _railAt(topY - railH / 2, 'top');
        _railAt(botY + railH / 2, 'bottom');
        if (hasMid) _railAt((topY + botY) / 2, 'mid');
      }

      var pitch = styleP.pitch;
      var nPickets = Math.max(1, Math.floor((bayLen + 0.001) / pitch));
      var actualPitch = bayLen / nPickets;
      var picketH = topY - botY;
      var useDogEar = (style === 'open_board');

      function _placePicketX(xCenter, zOff) {
        if (picketH <= 0.05) return;
        var gPick = useDogEar
          ? self._makeDogEarGeometry(pickW, picketH, pickT)
          : new T.BoxGeometry(pickW, picketH, pickT);
        var mPick = new T.Mesh(gPick, mats.fenceWoodPicket);
        mPick.position.set(xCenter, (topY + botY) / 2, zOff);
        /* Explode OUTWARD along the side the plank physically sits on
           (street-side planks at -Z fan to -Z, inner shadow-box planks
           at +Z fan to +Z). Posts stay put, planks pull off their rail
           face. A small lift keeps it readable. */
        var outward = (zOff < 0) ? -1 : 1;
        var plankEx  = [0, 0.10, outward * 0.45];
        var screwEx  = [0, 0.10, outward * 0.72];   /* screws fan a bit further out */
        mPick.userData = { assetType: 'fence-picket-wood', boqCode: styleP.boardCode, category: 'wood', explodeOffset: plankEx };
        eng.sceneGroup.add(mPick);

        /* Plank screws -- one at EVERY plankxrail crossing, on the centre
           line of both the plank (xCenter) and the rail (railYs[r]). The
           screw drives from the plank's OUTER face inward (toward the
           rail) so the head stands proud on the visible street face of
           the plank -- not buried. Explodes further out on the plank's
           own side. */
        var headZ   = zOff + outward * (pickT / 2);   /* plank outer face */
        var driveZ  = -outward;                        /* drive inward toward rail */
        for (var r = 0; r < railYs.length; r++) {
          _placeScrewDir(xCenter, railYs[r], headZ, [0, 0, driveZ], 0.045, 'fence-screw-plank', screwEx);
        }
      }

      if (styleP.sides === 2) {
        var outerZ = -(pickT / 2);
        var innerZ = railSide + (pickT / 2);
        for (var k = 0; k < nPickets; k++) {
          _placePicketX(innerL + actualPitch * (k + 0.5), outerZ);
        }
        var backOffset = actualPitch * styleP.offsetFrac;
        for (var k2 = 0; k2 < nPickets; k2++) {
          var xb = innerL + actualPitch * (k2 + 0.5) + backOffset;
          if (xb >= innerR - pickW / 2) continue;
          _placePicketX(xb, innerZ);
        }
      } else {
        var plankZ = -(pickT / 2);
        for (var k3 = 0; k3 < nPickets; k3++) {
          _placePicketX(innerL + actualPitch * (k3 + 0.5), plankZ);
        }
      }
    },

    _build3D_FenceVinyl: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('vinyl', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM      || 0.13;
      var panelH   = d.panelHeightM   || 1.8;
      var panelT   = d.panelThicknessM|| 0.03;

      var info = this._enumerateFencePosts(poly, spacing);

      var postH = panelH + 0.10;
      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(postSide, postH, postSide);
        var mPost = new T.Mesh(gPost, mats.fenceVinylPost);
        mPost.position.set(p.x, postH / 2, p.y);
        mPost.userData = { assetType: 'fence-post-vinyl', boqCode: 'FEC00016', category: 'vinyl' };
        eng.sceneGroup.add(mPost);
      }
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var slots = Math.max(1, Math.round((seg.end - seg.start) / spacing));
          var step  = (seg.end - seg.start) / slots;
          for (var k = 0; k < slots; k++) {
            var t0 = seg.start + step * k + postSide / 2;
            var t1 = seg.start + step * (k + 1) - postSide / 2;
            var panelLen = t1 - t0;
            if (panelLen <= 0.01) continue;
            var cx = ed.A[0] + ed.ux * (t0 + panelLen / 2);
            var cy = ed.A[1] + ed.uy * (t0 + panelLen / 2);
            var angle = Math.atan2(ed.uy, ed.ux);
            var gPanel = new T.BoxGeometry(panelLen, panelH, panelT);
            var mPanel = new T.Mesh(gPanel, mats.fenceVinylPanel);
            mPanel.position.set(cx, panelH / 2 + 0.10, cy);
            mPanel.rotation.y = -angle;
            mPanel.userData = { assetType: 'fence-panel-vinyl', boqCode: 'FEC00015', category: 'vinyl' };
            eng.sceneGroup.add(mPanel);
          }
        }
      }
    },

    _build3D_FenceChainLink: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('chain_link', STATE.lvl) || {};
      var spacing  = getCurrentSpacing();
      var postSide = d.postSideM    || 0.05;
      var panelH   = d.panelHeightM || 1.7;
      var barbedH  = d.barbedWireH  || 0.25;
      /* Posts stand ~30 cm above the mesh fabric so the top rail
         (here represented by the barbed strand) has something to clip
         onto -- visually matches the reference photo. */
      var postH    = panelH + barbedH + 0.05;

      var info = this._enumerateFencePosts(poly, spacing);

      /* Slim line posts (round-ish -- we still use a thin square box). */
      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(postSide, postH, postSide);
        var mPost = new T.Mesh(gPost, mats.fenceChainLinkPost);
        mPost.position.set(p.x, postH / 2, p.y);
        mPost.userData = { assetType: 'fence-post-chain', boqCode: 'FEC00016', category: 'metal' };
        eng.sceneGroup.add(mPost);
      }

      /* Mesh fabric + barbed strand per segment per edge -- gaps are
         skipped. Per slot (post-to-post panel) we render:
           - a wireframe plane oriented PARALLEL to the edge -- this is
             the chain-link fabric (diamond pattern from PlaneGeometry
             subdivisions). Bug fix: previous code rotated this 90 deg
             perpendicular to the edge, so panels stood across the
             fence line instead of along it.
           - a thin horizontal cylinder above the fabric, representing
             a single strand of barbed wire (taut between posts). */
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        var angle = Math.atan2(ed.uy, ed.ux);
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var slots = Math.max(1, Math.round((seg.end - seg.start) / spacing));
          var step  = (seg.end - seg.start) / slots;
          for (var k = 0; k < slots; k++) {
            var t0 = seg.start + step * k + postSide / 2;
            var t1 = seg.start + step * (k + 1) - postSide / 2;
            var panelLen = t1 - t0;
            if (panelLen <= 0.01) continue;
            var cx = ed.A[0] + ed.ux * (t0 + panelLen / 2);
            var cy = ed.A[1] + ed.uy * (t0 + panelLen / 2);

            /* CHAIN-LINK FABRIC -- diamond pattern via LineSegments.
               We build two sets of parallel 45 deg diagonals:
                 - "/" diagonals (slope +1)
                 - "\" diagonals (slope -1)
               Their intersection gives the classic chain-link look
               with diamonds standing on their points (<>).
               Local coords: x  in  [-halfL, +halfL], y  in  [0, panelH].
               The mesh is added at (cx, 0, cy) so y=0 sits on the
               ground -- no half-height offset needed. */
            var diaPitch = 0.12;
            var positions = [];
            var halfL = panelLen / 2;
            /* Convert diagonal direction into a per-line "intercept"
               and emit clipped segments. For "/" with slope +1, line
               equation is y = x - c. For "\" with slope -1: y = c - x.
               Range of c covers all diagonals that touch the panel
               rectangle, stepped by diaPitch * sqrt2 (Manhattan spacing). */
            var diaStep = diaPitch * Math.SQRT2;
            /* "/" diagonals -- slope +1, intercept c, line: y = x - c.
               Touches panel when c  in  [-halfL - panelH, +halfL]. */
            var cMin = -halfL - panelH;
            var cMax =  halfL;
            for (var c = cMin; c <= cMax + 1e-6; c += diaStep) {
              /* Intersect with the four panel edges to get clipped
                 endpoints. Line y = x - c. */
              var x1, y1, x2, y2;
              /* Enters panel at the LEFT edge (x = -halfL) if y is
                 in range there, otherwise at the BOTTOM (y = 0). */
              var yAtLeft  = -halfL - c;
              if (yAtLeft >= 0 && yAtLeft <= panelH) {
                x1 = -halfL; y1 = yAtLeft;
              } else {
                /* Enter via bottom edge: y = 0 => x = c. */
                x1 = c; y1 = 0;
              }
              /* Exits at the RIGHT edge (x = +halfL) if y is in range,
                 otherwise at the TOP (y = panelH). */
              var yAtRight = halfL - c;
              if (yAtRight >= 0 && yAtRight <= panelH) {
                x2 = halfL; y2 = yAtRight;
              } else {
                /* Exit via top edge: y = panelH => x = c + panelH. */
                x2 = c + panelH; y2 = panelH;
              }
              /* Skip degenerate or out-of-bounds segments. */
              if (x2 - x1 < 0.001 && y2 - y1 < 0.001) continue;
              if (x1 < -halfL - 0.001 || x2 > halfL + 0.001) continue;
              if (y1 < -0.001 || y2 > panelH + 0.001) continue;
              positions.push(x1, y1, 0, x2, y2, 0);
            }
            /* "\" diagonals -- slope -1, intercept c, line: y = c - x.
               Touches panel when c  in  [-halfL, +halfL + panelH]. */
            cMin = -halfL;
            cMax =  halfL + panelH;
            for (var c2 = cMin; c2 <= cMax + 1e-6; c2 += diaStep) {
              var bx1, by1, bx2, by2;
              /* Enter via LEFT edge (x = -halfL): y = c2 + halfL. */
              var yAtL = c2 + halfL;
              if (yAtL >= 0 && yAtL <= panelH) {
                bx1 = -halfL; by1 = yAtL;
              } else {
                /* Enter via TOP edge: y = panelH => x = c2 - panelH. */
                bx1 = c2 - panelH; by1 = panelH;
              }
              /* Exit via RIGHT edge (x = +halfL): y = c2 - halfL. */
              var yAtR = c2 - halfL;
              if (yAtR >= 0 && yAtR <= panelH) {
                bx2 = halfL; by2 = yAtR;
              } else {
                /* Exit via BOTTOM edge: y = 0 => x = c2. */
                bx2 = c2; by2 = 0;
              }
              if (bx2 - bx1 < 0.001 && Math.abs(by2 - by1) < 0.001) continue;
              if (bx1 < -halfL - 0.001 || bx2 > halfL + 0.001) continue;
              if (by1 < -0.001 || by1 > panelH + 0.001) continue;
              if (by2 < -0.001 || by2 > panelH + 0.001) continue;
              positions.push(bx1, by1, 0, bx2, by2, 0);
            }
            var gMesh = new T.BufferGeometry();
            gMesh.setAttribute('position',
              new T.Float32BufferAttribute(new Float32Array(positions), 3));
            var mMesh = new T.LineSegments(gMesh, mats.fenceChainLinkMesh);
            /* y=0 of the geometry sits on the ground because we
               placed vertices in y  in  [0, panelH]. */
            mMesh.position.set(cx, 0, cy);
            mMesh.rotation.y = -angle;
            mMesh.userData = { assetType: 'fence-mesh-chain', boqCode: 'FEC00014', category: 'metal' };
            eng.sceneGroup.add(mMesh);

            /* BARBED WIRE -- single strand of small cylinder running
               post-to-post, sitting just above the mesh fabric. The
               cylinder's natural axis is +Y, so rotate it 90 deg about
               Z to lay it horizontally, then around Y to align with
               the edge. */
            var gBarbed = new T.CylinderGeometry(0.012, 0.012, panelLen, 6);
            var mBarbed = new T.Mesh(gBarbed, mats.fenceChainLinkBarbed);
            mBarbed.position.set(cx, panelH + barbedH * 0.6, cy);
            mBarbed.rotation.z = Math.PI / 2;
            mBarbed.rotation.y = -angle;
            mBarbed.userData = { assetType: 'fence-barbed-wire', boqCode: 'FEC00014', category: 'metal' };
            eng.sceneGroup.add(mBarbed);
          }
        }
      }
    },

    _build3D_FenceConcretePanel: function (poly) {
      var T = global.THREE;
      var eng = global.BaseEngine;
      var mats = this._ensureMaterials();
      var d = getVariantDefaults('concrete_panel', STATE.lvl) || {};
      var spacing = getCurrentSpacing();
      var postSide = d.postSideM      || 0.15;
      var panelH   = d.panelHeightM   || 2.0;
      var panelT   = d.panelThicknessM|| 0.05;

      var info = this._enumerateFencePosts(poly, spacing);

      /* H-section concrete posts */
      var postH = panelH + 0.05;
      for (var i = 0; i < info.posts.length; i++) {
        var p = info.posts[i];
        var gPost = new T.BoxGeometry(postSide, postH, postSide);
        var mPost = new T.Mesh(gPost, mats.fenceConcretePanelPost);
        mPost.position.set(p.x, postH / 2, p.y);
        mPost.userData = { assetType: 'fence-post-cp', boqCode: 'FEC00005', category: 'concrete' };
        eng.sceneGroup.add(mPost);
      }
      /* Stacked pre-cast panels between H-posts. Each panel board
         visualised as a single thick slab between posts. */
      for (var e = 0; e < info.edges.length; e++) {
        var ed = info.edges[e];
        for (var s = 0; s < ed.segments.length; s++) {
          var seg = ed.segments[s];
          var slots = Math.max(1, Math.round((seg.end - seg.start) / spacing));
          var step  = (seg.end - seg.start) / slots;
          for (var k = 0; k < slots; k++) {
            var t0 = seg.start + step * k + postSide / 2;
            var t1 = seg.start + step * (k + 1) - postSide / 2;
            var panelLen = t1 - t0;
            if (panelLen <= 0.01) continue;
            var cx = ed.A[0] + ed.ux * (t0 + panelLen / 2);
            var cy = ed.A[1] + ed.uy * (t0 + panelLen / 2);
            var angle = Math.atan2(ed.uy, ed.ux);
            var gPanel = new T.BoxGeometry(panelLen, panelH, panelT);
            var mPanel = new T.Mesh(gPanel, mats.fenceConcretePanelSlab);
            mPanel.position.set(cx, panelH / 2, cy);
            mPanel.rotation.y = -angle;
            mPanel.userData = { assetType: 'fence-panel-cp', boqCode: 'FEC00004', category: 'concrete' };
            eng.sceneGroup.add(mPanel);
          }
        }
      }
    },
  };

  function _insetVertex(prev, curr, next, inset) {
    var d1x = curr[0] - prev[0], d1z = curr[1] - prev[1];
    var d2x = next[0] - curr[0], d2z = next[1] - curr[1];
    var l1 = Math.sqrt(d1x*d1x + d1z*d1z) || 1;
    var l2 = Math.sqrt(d2x*d2x + d2z*d2z) || 1;
    var n1x = -d1z / l1, n1z = d1x / l1;
    var n2x = -d2z / l2, n2z = d2x / l2;
    var bx = (n1x + n2x), bz = (n1z + n2z);
    var bl = Math.sqrt(bx*bx + bz*bz) || 1;
    return [curr[0] - bx / bl * inset, curr[1] - bz / bl * inset];
  }

  function boot() {
    if (global.__BASE_BOOTED__) return;
    global.__BASE_BOOTED__ = true;
    var attempts = 0;
    function tryBoot() {
      if (!global.THREE || !(global.THREE.OrbitControls || global.OrbitControls)) {
        if (++attempts > 100) {
          _dbg('boot aborted: THREE / OrbitControls never loaded after 5s');
          global.__BASE_BOOTED__ = false;
          return;
        }
        setTimeout(tryBoot, 50);
        return;
      }
      if (!_initEngine()) return;
      _readShopifyVariantFromURL();
      _ProjectStore.init();
      _restoreFromProjectStore();
      wireSidebar();
      rebuildDragHandles();
      selectVariant(STATE.variant, true);
      var wrap = document.getElementById('cf-wrap-base');
      if (wrap) wrap.classList.add(STATE.view === '2d' ? 'cf-view-2d' : 'cf-view-3d');
      if (global.BaseEngine && global.BaseEngine._gridHelper) {
        _applyGridVisibility(global.BaseEngine);
      }
      refreshUI();
      if (global.BaseEngine && global.BaseEngine.fitCameraToFootprint) {
        global.BaseEngine.fitCameraToFootprint();
      }
      _initMap();
      _applyPendingMapRestore();
      setTimeout(_updateMapLockButton, 300);
      _AssetDrawer.init();
      _scheduleOnboardModal();
      _dbg('boot complete, version=' + VERSION);
    }
    tryBoot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (document.getElementById('cf-wrap-base')) boot();
    });
  } else {
    if (document.getElementById('cf-wrap-base')) boot();
  }

  /* PDF PAID IN TOKENS (fence-v10-credits, Marek: PDF za 10 tokenu, kredit = LEVA
     CREDITS). The shared follow-us sheet (configurator-pdf.liquid,
     pdf-pay-v1) says the price, asks for sign-in or a pack, and takes the
     tokens; whoever bought this product before keeps a free PDF (paid:true).
     The key is the bill of quantities and the variant, so the same design
     downloads again for free for 30 days. */
  var _pdfPaidNow = false;
  function _pdfKey() {
    try {
      var rows = computeBoQ() || [];
      return 'fence|' + STATE.variant + '|' + JSON.stringify(rows.map(function (r) { return [r[0], r[1]]; }));
    } catch (e) { return ''; }
  }
  function _openPdfSocialModal() {
    var BOOT = global.BASE_BOOT || {};
    var X = global.ConfiguratorPdfExporter;
    if (X && X.socialGate) {
      X.socialGate(BOOT.hasOwnership ? { paid: true } : { key: _pdfKey() }).then(function () {
        _pdfPaidNow = true;
        var run = exportPdf();
        _pdfPaidNow = false;
        run.catch(function (e) { console.error('[exportPdf]', e); });
      });
      return;
    }
    if (!BOOT.hasOwnership) {
      var T = BOOT.i18n || {};
      var productUrl = '/products/' + (BOOT.productHandle || 'foundations');
      var msg = T.pdfPaywallMsg || 'To download the PDF documentation, please purchase this product first.';
      var ok = window.confirm(msg + '\n\n' + (T.pdfPaywallCta || 'Go to product page?'));
      if (ok) window.location.href = productUrl;
      return;
    }
    var modal = document.getElementById('cf-pdf-modal');
    if (!modal) {
      exportPdf().catch(function (e) { console.error('[exportPdf]', e); });
      return;
    }
    modal.hidden = false;

    function close() {
      modal.hidden = true;
      modal.removeEventListener('click', onClick);
    }
    function onClick(e) {
      var closeEl = e.target.closest('[data-cf-pdf-close]');
      if (closeEl) {
        e.preventDefault();
        close();
        return;
      }
      var goEl = e.target.closest('[data-cf-pdf-go]');
      if (goEl) {
        e.preventDefault();
        var url = goEl.getAttribute('href');
        close();
        if (url) {
          try {
            var w = window.open(url, '_blank', 'noopener,noreferrer');
            if (w) w.opener = null;
          } catch (_) {
            window.open(url, '_blank');
          }
        }
        setTimeout(function () {
          exportPdf().catch(function (err) { console.error('[exportPdf]', err); });
        }, 60);
      }
    }
    modal.addEventListener('click', onClick);
  }

  /* ---------------------------------------------------------------
     Shared budget onboarding modal
     Goal: nudge a brand-new visitor to consider other people's
     real-world budgets without ever overwriting their own work.

     Trigger contract:
       * 15 seconds after page load
       * only on a configurator product page (we already are)
       * at most once every 30 days, per product, stored in
         localStorage keyed by productId
       * skipped if the user already has any budget activity for
         this product (any saved row, any open share drawer click,
         any price edit) -- checked via a permissive interaction
         flag the budget module sets when the user touches data

     On "Show me": close, open the existing share-budget drawer
     ($-icon target). On "Maybe later" / backdrop / X: close +
     write the timestamp so we don't bother them again for 30 days.
     --------------------------------------------------------------- */
  var ONBOARD_THROTTLE_DAYS = 30;
  var ONBOARD_DELAY_MS = 15000;

  function _onboardStorageKey() {
    var BOOT = global.BASE_BOOT || {};
    var pid = BOOT.productId || BOOT.productHandle || 'default';
    return 'cf-onboard-shared-budget-' + pid;
  }

  function _onboardShouldShow() {
    try {
      var raw = localStorage.getItem(_onboardStorageKey());
      if (!raw) return true;
      var ts = parseInt(raw, 10);
      if (!ts || isNaN(ts)) return true;
      var ageMs = Date.now() - ts;
      var maxAge = ONBOARD_THROTTLE_DAYS * 24 * 60 * 60 * 1000;
      return ageMs > maxAge;
    } catch (_) { return true; }
  }

  function _onboardMarkSeen() {
    try { localStorage.setItem(_onboardStorageKey(), String(Date.now())); }
    catch (_) {}
  }

  function _onboardUserHasOwnBudget() {
    /* Conservative interaction detection: if the user already
       opened the budget drawer (= touched the $ icon) or any
       price/qty input on this product, treat them as "engaged"
       and don't pop the onboarding modal at them. We sniff a few
       global flags set by the budget module + any existing rows
       under the well-known budget storage. */
    try {
      if (global.__cfBudgetUserTouched) return true;
      if (global.SharedBudgetUserEngaged) return true;
      var BOOT = global.BASE_BOOT || {};
      var pid = BOOT.productId;
      if (pid) {
        if (localStorage.getItem('cf-budget-touched-' + pid)) return true;
      }
    } catch (_) {}
    return false;
  }

  function _openOnboardModal() {
    var modal = document.getElementById('cf-onboard-modal');
    if (!modal) return;
    modal.hidden = false;

    /* Lock page scroll while the modal is open (prevents scrolling the page
       behind it). Remember previous inline values to restore exactly. */
    var de = document.documentElement, bd = document.body;
    var prevHtmlOv = de ? de.style.overflow : '';
    var prevBodyOv = bd ? bd.style.overflow : '';
    var prevBodyTA = bd ? bd.style.touchAction : '';
    if (de) de.style.overflow = 'hidden';
    if (bd) { bd.style.overflow = 'hidden'; bd.style.touchAction = 'none'; }

    function close() {
      modal.hidden = true;
      modal.removeEventListener('click', onClick);
      if (de) de.style.overflow = prevHtmlOv;
      if (bd) { bd.style.overflow = prevBodyOv; bd.style.touchAction = prevBodyTA; }
      _onboardMarkSeen();
    }
    function onClick(e) {
      if (e.target.closest('[data-cf-onboard-close]')) {
        e.preventDefault();
        close();
        return;
      }
      if (e.target.closest('[data-cf-onboard-go]')) {
        e.preventDefault();
        close();
        /* Tiny delay so the close animation can settle before the
           drawer slides in -- feels less jarring than an instant
           swap. */
        setTimeout(function () {
          try { openBudgetDrawer(); }
          catch (err) { console.warn('[onboard] open budget drawer failed', err); }
        }, 120);
      }
    }
    modal.addEventListener('click', onClick);
  }

  function _scheduleOnboardModal() {
    /* Only on configurator pages -- this script lives in the base
       configurator already, so being here is the gate. We also
       skip if document is hidden so the timer effectively starts
       when the user actually views the page (avoids burning the
       30-day throttle on a background tab). */
    if (!_onboardShouldShow()) return;
    if (_onboardUserHasOwnBudget()) return;

    var elapsed = 0;
    var tick = 1000;
    var timer = setInterval(function () {
      if (document.visibilityState === 'visible') elapsed += tick;
      if (elapsed >= ONBOARD_DELAY_MS) {
        clearInterval(timer);
        /* Re-check guards at fire time -- user may have opened the
           drawer in the meantime. */
        if (_onboardShouldShow() && !_onboardUserHasOwnBudget()) {
          _openOnboardModal();
        }
      }
    }, tick);
  }

  /* ---------------------------------------------------------------
     AR export pipeline
     Click -> export current Three.js scene as GLB -> upload to R2
     via the Cloudflare Worker -> render QR code pointing at the
     viewer page. Phone scans QR -> opens <model-viewer> page ->
     "View in AR" places the model in the user's space.

     Flow:
       1. POST {worker}/upload-url     -> { uploadUrl, viewerUrl }
       2. GLTFExporter.parse(scene)    -> ArrayBuffer (GLB binary)
       3. PUT {uploadUrl} body=glb     -> R2 receives the file
       4. render QR(viewerUrl) in modal

     The full export typically runs in 1-3 s -- slow enough to
     warrant a "Preparing scene..." status under the QR placeholder
     so the user doesn't think it's frozen.
     --------------------------------------------------------------- */
  function _setArStatus(msg) {
    var el = document.getElementById('cf-ar-modal-status');
    if (el) el.textContent = msg || '';
  }

  function _setArQR(dataUrl) {
    var el = document.getElementById('cf-ar-modal-qr');
    if (!el) return;
    el.innerHTML = '';
    if (!dataUrl) return;
    var img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'AR QR code';
    img.style.width = '200px';
    img.style.height = '200px';
    img.style.borderRadius = '8px';
    img.style.background = '#fff';
    img.style.padding = '8px';
    el.appendChild(img);
  }

  function _setArLink(viewerUrl) {
    var el = document.getElementById('cf-ar-modal-link');
    if (!el) return;
    if (viewerUrl) {
      el.style.display = 'block';
      el.innerHTML = '<a href="' + viewerUrl + '" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;">' + viewerUrl + '</a>';
    } else {
      el.style.display = 'none';
      el.innerHTML = '';
    }
  }

  function _arExportScene() {
    /* Builds a clean GLB from the current Three.js scene. We
       export ONLY the configurator's sceneGroup -- not the grid,
       map, slope visualisation overlays, or section-cut helper
       geometry -- because the AR viewer should show the actual
       foundation, nothing else.

       Returns a Promise<ArrayBuffer> that resolves to the GLB
       bytes ready for upload. */
    return new Promise(function (resolve, reject) {
      var T = global.THREE;
      var Exporter = global.GLTFExporter;
      var eng = global.BaseEngine;
      if (!T || !Exporter || !eng || !eng.sceneGroup) {
        reject(new Error('THREE / GLTFExporter / scene not ready'));
        return;
      }
      /* Build an export-only Group containing clones of the meshes
         we actually want in AR. Each mesh gets its OWN geometry
         clone -- Mesh.clone() is shallow and keeps the geometry
         reference, so without a deep clone the applyMatrix4 below
         would mutate the shared buffer and all-but-one mesh would
         end up at the same world position. This is the bug that
         made the AR scene show a single pier instead of the full
         grid of N piers (they all shared one geometry instance,
         all overwritten by the last transform). */
      var exportRoot = new T.Group();
      eng.sceneGroup.traverse(function (node) {
        if (!node.isMesh || !node.geometry || !node.material) return;
        var t = node.userData && node.userData.assetType;
        /* Skip anything that is purely a visualisation aid. */
        if (t === 'section-cut-aux') return;
        if (t === 'slope-terrain') return;
        if (t === 'excavation-box') return;  /* hole-in-ground viz, no real material */
        if (!node.visible) return;
        node.updateMatrixWorld(true);
        /* Deep clone: fresh geometry per mesh, then bake the world
           transform into THIS geometry only. Material can stay
           shared because we never mutate it here. */
        var geom = node.geometry.clone();
        geom.applyMatrix4(node.matrixWorld);
        var cloned = new T.Mesh(geom, node.material);
        cloned.name = node.name || (t || 'mesh') + '_' + exportRoot.children.length;
        cloned.userData = Object.assign({}, node.userData || {});
        exportRoot.add(cloned);
      });

      if (exportRoot.children.length === 0) {
        reject(new Error('No exportable meshes in scene'));
        return;
      }

      var exporter = new Exporter();
      exporter.parse(
        exportRoot,
        function (glb) {
          /* GLTFExporter returns either ArrayBuffer (binary=true)
             or a JSON object. We requested binary so we always
             expect ArrayBuffer. */
          if (glb instanceof ArrayBuffer) {
            resolve(glb);
          } else {
            reject(new Error('Expected GLB ArrayBuffer, got JSON'));
          }
        },
        function (err) { reject(err); },
        { binary: true }
      );
    });
  }

  async function _arUpload(glbBuffer) {
    var BOOT = global.BASE_BOOT || {};
    var T = BOOT.i18n || {};
    var workerUrl = BOOT.arWorkerUrl || (T.arWorkerUrl);
    if (!workerUrl) throw new Error('Worker URL missing');
    _dbg('[ar] worker URL = ' + workerUrl);

    /* Step 1: ask the worker for an upload URL + the matching
       viewer URL. Use GET to avoid a CORS preflight roundtrip
       (POST with application/json body triggers an OPTIONS check
       which makes "Failed to fetch" much more likely to surface
       on flaky connections). The worker generates a fresh UUID
       per request so two simultaneous exports don't collide. */
    _dbg('[ar] GET ' + workerUrl + '/upload-url');
    var r1;
    try {
      r1 = await fetch(workerUrl + '/upload-url');
    } catch (netErr) {
      _dbg('[ar] network error on /upload-url: ' + netErr);
      throw new Error('Network error: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _dbg('[ar] /upload-url response status=' + r1.status + ' ok=' + r1.ok);
    if (!r1.ok) throw new Error('upload-url failed: HTTP ' + r1.status);
    var info = await r1.json();
    _dbg('[ar] /upload-url payload: ' + JSON.stringify(info));
    if (!info || !info.uploadUrl) throw new Error('upload-url response invalid');

    /* Step 2: PUT the GLB straight to the worker's upload endpoint.
       The worker stores it in R2 under {uuid}.glb.

       PUT with a binary body + non-simple content-type triggers a
       CORS preflight OPTIONS request -- the worker handles it, but
       any tunnel / proxy in between can mess it up. Catch the
       network-level TypeError separately from HTTP errors so we
       can log the actual cause. */
    _dbg('[ar] PUT ' + info.uploadUrl + ' (' + (glbBuffer.byteLength / 1024).toFixed(1) + ' KB)');
    var r2;
    try {
      r2 = await fetch(info.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'model/gltf-binary' },
        body: glbBuffer
      });
    } catch (netErr) {
      _dbg('[ar] network error on PUT: ' + netErr);
      throw new Error('Network error on PUT: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _dbg('[ar] PUT response status=' + r2.status + ' ok=' + r2.ok);
    if (!r2.ok) {
      var errBody = '';
      try { errBody = await r2.text(); } catch (_) {}
      _dbg('[ar] PUT error body: ' + errBody);
      throw new Error('PUT failed: HTTP ' + r2.status + ' ' + errBody);
    }

    return info;  /* { uuid, uploadUrl, publicUrl, viewerUrl } */
  }

  function _arGenerateQR(url) {
    /* Use the same QRCode library the PDF exporter already loaded
       via <script defer>. If it isn't ready yet, fall back to a
       remote QR service so the modal still works on first paint
       before the CDN script lands. */
    return new Promise(function (resolve) {
      function tryLib() {
        if (global.QRCode && global.QRCode.toDataURL) {
          global.QRCode.toDataURL(url, { width: 400, margin: 1, errorCorrectionLevel: 'M' }, function (err, dataUrl) {
            if (err || !dataUrl) {
              resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
            } else {
              resolve(dataUrl);
            }
          });
          return true;
        }
        return false;
      }
      if (tryLib()) return;
      /* Poll for ~2s, then fall back. */
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (tryLib()) { clearInterval(iv); return; }
        if (attempts > 20) {
          clearInterval(iv);
          resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
        }
      }, 100);
    });
  }

  async function _openArModal() {
    var modal = document.getElementById('cf-ar-modal');
    if (!modal) return;
    var BOOT = global.BASE_BOOT || {};
    var T = BOOT.i18n || {};

    /* Reset modal to "preparing" state and show it immediately so
       the user sees feedback before the export runs. */
    _setArQR(null);
    _setArLink(null);
    _setArStatus(T.arPreparing || 'Preparing scene\u2026');
    modal.hidden = false;

    function close() {
      modal.hidden = true;
      modal.removeEventListener('click', onClick);
    }
    function onClick(e) {
      if (e.target.closest('[data-cf-ar-close]')) {
        e.preventDefault();
        close();
      }
    }
    modal.addEventListener('click', onClick);

    /* Make sure we're in 3D -- the AR scene is a 3D object. If the
       user clicks AR from the 2D plan view we briefly force a
       build of the 3D version into memory without changing the
       UI view (just so the meshes exist to be exported). */
    var needBuild = false;
    if (STATE.view !== '3d') {
      needBuild = true;
      try {
        if (global.BaseRenderer && global.BaseRenderer._build3D) {
          var poly = getFootprintPolygon();
          if (poly && poly.length >= 3) global.BaseRenderer._build3D(poly);
        }
      } catch (e) { _dbg('[ar] 3D pre-build failed', e); }
    }

    try {
      var glb = await _arExportScene();
      _dbg('[ar] GLB size = ' + (glb.byteLength / 1024).toFixed(1) + ' KB');
      _setArStatus(T.arUploading || 'Uploading to cloud\u2026');

      var info = await _arUpload(glb);
      _dbg('[ar] uploaded uuid=' + info.uuid + ' viewer=' + info.viewerUrl);

      var qrDataUrl = await _arGenerateQR(info.viewerUrl);
      _setArQR(qrDataUrl);
      _setArLink(info.viewerUrl);
      _setArStatus(T.arReady || 'Ready \u2014 scan with your phone');
    } catch (e) {
      console.error('[ar] export failed', e);
      _setArStatus((T.arError || 'AR export failed: ') + (e && e.message ? e.message : e));
    }

    /* If we needed a 3D pre-build for export but the user is back
       in 2D, restore the 2D build so the canvas doesn't show
       stray geometry after the modal closes. */
    if (needBuild && global.BaseRenderer && global.BaseRenderer.build) {
      try { global.BaseRenderer.build(); } catch (_) {}
    }
  }

  /* Build the fence cut list -- one entry per TIMBER part type, each with
     a single representative piece carrying its dimensions + quantity, so
     the PDF draws one outline per part with "N x length" and a width dim.
     Wood variant only; everything follows the live configuration (height,
     spacing, style, mid-rail). Returns [] for non-wood variants. */
  function _buildWoodCutList() {
    if (STATE.variant !== 1) return [];
    var T0 = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
    var bridge = global.ConfiguratorBudgetBridge || null;
    /* Metric->imperial dimension tokens per timber code, mirroring the
       assembly tooltip logic. When the BoQ name has the metric size baked
       in and units are imperial, swap it for the imperial size so the cut
       list never shows mm dims on an imperial document. */
    /* Inches as the plain abbreviation "in." on BOTH dimensions -- pure
       ASCII, no glyph the PDF base font can choke on. */
    var DIM = {
      KVH10100: { metric: '100 \u00d7 100', imperial: '4 in. x 4 in.' },
      KVH10060: { metric: '60 \u00d7 100',  imperial: '2 in. x 4 in.' },
      KVR16019: { metric: '160 \u00d7 19',  imperial: '6 in. x 3/4 in.' },
      KVR10019: { metric: '100 \u00d7 19',  imperial: '4 in. x 3/4 in.' }
    };
    /* Match either x or x in the source metric token (bridge names use
       one or the other); _swapDimToken already treats x/x as equivalent. */
    function boqName(code, fb) {
      var name = fb;
      if (bridge && bridge.getBoqItem) {
        var it = bridge.getBoqItem(code);
        if (it && it.name) name = String(it.name);
      }
      var dm = DIM[code];
      if (!STATE.isMetric && dm && typeof _swapDimToken === 'function') {
        name = _swapDimToken(name, dm.metric, dm.imperial);
      }
      return name;
    }

    var g = (typeof _fenceGeometry === 'function') ? _fenceGeometry() : null;
    if (!g) return [];
    var d = g.params || (getVariantDefaults('wood', STATE.lvl) || {});
    var spacing     = g.spacing     || 2.440;
    var postCount   = g.postCount   || 0;
    var panelMeters = g.panelMeters || 0;
    if (postCount <= 0 || panelMeters <= 0) return [];

    var bays        = Math.max(0, Math.round(panelMeters / spacing));
    var panelH      = getCurrentHeight();
    var postExtra   = d.postExposedExtraM   || 0.080;
    var pickBotClr  = d.picketBottomClearM  || 0.060;
    var bracketWallT = 0.005;

    var postSide    = d.postSideM           || 0.089;
    var railSide    = d.railSideM           || 0.038;
    var railDepth   = d.railDepthM          || 0.089;   /* 2x4 face = ~89 mm */
    var pickT       = d.pickThicknessM      || 0.019;

    var style       = STATE.woodStyle || 'privacy';
    var styleP      = _getWoodStyleParams(style);
    var pickW       = styleP.boardW;

    /* -- POST (KVH10100, 4x4) -- length = above-grade lumber length -- */
    var postLenM = Math.max(0, panelH + postExtra - bracketWallT);

    /* -- RAIL (KVH10060, 2x4) -- length = bay span; count = bays x
          rails-per-bay (mid rail for tall panels). -- */
    var hasMid      = (panelH >= 1.829);
    var railsPerBay = hasMid ? 3 : 2;
    var railCount   = bays * railsPerBay;
    var railLenM    = spacing;

    /* -- PLANK (KVR16019/KVR10019) -- length = panel height - bottom
          clearance; count = pickets across all bays. -- */
    var picketsPerBay = Math.max(1, Math.floor(spacing / styleP.pitch)) * styleP.sides;
    var plankCount    = bays * picketsPerBay;
    var plankLenM     = Math.max(0, panelH - pickBotClr);
    var plankCode     = styleP.boardCode;

    var groups = [];
    /* The cut-list visual page draws 'length'/'width' at face value and
       just appends 'unit' as a label (no internal conversion), so we must
       hand it dimensions ALREADY in the active units. All the geometry
       above is in metres -> convert here. */
    var U = STATE.isMetric ? 1 : (1 / 0.3048);   /* m -> ft when imperial */
    var unitLabel = STATE.isMetric ? 'm' : 'ft';

    if (postCount > 0 && postLenM > 0) {
      groups.push({
        profile: boqName('KVH10100', 'Post 4\u00d74'),
        profileCode: 'KVH10100',
        family: 'lumber',
        profileWidth: postSide * U, profileHeight: postSide * U,
        unit: unitLabel,
        pieces: [{ length: postLenM * U, width: postSide * U, qty: postCount, label: 'KVH10100' }]
      });
    }
    if (railCount > 0 && railLenM > 0) {
      groups.push({
        profile: boqName('KVH10060', 'Rail 2\u00d74'),
        profileCode: 'KVH10060',
        family: 'lumber',
        profileWidth: railDepth * U, profileHeight: railSide * U,
        unit: unitLabel,
        pieces: [{ length: railLenM * U, width: railDepth * U, qty: railCount, label: 'KVH10060' }]
      });
    }
    if (plankCount > 0 && plankLenM > 0) {
      groups.push({
        profile: boqName(plankCode, 'Plank'),
        profileCode: plankCode,
        family: 'lumber',
        profileWidth: pickW * U, profileHeight: pickT * U,
        unit: unitLabel,
        pieces: [{ length: plankLenM * U, width: pickW * U, qty: plankCount, label: plankCode }]
      });
    }
    return groups;
  }

  async function exportPdf() {
    var T = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};
    if (!global.ConfiguratorPdfExporter || !global.ConfiguratorPdfExporter.export) {
      alert(T.pdfLibLoading || 'PDF library not yet loaded \u2014 try again in a moment.');
      return;
    }
    var BOOT = global.BASE_BOOT || {};
    var bridge = global.ConfiguratorBudgetBridge || null;
    var btn = document.getElementById('cf-btn-pdf');

    /* fence-v10-credits: paid in tokens on the shared sheet -> no purchase needed */
    if (!BOOT.hasOwnership && !_pdfPaidNow) {
      var productUrl = '/products/' + (BOOT.productHandle || 'foundations');
      var msg = T.pdfPaywallMsg || 'To download the PDF documentation, please purchase this product first.';
      var ok = window.confirm(msg + '\n\n' + (T.pdfPaywallCta || 'Go to product page?'));
      if (ok) window.location.href = productUrl;
      return;
    }

    if (btn) btn.classList.add('active');

    if (bridge && typeof bridge.isReady === 'function' && !bridge.isReady()) {
      var waited = 0;
      var pollMs = 100;
      var timeoutMs = 5000;
      while (!bridge.isReady() && waited < timeoutMs) {
        await new Promise(function (res) { setTimeout(res, pollMs); });
        waited += pollMs;
      }
      if (!bridge.isReady()) {
        if (btn) btn.classList.remove('active');
        alert(T.pdfWaitingForData || 'Configuration data is still loading. Please wait a moment and try again.');
        return;
      }
    }

    try {
      try { publishBoQToBudget(); } catch (_) {}
      if (typeof global.updateTotal === 'function') {
        try { global.updateTotal(); } catch (_) {}
      }
      await new Promise(function (res) { setTimeout(res, 600); });

      var snapshots = await _captureForPdf();
      var instances = _buildInstancePayload();
      var boqNames  = _buildBoqNamesMap(bridge);
      var boq       = _buildBoq(bridge);
      var totals    = _buildTotals(boq);
      var baseDims  = _buildBaseDimensions();
      var variantName = _variantDisplayName();

      try { console.log('[exportPdf] totals=', totals); } catch (_) {}

      var planSnap  = (snapshots || []).find(function (s) { return s && s.key === 'plan'; });
      var crossSnap = (snapshots || []).find(function (s) { return s && s.key === 'cross-section'; });
      try {
        console.log('[exportPdf] snapshots=', snapshots ? snapshots.length : 0,
                    'planSnap=', planSnap ? (planSnap.imageDataUrl ? 'OK (' + planSnap.imageDataUrl.length + ' chars)' : 'no URL') : 'missing',
                    'crossSnap=', crossSnap ? (crossSnap.imageDataUrl ? 'OK' : 'no URL') : 'missing');
      } catch (_) {}

      var assemblyPayload = await _buildAssemblyPayload(boq);
      try { console.log('[exportPdf] assemblyPayload=', assemblyPayload ? 'OK (' + assemblyPayload.legend.length + ' legend rows)' : 'none'); } catch (_) {}

      global.ConfiguratorPdfExporter.export({
        productTitle:    (BOOT.productTitle || 'Foundation') + '\n' + (variantName ? variantName.toUpperCase() : ''),
        customerName:    BOOT.customerName || null,
        coverImage:      BOOT.coverImage   || null,
        shareUrl:        _buildShareUrl(),
        totals:          totals,
        baseAssembly:     assemblyPayload,
        basePlan:         planSnap  ? { imageDataUrl: planSnap.imageDataUrl, bottomLeft: planSnap.bottomLeft || null } : null,
        /* Fence layout has no engineering cross-section or cut-list page --
           leaving these out keeps the document at assembly -> plan -> BoQ
           (no blank pages after the plan). */
        baseCrossSection: null,
        baseDims:      baseDims,
        instances:     instances,
        boqNames:      boqNames,
        cutList:       _buildWoodCutList(),
        boq:           boq,
        meta: {
          locale:        BOOT.locale         || 'en',
          productHandle: BOOT.productHandle  || 'foundations',
          date:          new Date(),
          variantName:   variantName,
          lvl:           STATE.lvl,
          slope: {
            active: STATE.slopeActive,
            deg:    STATE.slopeDeg
          },
          i18n: {
            pagePlanTitle:           T.pdfPagePlanTitle           || 'Plan view',
            pageCrossSectionTitle:   T.pdfPageCrossSection        || 'Cross-section',
            pageCutListTitle:        T.pdfPageCutListTitle        || 'Cut list',
            pageBoqTitle:            T.pdfPageBoqTitle            || 'Bill of Quantities',
            pageIsoTitle:            T.pdfPageIsoTitle            || '3D visualisation',
            pageOrthosTitle:         T.pdfPageOrthosTitle         || 'Engineering views',
            pageCutListVisualTitle:  T.pdfPageCutListVisualTitle  || 'Cut list \u2014 visual',
            orthoTop:                T.pdfOrthoTop                || 'Top view',
            orthoFront:              T.pdfOrthoFront              || 'Front view',
            orthoSide:               T.pdfOrthoSide               || 'Side view',
            totalsTitle:             T.pdfTotalsTitle             || 'TOTAL',
            materialsLabel:          T.pdfMaterialsLabel          || 'Materials',
            executionLabel:          T.pdfExecutionLabel          || 'Execution',
            grandTotalLabel:         T.pdfGrandTotalLabel         || 'GRAND TOTAL',
            noCutsMessage:           T.pdfNoCutsMessage           || 'No cuttable items for this foundation type.',
            noModelData:             T.pdfNoModelData             || 'No 3D model data available.',
            continued:               T.pdfContinued               || '(continued)',
            pcs:                     T.pdfPcs                     || 'pcs',
            deckingLabel:            T.pdfDeckingLabel            || 'decking',
            slopeLabel:              T.pdfSlopeLabel              || 'Slope',
            lvlLabel:                T.pdfLvlLabel                || 'Reinforcement level',
            lvlNames: {
              1: T.pdfLvl1 || 'Standard',
              2: T.pdfLvl2 || 'Reinforced',
              3: T.pdfLvl3 || 'Robust'
            },
            boq: {
              headerCode:      T.pdfBoqHeaderCode      || 'Code',
              headerName:      T.pdfBoqHeaderName      || 'Description',
              headerQty:       T.pdfBoqHeaderQty       || 'Qty',
              headerUnitPrice: T.pdfBoqHeaderUnitPrice || 'Unit price',
              headerTotal:     T.pdfBoqHeaderTotal     || 'Total',
              grandTotal:      T.pdfBoqGrandTotal      || 'Grand total'
            },
            cutList: {
              headerPart:   T.pdfCutHeaderPart   || 'Part',
              headerDesc:   T.pdfCutHeaderDesc   || 'Description',
              headerLength: T.pdfCutHeaderLength || 'Length',
              headerQty:    T.pdfCutHeaderQty    || 'Qty'
            }
          }
        }
      });
    } catch (e) {
      console.error('[exportPdf]', e);
      alert((T.pdfFailed || 'PDF export failed: ') + (e && e.message ? e.message : 'unknown error'));
    } finally {
      if (btn) btn.classList.remove('active');
    }
  }

  async function _captureForPdf() {
    var T = global.THREE;
    var eng = global.BaseEngine;
    if (!T || !eng || !eng.scene || !eng.renderer) return [];

    var poly = getFootprintPolygon();
    if (!poly || poly.length < 3) return [];

    var prevStage = STATE.vizStage;
    var prevView  = STATE.view;
    var prevSlope = STATE.slopeActive;
    var prevOrthoD = eng._currentOrthoD;
    var prevCamLeft = eng.cameraOrtho ? eng.cameraOrtho.left : null;
    var prevCamRight = eng.cameraOrtho ? eng.cameraOrtho.right : null;
    var prevCamTop = eng.cameraOrtho ? eng.cameraOrtho.top : null;
    var prevCamBottom = eng.cameraOrtho ? eng.cameraOrtho.bottom : null;
    var prevMapZoom = (_MapManager && _MapManager.map) ? _MapManager.map.getZoom() : null;
    var wrap = document.getElementById('cf-wrap-base');
    STATE.vizStage = 1;
    STATE.view = '2d';
    STATE.slopeActive = false;
    /* Setting STATE.view alone never moved the camera -- toggleView did.
       The export got away with it while the fence booted into 2D: whoever
       had not toggled was already on the ortho camera. Now that a fresh
       load lands in 3D, an export straight after it would render the plan
       page through the perspective camera. */
    if (eng._applyViewCamera) eng._applyViewCamera();
    if (wrap) {
      wrap.classList.add('cf-view-2d');
      wrap.classList.remove('cf-view-3d');
      wrap.classList.add('cf-stage-view');
    }
    /* Deterministic camera framing -- reset the ortho camera to fit
       the polygon with a fixed padding so the export is identical
       for every user regardless of their current pan/zoom state on
       the web. Mapbox follows via _syncMapTo2D so the underlying
       map matches the scene scale. */
    if (eng.fitCameraToFootprint) eng.fitCameraToFootprint();
    if (_MapManager && _MapManager.map && _MapManager._syncMapTo2D) {
      _MapManager._syncMapTo2D(false);
    }
    if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
    if (_VisualizationManager && _VisualizationManager.apply) _VisualizationManager.apply();
    if (_BaseDimensions && _BaseDimensions.refresh) _BaseDimensions.refresh();

    await new Promise(function (res) { setTimeout(res, 600); });

    if (_BaseDimensions && _BaseDimensions.refresh) _BaseDimensions.refresh();

    var threeCanvas = eng.renderer.domElement;
    var mapCanvas = null;
    try {
      var mapEl = document.getElementById('cf-mapbox');
      if (mapEl) {
        var mapboxCanvas = mapEl.querySelector('canvas');
        if (mapboxCanvas) mapCanvas = mapboxCanvas;
      }
    } catch (_) {}

    var wrapRect = wrap ? wrap.getBoundingClientRect() : null;
    var threeRect = threeCanvas.getBoundingClientRect();
    var refRect = wrapRect || threeRect;

    var W_PX = 1600;
    var srcW = refRect.width || 1;
    var srcH = refRect.height || 1;
    var H_PX = Math.round(W_PX * srcH / srcW);
    var scale = W_PX / srcW;

    var tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = W_PX;
    tmpCanvas.height = H_PX;
    var ctx = tmpCanvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W_PX, H_PX);

    var mapDrew = false;
    if (mapCanvas) {
      try {
        if (_MapManager && _MapManager.map && _MapManager.map.triggerRepaint) {
          _MapManager.map.triggerRepaint();
        }
        await new Promise(function (res) { setTimeout(res, 250); });
        var mapElRect = mapEl.getBoundingClientRect();
        var mx = (mapElRect.left - refRect.left) * scale;
        var my = (mapElRect.top  - refRect.top)  * scale;
        var mw = mapElRect.width  * scale;
        var mh = mapElRect.height * scale;
        ctx.globalAlpha = 0.7;
        ctx.drawImage(mapCanvas, mx, my, mw, mh);
        ctx.globalAlpha = 1.0;
        mapDrew = true;
      } catch (e) { console.warn('[exportPdf] map composite failed', e); }
    }

    var sceneDrew = false;
    try {
      eng.renderer.render(eng.scene, eng.camera);
      var cx = (threeRect.left - refRect.left) * scale;
      var cy = (threeRect.top  - refRect.top)  * scale;
      var cw = threeRect.width  * scale;
      var ch = threeRect.height * scale;
      ctx.drawImage(threeCanvas, cx, cy, cw, ch);
      sceneDrew = true;
    } catch (e) { console.warn('[exportPdf] scene composite failed', e); }

    var dimLabelData = [];
    try {
      var labels = document.querySelectorAll('.cf-base-dim-label, .cf-dim-chip');
      var labelsVisible = 0;

      var svgEl = document.getElementById('cf-base-dims-svg');
      if (svgEl) {
        try {
          var svgClone = svgEl.cloneNode(true);
          svgClone.style.display = '';
          var svgRect = svgEl.getBoundingClientRect();
          if (svgRect.width && svgRect.height) {
            var svgStr = new XMLSerializer().serializeToString(svgClone);
            var svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
            var svgUrl = URL.createObjectURL(svgBlob);
            var svgImg = new Image();
            await new Promise(function (res) {
              svgImg.onload = function () { res(); };
              svgImg.onerror = function () { res(); };
              svgImg.src = svgUrl;
            });
            var sx2 = (svgRect.left - refRect.left) * scale;
            var sy2 = (svgRect.top  - refRect.top)  * scale;
            var sw2 = svgRect.width  * scale;
            var sh2 = svgRect.height * scale;
            try { ctx.drawImage(svgImg, sx2, sy2, sw2, sh2); } catch (_) {}
            URL.revokeObjectURL(svgUrl);
          }
        } catch (e) { console.warn('[exportPdf] svg dims composite failed', e); }
      }

      /* Collect label positions in source-canvas coords. Drawing
         is deferred until after crop so the font size stays fixed
         in output-canvas pixels (not multiplied by the crop zoom
         ratio). */
      for (var li = 0; li < labels.length; li++) {
        var lel = labels[li];
        var computed = window.getComputedStyle(lel);
        if (computed.display === 'none' || computed.visibility === 'hidden') continue;
        labelsVisible++;
        var lr = lel.getBoundingClientRect();
        if (!lr.width || !lr.height) continue;
        var lcx = ((lr.left + lr.width / 2)  - refRect.left) * scale;
        var lcy = ((lr.top  + lr.height / 2) - refRect.top)  * scale;
        var txt = (lel.textContent || '').trim();
        if (!txt) continue;
        dimLabelData.push({ cx: lcx, cy: lcy, text: txt });
      }
      try { console.log('[exportPdf] labels collected:', { found: labels.length, visible: labelsVisible, queued: dimLabelData.length, svgEl: !!svgEl }); } catch (_) {}
    } catch (e) { console.warn('[exportPdf] labels collect failed', e); }

    try {
      console.log('[exportPdf] composite: mapDrew=' + mapDrew + ' sceneDrew=' + sceneDrew + ' size=' + W_PX + 'x' + H_PX);
    } catch (_) {}

    var xMin = Infinity, xMax = -Infinity, zMin = Infinity, zMax = -Infinity;
    for (var i = 0; i < poly.length; i++) {
      if (poly[i][0] < xMin) xMin = poly[i][0];
      if (poly[i][0] > xMax) xMax = poly[i][0];
      if (poly[i][1] < zMin) zMin = poly[i][1];
      if (poly[i][1] > zMax) zMax = poly[i][1];
    }
    var bbL = xMax - xMin;
    var bbW = zMax - zMin;

    /* Crop tmpCanvas so the polygon fills ~75% of the cropped area.
       Probe each polygon corner via _worldToScreen to learn the
       polygon's screen bbox, then center the crop on the polygon
       centroid and size it so polygon_w / crop_w == 0.75. Output
       crop matches A4 landscape aspect (1.414) so the PDF page
       maps 1:1 with no further stretching needed. */
    var cropCanvas = tmpCanvas;
    var cropX = 0, cropY = 0, cropW = W_PX, cropH = H_PX;
    try {
      var corners = [
        _worldToScreen(xMin, zMin),
        _worldToScreen(xMax, zMin),
        _worldToScreen(xMax, zMax),
        _worldToScreen(xMin, zMax)
      ];
      var sxMin = Infinity, sxMax = -Infinity, syMin = Infinity, syMax = -Infinity;
      var ok = true;
      for (var ci = 0; ci < corners.length; ci++) {
        if (!corners[ci]) { ok = false; break; }
        var cpx = corners[ci].left * scale;
        var cpy = corners[ci].top  * scale;
        if (cpx < sxMin) sxMin = cpx;
        if (cpx > sxMax) sxMax = cpx;
        if (cpy < syMin) syMin = cpy;
        if (cpy > syMax) syMax = cpy;
      }
      if (ok && isFinite(sxMin) && isFinite(syMin)) {
        var polyW = sxMax - sxMin;
        var polyH = syMax - syMin;
        var polyCx = (sxMin + sxMax) / 2;
        var polyCy = (syMin + syMax) / 2;

        /* Polygon fills 75% of crop along each axis. To preserve
           A4 landscape aspect (1.414), pick crop dimensions that
           satisfy both polyW/cw==0.75 AND polyH/ch==0.75; the
           larger of the two derived crop sizes wins so neither
           axis is over-cropped. */
        /* Camera is now deterministic (forced fitCameraToFootprint
           with PADDING=4m), so this crop pass is mostly a sanity
           clamp. ~15% smaller polygon -> more breathing room around
           it; the crop window is correspondingly wider. */
        var TARGET_FILL = 0.48;
        var PAGE_AR = 297 / 210;
        var cwByX = polyW / TARGET_FILL;
        var cwByY = (polyH / TARGET_FILL) * PAGE_AR;
        var cw = Math.max(cwByX, cwByY);
        var ch = cw / PAGE_AR;

        /* Clamp crop window inside the source canvas so we never
           sample outside the composite (would produce black bands). */
        var cx = polyCx - cw / 2;
        var cy = polyCy - ch / 2;
        if (cx < 0) cx = 0;
        if (cy < 0) cy = 0;
        if (cx + cw > W_PX) cx = W_PX - cw;
        if (cy + ch > H_PX) cy = H_PX - ch;
        if (cw > W_PX) { cw = W_PX; cx = 0; }
        if (ch > H_PX) { ch = H_PX; cy = 0; }

        var OUT_W = 1600;
        var OUT_H = Math.round(OUT_W / PAGE_AR);
        var croppedCanvas = document.createElement('canvas');
        croppedCanvas.width  = OUT_W;
        croppedCanvas.height = OUT_H;
        var cctx = croppedCanvas.getContext('2d');
        cctx.fillStyle = '#ffffff';
        cctx.fillRect(0, 0, OUT_W, OUT_H);
        cctx.drawImage(tmpCanvas, cx, cy, cw, ch, 0, 0, OUT_W, OUT_H);

        cropCanvas = croppedCanvas;
        cropX = cx; cropY = cy; cropW = cw; cropH = ch;
        try {
          console.log('[exportPdf] crop:', { polyW: Math.round(polyW), polyH: Math.round(polyH), cropW: Math.round(cw), cropH: Math.round(ch), fillX: (polyW/cw).toFixed(2), fillY: (polyH/ch).toFixed(2) });
        } catch (_) {}
      }
    } catch (e) { console.warn('[exportPdf] crop failed', e); }

    /* Draw dim labels onto the cropped output canvas with a fixed
       font size so they look the same regardless of how aggressive
       the crop was. Also de-collides labels: if any pair would
       overlap, push each one along the axis with more headroom
       (vertical for horizontal-axis dims, horizontal for vertical
       ones). Iterates a few times until stable or capped. */
    try {
      var cctx2 = cropCanvas.getContext('2d');
      var outW = cropCanvas.width;
      var outH = cropCanvas.height;
      var LABEL_FONT_PX = 18;
      var LABEL_PAD_X = 7;
      var LABEL_PAD_Y = 4;

      cctx2.font = '600 ' + LABEL_FONT_PX + 'px Montserrat, sans-serif';
      cctx2.textAlign = 'center';
      cctx2.textBaseline = 'middle';

      /* Project each captured label from source-canvas coords into
         the cropped output coords, measure, and keep the box for
         collision testing. */
      var placed = [];
      for (var di = 0; di < dimLabelData.length; di++) {
        var d = dimLabelData[di];
        var oxLabel = (d.cx - cropX) / cropW * outW;
        var oyLabel = (d.cy - cropY) / cropH * outH;
        if (oxLabel < -50 || oxLabel > outW + 50) continue;
        if (oyLabel < -50 || oyLabel > outH + 50) continue;
        var tw2 = cctx2.measureText(d.text).width;
        var th2 = LABEL_FONT_PX;
        placed.push({
          text: d.text,
          cx: oxLabel, cy: oyLabel,
          w: tw2 + LABEL_PAD_X * 2,
          h: th2 + LABEL_PAD_Y * 2
        });
      }

      /* Simple de-overlap pass: for each colliding pair, nudge the
         later one away from the earlier one along the smaller-cost
         axis. A handful of iterations is enough for typical 4-8
         label scenes. */
      function rectsOverlap(a, b) {
        return Math.abs(a.cx - b.cx) < (a.w + b.w) / 2 - 1 &&
               Math.abs(a.cy - b.cy) < (a.h + b.h) / 2 - 1;
      }
      for (var iter = 0; iter < 8; iter++) {
        var anyMove = false;
        for (var ai = 0; ai < placed.length; ai++) {
          for (var bi = ai + 1; bi < placed.length; bi++) {
            var A = placed[ai], B = placed[bi];
            if (!rectsOverlap(A, B)) continue;
            var dx = B.cx - A.cx;
            var dy = B.cy - A.cy;
            var overlapX = (A.w + B.w) / 2 - Math.abs(dx);
            var overlapY = (A.h + B.h) / 2 - Math.abs(dy);
            if (overlapX < overlapY) {
              var nudge = overlapX / 2 + 1;
              if (dx >= 0) { A.cx -= nudge; B.cx += nudge; }
              else         { A.cx += nudge; B.cx -= nudge; }
            } else {
              var nudgeY = overlapY / 2 + 1;
              if (dy >= 0) { A.cy -= nudgeY; B.cy += nudgeY; }
              else         { A.cy += nudgeY; B.cy -= nudgeY; }
            }
            anyMove = true;
          }
        }
        if (!anyMove) break;
      }

      for (var pi = 0; pi < placed.length; pi++) {
        var P = placed[pi];
        var bx = P.cx - P.w / 2;
        var by = P.cy - P.h / 2;
        cctx2.fillStyle = '#ffffff';
        cctx2.fillRect(bx, by, P.w, P.h);
        cctx2.strokeStyle = '#999';
        cctx2.lineWidth = 0.7;
        cctx2.strokeRect(bx, by, P.w, P.h);
        cctx2.fillStyle = '#1a1a1a';
        cctx2.fillText(P.text, P.cx, P.cy);
      }
      try { console.log('[exportPdf] labels drawn post-crop:', placed.length); } catch (_) {}
    } catch (e) { console.warn('[exportPdf] labels post-crop draw failed', e); }

    var planImg = cropCanvas.toDataURL('image/png');

    var bottomLeft = null;
    try {
      var blScreen = _worldToScreen(xMin, zMax);
      if (blScreen) {
        /* Re-project bottom-left into the cropped image coords.
           The original blScreen is in scaled tmpCanvas space; we
           subtract the crop offset, scale to the output canvas,
           then normalise to 0..1. */
        var blCanvasX = blScreen.left * scale;
        var blCanvasY = blScreen.top  * scale;
        var blCropX = (blCanvasX - cropX) / cropW;
        var blCropY = (blCanvasY - cropY) / cropH;
        var bottomLeftGps = null;
        if (_MapManager && _MapManager.map && mapEl) {
          var mapRect = mapEl.getBoundingClientRect();
          var pixelInMap = [
            (blScreen.left + refRect.left) - mapRect.left,
            (blScreen.top  + refRect.top)  - mapRect.top
          ];
          try {
            var lngLat = _MapManager.map.unproject(pixelInMap);
            if (lngLat) bottomLeftGps = { lng: lngLat.lng, lat: lngLat.lat };
          } catch (e) { console.warn('[exportPdf] unproject failed', e); }
        }
        bottomLeft = {
          worldX: xMin, worldZ: zMax,
          normX: Math.max(0, Math.min(1, blCropX)),
          normY: Math.max(0, Math.min(1, blCropY)),
          gps:   bottomLeftGps
        };
        try { console.log('[exportPdf] bottomLeft=', bottomLeft); } catch (_) {}
      }
    } catch (e) { console.warn('[exportPdf] bottom-left calc failed', e); }

    STATE.vizStage = prevStage;
    STATE.view = prevView;
    STATE.slopeActive = prevSlope;
    if (eng._applyViewCamera) eng._applyViewCamera();
    if (wrap) {
      wrap.classList.toggle('cf-stage-view', prevStage === 1);
      wrap.classList.toggle('cf-view-2d', prevView === '2d');
      wrap.classList.toggle('cf-view-3d', prevView === '3d');
    }
    /* Restore the user's previous camera framing so the configurator
       looks the same as before the export. Without this, the user
       would end up with the PDF camera fit after every download. */
    if (eng.cameraOrtho && prevCamLeft !== null) {
      eng.cameraOrtho.left   = prevCamLeft;
      eng.cameraOrtho.right  = prevCamRight;
      eng.cameraOrtho.top    = prevCamTop;
      eng.cameraOrtho.bottom = prevCamBottom;
      eng.cameraOrtho.updateProjectionMatrix();
      eng._currentOrthoD = prevOrthoD;
    }
    if (_MapManager && _MapManager.map && prevMapZoom !== null) {
      try { _MapManager.map.setZoom(prevMapZoom); } catch (_) {}
    }
    if (global.BaseRenderer && global.BaseRenderer.build) global.BaseRenderer.build();
    if (_VisualizationManager && _VisualizationManager.apply) _VisualizationManager.apply();

    var crossImg = _renderCrossSectionSVG(bbL);

    return [
      {
        key: 'plan',
        label: '',
        imageDataUrl: planImg,
        worldW: bbL, worldH: bbW,
        aspect: W_PX / H_PX,
        bottomLeft: bottomLeft
      },
      {
        key: 'cross-section',
        label: '',
        imageDataUrl: crossImg,
        worldW: bbL, worldH: 2,
        aspect: bbL / 2
      }
    ];
  }

  function _renderCrossSectionSVG(bbL) {
    var d = getVariantDefaults(_currentVariantKey(), STATE.lvl);
    var vk = _currentVariantKey();
    var slopeRad = STATE.slopeActive && STATE.slopeDeg > 0 ? STATE.slopeDeg * Math.PI / 180 : 0;
    var maxDelta = bbL * Math.tan(slopeRad);
    var TT = (global.BASE_BOOT && global.BASE_BOOT.i18n) || {};

    var L = {
      concreteSlab:    TT.csLabelConcreteSlab    || 'Concrete slab',
      gravel:          TT.csLabelGravel          || 'Gravel',
      mesh:            TT.csLabelMesh            || 'Mesh',
      topSlab:         TT.csLabelTopSlab         || 'Top slab',
      strip:           TT.csLabelStrip           || 'Strip',
      concretePiers:   TT.csLabelConcretePiers   || 'Concrete piers',
      precastBlocks:   TT.csLabelPrecastBlocks   || 'Precast pier blocks',
      groundScrews:    TT.csLabelGroundScrews    || 'Ground screws',
      groundLevel:     TT.csLabelGroundLevel     || 'Ground level',
      slope:           TT.csLabelSlope           || 'Slope',
      slopeOver:       TT.csLabelSlopeOver       || 'over'
    };

    var W = 1400, H = 700;
    var marginX = 80, marginY = 80;
    var drawW = W - 2 * marginX;
    var drawH = H - 2 * marginY;

    var groundY = marginY + drawH * 0.30;
    var foundTopY = groundY + 10;

    var s = drawW / bbL;
    var leftX = marginX, rightX = marginX + drawW;

    var svg = ['<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '">'];
    svg.push('<rect width="' + W + '" height="' + H + '" fill="#ffffff"/>');

    var terrainRight = groundY;
    var terrainLeft = groundY - maxDelta * s;
    svg.push('<polygon points="' +
      leftX + ',' + terrainLeft + ' ' +
      rightX + ',' + terrainRight + ' ' +
      rightX + ',' + H + ' ' +
      leftX + ',' + H +
      '" fill="#e8d4b8" stroke="#b39570" stroke-width="1.5"/>');

    svg.push('<line x1="' + leftX + '" y1="' + terrainLeft + '" x2="' + rightX + '" y2="' + terrainRight + '" stroke="#8a6e4a" stroke-width="2" stroke-dasharray="6,4"/>');

    if (vk === 'slab') {
      var slabH = d.slabThicknessM * 50;
      var gravelH = d.gravelDepthM * 50;
      var excavH = d.excavationDepthM * 50;
      svg.push('<rect x="' + (leftX + 20) + '" y="' + foundTopY + '" width="' + (drawW - 40) + '" height="' + slabH + '" fill="#a8a8a8" stroke="#222" stroke-width="1.5"/>');
      svg.push('<text x="' + (leftX + 30) + '" y="' + (foundTopY + slabH / 2 + 4) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.concreteSlab + ' ' + (d.slabThicknessM * 1000) + ' mm</text>');
      svg.push('<rect x="' + (leftX + 20) + '" y="' + (foundTopY + slabH) + '" width="' + (drawW - 40) + '" height="' + gravelH + '" fill="#c7b9a3" stroke="#7a6b50" stroke-width="1"/>');
      svg.push('<text x="' + (leftX + 30) + '" y="' + (foundTopY + slabH + gravelH / 2 + 4) + '" font-family="sans-serif" font-size="10" fill="#333">' + L.gravel + ' ' + (d.gravelDepthM * 1000) + ' mm</text>');
      svg.push('<line x1="' + (leftX + 20) + '" y1="' + (foundTopY + slabH * 0.5) + '" x2="' + (rightX - 20) + '" y2="' + (foundTopY + slabH * 0.5) + '" stroke="#2266cc" stroke-width="2" stroke-dasharray="3,3"/>');
      svg.push('<text x="' + (rightX - 130) + '" y="' + (foundTopY + slabH * 0.5 - 4) + '" font-family="sans-serif" font-size="9" fill="#2266cc">' + L.mesh + ' FFR30001</text>');
    } else if (vk === 'strip') {
      var slabH2 = d.slabThicknessM * 50;
      var stripH = d.stripDepthM * 50;
      var stripW = d.stripWidthM * s * 1.5;
      svg.push('<rect x="' + (leftX + 20) + '" y="' + foundTopY + '" width="' + (drawW - 40) + '" height="' + slabH2 + '" fill="#a8a8a8" stroke="#222" stroke-width="1.5"/>');
      svg.push('<text x="' + (leftX + 30) + '" y="' + (foundTopY + slabH2 / 2 + 4) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.topSlab + ' ' + (d.slabThicknessM * 1000) + ' mm</text>');
      svg.push('<rect x="' + (leftX + 30) + '" y="' + (foundTopY + slabH2) + '" width="' + stripW + '" height="' + stripH + '" fill="#888" stroke="#222" stroke-width="1.5"/>');
      svg.push('<rect x="' + (rightX - 30 - stripW) + '" y="' + (foundTopY + slabH2) + '" width="' + stripW + '" height="' + stripH + '" fill="#888" stroke="#222" stroke-width="1.5"/>');
      svg.push('<text x="' + (leftX + 30) + '" y="' + (foundTopY + slabH2 + stripH + 18) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.strip + ' ' + (d.stripWidthM * 1000) + '\u00d7' + (d.stripDepthM * 1000) + ' mm</text>');
    } else if (vk === 'piers') {
      var pierH = d.pierDepthM * 50;
      var pierW = d.pierSideM * s * 1.5;
      var pierSpacing = d.pierSpacingM * s;
      var nPiers = Math.floor(drawW / pierSpacing) + 1;
      for (var pi = 0; pi < nPiers; pi++) {
        var px = leftX + 20 + pi * pierSpacing;
        if (px + pierW > rightX - 20) break;
        var extra = slopeRad > 0 ? ((nPiers - 1 - pi) * pierSpacing / s) * Math.tan(slopeRad) * 50 : 0;
        svg.push('<rect x="' + px + '" y="' + foundTopY + '" width="' + pierW + '" height="' + (pierH + extra) + '" fill="#888" stroke="#222" stroke-width="1.5"/>');
      }
      svg.push('<text x="' + (leftX + 20) + '" y="' + (foundTopY + pierH + 30) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.concretePiers + ' ' + (d.pierSideM * 1000) + '\u00d7' + (d.pierSideM * 1000) + '\u00d7' + (d.pierDepthM * 1000) + ' mm @ ' + (d.pierSpacingM * 1000) + ' mm</text>');
    } else if (vk === 'precast') {
      var blockH = d.blockHeightM * 50;
      var blockW = d.blockBaseM * s * 1.5;
      var blockTopW = d.blockTopM * s * 1.5;
      var bSpacing = d.blockSpacingM * s;
      var nBlocks = Math.floor(drawW / bSpacing) + 1;
      for (var bi = 0; bi < nBlocks; bi++) {
        var bx = leftX + 20 + bi * bSpacing;
        if (bx + blockW > rightX - 20) break;
        var extra2 = slopeRad > 0 ? ((nBlocks - 1 - bi) * bSpacing / s) * Math.tan(slopeRad) * 50 : 0;
        var bTopY = foundTopY;
        var bBotY = bTopY + blockH + extra2;
        svg.push('<polygon points="' +
          (bx + (blockW - blockTopW) / 2) + ',' + bTopY + ' ' +
          (bx + (blockW + blockTopW) / 2) + ',' + bTopY + ' ' +
          (bx + blockW) + ',' + bBotY + ' ' +
          bx + ',' + bBotY +
          '" fill="#999" stroke="#222" stroke-width="1.5"/>');
      }
      svg.push('<text x="' + (leftX + 20) + '" y="' + (foundTopY + blockH + 30) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.precastBlocks + ' ' + (d.blockBaseM * 1000) + '/' + (d.blockTopM * 1000) + '\u00d7' + (d.blockHeightM * 1000) + ' mm @ ' + (d.blockSpacingM * 1000) + ' mm</text>');
    } else if (vk === 'screws') {
      var screwH = d.screwLenM * 50;
      var screwSpacing = d.screwSpacingM * s;
      var nScrews = Math.floor(drawW / screwSpacing) + 1;
      for (var si = 0; si < nScrews; si++) {
        var sx = leftX + 20 + si * screwSpacing;
        if (sx > rightX - 20) break;
        svg.push('<rect x="' + (sx - 4) + '" y="' + foundTopY + '" width="8" height="' + screwH + '" fill="#444" stroke="#111" stroke-width="1"/>');
        svg.push('<rect x="' + (sx - 8) + '" y="' + (foundTopY - 6) + '" width="16" height="8" fill="#222" stroke="#111" stroke-width="1"/>');
      }
      svg.push('<text x="' + (leftX + 20) + '" y="' + (foundTopY + screwH + 24) + '" font-family="sans-serif" font-size="11" fill="#000">' + L.groundScrews + ' \u00d8' + (d.screwDiameterMm || 76) + ' \u00d7 ' + (d.screwLenM * 1000) + ' mm @ ' + (d.screwSpacingM * 1000) + ' mm</text>');
    }

    svg.push('<text x="' + leftX + '" y="' + (groundY - 12) + '" font-family="sans-serif" font-size="12" fill="#5a4a30" font-weight="700">' + L.groundLevel + '</text>');

    if (slopeRad > 0) {
      svg.push('<text x="' + (rightX - 220) + '" y="' + (terrainRight - 8) + '" font-family="sans-serif" font-size="11" fill="#8a6e4a">' + L.slope + ' ' + STATE.slopeDeg + '\u00b0 (\u0394 ' + (maxDelta * 1000).toFixed(0) + ' mm ' + L.slopeOver + ' ' + bbL.toFixed(1) + ' m)</text>');
    }

    svg.push('</svg>');
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg.join(''))));
  }

  function _buildInstancePayload() {
    var poly = getFootprintPolygon();
    var bb = getBBox();
    return {
      polygon:    poly,
      bboxL:      bb.L,
      bboxW:      bb.W,
      area:       computeArea(),
      perimeter:  computePerimeter(),
      variant:    STATE.variant,
      variantKey: _currentVariantKey(),
      lvl:        STATE.lvl,
      slope:      { active: STATE.slopeActive, deg: STATE.slopeDeg },
      isMetric:   STATE.isMetric
    };
  }

  function _buildBoqNamesMap(bridge) {
    var rows = computeBoQ();
    var out = {};
    for (var i = 0; i < rows.length; i++) {
      var code = rows[i][0];
      if (!code) continue;
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(code) : null;
      out[code] = (item && item.name) || code;
    }
    return out;
  }

  function _fetchImageAsDataUrl(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve(null); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var c = document.createElement('canvas');
          c.width  = img.naturalWidth  || img.width;
          c.height = img.naturalHeight || img.height;
          var cx = c.getContext('2d');
          cx.drawImage(img, 0, 0);
          resolve(c.toDataURL('image/jpeg', 0.92));
        } catch (e) {
          console.warn('[exportPdf] assembly image canvas tainted', e);
          resolve(null);
        }
      };
      img.onerror = function () {
        console.warn('[exportPdf] assembly image load failed', url);
        resolve(null);
      };
      img.src = url;
    });
  }

  async function _buildAssemblyPayload(boqRows) {
    var BOOT = global.BASE_BOOT || {};
    var images = BOOT.variantAssemblyImages || {};
    var variant = BOOT.variants && BOOT.variants[STATE.variant - 1];
    if (!variant) return null;
    var url = images[String(variant.id)] || images[variant.id];
    if (!url) {
      try { console.log('[exportPdf] assembly: no image for variant', variant.id); } catch (_) {}
      return null;
    }
    var dataUrl = await _fetchImageAsDataUrl(url);
    if (!dataUrl) return null;

    var legend = [];
    if (Array.isArray(boqRows)) {
      for (var i = 0; i < boqRows.length; i++) {
        var row = boqRows[i];
        if (!row || !row.name) continue;
        var qty = row.qty;
        var unit = row.unit || '';
        var qtyStr = '';
        if (typeof qty === 'number' && !isNaN(qty)) {
          qtyStr = ' \u2014 ' + (Math.abs(qty - Math.round(qty)) < 0.005 ? String(Math.round(qty)) : qty.toFixed(2)) + (unit ? ' ' + unit : '');
        }
        legend.push({ label: row.name + qtyStr, code: row.code });
      }
    }

    /* Dynamic dimension callouts -- labelled a, b, ... each carries
       a model-derived value that follows STATE.variant + STATE.lvl.
       The variant-specific picker below maps a clean engineering
       quantity ("thickness", "depth", "side", "length") to the
       VARIANT_DEFAULTS field for the current variant; values are
       in metres internally and rendered as mm rounded to the
       nearest integer for the typical foundation use case. */
    var dims = [];
    try {
      var vk = _currentVariantKey();
      var d  = getVariantDefaults(vk, STATE.lvl);
      var TT = (BOOT.i18n) || {};
      var fmtMM = function (m) {
        if (typeof m !== 'number' || isNaN(m)) return '';
        return Math.round(m * 1000) + ' mm';
      };
      var labelThickness = TT.assemblyDimSlabThickness || 'Slab thickness';
      var labelExcav     = TT.assemblyDimExcavation   || 'Excavation depth';
      var labelGravel    = TT.assemblyDimGravel       || 'Gravel layer';
      var labelStripW    = TT.assemblyDimStripWidth   || 'Strip width';
      var labelStripD    = TT.assemblyDimStripDepth   || 'Strip depth';
      var labelPierSide  = TT.assemblyDimPierSide     || 'Pier size';
      var labelPierDepth = TT.assemblyDimPierDepth    || 'Pier depth';
      var labelBlockH    = TT.assemblyDimBlockHeight  || 'Block height';
      var labelBlockBase = TT.assemblyDimBlockBase    || 'Block base width';
      var labelScrewLen  = TT.assemblyDimScrewLength  || 'Screw length';
      var labelScrewDia  = TT.assemblyDimScrewDia     || 'Screw diameter';

      if (vk === 'slab') {
        if (d.slabThicknessM)   dims.push({ label: labelThickness, value: fmtMM(d.slabThicknessM) });
        if (d.excavationDepthM) dims.push({ label: labelExcav,     value: fmtMM(d.excavationDepthM) });
      } else if (vk === 'strip') {
        if (d.slabThicknessM)   dims.push({ label: labelThickness, value: fmtMM(d.slabThicknessM) });
        if (d.stripDepthM)      dims.push({ label: labelStripD,    value: fmtMM(d.stripDepthM) });
      } else if (vk === 'piers') {
        if (d.pierSideM)        dims.push({ label: labelPierSide,  value: fmtMM(d.pierSideM) });
        if (d.pierDepthM)       dims.push({ label: labelPierDepth, value: fmtMM(d.pierDepthM) });
      } else if (vk === 'precast') {
        if (d.blockHeightM)     dims.push({ label: labelBlockH,    value: fmtMM(d.blockHeightM) });
        if (d.blockBaseM)       dims.push({ label: labelBlockBase, value: fmtMM(d.blockBaseM) });
      } else if (vk === 'screws') {
        if (d.screwLenM)        dims.push({ label: labelScrewLen,  value: fmtMM(d.screwLenM) });
        if (d.screwDiameterMm)  dims.push({ label: labelScrewDia,  value: Math.round(d.screwDiameterMm) + ' mm' });
      }
    } catch (e) { console.warn('[exportPdf] assembly dims failed', e); }

    return { imageDataUrl: dataUrl, legend: legend, dimensions: dims };
  }

  function _buildBoq(bridge) {
    var rows = computeBoQ();
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var code = rows[i][0];
      var qtyMetric = rows[i][1];
      if (qtyMetric == null) continue;
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(code) : null;
      var localUnit = item ? (STATE.isMetric ? item.unitMetric : item.unitImperial) : null;
      var displayQty = STATE.isMetric ? qtyMetric : (rows[i][2] != null ? rows[i][2] : qtyMetric);
      var userPrice = bridge && bridge.getUserPrice ? bridge.getUserPrice(code) : null;
      var unitPrice = userPrice && typeof userPrice.price === 'number' ? userPrice.price : null;
      var total = (unitPrice != null) ? unitPrice * displayQty : null;
      out.push({
        code:      code,
        name:      (item && item.name) || code,
        qty:       displayQty,
        unit:      localUnit || (STATE.isMetric ? 'pcs' : 'pcs'),
        unitPrice: unitPrice,
        total:     total
      });
    }
    return out;
  }

  function _buildTotals(boqRows) {
    function $idText(id) {
      var el = document.getElementById(id);
      if (!el || !el.textContent) return '';
      var t = el.textContent.trim();
      if (!t || t === '\u2014' || t === '-') return '';
      return t;
    }
    function $domText(selector) {
      var el = document.querySelector(selector);
      if (!el || !el.textContent) return '';
      var t = el.textContent.trim();
      if (!t || t === '\u2014' || t === '-') return '';
      return t;
    }
    var fmtPrice = (typeof global.formatPrice === 'function')
      ? global.formatPrice
      : function (n) { return Math.round(+n || 0).toLocaleString() + ' ' +
            ((global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || 'USD'); };

    var totalText    = $idText('cf-bw-total');
    var materialText = $idText('cf-bw-material');
    var execText     = $idText('cf-bw-execution');
    var timeText     = $idText('cf-bw-time');
    var capacityText = $idText('cf-bw-capacity');

    if (!totalText)    totalText    = $domText('[data-budget-total-cost]');
    if (!materialText) materialText = $domText('[data-budget-total-material]');
    if (!execText)     execText     = $domText('[data-budget-total-execution]');
    if (!timeText)     timeText     = $domText('[data-budget-total-time]');

    if (!materialText && typeof global.lastMaterialsTotal === 'number') {
      materialText = fmtPrice(global.lastMaterialsTotal);
    }
    if (!execText && typeof global.lastDeliveryTotal === 'number') {
      execText = fmtPrice(global.lastDeliveryTotal);
    }
    if (!totalText) {
      var mNum = (typeof global.lastMaterialsTotal === 'number') ? global.lastMaterialsTotal : 0;
      var eNum = (typeof global.lastDeliveryTotal  === 'number') ? global.lastDeliveryTotal  : 0;
      if (mNum || eNum) totalText = fmtPrice(mNum + eNum);
    }
    if (!timeText && typeof global.lastDurationTotal === 'number') {
      var mins = global.lastDurationTotal;
      timeText = (mins >= 60)
        ? (Math.round(mins / 60 * 10) / 10) + ' h'
        : Math.round(mins) + ' min';
    }

    if (!materialText) {
      var matSum = 0;
      if (Array.isArray(boqRows)) {
        boqRows.forEach(function (r) { if (r.total != null) matSum += r.total; });
      }
      if (matSum) materialText = fmtPrice(matSum);
    }
    if (!totalText && materialText) totalText = materialText;

    if (!capacityText) {
      try {
        var excavM3 = 0;
        if (Array.isArray(boqRows)) {
          for (var i = 0; i < boqRows.length; i++) {
            if (boqRows[i].code === 'FEE00001') { excavM3 = boqRows[i].qty || 0; break; }
          }
        }
        if (excavM3) {
          capacityText = STATE.isMetric
            ? excavM3.toFixed(1) + ' m\u00b3'
            : (excavM3 * 35.3147).toFixed(1) + ' ft\u00b3';
        }
      } catch (e) {}
    }

    return {
      texts: {
        total:    totalText    || '',
        material: materialText || '',
        execution: execText    || '',
        time:     timeText     || '',
        capacity: capacityText || ''
      },
      materials: { count: 0, value: 0 },
      execution: { count: 0, value: 0, hours: 0 },
      total:     0,
      area:      computeArea(),
      perimeter: computePerimeter(),
      currency:  (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || 'USD'
    };
  }

  function _buildBaseDimensions() {
    var poly = getFootprintPolygon();
    var bb = getBBox();
    var edges = [];
    for (var i = 0; i < poly.length; i++) {
      var a = poly[i], b = poly[(i + 1) % poly.length];
      var dx = b[0] - a[0], dz = b[1] - a[1];
      edges.push({
        index: i,
        a: { x: a[0], z: a[1] },
        b: { x: b[0], z: b[1] },
        length: Math.sqrt(dx * dx + dz * dz)
      });
    }
    return {
      vertices: poly.map(function (p) { return { x: p[0], z: p[1] }; }),
      edges:    edges,
      bboxL:    bb.L,
      bboxW:    bb.W
    };
  }

  function _buildShareUrl() {
    var boot = global.BASE_BOOT || {};
    var handle = boot.productHandle || 'foundations';
    var origin = (location && location.origin) || '';
    var url = origin + '/products/' + handle;
    if (boot.variants && boot.variants.length && STATE.variant) {
      var v = boot.variants[STATE.variant - 1];
      if (v && v.id) url += '?variant=' + v.id;
    }
    return url;
  }

  function _variantDisplayName() {
    var boot = global.BASE_BOOT || {};
    var T = boot.i18n || {};
    var key = 'fence_v' + STATE.variant;
    return T[key] || _currentVariantKey();
  }


/* ===== ZONE A -- replace from here to the END of the file ============ */

  /* -------------------------------------------------------------------------
     WHAT THIS PROJECT IS, FOR THE ASSISTANT.

     A FENCE IS A LINE, NOT A BUILDING. It has no floor area, no roof and no
     rooms, and the block that describes a project to the assistant was
     written against a house - so none of those are declared. What a fence has
     is a run, a height, a way of filling the space between the posts and the
     gaps left for gates, and that is what goes in.

     THE AREA IT ENCLOSES IS NOT ITS SIZE. A fence round a hectare and a fence
     round a garden cost by the metre of run, not by what is inside them, and
     an assistant handed a square metre figure would reach for it. The run is
     stated and the enclosed area is stated as what it is - the land inside,
     which nothing in this budget is priced by.

     computeArea and computePerimeter are the lib's own, so the assistant and
     the bill of quantities measure the same fence.
     ------------------------------------------------------------------------- */
  var FENCE_VARIANT_NAMES = {
    wood: 'timber fence',
    vinyl: 'vinyl fence',
    concrete_blocks: 'concrete block wall',
    chain_link: 'chain link fence',
    steel_panel: 'steel panel fence',
    gabion: 'gabion wall',
    concrete_panel: 'precast concrete panel fence'
  };

  var FENCE_STYLE_NAMES = {
    privacy: 'privacy - boards touching, no gaps',
    spaced: 'spaced - a small gap between boards for ventilation',
    shadow_box: 'shadow box - boards alternating on both sides of the rails, the same from either side',
    picket: 'picket - a wide decorative gap',
    open_board: 'open board - widely spaced boards'
  };

  function _factFenceBox() {
    var P = [], i;
    try { P = getFootprintPolygon() || []; } catch (e) { P = []; }
    if (P.length < 2) return null;
    var x0 = P[0][0], x1 = P[0][0], z0 = P[0][1], z1 = P[0][1];
    for (i = 1; i < P.length; i++) {
      x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
      z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
    }
    return { w: Math.round((x1 - x0) * 100) / 100, l: Math.round((z1 - z0) * 100) / 100, corners: P.length };
  }

  function _factGates() {
    var G = STATE.gates || [], i, out = [], w;
    for (i = 0; i < G.length; i++) {
      w = Math.round((Number(G[i].width) || 0) * 100) / 100;
      out.push(w + ' m');
    }
    return out;
  }

  function fenceFacts() {
    var S = STATE, box = _factFenceBox();
    var run = 0, area = 0, height = 0, vKey = '';
    try { run = computePerimeter(); } catch (e) { run = 0; }
    try { area = computeArea(); } catch (e) { area = 0; }
    try { height = getCurrentHeight(); } catch (e) { height = 0; }
    try { vKey = variantKey(); } catch (e) { vKey = ''; }
    var range = null;
    try { range = getCurrentHeightRange(); } catch (e) { range = null; }

    var gates = _factGates();
    var gateLen = 0, gi;
    for (gi = 0; gi < (S.gates || []).length; gi++) gateLen += Number(S.gates[gi].width) || 0;

    var extra = [];
    extra.push('this project is a fence, not a building: there is no roof, no storeys, no rooms and no floor area, and everything in the budget is priced by the metre of run or by the post.');
    extra.push('the run is ' + (Math.round(run * 10) / 10) + ' metres round '
      + (box ? (box.corners + ' corners') : 'the outline')
      + ', standing ' + (Math.round(height * 100) / 100) + ' metres high'
      + (range ? (' - the height can be set between ' + (Math.round(range.min * 100) / 100)
        + ' and ' + (Math.round(range.max * 100) / 100) + ' metres') : '') + '.');
    extra.push('it is a ' + (FENCE_VARIANT_NAMES[vKey] || vKey || 'fence')
      + (vKey === 'wood' ? (', in the ' + (FENCE_STYLE_NAMES[S.woodStyle] || S.woodStyle || 'privacy') + ' style') : '')
      + '. The systems on offer are: timber, vinyl, concrete blocks, chain link, steel panel, gabion and precast concrete panel.');
    if (gates.length){
      extra.push(gates.length + ' gap(s) left for gates: ' + gates.join(', ')
        + ' - ' + (Math.round(gateLen * 10) / 10) + ' metres of the run in total. A gate itself is not in this budget; the gap and the posts either side of it are.');
    } else {
      extra.push('no gaps for gates are set, so the run is continuous. That is worth mentioning - most fences need at least one way in.');
    }
    extra.push('the enclosed area is ' + (Math.round(area * 10) / 10)
      + ' square metres. That is the land inside the fence and nothing in this budget is priced by it - a fence costs by its run, so never treat this figure as the size of the job.');
    if (S.postSpacingOverride){
      extra.push('post spacing has been set by hand to ' + (Math.round(S.postSpacingOverride * 100) / 100)
        + ' metres rather than left at the default for this system.');
    }
    if (S.slopeActive){
      extra.push('the ground slopes ' + (Math.round((S.slopeDeg || 0) * 10) / 10)
        + ' degrees along the line, so the panels step or rake with it.');
    }

    return {
      product: 'fence',
      version: VERSION,
      units: S.isMetric ? 'metric' : 'imperial',
      width: box ? box.w : 0,
      length: box ? box.l : 0,
      extra: extra,
      site: { address: String(S.address || '') },
      changeable: [
        'the line of the fence - every corner can be dragged, and corners can be added or removed',
        'the height, within the range stated above',
        'the system - timber, vinyl, concrete blocks, chain link, steel panel, gabion or precast concrete panel',
        'for a timber fence, the style of the boarding from the five listed above',
        'gaps for gates - where they sit on the run and how wide they are',
        'the spacing of the posts, within what the chosen system allows',
        'the slope of the ground the fence follows',
        'metric or imperial display'
      ],
      fixed: [
        'a roof, storeys, rooms or a floor area - this is a fence and none of those exist on it',
        'the gate itself - the configurator leaves the opening and the posts either side; the leaf, the hardware and any automation are not in this budget',
        'retaining walls - this fence stands on the ground, it does not hold ground back',
        'mixing two systems on one run: the whole fence is one system at a time'
      ]
    };
  }

/* ===== ZONE A -- replace from here to the END of the file ============ */

  /* -------------------------------------------------------------------------
     WHAT THIS PROJECT IS, FOR THE ASSISTANT.

     A FENCE IS A LINE, NOT A BUILDING. It has no floor area, no roof and no
     rooms, and the block that describes a project to the assistant was
     written against a house - so none of those are declared. What a fence has
     is a run, a height, a way of filling the space between the posts and the
     gaps left for gates, and that is what goes in.

     THE AREA IT ENCLOSES IS NOT ITS SIZE. A fence round a hectare and a fence
     round a garden cost by the metre of run, not by what is inside them, and
     an assistant handed a square metre figure would reach for it. The run is
     stated and the enclosed area is stated as what it is - the land inside,
     which nothing in this budget is priced by.

     computeArea and computePerimeter are the lib's own, so the assistant and
     the bill of quantities measure the same fence.
     ------------------------------------------------------------------------- */
  var FENCE_VARIANT_NAMES = {
    wood: 'timber fence',
    vinyl: 'vinyl fence',
    concrete_blocks: 'concrete block wall',
    chain_link: 'chain link fence',
    steel_panel: 'steel panel fence',
    gabion: 'gabion wall',
    concrete_panel: 'precast concrete panel fence'
  };

  var FENCE_STYLE_NAMES = {
    privacy: 'privacy - boards touching, no gaps',
    spaced: 'spaced - a small gap between boards for ventilation',
    shadow_box: 'shadow box - boards alternating on both sides of the rails, the same from either side',
    picket: 'picket - a wide decorative gap',
    open_board: 'open board - widely spaced boards'
  };

  function _factFenceBox() {
    var P = [], i;
    try { P = getFootprintPolygon() || []; } catch (e) { P = []; }
    if (P.length < 2) return null;
    var x0 = P[0][0], x1 = P[0][0], z0 = P[0][1], z1 = P[0][1];
    for (i = 1; i < P.length; i++) {
      x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
      z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
    }
    return { w: Math.round((x1 - x0) * 100) / 100, l: Math.round((z1 - z0) * 100) / 100, corners: P.length };
  }

  function _factGates() {
    var G = STATE.gates || [], i, out = [], w;
    for (i = 0; i < G.length; i++) {
      w = Math.round((Number(G[i].width) || 0) * 100) / 100;
      out.push(w + ' m');
    }
    return out;
  }

  function fenceFacts() {
    var S = STATE, box = _factFenceBox();
    var run = 0, area = 0, height = 0, vKey = '';
    try { run = computePerimeter(); } catch (e) { run = 0; }
    try { area = computeArea(); } catch (e) { area = 0; }
    try { height = getCurrentHeight(); } catch (e) { height = 0; }
    try { vKey = variantKey(); } catch (e) { vKey = ''; }
    var range = null;
    try { range = getCurrentHeightRange(); } catch (e) { range = null; }

    var gates = _factGates();
    var gateLen = 0, gi;
    for (gi = 0; gi < (S.gates || []).length; gi++) gateLen += Number(S.gates[gi].width) || 0;

    var extra = [];
    extra.push('this project is a fence, not a building: there is no roof, no storeys, no rooms and no floor area, and everything in the budget is priced by the metre of run or by the post.');
    extra.push('the run is ' + (Math.round(run * 10) / 10) + ' metres round '
      + (box ? (box.corners + ' corners') : 'the outline')
      + ', standing ' + (Math.round(height * 100) / 100) + ' metres high'
      + (range ? (' - the height can be set between ' + (Math.round(range.min * 100) / 100)
        + ' and ' + (Math.round(range.max * 100) / 100) + ' metres') : '') + '.');
    extra.push('it is a ' + (FENCE_VARIANT_NAMES[vKey] || vKey || 'fence')
      + (vKey === 'wood' ? (', in the ' + (FENCE_STYLE_NAMES[S.woodStyle] || S.woodStyle || 'privacy') + ' style') : '')
      + '. The systems on offer are: timber, vinyl, concrete blocks, chain link, steel panel, gabion and precast concrete panel.');
    if (gates.length){
      extra.push(gates.length + ' gap(s) left for gates: ' + gates.join(', ')
        + ' - ' + (Math.round(gateLen * 10) / 10) + ' metres of the run in total. A gate itself is not in this budget; the gap and the posts either side of it are.');
    } else {
      extra.push('no gaps for gates are set, so the run is continuous. That is worth mentioning - most fences need at least one way in.');
    }
    extra.push('the enclosed area is ' + (Math.round(area * 10) / 10)
      + ' square metres. That is the land inside the fence and nothing in this budget is priced by it - a fence costs by its run, so never treat this figure as the size of the job.');
    if (S.postSpacingOverride){
      extra.push('post spacing has been set by hand to ' + (Math.round(S.postSpacingOverride * 100) / 100)
        + ' metres rather than left at the default for this system.');
    }
    if (S.slopeActive){
      extra.push('the ground slopes ' + (Math.round((S.slopeDeg || 0) * 10) / 10)
        + ' degrees along the line, so the panels step or rake with it.');
    }

    return {
      product: 'fence',
      version: VERSION,
      units: S.isMetric ? 'metric' : 'imperial',
      width: box ? box.w : 0,
      length: box ? box.l : 0,
      extra: extra,
      site: { address: String(S.address || '') },
      changeable: [
        'the line of the fence - every corner can be dragged, and corners can be added or removed',
        'the height, within the range stated above',
        'the system - timber, vinyl, concrete blocks, chain link, steel panel, gabion or precast concrete panel',
        'for a timber fence, the style of the boarding from the five listed above',
        'gaps for gates - where they sit on the run and how wide they are',
        'the spacing of the posts, within what the chosen system allows',
        'the slope of the ground the fence follows',
        'metric or imperial display'
      ],
      fixed: [
        'a roof, storeys, rooms or a floor area - this is a fence and none of those exist on it',
        'the gate itself - the configurator leaves the opening and the posts either side; the leaf, the hardware and any automation are not in this budget',
        'retaining walls - this fence stands on the ground, it does not hold ground back',
        'mixing two systems on one run: the whole fence is one system at a time'
      ]
    };
  }

  global.BaseLib = {
    __version:        VERSION,
    /* WHAT THIS PROJECT IS. Read by the AI drawer, which asks every lib for
       facts() and otherwise reads STATE as though it were a house. */
    facts:            fenceFacts,
    exportPdf:        exportPdf,
    STATE:            STATE,
    DEFAULTS:         DEFAULTS,
    VARIANT_DEFAULTS: VARIANT_DEFAULTS,
    boot:             boot,
    refreshUI:        refreshUI,
    reset:            reset,
    undo:             undo,
    cycleLvl:         cycleLvl,
    cycleAssembly:    cycleAssembly,
    UndoHistory:      _UndoHistory,
    toggleUnits:      toggleUnits,
    toggleView:       toggleView,
    selectVariant:    selectVariant,
    nextVariant:      nextVariant,
    prevVariant:      prevVariant,
    setWoodStyle:     setWoodStyle,
    getWoodStyle:     getWoodStyle,
    cycleWoodStyle:   cycleWoodStyle,
    setWoodHeight:    setWoodHeight,
    getCurrentHeight: getCurrentHeight,
    openBudgetDrawer: openBudgetDrawer,
    share:            share,
    publishBoQToBudget: publishBoQToBudget,
    computeBoQ:       computeBoQ,
    getFootprintPolygon: getFootprintPolygon,
    computeArea:      computeArea,
    computePerimeter: computePerimeter,
    VisualizationManager: _VisualizationManager,
    CornerInserter:   _CornerInserter,
    AssetDrawer:      _AssetDrawer,
    ProjectStore:     _ProjectStore
  };

  /* Public alias -- fence configurator uses the same engine as base.
     Other code (status registry, AR worker calls, share generation,
     BoQ pipeline) keys on window.BaseLib so we keep that name as the
     primary export. FenceLib is an additional alias so future
     fence-specific extensions have a clean namespace. */
  global.FenceLib = global.BaseLib;

})(window);